import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import SignatureCanvas from 'react-signature-canvas';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
// NMI card capture is live again (dual gateway, 2026-10-12). Collect.js is
// loaded by this component only when NMI is the active processor, so a
// Stripe-routed checkout still never fetches it.
import NmiCardForm from './NmiCardForm';

// Initialize Stripe - loads from environment variable
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const API_URL = '';
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ==================== REFERRAL LINK CAPTURE ====================
// A ?ref= code can arrive on any page, but the person may not book or complete
// intake in the same visit — so it is persisted rather than read from the URL at
// submit time. Every read/write is guarded: storage throws in private mode and
// some embedded browsers, and a referral must never break a booking.
const REFERRAL_STORAGE_KEY = 'ascent_ref_code';
function captureReferralFromUrl() {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (!code) return null;
    const clean = code.trim().toUpperCase().slice(0, 64);
    if (!clean) return null;
    // First code wins: if someone arrives via one friend's link and later
    // another's, the original introduction is the one that stands.
    try {
      if (!window.localStorage.getItem(REFERRAL_STORAGE_KEY)) {
        window.localStorage.setItem(REFERRAL_STORAGE_KEY, clean);
      }
    } catch (_) { /* storage unavailable — fall through to the in-URL value */ }
    return clean;
  } catch (_) { return null; }
}
function getStoredReferralCode() {
  try { return window.localStorage.getItem(REFERRAL_STORAGE_KEY) || null; } catch (_) { return null; }
}
function clearStoredReferralCode() {
  try { window.localStorage.removeItem(REFERRAL_STORAGE_KEY); } catch (_) {}
}

const api = {
  async fetch(endpoint, options = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, { ...options, credentials: 'include', headers: { 'Content-Type': 'application/json', ...options.headers } });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      const err = new Error(error.error || 'Request failed');
      // Carry the machine-readable parts through. Callers that only read
      // `message` are unaffected; the ones that need to branch on an outcome
      // (a blocked product, an unavailable gateway) can now do so.
      err.status = response.status;
      err.code = error.code || null;
      err.body = error;
      throw err;
    }
    return response.json();
  },
  get: (endpoint) => api.fetch(endpoint),
  post: (endpoint, data, headers) => api.fetch(endpoint, { method: 'POST', body: JSON.stringify(data), ...(headers ? { headers } : {}) }),
  put: (endpoint, data) => api.fetch(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  patch: (endpoint, data) => api.fetch(endpoint, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (endpoint) => api.fetch(endpoint, { method: 'DELETE' }),
};

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => { const timer = setTimeout(onClose, 3000); return () => clearTimeout(timer); }, [onClose]);
  return <div className={`toast ${type}`}>{type === 'success' ? '✓' : '⚠️'} {message}</div>;
};

// Searchable dropdown component for patient/client selection
const SearchableSelect = ({ options, value, onChange, placeholder = 'Search...', disabled = false, displayKey = 'name', valueKey = 'id', renderOption }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = options.filter(opt => {
    const searchLower = search.toLowerCase();
    const name = (opt[displayKey] || '').toLowerCase();
    const email = (opt.email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  const selectedOption = options.find(opt => opt[valueKey] === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setHighlightIndex(0); }, [search]);

  const handleKeyDown = (e) => {
    if (!isOpen) { if (e.key === 'ArrowDown' || e.key === 'Enter') setIsOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && filtered[highlightIndex]) { e.preventDefault(); handleSelect(filtered[highlightIndex]); }
    else if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
  };

  const handleSelect = (opt) => {
    onChange(opt[valueKey], opt);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearch('');
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="searchable-select" style={{ position: 'relative' }}>
      <div className="searchable-select-input-wrapper" style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          className="form-input"
          placeholder={selectedOption ? (renderOption ? renderOption(selectedOption) : `${selectedOption[displayKey]}${selectedOption.email ? ` (${selectedOption.email})` : ''}`) : placeholder}
          value={isOpen ? search : ''}
          onChange={e => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{ paddingRight: value ? '30px' : '12px' }}
        />
        {value && !disabled && (
          <button type="button" onClick={handleClear} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '16px', padding: '4px' }}>×</button>
        )}
      </div>
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown" style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '200px', overflowY: 'auto', background: 'white', border: '1px solid var(--border-color)', borderRadius: '8px', marginTop: '4px', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>No matches found</div>
          ) : (
            filtered.slice(0, 50).map((opt, i) => (
              <div
                key={opt[valueKey]}
                onClick={() => handleSelect(opt)}
                style={{ padding: '10px 12px', cursor: 'pointer', backgroundColor: i === highlightIndex ? 'var(--cream)' : 'white', borderBottom: '1px solid var(--border-color)' }}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                {renderOption ? renderOption(opt) : <><strong>{opt[displayKey]}</strong>{opt.email && <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>{opt.email}</span>}</>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Icons = {
  Dashboard: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>,
  Services: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>,
  History: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Users: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
  Wallet: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
  Products: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
  MyInfo: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
  Settings: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  Calendar: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  AuditLog: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  Clipboard: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
  Prescription: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>,
  Nutrition: () => <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const checkAuth = useCallback(async () => { try { setUser(await api.get('/api/auth/me')); } catch (error) { setUser(null); } finally { setLoading(false); } }, []);
  useEffect(() => { checkAuth(); }, [checkAuth]);
  const login = async (email, password) => { const { user } = await api.post('/api/auth/login', { email, password }); setUser(user); return user; };
  const logout = async () => { await api.post('/api/auth/logout'); setUser(null); };
  const updateUserBalance = (newBalance) => { setUser(prev => ({ ...prev, balance: newBalance })); };
  const hasSection = useCallback((section) => {
    if (!user || user.role !== 'admin') return false;
    if (!user.allowedSections) return true; // No role assigned = full access
    return user.allowedSections.includes(section);
  }, [user]);
  return <AuthContext.Provider value={{ user, loading, login, logout, updateUserBalance, refreshUser: checkAuth, hasSection }}>{children}</AuthContext.Provider>;
}

function LoginPage() {
  const [email, setEmail] = useState(''); const [password, setPassword] = useState(''); const [error, setError] = useState(''); const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth(); const navigate = useNavigate();
  const getAdminLandingPage = (u) => {
    if (!u.allowedSections) return '/admin/users';
    const sectionToPath = { patients: '/admin/users', scheduling: '/admin/calendar', commerce: '/admin/products', analytics: '/admin/metrics', patient_board: '/admin/patient-board', patient_sources: '/admin/patient-sources', reminders: '/admin/reminders', ghl: '/admin/campaigns', settings: '/admin/admins', audit_log: '/admin/audit-log', api_keys: '/admin/api-keys', guide: '/admin/guide' };
    for (const s of u.allowedSections) { if (sectionToPath[s]) return sectionToPath[s]; }
    return '/admin/guide';
  };
  const handleSubmit = async (e) => { e.preventDefault(); setError(''); setIsLoading(true); try { const user = await login(email, password); navigate(user.role === 'admin' ? getAdminLandingPage(user) : '/dashboard'); } catch (err) { setError(err.message || 'Invalid email or password'); } finally { setIsLoading(false); } };
  return (
    <div className="login-page"><div className="login-card">
      <div className="login-logo"><h1>Ascent Trim</h1><span>Patient Portal</span></div>
      <h2 className="login-title">Welcome Back</h2>
      {error && <div className="error-message"><span>⚠️</span> {error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group"><label className="form-label">Email Address</label><input type="email" className="form-input" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} /></div>
        <div className="form-group"><label className="form-label">Password</label><input type="password" className="form-input" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isLoading} /></div>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>{isLoading ? 'Signing in...' : 'Sign In'}</button>
      </form>
      <div className="login-intake-link"><p>New patient? <Link to="/intake">Complete your intake form</Link></p><p style={{marginTop: "8px"}}><Link to="/book">Book an appointment</Link></p></div>
    </div></div>
  );
}

// Intake form helper components - defined outside to prevent re-renders
const IntakeCheckboxGroup = ({ options, field, other = false, formData, toggleArrayField, updateField }) => (
  <div className="checkbox-group">
    {options.map(opt => (<label key={opt} className="checkbox-label"><input type="checkbox" checked={(formData[field] || []).includes(opt)} onChange={() => toggleArrayField(field, opt)} /><span>{opt}</span></label>))}
    {other && <label className="checkbox-label"><input type="checkbox" checked={(formData[field] || []).includes('Other')} onChange={() => toggleArrayField(field, 'Other')} /><span>Other</span></label>}
    {other && (formData[field] || []).includes('Other') && <input type="text" className="form-input" style={{marginTop: '8px'}} placeholder="Please specify..." value={formData[field + 'Other'] || ''} onChange={e => updateField(field + 'Other', e.target.value)} />}
  </div>
);
const IntakeRadioGroup = ({ options, field, formData, updateField }) => (
  <div className="checkbox-group">{options.map(opt => (<label key={opt} className="checkbox-label"><input type="radio" name={field} checked={formData[field] === opt} onChange={() => updateField(field, opt)} /><span>{opt}</span></label>))}</div>
);

// Masked date input - auto-formats as MM/DD/YYYY
// A date of birth is only acceptable as a complete ISO date that is a real
// calendar day and a plausible birth date. Returns null when valid, otherwise a
// message for the patient. Mirrored server-side — the client message is the
// helpful one, the server check is the one that protects the database.
function dobError(value) {
  if (!value) return 'Please enter your date of birth';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return 'Please enter your date of birth as MM/DD/YYYY';
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  // Catches Feb 30 and similar: JS rolls them into the next month.
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    return 'That date does not exist — please check the month and day';
  }
  const today = new Date();
  if (dt.getTime() > today.getTime()) return 'Date of birth cannot be in the future';
  if (y < 1900) return 'Please check the year of birth';
  return null;
}

const MaskedDateInput = ({ value, onChange, className, placeholder = "MM/DD/YYYY" }) => {
  // Convert ISO date (YYYY-MM-DD) to display format (MM/DD/YYYY)
  const toDisplay = (isoDate) => {
    if (!isoDate) return '';
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return `${match[2]}/${match[3]}/${match[1]}`;
    return isoDate;
  };
  // Convert display format to ISO for storage
  const toISO = (display) => {
    const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) return `${match[3]}-${match[1]}-${match[2]}`;
    return '';
  };
  const handleChange = (e) => {
    let input = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (input.length > 8) input = input.slice(0, 8);
    // Guard the month and day at the point of typing. A first month digit above
    // 1, or a first day digit above 3, cannot start a valid value, so pad it:
    // typing "5" for the month becomes "05" rather than allowing "51".
    // This is what let "51/35/9" reach the database and 500 a real signup.
    if (input.length >= 1 && input[0] > '1') input = '0' + input.slice(0, 7);
    if (input.length >= 3 && input[2] > '3') input = input.slice(0, 2) + '0' + input.slice(2, 7);
    // Clamp the completed month and day to real ranges.
    if (input.length >= 2) {
      const mm = Math.min(Math.max(parseInt(input.slice(0, 2), 10) || 1, 1), 12);
      input = String(mm).padStart(2, '0') + input.slice(2);
    }
    if (input.length >= 4) {
      const dd = Math.min(Math.max(parseInt(input.slice(2, 4), 10) || 1, 1), 31);
      input = input.slice(0, 2) + String(dd).padStart(2, '0') + input.slice(4);
    }
    // Auto-format with slashes
    let formatted = '';
    if (input.length > 0) formatted = input.slice(0, 2);
    if (input.length > 2) formatted += '/' + input.slice(2, 4);
    if (input.length > 4) formatted += '/' + input.slice(4, 8);
    // Store as ISO only when complete. A partial entry is still passed up so the
    // field shows what was typed, but isValidDob() below rejects it at submit —
    // a half-finished date must never reach the server as a date value.
    if (input.length === 8) {
      onChange(toISO(formatted));
    } else {
      onChange(formatted);
    }
  };
  return <input type="text" className={className} value={toDisplay(value) || value} onChange={handleChange} placeholder={placeholder} maxLength={10} />;
};

function IntakeFormPage() {
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [success, setSuccess] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const totalSteps = 8;

  const [formData, setFormData] = useState({
    // Account
    email: '', password: '', confirmPassword: '',
    // Contact Info
    firstName: '', lastName: '', streetAddress: '', city: '', state: '', country: '', postalCode: '',
    phone: '', gender: '', dateOfBirth: '', preferredCommunication: [],
    // Emergency & Physical
    emergencyContactName: '', emergencyContactPhone: '', currentWeight: '', heightInches: '',
    // How heard about us
    howHeardAboutUs: '',
    // Goals
    whatBringsYou: [], whatBringsYouOther: '', primaryGoal: '', primaryGoalOther: '',
    previouslyTriedWeightLoss: null, previousWeightLossDetails: '',
    weightLossChallenges: [], weightLossChallengesOther: '',
    // Lifestyle
    nutritionPatterns: [], mealsPerDay: '', dailyWaterIntake: '',
    physicalActivityLevel: '', physicalActivityTypes: [], physicalActivityOther: '',
    sleepQuality: '', avgSleepHours: '', alcoholUse: '', stressLevel: '', supportAtHome: '',
    // Medical History
    metabolicHealth: [], cardiovascular: [], autoimmuneConditions: [], giConditions: [],
    kidneyHealth: [], mentalHealth: [], otherDiagnoses: '',
    // Surgical
    surgicalHistory: [], surgicalHistoryOther: '',
    // Medications
    currentMedications: [], usesSupplements: '', supplementsList: '', hasAllergies: '', allergiesList: '',
    // Family
    familyHistory: [],
    // Pregnancy
    pregnancyStatus: '',
    // Treatment Interests
    treatmentInterests: [],
    // Consent
    consentMessages: false, consentAccuracy: false, consentNoRefundRx: false, signatureData: ''
  });

  const clearSignature = () => { if (signatureRef.current) { signatureRef.current.clear(); setSignatureEmpty(true); updateField('signatureData', ''); } };
  const handleSignatureEnd = () => { if (signatureRef.current && !signatureRef.current.isEmpty()) { setSignatureEmpty(false); updateField('signatureData', signatureRef.current.toDataURL('image/png')); } };
  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.email || !formData.firstName || !formData.lastName || !formData.password || !formData.confirmPassword || !formData.phone || !formData.dateOfBirth) {
        setToast({ message: 'Please fill in all required fields', type: 'error' }); return false;
      }
      { const e = dobError(formData.dateOfBirth); if (e) { setToast({ message: e, type: 'error' }); return false; } }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setToast({ message: 'Please enter a valid email address', type: 'error' }); return false; }
      if (formData.password.length < 6) { setToast({ message: 'Password must be at least 6 characters', type: 'error' }); return false; }
      if (formData.password !== formData.confirmPassword) { setToast({ message: 'Passwords do not match', type: 'error' }); return false; }
    }
    return true;
  };

  const nextStep = () => { if (validateStep(step)) setStep(s => Math.min(s + 1, totalSteps)); };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!formData.consentAccuracy) { setToast({ message: 'You must certify the accuracy of your information', type: 'error' }); return; }
    if (!formData.consentNoRefundRx) { setToast({ message: 'You must acknowledge that prescription medications are non-refundable', type: 'error' }); return; }
    if (signatureEmpty || !formData.signatureData) { setToast({ message: 'Please provide your signature', type: 'error' }); return; }
    setIsSubmitting(true);
    try {
      // Attach the referral code captured from a ?ref= link, if any. Sent
      // alongside the form rather than stored on it, so a failed submit that the
      // patient retries still carries the attribution.
      const response = await fetch('/api/intake', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...formData, referralCode: getStoredReferralCode() }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Submission failed');
      setSuccess(true);
    } catch (error) { setToast({ message: error.message, type: 'error' }); }
    finally { setIsSubmitting(false); }
  };

  if (success) {
    return (<div className="login-page"><div className="login-card" style={{ textAlign: 'center' }}><div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div><h2 style={{ marginBottom: '8px' }}>Account Created!</h2><p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Your intake form has been submitted successfully.</p><button className="btn btn-gold" onClick={() => navigate('/login')}>Go to Login</button></div></div>);
  }

  const stepTitles = ['Contact Info', 'Goals', 'Lifestyle', 'Medical History', 'Surgical History', 'Medications', 'Family & Status', 'Consent'];
  // Helper to pass common props to checkbox/radio groups
  const CheckboxGroup = (props) => <IntakeCheckboxGroup {...props} formData={formData} toggleArrayField={toggleArrayField} updateField={updateField} />;
  const RadioGroup = (props) => <IntakeRadioGroup {...props} formData={formData} updateField={updateField} />;

  return (
    <div className="login-page">
      <div className="intake-card">
        <div className="login-logo"><h1>Ascent Trim</h1><span>New Patient Intake Form</span></div>
        <div className="intake-progress-bar"><div className="progress-fill" style={{width: `${(step/totalSteps)*100}%`}}></div></div>
        <div className="intake-step-indicator">Step {step} of {totalSteps}: {stepTitles[step-1]}</div>

        <div className="intake-form">
          {step === 1 && (
            <div className="intake-step">
              <h3>Contact Information</h3>
              <div className="intake-form-grid">
                <div className="form-group"><label className="form-label">First Name *</label><input type="text" className="form-input" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Last Name *</label><input type="text" className="form-input" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} /></div>
                <div className="form-group full-width"><label className="form-label">Street Address</label><input type="text" className="form-input" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">City</label><input type="text" className="form-input" value={formData.city} onChange={e => updateField('city', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">State</label><input type="text" className="form-input" value={formData.state} onChange={e => updateField('state', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Country</label><input type="text" className="form-input" value={formData.country} onChange={e => updateField('country', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Postal Code</label><input type="text" className="form-input" value={formData.postalCode} onChange={e => updateField('postalCode', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Email *</label><input type="email" className="form-input" value={formData.email} onChange={e => updateField('email', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Phone *</label><input type="tel" className="form-input" value={formData.phone} onChange={e => updateField('phone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Gender</label><select className="form-input" value={formData.gender} onChange={e => updateField('gender', e.target.value)}><option value="">Select...</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option></select></div>
                <div className="form-group"><label className="form-label">Date of Birth *</label><MaskedDateInput className="form-input" value={formData.dateOfBirth} onChange={v => updateField('dateOfBirth', v)} /></div>
                <div className="form-group full-width"><label className="form-label">Preferred Method of Communication</label><CheckboxGroup options={['Text', 'Email', 'Phone Call']} field="preferredCommunication" /></div>
                <div className="form-group"><label className="form-label">Emergency Contact Name *</label><input type="text" className="form-input" value={formData.emergencyContactName} onChange={e => updateField('emergencyContactName', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Emergency Contact Phone *</label><input type="tel" className="form-input" value={formData.emergencyContactPhone} onChange={e => updateField('emergencyContactPhone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Current Weight (lbs) *</label><input type="number" className="form-input" value={formData.currentWeight} onChange={e => updateField('currentWeight', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Height (inches) *</label><input type="number" className="form-input" value={formData.heightInches} onChange={e => updateField('heightInches', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Password *</label><input type="password" className="form-input" value={formData.password} onChange={e => updateField('password', e.target.value)} placeholder="At least 6 characters" /></div>
                <div className="form-group"><label className="form-label">Confirm Password *</label><input type="password" className="form-input" value={formData.confirmPassword} onChange={e => updateField('confirmPassword', e.target.value)} /></div>
                <div className="form-group full-width"><label className="form-label">How did you hear about us?</label><select className="form-input" value={formData.howHeardAboutUs} onChange={e => updateField('howHeardAboutUs', e.target.value)}><option value="">Select...</option><option>Google Search</option><option>Facebook/Instagram</option><option>TikTok</option><option>Friend/Family Referral</option><option>Patient Referral</option><option>Doctor Referral</option><option>Local Event</option><option>TV/Radio</option><option>Newspaper/Magazine</option><option>Other</option></select></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="intake-step">
              <h3>Goals & Primary Concerns</h3>
              <div className="form-group"><label className="form-label">What brings you in today? (Check all that apply)</label><CheckboxGroup options={['Weight loss and metabolic support', 'Appetite control', 'Energy improvement', 'Hormone symptoms', 'Body recomposition', 'Lifestyle change coaching', 'Grief or sudden life change']} field="whatBringsYou" other /></div>
              <div className="form-group"><label className="form-label">What is your #1 goal for the next 90 days?</label><RadioGroup options={['Lose pounds', 'Lose inches', 'Prepare for an event', 'Improve long-term health', 'Other']} field="primaryGoal" />{formData.primaryGoal === 'Other' && <input type="text" className="form-input" style={{marginTop: '8px'}} placeholder="Please specify..." value={formData.primaryGoalOther} onChange={e => updateField('primaryGoalOther', e.target.value)} />}</div>
              <div className="form-group"><label className="form-label">Have you previously tried to lose weight?</label><RadioGroup options={['Yes', 'No']} field="previouslyTriedWeightLoss" /></div>
              {formData.previouslyTriedWeightLoss === 'Yes' && <div className="form-group"><label className="form-label">If yes, what did you use and what were your results?</label><textarea className="form-input" value={formData.previousWeightLossDetails} onChange={e => updateField('previousWeightLossDetails', e.target.value)} rows={3} /></div>}
              <p style={{color: 'var(--text-muted)', fontSize: '14px', fontStyle: 'italic', margin: '16px 0'}}>If you have a recent or active prescription for weight loss medications, please bring documentation with you to your scheduled consultation.</p>
              <div className="form-group"><label className="form-label">What has been your biggest challenge to weight loss? (Check all that apply)</label><CheckboxGroup options={['Sustaining meaningful results', 'Gaining back weight', 'Cravings', 'Irregular eating habits or schedule', 'Emotional eating', 'Motivation', 'Time constraints']} field="weightLossChallenges" other /></div>
            </div>
          )}

          {step === 3 && (
            <div className="intake-step">
              <h3>Current Lifestyle</h3>
              <div className="form-group"><label className="form-label">Nutrition pattern (Check all that apply)</label><CheckboxGroup options={['Structured/planned', 'Emotional eating', 'Skipping meals', 'High sugar or carb cravings']} field="nutritionPatterns" /></div>
              <div className="form-group"><label className="form-label">How many times per day do you eat?</label><RadioGroup options={['1', '2', '3', 'More than 3']} field="mealsPerDay" /></div>
              <div className="form-group"><label className="form-label">Daily Water Intake</label><RadioGroup options={['Less than 30 ounces', '30-60 ounces', '60-90 ounces', '90+ ounces']} field="dailyWaterIntake" /></div>
              <div className="form-group"><label className="form-label">Physical activity level</label><select className="form-input" value={formData.physicalActivityLevel} onChange={e => updateField('physicalActivityLevel', e.target.value)}><option value="">Select...</option><option>Sedentary</option><option>Lightly active</option><option>Moderately active</option><option>Very active</option></select></div>
              <div className="form-group"><label className="form-label">Type of physical activity (Check all that apply)</label><CheckboxGroup options={['HIIT', 'Cardio', 'Weightlifting', 'Strength training', 'Recreational aerobic activity']} field="physicalActivityTypes" other /></div>
              <div className="form-group"><label className="form-label">Sleep quality</label><select className="form-input" value={formData.sleepQuality} onChange={e => updateField('sleepQuality', e.target.value)}><option value="">Select...</option><option>Poor</option><option>Fair</option><option>Good</option><option>Excellent</option></select></div>
              <div className="form-group"><label className="form-label">Average hours of sleep per night</label><input type="text" className="form-input" value={formData.avgSleepHours} onChange={e => updateField('avgSleepHours', e.target.value)} placeholder="e.g., 7" /></div>
              <div className="form-group"><label className="form-label">Do you drink Alcohol?</label><select className="form-input" value={formData.alcoholUse} onChange={e => updateField('alcoholUse', e.target.value)}><option value="">Select...</option><option>Never</option><option>Rarely</option><option>Occasionally</option><option>Frequently</option></select></div>
              <div className="form-group"><label className="form-label">Stress Level</label><select className="form-input" value={formData.stressLevel} onChange={e => updateField('stressLevel', e.target.value)}><option value="">Select...</option><option>Low</option><option>Moderate</option><option>High</option><option>Very High</option></select></div>
              <div className="form-group"><label className="form-label">Level of support at home</label><select className="form-input" value={formData.supportAtHome} onChange={e => updateField('supportAtHome', e.target.value)}><option value="">Select...</option><option>Very supportive</option><option>Somewhat supportive</option><option>Neutral</option><option>Not supportive</option></select></div>
            </div>
          )}

          {step === 4 && (
            <div className="intake-step">
              <h3>Medical History</h3>
              <div className="form-group"><label className="form-label">Metabolic Health (check all that apply)</label><CheckboxGroup options={['Type 1 diabetes', 'Type 2 diabetes', 'Pre-diabetes/Insulin Resistance', 'Thyroid disease (Hypo/Hyper)', 'PCOS', 'High cholesterol', 'N/A']} field="metabolicHealth" /></div>
              <div className="form-group"><label className="form-label">Cardiovascular (Check all that apply)</label><CheckboxGroup options={['Hypertension (High Blood Pressure)', 'Heart Disease', 'Arrhythmia', 'Stroke or TIA', 'N/A']} field="cardiovascular" /></div>
              <div className="form-group"><label className="form-label">Autoimmune Conditions (Check all that apply)</label><CheckboxGroup options={['Rheumatoid arthritis', 'Lupus', 'Multiple sclerosis', "Hashimoto's", "Still's disease", 'Inflammatory bowel disease', 'N/A']} field="autoimmuneConditions" /></div>
              <div className="form-group"><label className="form-label">Gastrointestinal & GLP-1 Relevant Conditions (Check all that apply)</label><CheckboxGroup options={['Gallstone/Gallbladder disease', 'Pancreatitis (history of)', 'Gastroparesis', 'Severe GERD', 'Liver disease (NAFLD, hepatitis, cirrhosis)', 'Diverticulosis', 'Diverticulitis', 'N/A']} field="giConditions" /></div>
              <div className="form-group"><label className="form-label">Kidney/Other Organ Health (Check all that apply)</label><CheckboxGroup options={['Kidney disease', 'Dialysis', 'N/A']} field="kidneyHealth" /></div>
              <div className="form-group"><label className="form-label">Mental Health (Check all that apply)</label><CheckboxGroup options={['Anxiety', 'Depression', 'Eating disorder (history of)', 'OCD', 'N/A']} field="mentalHealth" /></div>
              <div className="form-group"><label className="form-label">Other diagnoses not listed above</label><input type="text" className="form-input" value={formData.otherDiagnoses} onChange={e => updateField('otherDiagnoses', e.target.value)} /></div>
            </div>
          )}

          {step === 5 && (
            <div className="intake-step">
              <h3>Surgical & Injury History</h3>
              <div className="form-group"><label className="form-label">Please check all surgeries you have had</label><CheckboxGroup options={['Brain surgery', 'Arm surgery', 'Leg surgery', 'Knee or ankle surgery', 'Hip surgery', 'Shoulder surgery', 'Stomach or abdominal surgery', 'Gallbladder removal', 'Appendectomy', 'Hernia repair', 'Elective cosmetic surgery', 'Organ-related surgery', 'Spine or back surgery', 'Pelvic or reproductive surgery', 'Orthopedic surgery (general)', 'None']} field="surgicalHistory" other /></div>
            </div>
          )}

          {step === 6 && (
            <div className="intake-step">
              <h3>Medications & Supplements</h3>
              <div className="form-group"><label className="form-label">Are you currently taking any of the following? (Check all that apply)</label><CheckboxGroup options={['Insulin', 'Metformin', 'Thyroid medications', 'Blood thinners', 'Steroids (oral or injectable)', 'Appetite suppressants', 'Hormone therapy (Bio-identical or synthetic)', 'None']} field="currentMedications" /></div>
              <div className="form-group"><label className="form-label">Do you use dietary supplements or vitamins?</label><select className="form-input" value={formData.usesSupplements} onChange={e => updateField('usesSupplements', e.target.value)}><option value="">Select...</option><option>Yes</option><option>No</option></select></div>
              {formData.usesSupplements === 'Yes' && <div className="form-group"><label className="form-label">If yes, please list</label><input type="text" className="form-input" value={formData.supplementsList} onChange={e => updateField('supplementsList', e.target.value)} /></div>}
              <div className="form-group"><label className="form-label">Do you have any known food or drug allergies?</label><select className="form-input" value={formData.hasAllergies} onChange={e => updateField('hasAllergies', e.target.value)}><option value="">Select...</option><option>Yes</option><option>No</option></select></div>
              {formData.hasAllergies === 'Yes' && <div className="form-group"><label className="form-label">If yes, please list</label><input type="text" className="form-input" value={formData.allergiesList} onChange={e => updateField('allergiesList', e.target.value)} /></div>}
            </div>
          )}

          {step === 7 && (
            <div className="intake-step">
              <h3>Family History & Status</h3>
              <div className="form-group"><label className="form-label">Do any immediate family members have any of the following conditions? (Check all that apply)</label><CheckboxGroup options={['Type 2 diabetes', 'Heart disease', 'High blood pressure', 'Stroke', 'Cancer', 'Thyroid disease']} field="familyHistory" /></div>
              <div className="form-group"><label className="form-label">Pregnancy & Breastfeeding Status</label><RadioGroup options={['Not pregnant or breastfeeding', 'Pregnant', 'Breastfeeding', 'Pregnant and breastfeeding']} field="pregnancyStatus" /></div>
              <div className="form-group"><label className="form-label">What treatments would you be interested in learning more about? (Check all that apply)</label><CheckboxGroup options={['Red light therapy', 'Peptide therapy', 'Body sculpting', 'Muscle building/toning (EMS stimulation)', 'Skin tightening', 'Natural weight loss solutions', 'Nutrition coaching', 'Weight Loss Medication']} field="treatmentInterests" /></div>
            </div>
          )}

          {step === 8 && (
            <div className="intake-step">
              <h3>Consent & Acknowledgement</h3>
              <div className="consent-box">
                <label className="consent-label"><input type="checkbox" checked={formData.consentMessages} onChange={e => updateField('consentMessages', e.target.checked)} /><span>By checking this box, I consent to receive transactional messages related to my account, orders, or services I have requested. These messages may include appointment reminders, order confirmations, and other transactional communications from Ascent Trim. Message and data rates may apply. Reply HELP for help or STOP to opt out.</span></label>
              </div>
              <div className="consent-box" style={{marginTop: '16px'}}>
                <label className="consent-label"><input type="checkbox" checked={formData.consentAccuracy} onChange={e => updateField('consentAccuracy', e.target.checked)} /><span>I certify that the information provided above is accurate and complete to the best of my knowledge. I understand that this information will be used to determine my eligibility for treatment and to create a personalized care plan.</span></label>
              </div>
              <div className="consent-box" style={{marginTop: '16px', borderLeft: '3px solid var(--accent-gold, #b8893b)'}}>
                <label className="consent-label"><input type="checkbox" checked={formData.consentNoRefundRx} onChange={e => updateField('consentNoRefundRx', e.target.checked)} /><span>I understand that <strong>prescription medications are non-refundable</strong>. Once a prescription medication has been dispensed to me, it cannot be returned, exchanged, or refunded for any reason, including if I discontinue treatment or experience side effects. *</span></label>
              </div>
              <div className="signature-section">
                <div className="signature-header"><label className="form-label">Patient Signature *</label><button type="button" className="btn btn-ghost btn-sm" onClick={clearSignature}>Clear</button></div>
                <div className="signature-pad-wrapper"><SignatureCanvas ref={signatureRef} canvasProps={{ className: 'signature-canvas' }} onEnd={handleSignatureEnd} backgroundColor="white" /></div>
                {signatureEmpty && <p className="signature-hint">Please sign above using your mouse or finger</p>}
              </div>
              <p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '16px'}}>Submission of this form is consent to receive communications automated and manual, to include AI generated calls and text from Ascent Trim & Wellness.</p>
            </div>
          )}

          <div className="intake-actions">
            {step > 1 && <button className="btn btn-secondary" onClick={prevStep} disabled={isSubmitting}>Back</button>}
            {step < totalSteps && <button className="btn btn-gold" onClick={nextStep}>Continue</button>}
            {step === totalSteps && <button className="btn btn-gold" onClick={handleSubmit} disabled={isSubmitting || !formData.consentAccuracy || !formData.consentNoRefundRx || signatureEmpty}>{isSubmitting ? 'Submitting...' : 'Submit Intake Form'}</button>}
          </div>
          <div className="intake-login-link"><p>Already have an account? <Link to="/login">Sign in</Link></p></div>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Patient submits a NEW intake form (already logged in)
function PatientIntakeFormPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const signatureRef = useRef(null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [success, setSuccess] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const totalSteps = 7; // No account creation step

  const [formData, setFormData] = useState({
    // Contact Info (pre-filled from user where possible)
    firstName: '', lastName: '', streetAddress: '', city: '', state: '', country: '', postalCode: '',
    phone: '', gender: '', dateOfBirth: '', preferredCommunication: [],
    // Emergency & Physical
    emergencyContactName: '', emergencyContactPhone: '', currentWeight: '', heightInches: '',
    // Goals
    whatBringsYou: [], whatBringsYouOther: '', primaryGoal: '', primaryGoalOther: '',
    previouslyTriedWeightLoss: null, previousWeightLossDetails: '',
    weightLossChallenges: [], weightLossChallengesOther: '',
    // Lifestyle
    nutritionPatterns: [], mealsPerDay: '', dailyWaterIntake: '',
    physicalActivityLevel: '', physicalActivityTypes: [], physicalActivityOther: '',
    sleepQuality: '', avgSleepHours: '', alcoholUse: '', stressLevel: '', supportAtHome: '',
    // Medical History
    metabolicHealth: [], cardiovascular: [], autoimmuneConditions: [], giConditions: [],
    kidneyHealth: [], mentalHealth: [], otherDiagnoses: '',
    // Surgical
    surgicalHistory: [], surgicalHistoryOther: '',
    // Medications
    currentMedications: [], usesSupplements: '', supplementsList: '', hasAllergies: '', allergiesList: '',
    // Family
    familyHistory: [],
    // Pregnancy
    pregnancyStatus: '',
    // Treatment Interests
    treatmentInterests: [],
    // Consent
    consentMessages: false, consentAccuracy: false, consentNoRefundRx: false, signatureData: ''
  });

  // Pre-fill name from user data
  useEffect(() => {
    if (user?.name) {
      const nameParts = user.name.split(' ');
      setFormData(prev => ({
        ...prev,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const clearSignature = () => { if (signatureRef.current) { signatureRef.current.clear(); setSignatureEmpty(true); updateField('signatureData', ''); } };
  const handleSignatureEnd = () => { if (signatureRef.current && !signatureRef.current.isEmpty()) { setSignatureEmpty(false); updateField('signatureData', signatureRef.current.toDataURL('image/png')); } };
  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.firstName || !formData.lastName || !formData.phone || !formData.dateOfBirth) {
        setToast({ message: 'Please fill in all required fields', type: 'error' }); return false;
      }
      { const e = dobError(formData.dateOfBirth); if (e) { setToast({ message: e, type: 'error' }); return false; } }
    }
    return true;
  };

  const nextStep = () => { if (validateStep(step)) setStep(s => Math.min(s + 1, totalSteps)); };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!formData.consentAccuracy) { setToast({ message: 'You must certify the accuracy of your information', type: 'error' }); return; }
    if (!formData.consentNoRefundRx) { setToast({ message: 'You must acknowledge that prescription medications are non-refundable', type: 'error' }); return; }
    if (signatureEmpty || !formData.signatureData) { setToast({ message: 'Please provide your signature', type: 'error' }); return; }
    setIsSubmitting(true);
    try {
      await api.post('/api/patient/intake', formData);
      setSuccess(true);
    } catch (error) { setToast({ message: error.message || 'Submission failed', type: 'error' }); }
    finally { setIsSubmitting(false); }
  };

  if (success) {
    return (
      <div>
        <div className="page-header"><h1 className="page-title">Intake Form Submitted</h1></div>
        <div className="section-card" style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', color: '#059669' }}>&#10003;</div>
          <h2>Thank You!</h2>
          <p style={{ color: 'var(--text-muted)', margin: '16px 0' }}>Your intake form has been submitted successfully. Our team will review it shortly.</p>
          <button className="btn btn-gold" onClick={() => navigate('/dashboard')}>Return to Dashboard</button>
        </div>
      </div>
    );
  }

  const stepTitles = ['Contact Info', 'Goals', 'Lifestyle', 'Medical History', 'Surgical History', 'Medications', 'Consent'];
  const CheckboxGroup = (props) => <IntakeCheckboxGroup {...props} formData={formData} toggleArrayField={toggleArrayField} updateField={updateField} />;
  const RadioGroup = (props) => <IntakeRadioGroup {...props} formData={formData} updateField={updateField} />;

  return (
    <div>
      <div className="page-header"><h1 className="page-title">New Intake Form</h1><p className="page-subtitle">Please complete all sections</p></div>
      <div className="section-card">
        <div className="intake-progress-bar" style={{ marginBottom: '16px' }}><div className="progress-fill" style={{width: `${(step/totalSteps)*100}%`}}></div></div>
        <div style={{ textAlign: 'center', marginBottom: '24px', color: 'var(--text-muted)' }}>Step {step} of {totalSteps}: {stepTitles[step-1]}</div>

        <div className="intake-form" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {step === 1 && (
            <div className="intake-step">
              <h3>Contact Information</h3>
              <div className="intake-form-grid">
                <div className="form-group"><label className="form-label">First Name *</label><input type="text" className="form-input" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Last Name *</label><input type="text" className="form-input" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} /></div>
                <div className="form-group full-width"><label className="form-label">Street Address</label><input type="text" className="form-input" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">City</label><input type="text" className="form-input" value={formData.city} onChange={e => updateField('city', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">State</label><input type="text" className="form-input" value={formData.state} onChange={e => updateField('state', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Country</label><input type="text" className="form-input" value={formData.country} onChange={e => updateField('country', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Postal Code</label><input type="text" className="form-input" value={formData.postalCode} onChange={e => updateField('postalCode', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Phone *</label><input type="tel" className="form-input" value={formData.phone} onChange={e => updateField('phone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Gender</label><select className="form-input" value={formData.gender} onChange={e => updateField('gender', e.target.value)}><option value="">Select...</option><option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option></select></div>
                <div className="form-group"><label className="form-label">Date of Birth *</label><MaskedDateInput className="form-input" value={formData.dateOfBirth} onChange={v => updateField('dateOfBirth', v)} /></div>
                <div className="form-group full-width"><label className="form-label">Preferred Method of Communication</label><CheckboxGroup options={['Text', 'Email', 'Phone Call']} field="preferredCommunication" /></div>
                <div className="form-group"><label className="form-label">Emergency Contact Name</label><input type="text" className="form-input" value={formData.emergencyContactName} onChange={e => updateField('emergencyContactName', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Emergency Contact Phone</label><input type="tel" className="form-input" value={formData.emergencyContactPhone} onChange={e => updateField('emergencyContactPhone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Current Weight (lbs)</label><input type="number" className="form-input" value={formData.currentWeight} onChange={e => updateField('currentWeight', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Height (inches)</label><input type="number" className="form-input" value={formData.heightInches} onChange={e => updateField('heightInches', e.target.value)} /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="intake-step">
              <h3>Goals & Primary Concerns</h3>
              <div className="form-group"><label className="form-label">What brings you in today? (Check all that apply)</label><CheckboxGroup options={['Weight loss and metabolic support', 'Appetite control', 'Energy improvement', 'Hormone symptoms', 'Body recomposition', 'Lifestyle change coaching', 'Grief or sudden life change']} field="whatBringsYou" other /></div>
              <div className="form-group"><label className="form-label">What is your #1 goal for the next 90 days?</label><RadioGroup options={['Lose pounds', 'Lose inches', 'Prepare for an event', 'Improve long-term health', 'Other']} field="primaryGoal" />{formData.primaryGoal === 'Other' && <input type="text" className="form-input" style={{marginTop: '8px'}} placeholder="Please specify..." value={formData.primaryGoalOther} onChange={e => updateField('primaryGoalOther', e.target.value)} />}</div>
              <div className="form-group"><label className="form-label">Have you previously tried to lose weight?</label><RadioGroup options={['Yes', 'No']} field="previouslyTriedWeightLoss" /></div>
              {formData.previouslyTriedWeightLoss === 'Yes' && <div className="form-group"><label className="form-label">If yes, what did you use and what were your results?</label><textarea className="form-input" value={formData.previousWeightLossDetails} onChange={e => updateField('previousWeightLossDetails', e.target.value)} rows={3} /></div>}
              <div className="form-group"><label className="form-label">What has been your biggest challenge to weight loss? (Check all that apply)</label><CheckboxGroup options={['Sustaining meaningful results', 'Gaining back weight', 'Cravings', 'Irregular eating habits or schedule', 'Emotional eating', 'Motivation', 'Time constraints']} field="weightLossChallenges" other /></div>
            </div>
          )}

          {step === 3 && (
            <div className="intake-step">
              <h3>Current Lifestyle</h3>
              <div className="form-group"><label className="form-label">Nutrition pattern (Check all that apply)</label><CheckboxGroup options={['Structured/planned', 'Emotional eating', 'Skipping meals', 'High sugar or carb cravings']} field="nutritionPatterns" /></div>
              <div className="form-group"><label className="form-label">How many times per day do you eat?</label><RadioGroup options={['1', '2', '3', 'More than 3']} field="mealsPerDay" /></div>
              <div className="form-group"><label className="form-label">Daily Water Intake</label><RadioGroup options={['Less than 30 ounces', '30-60 ounces', '60-90 ounces', '90+ ounces']} field="dailyWaterIntake" /></div>
              <div className="form-group"><label className="form-label">Physical activity level</label><select className="form-input" value={formData.physicalActivityLevel} onChange={e => updateField('physicalActivityLevel', e.target.value)}><option value="">Select...</option><option>Sedentary</option><option>Lightly active</option><option>Moderately active</option><option>Very active</option></select></div>
              <div className="form-group"><label className="form-label">Type of physical activity (Check all that apply)</label><CheckboxGroup options={['HIIT', 'Cardio', 'Weightlifting', 'Strength training', 'Recreational aerobic activity']} field="physicalActivityTypes" other /></div>
              <div className="form-group"><label className="form-label">Sleep quality</label><select className="form-input" value={formData.sleepQuality} onChange={e => updateField('sleepQuality', e.target.value)}><option value="">Select...</option><option>Poor</option><option>Fair</option><option>Good</option><option>Excellent</option></select></div>
              <div className="form-group"><label className="form-label">Average hours of sleep per night</label><input type="text" className="form-input" value={formData.avgSleepHours} onChange={e => updateField('avgSleepHours', e.target.value)} placeholder="e.g., 7" /></div>
              <div className="form-group"><label className="form-label">Do you drink Alcohol?</label><select className="form-input" value={formData.alcoholUse} onChange={e => updateField('alcoholUse', e.target.value)}><option value="">Select...</option><option>Never</option><option>Rarely</option><option>Occasionally</option><option>Frequently</option></select></div>
              <div className="form-group"><label className="form-label">Stress Level</label><select className="form-input" value={formData.stressLevel} onChange={e => updateField('stressLevel', e.target.value)}><option value="">Select...</option><option>Low</option><option>Moderate</option><option>High</option><option>Very High</option></select></div>
              <div className="form-group"><label className="form-label">Level of support at home</label><select className="form-input" value={formData.supportAtHome} onChange={e => updateField('supportAtHome', e.target.value)}><option value="">Select...</option><option>Very supportive</option><option>Somewhat supportive</option><option>Neutral</option><option>Not supportive</option></select></div>
            </div>
          )}

          {step === 4 && (
            <div className="intake-step">
              <h3>Medical History</h3>
              <div className="form-group"><label className="form-label">Metabolic Health (check all that apply)</label><CheckboxGroup options={['Type 1 diabetes', 'Type 2 diabetes', 'Pre-diabetes/Insulin Resistance', 'Thyroid disease (Hypo/Hyper)', 'PCOS', 'High cholesterol', 'N/A']} field="metabolicHealth" /></div>
              <div className="form-group"><label className="form-label">Cardiovascular (Check all that apply)</label><CheckboxGroup options={['Hypertension (High Blood Pressure)', 'Heart Disease', 'Arrhythmia', 'Stroke or TIA', 'N/A']} field="cardiovascular" /></div>
              <div className="form-group"><label className="form-label">Autoimmune Conditions (Check all that apply)</label><CheckboxGroup options={['Rheumatoid arthritis', 'Lupus', 'Multiple sclerosis', "Hashimoto's", "Still's disease", 'Inflammatory bowel disease', 'N/A']} field="autoimmuneConditions" /></div>
              <div className="form-group"><label className="form-label">Gastrointestinal & GLP-1 Relevant Conditions (Check all that apply)</label><CheckboxGroup options={['Gallstone/Gallbladder disease', 'Pancreatitis (history of)', 'Gastroparesis', 'Severe GERD', 'Liver disease (NAFLD, hepatitis, cirrhosis)', 'Diverticulosis', 'Diverticulitis', 'N/A']} field="giConditions" /></div>
              <div className="form-group"><label className="form-label">Kidney/Other Organ Health (Check all that apply)</label><CheckboxGroup options={['Kidney disease', 'Dialysis', 'N/A']} field="kidneyHealth" /></div>
              <div className="form-group"><label className="form-label">Mental Health (Check all that apply)</label><CheckboxGroup options={['Anxiety', 'Depression', 'Eating disorder (history of)', 'OCD', 'N/A']} field="mentalHealth" /></div>
              <div className="form-group"><label className="form-label">Other diagnoses not listed above</label><input type="text" className="form-input" value={formData.otherDiagnoses} onChange={e => updateField('otherDiagnoses', e.target.value)} /></div>
            </div>
          )}

          {step === 5 && (
            <div className="intake-step">
              <h3>Surgical & Injury History</h3>
              <div className="form-group"><label className="form-label">Please check all surgeries you have had</label><CheckboxGroup options={['Brain surgery', 'Arm surgery', 'Leg surgery', 'Knee or ankle surgery', 'Hip surgery', 'Shoulder surgery', 'Stomach or abdominal surgery', 'Gallbladder removal', 'Appendectomy', 'Hernia repair', 'Elective cosmetic surgery', 'Organ-related surgery', 'Spine or back surgery', 'Pelvic or reproductive surgery', 'Orthopedic surgery (general)', 'None']} field="surgicalHistory" other /></div>
            </div>
          )}

          {step === 6 && (
            <div className="intake-step">
              <h3>Medications, Supplements & Family History</h3>
              <div className="form-group"><label className="form-label">Are you currently taking any of the following? (Check all that apply)</label><CheckboxGroup options={['Insulin', 'Metformin', 'Thyroid medications', 'Blood thinners', 'Steroids (oral or injectable)', 'Appetite suppressants', 'Hormone therapy (Bio-identical or synthetic)', 'None']} field="currentMedications" /></div>
              <div className="form-group"><label className="form-label">Do you use dietary supplements or vitamins?</label><select className="form-input" value={formData.usesSupplements} onChange={e => updateField('usesSupplements', e.target.value)}><option value="">Select...</option><option>Yes</option><option>No</option></select></div>
              {formData.usesSupplements === 'Yes' && <div className="form-group"><label className="form-label">If yes, please list</label><input type="text" className="form-input" value={formData.supplementsList} onChange={e => updateField('supplementsList', e.target.value)} /></div>}
              <div className="form-group"><label className="form-label">Do you have any known food or drug allergies?</label><select className="form-input" value={formData.hasAllergies} onChange={e => updateField('hasAllergies', e.target.value)}><option value="">Select...</option><option>Yes</option><option>No</option></select></div>
              {formData.hasAllergies === 'Yes' && <div className="form-group"><label className="form-label">If yes, please list</label><input type="text" className="form-input" value={formData.allergiesList} onChange={e => updateField('allergiesList', e.target.value)} /></div>}
              <div className="form-group"><label className="form-label">Do any immediate family members have any of the following conditions? (Check all that apply)</label><CheckboxGroup options={['Type 2 diabetes', 'Heart disease', 'High blood pressure', 'Stroke', 'Cancer', 'Thyroid disease']} field="familyHistory" /></div>
              <div className="form-group"><label className="form-label">Pregnancy & Breastfeeding Status</label><RadioGroup options={['Not pregnant or breastfeeding', 'Pregnant', 'Breastfeeding', 'Pregnant and breastfeeding']} field="pregnancyStatus" /></div>
              <div className="form-group"><label className="form-label">What treatments would you be interested in learning more about? (Check all that apply)</label><CheckboxGroup options={['Red light therapy', 'Peptide therapy', 'Body sculpting', 'Muscle building/toning (EMS stimulation)', 'Skin tightening', 'Natural weight loss solutions', 'Nutrition coaching', 'Weight Loss Medication']} field="treatmentInterests" /></div>
            </div>
          )}

          {step === 7 && (
            <div className="intake-step">
              <h3>Consent & Signature</h3>
              <div style={{ background: 'var(--cream)', padding: '16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                <p style={{ marginBottom: '12px' }}>By signing below, I certify that the information provided is accurate to the best of my knowledge and I consent to treatment based on this information.</p>
                <label className="checkbox-label"><input type="checkbox" checked={formData.consentMessages} onChange={e => updateField('consentMessages', e.target.checked)} /><span>I consent to receive text messages and communications from Ascent Trim</span></label>
                <label className="checkbox-label" style={{ marginTop: '8px' }}><input type="checkbox" checked={formData.consentAccuracy} onChange={e => updateField('consentAccuracy', e.target.checked)} /><span><strong>I certify that all information provided is accurate and complete *</strong></span></label>
                <label className="checkbox-label" style={{ marginTop: '8px' }}><input type="checkbox" checked={formData.consentNoRefundRx} onChange={e => updateField('consentNoRefundRx', e.target.checked)} /><span> I understand that <strong>prescription medications are non-refundable</strong>. Once a prescription medication has been dispensed to me, it cannot be returned, exchanged, or refunded for any reason, including if I discontinue treatment or experience side effects. *</span></label>
              </div>
              <div className="form-group">
                <label className="form-label">Signature *</label>
                <div style={{ border: '1px solid var(--border)', borderRadius: '8px', background: 'white' }}>
                  <SignatureCanvas ref={signatureRef} penColor="black" canvasProps={{ className: 'signature-canvas', style: { width: '100%', height: '150px' } }} onEnd={handleSignatureEnd} />
                </div>
                <button type="button" className="btn btn-secondary" style={{ marginTop: '8px' }} onClick={clearSignature}>Clear Signature</button>
              </div>
            </div>
          )}
        </div>

        <div className="intake-actions" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
          {step > 1 && <button className="btn btn-secondary" onClick={prevStep} disabled={isSubmitting}>Back</button>}
          {step < totalSteps && <button className="btn btn-gold" onClick={nextStep}>Continue</button>}
          {step === totalSteps && <button className="btn btn-gold" onClick={handleSubmit} disabled={isSubmitting || !formData.consentAccuracy || !formData.consentNoRefundRx || signatureEmpty}>{isSubmitting ? 'Submitting...' : 'Submit Intake Form'}</button>}
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function MyIntakeFormsPage() {
  const [intakeForms, setIntakeForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const data = await api.get('/api/my-intake-forms');
        setIntakeForms(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchForms();
  }, []);

  const viewForm = async (formId) => {
    try {
      const form = await api.get(`/api/my-intake-forms/${formId}`);
      setSelectedForm(form);
      setShowViewModal(true);
    } catch (e) { console.error(e); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  return (
    <div>
      <div className="page-header"><h1 className="page-title">My Intake Forms</h1><p className="page-subtitle">View your submitted intake forms</p></div>
      <div className="section-card">
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : intakeForms.length > 0 ? (
          <div className="intake-forms-list">
            {intakeForms.map((form, idx) => (
              <div key={form.id} className="intake-form-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: idx === 0 ? 'var(--cream)' : 'white', borderRadius: '8px', marginBottom: '12px', border: idx === 0 ? '2px solid var(--accent-gold)' : '1px solid var(--cream-dark)' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>{form.first_name} {form.last_name}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    Submitted: {formatDate(form.created_at)}
                    {idx === 0 && <span style={{ marginLeft: '12px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-gold)' }}>MOST RECENT</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => viewForm(form.id)}>View</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => window.open(`/api/my-intake-forms/${form.id}/pdf`, '_blank')}>Download PDF</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No intake forms yet</h3><p>Complete your first intake form to get started.</p></div>
        )}
      </div>

      {showViewModal && selectedForm && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal prescription-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Intake Form: {selectedForm.first_name} {selectedForm.last_name}</h2><button className="modal-close" onClick={() => setShowViewModal(false)}>×</button></div>
            <div className="prescription-review" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="review-section"><h4>Contact Information</h4>
                <div className="review-grid">
                  <div><span>Name:</span> {selectedForm.first_name} {selectedForm.last_name}</div>
                  <div><span>Phone:</span> {selectedForm.phone || '-'}</div>
                  <div><span>DOB:</span> {formatDate(selectedForm.date_of_birth)}</div>
                  <div><span>Gender:</span> {selectedForm.gender || '-'}</div>
                </div>
                {selectedForm.street_address && <p style={{marginTop: '8px'}}><span>Address:</span> {selectedForm.street_address}, {selectedForm.city}, {selectedForm.state} {selectedForm.postal_code}</p>}
              </div>
              <div className="review-section"><h4>Physical & Emergency</h4>
                <div className="review-grid">
                  <div><span>Weight:</span> {selectedForm.current_weight || '-'} lbs</div>
                  <div><span>Height:</span> {selectedForm.height_inches || '-'}"</div>
                  <div><span>Emergency Contact:</span> {selectedForm.emergency_contact_name || '-'}</div>
                  <div><span>Emergency Phone:</span> {selectedForm.emergency_contact_phone || '-'}</div>
                </div>
              </div>
              {selectedForm.what_brings_you && <div className="review-section"><h4>What Brings You In</h4><p>{(Array.isArray(selectedForm.what_brings_you) ? selectedForm.what_brings_you : JSON.parse(selectedForm.what_brings_you || '[]')).join(', ') || '-'}</p></div>}
              {selectedForm.primary_goal && <div className="review-section"><h4>Primary Goal</h4><p>{selectedForm.primary_goal}</p></div>}
              {selectedForm.metabolic_health && <div className="review-section"><h4>Medical History - Metabolic</h4><p>{(Array.isArray(selectedForm.metabolic_health) ? selectedForm.metabolic_health : JSON.parse(selectedForm.metabolic_health || '[]')).join(', ') || 'None'}</p></div>}
              {selectedForm.cardiovascular && <div className="review-section"><h4>Medical History - Cardiovascular</h4><p>{(Array.isArray(selectedForm.cardiovascular) ? selectedForm.cardiovascular : JSON.parse(selectedForm.cardiovascular || '[]')).join(', ') || 'None'}</p></div>}
              {selectedForm.current_medications && <div className="review-section"><h4>Current Medications</h4><p>{(Array.isArray(selectedForm.current_medications) ? selectedForm.current_medications : JSON.parse(selectedForm.current_medications || '[]')).join(', ') || 'None'}</p></div>}
              {selectedForm.allergies_list && <div className="review-section"><h4>Allergies</h4><p>{selectedForm.allergies_list}</p></div>}
              {selectedForm.signature_data && <div className="review-section"><h4>Your Signature</h4><img src={selectedForm.signature_data} alt="Your Signature" style={{maxWidth: '300px', border: '1px solid var(--cream-dark)', borderRadius: '8px'}} /><p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px'}}>Signed: {new Date(selectedForm.consent_timestamp).toLocaleString('en-US', { timeZone: 'America/Denver' })}</p></div>}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
              <button className="btn btn-gold" onClick={() => window.open(`/api/my-intake-forms/${selectedForm.id}/pdf`, '_blank')}>Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth(); const location = useLocation(); const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Collapsible nav groups for admin
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem('navExpandedGroups');
    return saved ? JSON.parse(saved) : { scheduling: false, patients: false, commerce: false, analytics: false, tools: false, settings: false };
  });

  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    const fetchCount = () => {
      api.get('/api/admin/appointments/needs-review')
        .then(data => setNeedsReviewCount(Array.isArray(data) ? data.length : 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const toggleGroup = (group) => {
    const newState = { ...expandedGroups, [group]: !expandedGroups[group] };
    setExpandedGroups(newState);
    localStorage.setItem('navExpandedGroups', JSON.stringify(newState));
  };

  const { hasSection } = useAuth();
  const allAdminNavGroups = [
    {
      id: 'scheduling',
      label: 'Scheduling',
      icon: <Icons.Calendar />,
      items: [
        { path: '/admin/calendar', label: 'Calendar', icon: <Icons.Calendar />, section: 'scheduling' },
        { path: '/admin/needs-review', label: 'Needs Review', icon: <Icons.Clipboard />, section: 'scheduling', badge: needsReviewCount },
        { path: '/admin/reminders', label: 'Reminders', icon: <Icons.Clipboard />, section: 'reminders' },
        { path: '/admin/scheduling', label: 'Services & Availability', icon: <Icons.Settings />, section: 'scheduling' },
      ]
    },
    {
      id: 'patients',
      label: 'Patients',
      icon: <Icons.Users />,
      items: [
        { path: '/admin/patient-board', label: 'Status Board', icon: <Icons.Dashboard />, section: 'patient_board' },
        { path: '/admin/users', label: 'Manage Users', icon: <Icons.Users />, section: 'patients' },
        { path: '/admin/intake-reviews', label: 'Intake Reviews', icon: <Icons.Clipboard />, section: 'patients' },
        { path: '/admin/nutrition-reviews', label: 'Nutrition Reviews', icon: <Icons.Services />, section: 'patients' },
        { path: '/admin/doctor-queue', label: 'Doctor Queue', icon: <Icons.Prescription />, section: 'patients' },
      ]
    },
    {
      id: 'commerce',
      label: 'Commerce',
      icon: <Icons.Products />,
      section: 'commerce',
      items: [
        { path: '/shop', label: 'Shop', icon: <Icons.Products />, section: 'commerce' },
        { path: '/admin/products', label: 'Products', icon: <Icons.Products />, section: 'commerce' },
        { path: '/admin/orders', label: 'Orders', icon: <Icons.History />, section: 'commerce' },
        { path: '/admin/membership-plans', label: 'Memberships', icon: <Icons.Services />, section: 'commerce' },
        { path: '/admin/coupons', label: 'Coupons', icon: <Icons.Wallet />, section: 'commerce' },
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <Icons.Dashboard />,
      items: [
        { path: '/admin/metrics', label: 'Metrics', icon: <Icons.Dashboard />, section: 'analytics' },
        { path: '/admin/patient-sources', label: 'Patient Sources', icon: <Icons.Users />, section: 'patient_sources' },
        { path: '/admin/campaigns', label: 'Campaigns', icon: <Icons.Dashboard />, section: 'analytics' },
        { path: '/admin/audit-log', label: 'Audit Log', icon: <Icons.AuditLog />, section: 'audit_log' },
      ]
    },
    {
      id: 'tools',
      label: 'Tools',
      icon: <Icons.Services />,
      items: [
        { path: '/admin/nutrition-roadmaps', label: 'Nutrition Roadmaps', icon: <Icons.Nutrition />, section: 'patients' },
        { path: '/admin/patient-reminder', label: 'Patient Call Reminders', icon: <Icons.Clipboard />, section: 'patients' },
        { path: 'https://ana.ascenttrim.com/', label: 'Nutrition Architect', icon: <Icons.Services />, external: true },
      ]
    },
    {
      id: 'content',
      label: 'Website',
      icon: <Icons.Clipboard />,
      items: [
        { path: '/admin/blog', label: 'Blog', icon: <Icons.Clipboard />, section: 'content' },
          { path: '/admin/recipes', label: 'Recipes', icon: <Icons.Nutrition />, section: 'content' },
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Icons.Settings />,
      section: 'settings',
      items: [
        { path: '/admin/admins', label: 'Admins', icon: <Icons.Settings />, section: 'settings' },
        { path: '/admin/roles', label: 'Roles', icon: <Icons.Settings />, section: 'settings' },
        { path: '/admin/settings', label: 'Settings', icon: <Icons.Settings />, section: 'settings' },
        { path: '/admin/webhook-logs', label: 'Webhook Logs', icon: <Icons.AuditLog />, section: 'settings' },
        { path: '/admin/api-keys', label: 'API Keys', icon: <Icons.Settings />, section: 'api_keys' },
        { path: '/admin/report-issue', label: 'Report Issue', icon: <Icons.Clipboard />, section: 'settings' },
        { path: '/admin/guide', label: 'User Guide', icon: <Icons.Clipboard />, section: 'guide' },
      ]
    },
  ];

  // Filter nav groups and items based on user's role sections
  const adminNavGroups = allAdminNavGroups.map(group => {
    const filteredItems = group.items.filter(item => !item.section || hasSection(item.section));
    return { ...group, items: filteredItems };
  }).filter(group => {
    if (group.section && !hasSection(group.section)) return false;
    return group.items.length > 0;
  });

  const patientNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Icons.Dashboard /> },
    { path: '/book-appointment', label: 'Book Appointment', icon: <Icons.Calendar /> },
    { path: '/my-appointments', label: 'My Appointments', icon: <Icons.Calendar /> },
    { path: '/shop', label: 'Shop', icon: <Icons.Products /> },
    { path: '/memberships', label: 'Memberships', icon: <Icons.Services /> },
    { path: '/my-orders', label: 'My Orders', icon: <Icons.History /> },
    { path: '/my-products', label: 'My Products', icon: <Icons.Products /> },
    { path: '/my-referrals', label: 'Refer a Friend', icon: <Icons.MyInfo /> },
    { path: '/my-info', label: 'My Info', icon: <Icons.MyInfo /> },
    { path: '/new-intake', label: 'New Intake Form', icon: <Icons.Clipboard /> },
    { path: '/my-intake-forms', label: 'My Intake Forms', icon: <Icons.Clipboard /> },
    { path: '/history', label: 'Transactions', icon: <Icons.History /> },
  ];

  const handleNavClick = () => { if (onClose) onClose(); };

  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isGroupActive = (group) => group.items.some(item => isPathActive(item.path));

  const navBadge = (n) => n > 0 ? (
    <span style={{ marginLeft: 'auto', background: '#dc3545', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 7px', borderRadius: '10px', minWidth: '20px', textAlign: 'center' }}>{n}</span>
  ) : null;

  const renderNavItem = (item) => item.external ? (
    <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer" className="nav-item nav-subitem" onClick={handleNavClick}>
      {item.icon}<span>{item.label}</span>{navBadge(item.badge)}
    </a>
  ) : (
    <Link key={item.path} to={item.path} className={`nav-item nav-subitem ${isPathActive(item.path) ? 'active' : ''}`} onClick={handleNavClick}>
      {item.icon}<span>{item.label}</span>{navBadge(item.badge)}
    </Link>
  );

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-logo"><h1>Ascent Trim</h1><span>Patient Portal</span></div>
      <nav className="sidebar-nav">
        {isAdmin ? (
          adminNavGroups.map(group => (
            <div key={group.id} className={`nav-group ${isGroupActive(group) ? 'has-active' : ''}`}>
              <button className={`nav-group-header ${expandedGroups[group.id] ? 'expanded' : ''}`} onClick={() => toggleGroup(group.id)}>
                {group.icon}
                <span>{group.label}</span>
                <svg className="nav-group-arrow" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
              </button>
              {expandedGroups[group.id] && (
                <div className="nav-group-items">
                  {group.items.map(renderNavItem)}
                </div>
              )}
            </div>
          ))
        ) : (
          patientNavItems.map(item => (
            <Link key={item.path} to={item.path} className={`nav-item ${isPathActive(item.path) ? 'active' : ''}`} onClick={handleNavClick}>
              {item.icon}<span>{item.label}</span>
            </Link>
          ))
        )}
      </nav>
      <div className="sidebar-footer">
        <div className="user-info"><div className="user-avatar">{user?.name?.split(' ').map(n => n[0]).join('') || '?'}</div><div className="user-details"><h4>{user?.name}</h4><p>{isAdmin ? 'Administrator' : 'Patient'}</p></div></div>
        <button className="logout-btn" onClick={handleLogout}>Sign Out</button>
      </div>
    </aside>
  );
}

function DashboardPage() {
  const { user } = useAuth(); const [transactions, setTransactions] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/api/users/transactions').then(setTransactions).catch(console.error).finally(() => setLoading(false)); }, []);
  const totalSpent = transactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + Math.abs(parseFloat(t.amount)), 0);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' });
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Welcome back, {user?.name?.split(' ')[0]}</h1><p className="page-subtitle">Here's your Clinic Cash overview</p></div>
      <div className="dashboard-grid">
        <div className="stat-card balance"><div className="stat-label"><Icons.Wallet /> Available Clinic Cash</div><div className="stat-value">${user?.balance?.toFixed(2) || '0.00'}</div></div>
        <div className="stat-card"><div className="stat-label">Total Services Used</div><div className="stat-value">{transactions.filter(t => t.type === 'debit').length}</div></div>
        <div className="stat-card"><div className="stat-label">Total Spent</div><div className="stat-value">${totalSpent.toFixed(2)}</div></div>
      </div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Recent Transactions</h2><Link to="/history" className="btn btn-ghost btn-sm">View All</Link></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : transactions.slice(0, 5).length > 0 ? (
          <div className="transaction-list">{transactions.slice(0, 5).map(t => (<div key={t.id} className="transaction-item"><div className="transaction-info"><div className={`transaction-icon ${t.type}`}>{t.type === 'credit' ? '↓' : '↑'}</div><div className="transaction-details"><h4>{t.description}</h4><p>{formatDate(t.created_at)}</p></div></div><div className={`transaction-amount ${t.type}`}>{t.type === 'credit' ? '+' : '-'}${Math.abs(parseFloat(t.amount)).toFixed(2)}</div></div>))}</div>
        ) : <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No transactions yet</h3></div>}
      </div>
    </div>
  );
}

function MyProductsPage() {
  const [products, setProducts] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/api/users/products').then(setProducts).catch(console.error).finally(() => setLoading(false)); }, []);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' });
  return (
    <div>
      <div className="page-header"><h1 className="page-title">My Products</h1><p className="page-subtitle">Products and treatments dispensed to you</p></div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : products.length > 0 ? (
        <div className="section-card"><div className="product-history-list">{products.map(p => (<div key={p.id} className="product-history-item"><div className="product-history-info"><h4>{p.product_name}</h4><p>Qty: {p.quantity} • {formatDate(p.created_at)}</p>{p.notes && <p className="product-notes">{p.notes}</p>}</div></div>))}</div></div>
      ) : <div className="section-card"><div className="empty-state"><div className="empty-state-icon">📦</div><h3>No products yet</h3><p>Products dispensed to you will appear here.</p></div></div>}
    </div>
  );
}

function MyInfoPage() {
  const [profile, setProfile] = useState(null);
  const [vitals, setVitals] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [nutritionIntakes, setNutritionIntakes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    Promise.all([
      api.get('/api/my-profile'),
      api.get('/api/my-prescriptions'),
      api.get('/api/my-nutrition'),
      api.get('/api/my-info/appointments')
    ]).then(([profileData, rxData, nutritionData, apptData]) => {
      setProfile(profileData.profile);
      setVitals(profileData.vitals);
      setPrescriptions(rxData);
      setNutritionIntakes(nutritionData);
      setAppointments(apptData.appointments || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-';
  const formatArray = (arr) => Array.isArray(arr) ? arr.join(', ') : (arr || '-');
  const calculateAge = (dob) => {
    if (!dob) return '-';
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  if (loading) return <div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div>;

  const InfoItem = ({ label, value }) => (
    <div className="info-item"><label>{label}</label><p>{value || '-'}</p></div>
  );

  const InfoSection = ({ title, children }) => (
    <div style={{ marginBottom: '24px' }}>
      <h4 style={{ color: 'var(--gold)', marginBottom: '12px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>{title}</h4>
      <div className="info-grid">{children}</div>
    </div>
  );

  return (
    <div>
      <div className="page-header"><h1 className="page-title">My Information</h1><p className="page-subtitle">Your personal health profile and history</p></div>
      <div className="tab-nav" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <button className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}><Icons.MyInfo /> Profile</button>
        <button className={`tab-btn ${activeTab === 'medical' ? 'active' : ''}`} onClick={() => setActiveTab('medical')}><Icons.Clipboard /> Medical History</button>
        <button className={`tab-btn ${activeTab === 'prescriptions' ? 'active' : ''}`} onClick={() => setActiveTab('prescriptions')}><Icons.Prescription /> Prescriptions ({prescriptions.length})</button>
        <button className={`tab-btn ${activeTab === 'nutrition' ? 'active' : ''}`} onClick={() => setActiveTab('nutrition')}><Icons.Services /> Nutrition ({nutritionIntakes.length})</button>
        <button className={`tab-btn ${activeTab === 'appointments' ? 'active' : ''}`} onClick={() => setActiveTab('appointments')}><Icons.Calendar /> Appointments</button>
      </div>

      {activeTab === 'profile' && (
        <div className="section-card">
          <h2 className="section-title" style={{ marginBottom: '24px' }}>Personal Details</h2>
          <InfoSection title="Contact Information">
            <InfoItem label="Full Name" value={`${profile?.first_name || ''} ${profile?.last_name || profile?.name || ''}`} />
            <InfoItem label="Email" value={profile?.email} />
            <InfoItem label="Phone" value={profile?.phone} />
            <InfoItem label="Date of Birth" value={profile?.date_of_birth ? `${formatDate(profile.date_of_birth)} (Age ${calculateAge(profile.date_of_birth)})` : '-'} />
            <InfoItem label="Gender" value={profile?.gender} />
          </InfoSection>
          <InfoSection title="Address">
            <InfoItem label="Street" value={profile?.street_address} />
            <InfoItem label="City" value={profile?.city} />
            <InfoItem label="State" value={profile?.state} />
            <InfoItem label="Country" value={profile?.country} />
            <InfoItem label="Postal Code" value={profile?.postal_code} />
          </InfoSection>
          <InfoSection title="Emergency Contact">
            <InfoItem label="Name" value={profile?.emergency_contact_name} />
            <InfoItem label="Phone" value={profile?.emergency_contact_phone} />
          </InfoSection>
          {vitals && (
            <InfoSection title="Latest Vitals">
              <InfoItem label="Weight" value={vitals.current_weight ? `${vitals.current_weight} lbs` : null} />
              <InfoItem label="Height" value={vitals.height_inches ? `${Math.floor(vitals.height_inches/12)}'${vitals.height_inches%12}"` : null} />
              <InfoItem label="Blood Pressure" value={vitals.blood_pressure} />
              <InfoItem label="BMI" value={vitals.bmi} />
              <InfoItem label="Pulse" value={vitals.pulse ? `${vitals.pulse} bpm` : null} />
              <InfoItem label="Last Updated" value={formatDate(vitals.updated_at)} />
            </InfoSection>
          )}
          <InfoSection title="Account">
            <InfoItem label="Clinic Cash Balance" value={`$${parseFloat(profile?.balance || 0).toFixed(2)}`} />
            <InfoItem label="Member Since" value={formatDate(profile?.created_at)} />
          </InfoSection>
        </div>
      )}

      {activeTab === 'medical' && (
        <div className="section-card">
          <h2 className="section-title" style={{ marginBottom: '24px' }}>Medical History</h2>
          <InfoSection title="Health Goals">
            <InfoItem label="Primary Goal" value={profile?.primary_goal === 'Other' ? profile?.primary_goal_other : profile?.primary_goal} />
            <InfoItem label="What Brings You In" value={formatArray(profile?.what_brings_you)} />
            <InfoItem label="Previous Weight Loss Attempts" value={profile?.previously_tried_weight_loss ? 'Yes' : 'No'} />
            {profile?.previously_tried_weight_loss && <InfoItem label="Details" value={profile?.previous_weight_loss_details} />}
            <InfoItem label="Weight Loss Challenges" value={formatArray(profile?.weight_loss_challenges)} />
          </InfoSection>
          <InfoSection title="Lifestyle">
            <InfoItem label="Meals Per Day" value={profile?.meals_per_day} />
            <InfoItem label="Daily Water Intake" value={profile?.daily_water_intake} />
            <InfoItem label="Nutrition Patterns" value={formatArray(profile?.nutrition_patterns)} />
            <InfoItem label="Physical Activity Level" value={profile?.physical_activity_level} />
            <InfoItem label="Activity Types" value={formatArray(profile?.physical_activity_types)} />
            <InfoItem label="Sleep Quality" value={profile?.sleep_quality} />
            <InfoItem label="Average Sleep" value={profile?.avg_sleep_hours} />
            <InfoItem label="Alcohol Use" value={profile?.alcohol_use} />
            <InfoItem label="Stress Level" value={profile?.stress_level} />
            <InfoItem label="Support at Home" value={profile?.support_at_home} />
          </InfoSection>
          <InfoSection title="Medical Conditions">
            <InfoItem label="Metabolic Health" value={formatArray(profile?.metabolic_health)} />
            <InfoItem label="Cardiovascular" value={formatArray(profile?.cardiovascular)} />
            <InfoItem label="Autoimmune Conditions" value={formatArray(profile?.autoimmune_conditions)} />
            <InfoItem label="GI Conditions" value={formatArray(profile?.gi_conditions)} />
            <InfoItem label="Kidney Health" value={formatArray(profile?.kidney_health)} />
            <InfoItem label="Mental Health" value={formatArray(profile?.mental_health)} />
            <InfoItem label="Other Diagnoses" value={profile?.other_diagnoses} />
          </InfoSection>
          <InfoSection title="Surgical History">
            <InfoItem label="Surgeries" value={formatArray(profile?.surgical_history)} />
            {profile?.surgical_history_other && <InfoItem label="Other Surgeries" value={profile?.surgical_history_other} />}
          </InfoSection>
          <InfoSection title="Medications & Allergies">
            <InfoItem label="Current Medications" value={formatArray(profile?.current_medications)} />
            <InfoItem label="Uses Supplements" value={profile?.uses_supplements} />
            {profile?.uses_supplements === 'Yes' && <InfoItem label="Supplements" value={profile?.supplements_list} />}
            <InfoItem label="Has Allergies" value={profile?.has_allergies} />
            {profile?.has_allergies === 'Yes' && <InfoItem label="Allergies" value={profile?.allergies_list} />}
          </InfoSection>
          <InfoSection title="Family History">
            <InfoItem label="Family Conditions" value={formatArray(profile?.family_history)} />
          </InfoSection>
          {profile?.gender === 'Female' && (
            <InfoSection title="Pregnancy Status">
              <InfoItem label="Status" value={profile?.pregnancy_status} />
            </InfoSection>
          )}
          <InfoSection title="Treatment Interests">
            <InfoItem label="Interested In" value={formatArray(profile?.treatment_interests)} />
          </InfoSection>
        </div>
      )}

      {activeTab === 'prescriptions' && (
        <div className="section-card">
          <h2 className="section-title" style={{ marginBottom: '24px' }}>Prescription History</h2>
          {prescriptions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {prescriptions.map(rx => (
                <div key={rx.id} className="prescription-card" style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>{formatDate(rx.created_at)}</div>
                      <div style={{ fontWeight: '600', marginTop: '4px' }}>
                        {rx.semaglutide_type && `Semaglutide (${rx.semaglutide_type})`}
                        {rx.semaglutide_type && rx.tirzepatide_type && ' + '}
                        {rx.tirzepatide_type && `Tirzepatide (${rx.tirzepatide_type})`}
                        {rx.other_medication && (rx.semaglutide_type || rx.tirzepatide_type ? ' + ' : '') + rx.other_medication}
                      </div>
                    </div>
                    <span className={`status-badge ${rx.status === 'signed' ? 'approved' : rx.status === 'patient_signed' ? 'pending' : 'pending'}`} style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                      {rx.status === 'signed' ? 'Signed' : rx.status === 'patient_signed' ? 'Awaiting Doctor' : rx.status === 'pending_signature' ? 'Awaiting Signature' : rx.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', fontSize: '14px' }}>
                    {rx.semaglutide_dosing && <div><span style={{ color: 'var(--text-muted)' }}>Semaglutide Dosing:</span><br/>{rx.semaglutide_dosing}</div>}
                    {rx.tirzepatide_dosing && <div><span style={{ color: 'var(--text-muted)' }}>Tirzepatide Dosing:</span><br/>{rx.tirzepatide_dosing}</div>}
                    {rx.other_medication_dosing && <div><span style={{ color: 'var(--text-muted)' }}>Other Dosing:</span><br/>{rx.other_medication_dosing}</div>}
                    {rx.current_weight && <div><span style={{ color: 'var(--text-muted)' }}>Weight:</span> {rx.current_weight} lbs</div>}
                    {rx.blood_pressure && <div><span style={{ color: 'var(--text-muted)' }}>BP:</span> {rx.blood_pressure}</div>}
                    {rx.bmi && <div><span style={{ color: 'var(--text-muted)' }}>BMI:</span> {rx.bmi}</div>}
                  </div>
                  {rx.physician_name && rx.signed_at && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)', fontSize: '13px', color: 'var(--text-muted)' }}>
                      Signed by {rx.physician_name} on {formatDateTime(rx.signed_at)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No Prescriptions Yet</h3><p>Your prescription history will appear here.</p></div>
          )}
        </div>
      )}

      {activeTab === 'nutrition' && (
        <div className="section-card">
          <h2 className="section-title" style={{ marginBottom: '24px' }}>Nutrition Intake History</h2>
          {nutritionIntakes.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {nutritionIntakes.map(ni => (
                <div key={ni.id} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                      {ni.status === 'completed' ? `Completed ${formatDateTime(ni.completed_at)}` : `Started ${formatDate(ni.created_at)}`}
                    </div>
                    <span className={`status-badge ${ni.status === 'completed' ? 'approved' : 'pending'}`} style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                      {ni.status === 'completed' ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                  {ni.status === 'completed' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', fontSize: '14px' }}>
                      {ni.meals_per_day && <div><span style={{ color: 'var(--text-muted)' }}>Meals Per Day:</span> {ni.meals_per_day}</div>}
                      {ni.energy_crashes && <div><span style={{ color: 'var(--text-muted)' }}>Energy Crashes:</span> {ni.energy_crashes}</div>}
                      {ni.exercise_routine && <div><span style={{ color: 'var(--text-muted)' }}>Exercise:</span> {ni.exercise_routine}</div>}
                      {ni.stress_level && <div><span style={{ color: 'var(--text-muted)' }}>Stress Level:</span> {ni.stress_level}/10</div>}
                      {ni.hardest_about_nutrition && <div style={{ gridColumn: 'span 2' }}><span style={{ color: 'var(--text-muted)' }}>Biggest Challenge:</span> {ni.hardest_about_nutrition}</div>}
                      {ni.success_with_nutrition && <div style={{ gridColumn: 'span 2' }}><span style={{ color: 'var(--text-muted)' }}>Previous Success:</span> {ni.success_with_nutrition}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">🥗</div><h3>No Nutrition Intakes Yet</h3><p>Your nutrition intake forms will appear here.</p></div>
          )}
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="section-card">
          <h2 className="section-title" style={{ marginBottom: '24px' }}>Your Appointments</h2>
          {appointments.length > 0 ? (
            <div className="appointments-list">
              {appointments.map(apt => (
                <div key={apt.id} className="appointment-card">
                  <div className="appointment-icon"><Icons.Calendar /></div>
                  <div className="appointment-details"><h4>{apt.title}</h4><p className="appointment-time">{formatDateTime(apt.startTime)}</p></div>
                  <div className={`appointment-status ${apt.status?.toLowerCase() || 'scheduled'}`}>{apt.status || 'Scheduled'}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state"><div className="empty-state-icon">📅</div><h3>No Appointments</h3><p>Your scheduled appointments will appear here.</p></div>
          )}
        </div>
      )}
    </div>
  );
}

function ServicesPage() {
  const { user, updateUserBalance } = useAuth(); const [services, setServices] = useState([]); const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null); const [showModal, setShowModal] = useState(false); const [purchasing, setPurchasing] = useState(false); const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  useEffect(() => { api.get('/api/services').then(setServices).catch(console.error).finally(() => setLoading(false)); }, []);
  const handlePurchase = async () => { if (!selectedService || purchasing) return; setPurchasing(true); try { const { newBalance } = await api.post(`/api/services/${selectedService.id}/purchase`); updateUserBalance(newBalance); setShowModal(false); setToast({ message: `Successfully purchased ${selectedService.name}!`, type: 'success' }); } catch (error) { setToast({ message: error.message || 'Purchase failed', type: 'error' }); } finally { setPurchasing(false); } };
  if (loading) return <div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div>;
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Our Services</h1><p className="page-subtitle">Browse and purchase services</p></div>
      <div className="services-grid">{services.map(service => (<div key={service.id} className="service-card"><div className="service-header"><div className="service-icon">{service.icon}</div><div className="service-title-wrap"><h3>{service.name}</h3><span className="service-category">{service.category}</span></div></div><p className="service-description">{service.description}</p><div className="service-footer"><div className="service-price">${parseFloat(service.price).toFixed(0)}<span>/{service.duration}</span></div><button className="btn btn-gold btn-sm" onClick={() => { setSelectedService(service); setShowModal(true); }}>Purchase</button></div></div>))}</div>
      {showModal && selectedService && (<div className="modal-overlay" onClick={() => !purchasing && setShowModal(false)}><div className="modal" onClick={e => e.stopPropagation()}><div className="modal-header"><h2 className="modal-title">Confirm Purchase</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={purchasing}>×</button></div><div className="purchase-summary"><div className="purchase-row"><span>Service</span><span>{selectedService.name}</span></div><div className="purchase-row total"><span>Total</span><span>${parseFloat(selectedService.price).toFixed(2)}</span></div></div>{parseFloat(user?.balance) < parseFloat(selectedService.price) && <div className="balance-warning">Insufficient balance.</div>}<div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={purchasing}>Cancel</button><button className="btn btn-gold" onClick={handlePurchase} disabled={purchasing || parseFloat(user?.balance) < parseFloat(selectedService.price)}>{purchasing ? 'Processing...' : 'Confirm'}</button></div></div></div>)}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function HistoryPage() {
  const [transactions, setTransactions] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/api/users/transactions').then(setTransactions).catch(console.error).finally(() => setLoading(false)); }, []);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' });
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Transaction History</h1><p className="page-subtitle">Your complete Clinic Cash history</p></div>
      <div className="section-card">{loading ? <div className="loading-spinner"><div className="spinner"></div></div> : transactions.length > 0 ? (<div className="transaction-list">{transactions.map(t => (<div key={t.id} className="transaction-item"><div className="transaction-info"><div className={`transaction-icon ${t.type}`}>{t.type === 'credit' ? '↓' : '↑'}</div><div className="transaction-details"><h4>{t.description}</h4><p>{formatDate(t.created_at)}</p></div></div><div className={`transaction-amount ${t.type}`}>{t.type === 'credit' ? '+' : '-'}${Math.abs(parseFloat(t.amount)).toFixed(2)}</div></div>))}</div>) : <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No transactions yet</h3></div>}</div>
    </div>
  );
}

// Referral attribution panel for a patient profile. Self-contained: owns its own
// fetch and state so the profile modal's state is untouched. Read-only until an
// admin explicitly edits, and every change is written to an append-only log
// server-side, so a prior attribution is never silently lost.
function ReferralPanel({ patientId, patientName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [freeName, setFreeName] = useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    api.get(`/api/admin/users/${patientId}/referral`)
      .then(setData).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    let live = true;
    const t = setTimeout(() => {
      api.get(`/api/admin/referral-candidates?q=${encodeURIComponent(q)}&exclude=${patientId}`)
        .then(r => { if (live) setResults(r); }).catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, patientId]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await api.put(`/api/admin/users/${patientId}/referral`, {
        referred_by_user_id: picked ? picked.id : null,
        referred_by_name: picked ? '' : freeName.trim()
      });
      setEditing(false); setPicked(null); setFreeName(''); setQ(''); setResults([]);
      load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="review-section"><h4>Referrals</h4><div className="loading-spinner"><div className="spinner"></div></div></div>;

  const referrer = data && (data.referrer_name || data.referred_by_name);
  return (
    <div className="review-section">
      <h4>Referrals</h4>
      {err && <div style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '8px' }}>{err}</div>}

      <div className="review-grid">
        <div>
          <span>Referred by:</span>{' '}
          {referrer
            ? <strong>{data.referrer_name || data.referred_by_name}{!data.referrer_name && data.referred_by_name ? ' (not a portal patient)' : ''}</strong>
            : <span style={{ color: 'var(--text-muted)' }}>Not recorded</span>}
          {!editing && (
            <button className="action-btn" style={{ marginLeft: '10px' }} onClick={() => setEditing(true)}>
              {referrer ? 'Change' : 'Set'}
            </button>
          )}
        </div>
        <div><span>Referrals made:</span> <strong>{data.referred.length}</strong></div>
      </div>

      {editing && (
        <div style={{ marginTop: '10px', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px' }}>
          <label className="form-label">Search for the referring patient</label>
          <input className="form-input" placeholder="Name, email or phone…" value={q}
                 onChange={e => { setQ(e.target.value); setPicked(null); }} />
          {results.length > 0 && !picked && (
            <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '6px', border: '1px solid var(--border)', borderRadius: '6px' }}>
              {results.map(r => (
                <div key={r.id} style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                     onClick={() => { setPicked(r); setQ(r.name); setResults([]); }}>
                  <strong>{r.name}</strong>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>{r.email || r.phone}</span>
                </div>
              ))}
            </div>
          )}
          {picked && <div style={{ marginTop: '6px', fontSize: '13px' }}>Selected: <strong>{picked.name}</strong>{' '}
            <button className="action-btn" onClick={() => { setPicked(null); setQ(''); }}>clear</button></div>}
          <div style={{ marginTop: '10px' }}>
            <label className="form-label">…or record a referrer who is not a patient</label>
            <input className="form-input" placeholder="e.g. Dr. Smith, or a business name"
                   value={freeName} onChange={e => setFreeName(e.target.value)} disabled={!!picked} />
          </div>
          <div className="modal-actions" style={{ marginTop: '10px' }}>
            <button className="btn btn-primary" disabled={saving || (!picked && !freeName.trim())} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary" disabled={saving}
                    onClick={() => { setEditing(false); setPicked(null); setFreeName(''); setQ(''); setErr(null); }}>Cancel</button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px', marginBottom: 0 }}>
            The previous value is kept in the referral audit log — nothing is overwritten silently.
          </p>
        </div>
      )}

      {data.referred.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{patientName} referred:</div>
          <table className="admin-table">
            <thead><tr><th>Patient</th><th>Referred</th><th>Paid orders</th><th>First purchase</th></tr></thead>
            <tbody>
              {data.referred.map(r => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong><br /><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{r.email}</span></td>
                  <td style={{ fontSize: '12px' }}>{r.referred_at ? new Date(r.referred_at).toLocaleDateString('en-US', { timeZone: 'America/Denver' }) : '-'}</td>
                  <td>{r.paid_orders}</td>
                  <td style={{ fontSize: '12px' }}>{r.first_paid_at ? new Date(r.first_paid_at).toLocaleDateString('en-US', { timeZone: 'America/Denver' }) : <span style={{ color: 'var(--text-muted)' }}>none yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminUsersPage() {
  const [users, setUsers] = useState([]); const [loading, setLoading] = useState(true); const [showModal, setShowModal] = useState(false); const [showDispenseModal, setShowDispenseModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null); const [dispenseUser, setDispenseUser] = useState(null); const [products, setProducts] = useState([]); const [selectedProduct, setSelectedProduct] = useState(null);
  const [dispenseQuantity, setDispenseQuantity] = useState(1); const [dispenseNotes, setDispenseNotes] = useState(''); const [dispenseCost, setDispenseCost] = useState(0); const [userHistory, setUserHistory] = useState([]); const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isOtherProduct, setIsOtherProduct] = useState(false); const [otherProductName, setOtherProductName] = useState('');
  const [historyUser, setHistoryUser] = useState(null); const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '', balance: 0 }); const [saving, setSaving] = useState(false); const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false); const [profileData, setProfileData] = useState(null); const [profileLoading, setProfileLoading] = useState(false);
  const [editingClientSource, setEditingClientSource] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' for A-Z, 'desc' for Z-A
  const fetchUsers = async () => { try { setUsers(await api.get('/api/admin/users')); } catch (e) { console.error(e); } finally { setLoading(false); } };
  const fetchAdminUsers = async () => { try { setAdminUsers(await api.get('/api/admin/users/admins')); } catch (e) { console.error(e); } };
  const handleUpdateClientSource = async (userId, clientSource) => {
    try {
      await api.put(`/api/admin/users/${userId}/client-source`, { clientSource });
      setProfileData(prev => prev ? { ...prev, patient: { ...prev.patient, client_source: clientSource } } : prev);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, client_source: clientSource } : u));
      setToast({ message: 'Client source updated!', type: 'success' });
      setEditingClientSource(null);
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };
  const fetchProducts = async () => { try { setProducts(await api.get('/api/products')); } catch (e) { console.error(e); } };
  useEffect(() => { fetchUsers(); fetchProducts(); fetchAdminUsers(); }, []);
  const openProfileModal = async (u) => { setProfileLoading(true); setShowProfileModal(true); try { const data = await api.get(`/api/admin/users/${u.id}/profile`); setProfileData(data); } catch (e) { console.error(e); setToast({ message: 'Failed to load profile', type: 'error' }); } finally { setProfileLoading(false); } };
  const openEditModal = (u) => { setEditingUser(u); setFormData({ name: u.name, email: u.email, password: '', phone: u.phone || '', balance: u.balance }); setShowModal(true); };
  const openNewModal = () => { setEditingUser(null); setFormData({ name: '', email: '', password: '', phone: '', balance: 0 }); setShowModal(true); };
  const openDispenseModal = (u) => { setDispenseUser(u); setSelectedProduct(null); setIsOtherProduct(false); setOtherProductName(''); setDispenseQuantity(1); setDispenseNotes(''); setDispenseCost(0); setShowDispenseModal(true); };
  const openHistoryModal = async (u) => { setHistoryUser(u); try { setUserHistory(await api.get(`/api/admin/users/${u.id}/products`)); } catch (e) { console.error(e); } setShowHistoryModal(true); };
  const handleSave = async () => { setSaving(true); try { if (editingUser) { await api.put(`/api/admin/users/${editingUser.id}`, { name: formData.name, email: formData.email, phone: formData.phone, password: formData.password || undefined }); } else { await api.post('/api/admin/users', formData); } setToast({ message: editingUser ? 'User updated!' : 'User created!', type: 'success' }); setShowModal(false); fetchUsers(); } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); } };
  const handleDispense = async () => {
    if (isOtherProduct) { if (!otherProductName.trim() || !dispenseUser) return; }
    else { if (!selectedProduct || !dispenseUser) return; }
    setSaving(true);
    try {
      const productName = isOtherProduct ? otherProductName.trim() : selectedProduct.name;
      const productId = isOtherProduct ? null : selectedProduct.id;
      await api.post(`/api/admin/users/${dispenseUser.id}/dispense`, { productId, productName, quantity: dispenseQuantity, notes: dispenseNotes, cost: dispenseCost });
      setToast({ message: `${productName} dispensed!`, type: 'success' });
      setShowDispenseModal(false); fetchUsers();
    } catch (error) { setToast({ message: error.message, type: 'error' }); }
    finally { setSaving(false); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete this user?')) return; try { await api.delete(`/api/admin/users/${id}`); setToast({ message: 'User deleted!', type: 'success' }); fetchUsers(); } catch (error) { setToast({ message: error.message, type: 'error' }); } };
  const handleAddFunds = async (id) => { const amount = prompt('Enter amount to ADD:', '100'); if (!amount) return; const num = parseFloat(amount); if (isNaN(num) || num <= 0) { setToast({ message: 'Invalid amount', type: 'error' }); return; } try { await api.post(`/api/admin/users/${id}/add-funds`, { amount: num }); setToast({ message: `$${num.toFixed(2)} added!`, type: 'success' }); fetchUsers(); } catch (error) { setToast({ message: error.message, type: 'error' }); } };
  const handleDeductFunds = async (id) => { const amount = prompt('Enter amount to DEDUCT:', '100'); if (!amount) return; const num = parseFloat(amount); if (isNaN(num) || num <= 0) { setToast({ message: 'Invalid amount', type: 'error' }); return; } try { await api.post(`/api/admin/users/${id}/deduct-funds`, { amount: num }); setToast({ message: `$${num.toFixed(2)} deducted!`, type: 'success' }); fetchUsers(); } catch (error) { setToast({ message: error.message, type: 'error' }); } };
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' });
  // Medications (medically_restricted) go through the signed pickup workflow on the Orders page.
  // Hide them from this general-purpose Dispense modal so staff can't bypass the signature step.
  const groupedProducts = products.filter(p => !p.medically_restricted).reduce((acc, p) => { if (!acc[p.category]) acc[p.category] = []; acc[p.category].push(p); return acc; }, {});

  // Helper to get last name from full name
  const getLastName = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(u => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (u.name?.toLowerCase().includes(search) || u.email?.toLowerCase().includes(search) || u.phone?.includes(search));
    })
    .sort((a, b) => {
      const lastNameA = getLastName(a.name);
      const lastNameB = getLastName(b.name);
      if (sortOrder === 'asc') return lastNameA.localeCompare(lastNameB);
      return lastNameB.localeCompare(lastNameA);
    });

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Manage Users</h1><p className="page-subtitle">View and manage patient accounts</p></div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">All Patients</h2><button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add Patient</button></div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', maxWidth: '300px' }}>
            <input type="text" className="form-input" placeholder="Search by name, email, or phone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ margin: 0 }} />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} style={{ whiteSpace: 'nowrap' }}>
            Last Name: {sortOrder === 'asc' ? 'A → Z' : 'Z → A'}
          </button>
          {searchTerm && <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{filteredUsers.length} of {users.length} patients</span>}
        </div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : filteredUsers.length > 0 ? (
          <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Patient</th><th>Phone</th><th>Balance</th><th>Actions</th></tr></thead><tbody>
            {filteredUsers.map(u => (<tr key={u.id}><td><div className="user-cell"><div className="user-cell-avatar">{u.name?.split(' ').map(n => n[0]).join('')}</div><div className="user-cell-info"><h4 className="patient-name-link" onClick={() => openProfileModal(u)}>{u.name}</h4><p>{u.email}</p></div></div></td><td>{u.phone || '-'}</td><td><strong style={{ color: 'var(--primary)' }}>${u.balance?.toFixed(2)}</strong></td><td><div className="action-btns"><button className="action-btn edit" onClick={() => openDispenseModal(u)}>Dispense</button><button className="action-btn edit" onClick={() => openHistoryModal(u)}>History</button><button className="action-btn edit" onClick={() => handleAddFunds(u.id)}>+$</button><button className="action-btn delete" onClick={() => handleDeductFunds(u.id)}>-$</button><button className="action-btn edit" onClick={() => openEditModal(u)}>Edit</button><button className="action-btn delete" onClick={() => handleDelete(u.id)}>Del</button></div></td></tr>))}
          </tbody></table></div>
        ) : searchTerm ? <div className="empty-state"><div className="empty-state-icon">🔍</div><h3>No patients found</h3><p>Try a different search term</p></div> : <div className="empty-state"><div className="empty-state-icon">👥</div><h3>No patients yet</h3></div>}
      </div>
      {showModal && (<div className="modal-overlay" onClick={() => !saving && setShowModal(false)}><div className="modal" onClick={e => e.stopPropagation()}><div className="modal-header"><h2 className="modal-title">{editingUser ? 'Edit Patient' : 'Add Patient'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div><div className="admin-form-grid"><div className="form-group"><label className="form-label">Full Name</label><input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} disabled={saving} /></div><div className="form-group"><label className="form-label">Phone</label><input type="tel" className="form-input" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} disabled={saving} /></div><div className="form-group full-width"><label className="form-label">Email</label><input type="email" className="form-input" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} disabled={saving} /></div><div className="form-group"><label className="form-label">{editingUser ? 'New Password' : 'Password'}</label><input type="password" className="form-input" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} disabled={saving} /></div>{!editingUser && <div className="form-group"><label className="form-label">Initial Balance</label><input type="number" className="form-input" value={formData.balance} onChange={e => setFormData({ ...formData, balance: parseFloat(e.target.value) || 0 })} disabled={saving} /></div>}</div><div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div></div></div>)}
      {showDispenseModal && dispenseUser && (<div className="modal-overlay" onClick={() => !saving && setShowDispenseModal(false)}><div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}><div className="modal-header"><h2 className="modal-title">Dispense to {dispenseUser.name}</h2><button className="modal-close" onClick={() => setShowDispenseModal(false)} disabled={saving}>×</button></div><div style={{ marginBottom: '12px', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}><strong>Patient Balance:</strong> ${dispenseUser.balance?.toFixed(2)}</div><div className="form-group"><label className="form-label">Select Product</label><div className="product-select-grid">{Object.entries(groupedProducts).map(([cat, prods]) => (<div key={cat}><h4 style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '12px 0 8px', textTransform: 'uppercase' }}>{cat}</h4>{prods.map(p => (<div key={p.id} className={`product-select-item ${selectedProduct?.id === p.id && !isOtherProduct ? 'selected' : ''}`} onClick={() => { setSelectedProduct(p); setIsOtherProduct(false); }}><span className="product-select-icon">{p.icon}</span><span>{p.name}</span></div>))}</div>))}<div><h4 style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '12px 0 8px', textTransform: 'uppercase' }}>Other</h4><div className={`product-select-item ${isOtherProduct ? 'selected' : ''}`} onClick={() => { setIsOtherProduct(true); setSelectedProduct(null); }}><span className="product-select-icon">📝</span><span>Other (Custom)</span></div></div></div></div>{isOtherProduct && <div className="form-group"><label className="form-label">Product Name *</label><input type="text" className="form-input" value={otherProductName} onChange={e => setOtherProductName(e.target.value)} placeholder="Enter product name..." disabled={saving} /></div>}<div className="admin-form-grid"><div className="form-group"><label className="form-label">Quantity</label><input type="number" className="form-input" value={dispenseQuantity} onChange={e => setDispenseQuantity(parseInt(e.target.value) || 1)} min="1" disabled={saving} /></div><div className="form-group"><label className="form-label">Cost ($)</label><input type="number" className="form-input" value={dispenseCost} onChange={e => setDispenseCost(parseFloat(e.target.value) || 0)} min="0" step="0.01" disabled={saving} /></div><div className="form-group full-width"><label className="form-label">Notes</label><input type="text" className="form-input" value={dispenseNotes} onChange={e => setDispenseNotes(e.target.value)} placeholder="Dosage, instructions, etc." disabled={saving} /></div></div>{dispenseCost > 0 && dispenseCost > dispenseUser.balance && <div className="balance-warning">Warning: Cost exceeds patient balance!</div>}<div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowDispenseModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleDispense} disabled={saving || (!selectedProduct && !isOtherProduct) || (isOtherProduct && !otherProductName.trim())}>{saving ? 'Dispensing...' : dispenseCost > 0 ? `Dispense & Charge $${dispenseCost.toFixed(2)}` : 'Dispense'}</button></div></div></div>)}
      {showHistoryModal && historyUser && (<div className="modal-overlay" onClick={() => setShowHistoryModal(false)}><div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}><div className="modal-header"><h2 className="modal-title">{historyUser.name}'s Products</h2><button className="modal-close" onClick={() => setShowHistoryModal(false)}>×</button></div>{userHistory.length > 0 ? <div className="product-history-list">{userHistory.map(p => (<div key={p.id} className="product-history-item"><div className="product-history-info"><h4>{p.product_name}</h4><p>Qty: {p.quantity} • {formatDate(p.created_at)}</p>{p.notes && <p className="product-notes">{p.notes}</p>}</div></div>))}</div> : <div className="empty-state"><div className="empty-state-icon">📦</div><h3>No products yet</h3></div>}</div></div>)}
      {showProfileModal && (<div className="modal-overlay" onClick={() => setShowProfileModal(false)}><div className="modal prescription-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}><div className="modal-header"><h2 className="modal-title">Patient Profile{profileData?.patient && `: ${profileData.patient.name}`}</h2><button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button></div>
        {profileLoading ? <div className="loading-spinner"><div className="spinner"></div></div> : profileData && (
          <div className="prescription-review">
            <div className="review-section"><h4>Patient Information</h4>
              <div className="review-grid">
                <div><span>Name:</span> {profileData.patient.first_name || ''} {profileData.patient.last_name || profileData.patient.name}</div>
                <div><span>Email:</span> {profileData.patient.email}</div>
                <div><span>Phone:</span> {profileData.patient.phone || '-'}</div>
                <div><span>DOB:</span> {profileData.patient.date_of_birth ? formatDate(profileData.patient.date_of_birth) : '-'}</div>
                <div><span>Gender:</span> {profileData.patient.gender || '-'}</div>
                <div><span>Balance:</span> <strong style={{color: 'var(--primary)'}}>${parseFloat(profileData.patient.balance || 0).toFixed(2)}</strong></div>
                <div><span>Client Source:</span> {editingClientSource === profileData.patient.id ? (
                  <select className="form-input" style={{display: 'inline-block', width: 'auto', padding: '4px 8px', marginLeft: '8px'}} defaultValue={profileData.patient.client_source || ''} onChange={e => handleUpdateClientSource(profileData.patient.id, e.target.value)} onBlur={() => setEditingClientSource(null)}>
                    <option value="">Not Set</option>
                    {adminUsers.map(admin => <option key={admin.id} value={admin.name}>{admin.name}</option>)}
                  </select>
                ) : (<span style={{cursor: 'pointer', textDecoration: 'underline', color: 'var(--primary)'}} onClick={() => setEditingClientSource(profileData.patient.id)}>{profileData.patient.client_source || 'Not Set'}</span>)}</div>
                {profileData.patient.how_heard_about_us && <div><span>How Heard (Intake):</span> {profileData.patient.how_heard_about_us}</div>}
              </div>
              {profileData.patient.street_address && <p style={{marginTop: '8px'}}><span>Address:</span> {profileData.patient.street_address}, {profileData.patient.city}, {profileData.patient.state} {profileData.patient.postal_code}</p>}
              {profileData.patient.emergency_contact_name && <p><span>Emergency Contact:</span> {profileData.patient.emergency_contact_name} ({profileData.patient.emergency_contact_phone})</p>}
            </div>

            <ReferralPanel patientId={profileData.patient.id} patientName={profileData.patient.name} />

            <div className="review-section"><h4>Current Vitals</h4>
              {profileData.vitals ? (
                <div className="review-grid">
                  <div><span>Weight:</span> {profileData.vitals.current_weight || '-'} lbs</div>
                  <div><span>Height:</span> {profileData.vitals.height_inches || '-'}"</div>
                  <div><span>Blood Pressure:</span> {profileData.vitals.blood_pressure || '-'}</div>
                  <div><span>BMI:</span> {profileData.vitals.bmi || '-'}</div>
                  <div><span>Pulse:</span> {profileData.vitals.pulse || '-'}</div>
                  <div><span>Last Updated:</span> {formatDate(profileData.vitals.updated_at)}</div>
                  {profileData.vitals.has_allergies && <div className="full-width" style={{color: '#dc2626'}}><span>Allergies:</span> {profileData.vitals.allergy_list}</div>}
                </div>
              ) : <p style={{color: 'var(--text-muted)'}}>No vitals recorded yet</p>}
            </div>

            {profileData.vitals?.clinical_reasoning && (
              <div className="review-section"><h4>Clinic Notes</h4><p>{profileData.vitals.clinical_reasoning}</p></div>
            )}

            {profileData.patient.primary_goal && (
              <div className="review-section"><h4>Goals</h4><p>{profileData.patient.primary_goal}</p></div>
            )}

            {(profileData.patient.metabolic_health || profileData.patient.cardiovascular || profileData.patient.intake_medications) && (
              <div className="review-section"><h4>Medical History</h4>
                {profileData.patient.metabolic_health && <p><span>Metabolic:</span> {(Array.isArray(profileData.patient.metabolic_health) ? profileData.patient.metabolic_health : []).join(', ') || 'None'}</p>}
                {profileData.patient.cardiovascular && <p><span>Cardiovascular:</span> {(Array.isArray(profileData.patient.cardiovascular) ? profileData.patient.cardiovascular : []).join(', ') || 'None'}</p>}
                {profileData.patient.intake_medications && <p><span>Medications:</span> {(Array.isArray(profileData.patient.intake_medications) ? profileData.patient.intake_medications : []).join(', ') || 'None'}</p>}
                {profileData.patient.intake_allergies && <p><span>Allergies (intake):</span> {profileData.patient.intake_allergies}</p>}
              </div>
            )}

            <div className="review-section"><h4>Medicines Dispensed ({profileData.products.length})</h4>
              {profileData.products.length > 0 ? (
                <div className="product-history-list" style={{maxHeight: '200px', overflowY: 'auto'}}>
                  {profileData.products.map(p => (
                    <div key={p.id} className="product-history-item">
                      <div className="product-history-info">
                        <h4>{p.product_name}</h4>
                        <p>Qty: {p.quantity} • {formatDate(p.created_at)}{p.dispensed_by_name && ` • By: ${p.dispensed_by_name}`}</p>
                        {p.notes && <p className="product-notes">{p.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p style={{color: 'var(--text-muted)'}}>No products dispensed yet</p>}
            </div>

            <div className="review-section"><h4>Prescription History ({profileData.prescriptions.length})</h4>
              {profileData.prescriptions.length > 0 ? (
                <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                  {profileData.prescriptions.map(rx => (
                    <div key={rx.id} style={{padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '8px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                        <strong>{formatDate(rx.created_at)}</strong>
                        <span style={{padding: '2px 8px', borderRadius: '8px', fontSize: '11px', background: rx.status === 'approved' ? '#d1fae5' : '#fef3c7', color: rx.status === 'approved' ? '#065f46' : '#92400e'}}>{rx.status === 'approved' ? 'Signed' : 'Pending'}</span>
                      </div>
                      {rx.semaglutide_dosing && <p style={{fontSize: '13px'}}><span>Semaglutide:</span> {rx.semaglutide_type} {rx.semaglutide_concentration} - {rx.semaglutide_dosing}</p>}
                      {rx.tirzepatide_dosing && <p style={{fontSize: '13px'}}><span>Tirzepatide:</span> {rx.tirzepatide_type} {rx.tirzepatide_concentration} - {rx.tirzepatide_dosing}</p>}
                      {rx.physician_name && <p style={{fontSize: '12px', color: 'var(--text-muted)'}}>Signed by: {rx.physician_name}</p>}
                    </div>
                  ))}
                </div>
              ) : <p style={{color: 'var(--text-muted)'}}>No prescriptions yet</p>}
            </div>

            <div className="review-section"><h4>Nutrition Intakes ({profileData.nutritionIntakes?.length || 0})</h4>
              {profileData.nutritionIntakes?.length > 0 ? (
                <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                  {profileData.nutritionIntakes.map(ni => (
                    <div key={ni.id} style={{padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '8px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                        <strong>{formatDate(ni.created_at)}</strong>
                        <span style={{padding: '2px 8px', borderRadius: '8px', fontSize: '11px', background: ni.status === 'completed' ? '#d1fae5' : '#fef3c7', color: ni.status === 'completed' ? '#065f46' : '#92400e'}}>{ni.status === 'completed' ? 'Completed' : 'Pending'}</span>
                      </div>
                      {ni.status === 'completed' && (
                        <>
                          {ni.meals_per_day && <p style={{fontSize: '13px'}}><span>Meals/day:</span> {ni.meals_per_day}</p>}
                          {ni.stress_level && <p style={{fontSize: '13px'}}><span>Stress level:</span> {ni.stress_level}/10</p>}
                          {ni.hardest_about_nutrition && <p style={{fontSize: '13px'}}><span>Hardest about nutrition:</span> {ni.hardest_about_nutrition}</p>}
                          <p style={{fontSize: '12px', color: 'var(--text-muted)'}}>Completed by: {ni.completed_by === 'patient' ? 'Patient (via email)' : 'Staff'}</p>
                        </>
                      )}
                      {ni.status === 'pending' && <p style={{fontSize: '12px', color: 'var(--text-muted)'}}>Awaiting patient response</p>}
                    </div>
                  ))}
                </div>
              ) : <p style={{color: 'var(--text-muted)'}}>No nutrition intakes yet</p>}
            </div>

            <div className="review-section"><h4>Notes &amp; History ({profileData.appointmentNotes?.length || 0})</h4>
              {profileData.appointmentNotes?.length > 0 ? (
                <div style={{maxHeight: '320px', overflowY: 'auto'}}>
                  {profileData.appointmentNotes.map(a => {
                    const dt = new Date(a.start_dt);
                    const when = dt.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
                                 dt.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit', hour12: true });
                    const badge = (label, bg, color) => (
                      <span style={{padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, background: bg, color: color, marginRight: '6px', textTransform: 'uppercase', letterSpacing: '0.3px'}}>{label}</span>
                    );
                    return (
                      <div key={a.id} style={{padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '8px', borderLeft: `3px solid ${a.service_color || '#3788d8'}`}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '6px', marginBottom: '8px'}}>
                          <strong style={{fontSize: '13px'}}>{when}</strong>
                          <span style={{fontSize: '12px', color: 'var(--text-muted)'}}>
                            {a.service_name || 'Unknown service'} · {a.staff_name || 'Unassigned'}
                            {a.outcome && <> · outcome: <strong>{a.outcome.replace(/_/g, ' ')}</strong></>}
                            {!a.outcome && a.status && <> · status: {a.status}</>}
                          </span>
                        </div>
                        {a.notes && (
                          <div style={{marginBottom: '6px'}}>
                            {badge('Patient-visible', '#dbeafe', '#1e40af')}
                            <span style={{fontSize: '13px', whiteSpace: 'pre-wrap'}}>{a.notes}</span>
                          </div>
                        )}
                        {a.internal_notes && (
                          <div style={{marginBottom: '6px'}}>
                            {badge('Internal', '#f3f4f6', '#374151')}
                            <span style={{fontSize: '13px', whiteSpace: 'pre-wrap'}}>{a.internal_notes}</span>
                          </div>
                        )}
                        {a.outcome_notes && (
                          <div style={{marginBottom: '6px'}}>
                            {badge('Outcome', '#d1fae5', '#065f46')}
                            <span style={{fontSize: '13px', whiteSpace: 'pre-wrap'}}>{a.outcome_notes}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : <p style={{color: 'var(--text-muted)'}}>No notes found</p>}
            </div>
          </div>
        )}
        <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowProfileModal(false)}>Close</button></div>
      </div></div>)}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminProductsPage() {
  const [products, setProducts] = useState([]); const [loading, setLoading] = useState(true); const [showModal, setShowModal] = useState(false); const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', category: 'Supplements', icon: '💊', price: '', showInStore: false, medicallyRestricted: false, inventory_tracked: false, stock_quantity: 0, low_stock_threshold: 5 }); const [saving, setSaving] = useState(false); const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [stockAdjust, setStockAdjust] = useState(null); const [stockForm, setStockForm] = useState({ quantity: '', reason: 'restock', notes: '' }); const [alerts, setAlerts] = useState([]);
  const fetchProducts = async () => { try { setProducts(await api.get('/api/admin/products')); } catch (e) { console.error(e); } finally { setLoading(false); } };
  const fetchAlerts = async () => { try { setAlerts(await api.get('/api/admin/inventory/alerts')); } catch (e) { console.error(e); } };
  useEffect(() => { fetchProducts(); fetchAlerts(); }, []);
  const openEditModal = (p) => { setEditingProduct(p); setFormData({ name: p.name, description: p.description, category: p.category, icon: p.icon, price: p.price || '', showInStore: p.show_in_store || false, medicallyRestricted: p.medically_restricted || false, inventory_tracked: p.inventory_tracked || false, stock_quantity: p.stock_quantity || 0, low_stock_threshold: p.low_stock_threshold || 5 }); setShowModal(true); };
  const openNewModal = () => { setEditingProduct(null); setFormData({ name: '', description: '', category: 'Supplements', icon: '💊', price: '', showInStore: false, medicallyRestricted: false, inventory_tracked: false, stock_quantity: 0, low_stock_threshold: 5 }); setShowModal(true); };
  const handleStockAdjust = async () => {
    if (!stockForm.quantity) return;
    try {
      setSaving(true);
      const result = await api.post(`/api/admin/products/${stockAdjust.id}/stock-adjust`, { quantity: parseInt(stockForm.quantity), reason: stockForm.reason, notes: stockForm.notes || null });
      setToast({ message: `Stock updated to ${result.stockAfter}${result.warning ? ' — ' + result.warning : ''}`, type: result.warning ? 'warning' : 'success' });
      setStockAdjust(null); setStockForm({ quantity: '', reason: 'restock', notes: '' });
      fetchProducts(); fetchAlerts();
    } catch (e) { setToast({ message: e.message, type: 'error' }); } finally { setSaving(false); }
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingProduct) {
        await api.put(`/api/admin/products/${editingProduct.id}`, { ...formData, active: true });
        if (formData.showInStore || formData.price) {
          await api.put(`/api/admin/products/${editingProduct.id}/store`, { showInStore: formData.showInStore, price: formData.price || 0 });
        }
      } else {
        await api.post('/api/admin/products', formData);
      }
      setToast({ message: editingProduct ? 'Updated!' : 'Created!', type: 'success' });
      setShowModal(false);
      fetchProducts(); fetchAlerts();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/api/admin/products/${id}`); setToast({ message: 'Deleted!', type: 'success' }); fetchProducts(); } catch (error) { setToast({ message: error.message, type: 'error' }); } };
  const grouped = products.reduce((acc, p) => { if (!acc[p.category]) acc[p.category] = []; acc[p.category].push(p); return acc; }, {});
  const categories = ['Body Contouring', 'Wellness Optimization', 'Supplements', 'Coaching'];
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Products</h1><p className="page-subtitle">Manage products for dispensing and store</p></div>
      {/* Low stock alerts */}
      {alerts.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '14px 18px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '16px' }}>&#9888;</span>
            <span style={{ fontWeight: 600, color: '#991b1b', fontSize: '14px' }}>{alerts.length} product{alerts.length !== 1 ? 's' : ''} low or out of stock</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {alerts.map(a => (
              <span key={a.id} style={{ background: a.stock_quantity <= 0 ? '#fee2e2' : '#fef3c7', color: a.stock_quantity <= 0 ? '#dc2626' : '#92400e', padding: '3px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }} onClick={() => { setStockAdjust(a); setStockForm({ quantity: '', reason: 'restock', notes: '' }); }}>
                {a.icon} {a.name}: {a.stock_quantity <= 0 ? 'OUT' : a.stock_quantity + ' left'}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header"><h2 className="section-title">All Products</h2><button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add</button></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : Object.entries(grouped).map(([cat, items]) => (<div key={cat} style={{ marginBottom: '24px' }}><h3 style={{ fontSize: '13px', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>{cat}</h3><div className="products-grid">{items.map(p => (<div key={p.id} className="product-card"><div className="product-card-icon">{p.icon}</div><div className="product-card-info"><h4>{p.name}</h4><p>{p.description?.substring(0, 50)}...</p>{p.show_in_store && <span style={{ fontSize: '12px', color: 'var(--accent-gold)', fontWeight: 600 }}>${parseFloat(p.price || 0).toFixed(2)} - In Store</span>}{p.medically_restricted && <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600, display: 'block', marginTop: '4px' }}>Rx Required</span>}{p.inventory_tracked && <span style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginTop: '4px', color: p.stock_quantity <= 0 ? '#dc2626' : p.stock_quantity <= p.low_stock_threshold ? '#d97706' : '#059669' }}>{p.stock_quantity <= 0 ? 'OUT OF STOCK' : p.stock_quantity + ' in stock'}</span>}</div><div className="product-card-actions">{p.inventory_tracked && <button className="action-btn" onClick={() => { setStockAdjust(p); setStockForm({ quantity: '', reason: 'restock', notes: '' }); }} title="Adjust stock" style={{ fontSize: '11px' }}>Stock</button>}<button className="action-btn edit" onClick={() => openEditModal(p)}>Edit</button><button className="action-btn delete" onClick={() => handleDelete(p.id)}>Del</button></div></div>))}</div></div>))}
      </div>
      {showModal && (<div className="modal-overlay" onClick={() => !saving && setShowModal(false)}><div className="modal" onClick={e => e.stopPropagation()}><div className="modal-header"><h2 className="modal-title">{editingProduct ? 'Edit' : 'Add'} Product</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div><div className="admin-form-grid"><div className="form-group full-width"><label className="form-label">Name</label><input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} disabled={saving} /></div><div className="form-group full-width"><label className="form-label">Description</label><textarea className="form-input" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} disabled={saving} rows={3} /></div><div className="form-group"><label className="form-label">Category</label><select className="form-input" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} disabled={saving}>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select></div><div className="form-group"><label className="form-label">Icon</label><input type="text" className="form-input" value={formData.icon} onChange={e => setFormData({ ...formData, icon: e.target.value })} disabled={saving} /></div><div className="form-group"><label className="form-label">Price ($)</label><input type="number" step="0.01" className="form-input" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} disabled={saving} placeholder="0.00" /></div><div className="form-group"><label className="form-label">Store</label><label className="consent-label"><input type="checkbox" checked={formData.showInStore} onChange={e => setFormData({ ...formData, showInStore: e.target.checked })} disabled={saving} /><span>Show in patient store</span></label></div><div className="form-group"><label className="form-label">Restrictions</label><label className="consent-label"><input type="checkbox" checked={formData.medicallyRestricted} onChange={e => setFormData({ ...formData, medicallyRestricted: e.target.checked })} disabled={saving} /><span>Medically restricted (admin purchase only)</span></label></div><div className="form-group"><label className="form-label">Inventory</label><label className="consent-label"><input type="checkbox" checked={formData.inventory_tracked} onChange={e => setFormData({ ...formData, inventory_tracked: e.target.checked })} disabled={saving} /><span>Track inventory for this product</span></label></div>{formData.inventory_tracked && <><div className="form-group"><label className="form-label">Current Stock{!editingProduct ? '' : ' (use Stock button to adjust)'}</label><input type="number" className="form-input" value={formData.stock_quantity} onChange={e => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })} disabled={saving || !!editingProduct} /></div><div className="form-group"><label className="form-label">Low Stock Alert Threshold</label><input type="number" className="form-input" value={formData.low_stock_threshold} onChange={e => setFormData({ ...formData, low_stock_threshold: parseInt(e.target.value) || 5 })} disabled={saving} /></div></>}</div><div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div></div></div>)}
      {/* Stock Adjustment Modal */}
      {stockAdjust && (<div className="modal-overlay" onClick={() => !saving && setStockAdjust(null)}><div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}><div className="modal-header"><h2 className="modal-title">Adjust Stock: {stockAdjust.name}</h2><button className="modal-close" onClick={() => setStockAdjust(null)} disabled={saving}>×</button></div><p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '0 0 16px' }}>Current stock: <strong>{stockAdjust.stock_quantity}</strong></p><div className="form-group"><label className="form-label">Quantity (positive = add, negative = remove)</label><input type="number" className="form-input" value={stockForm.quantity} onChange={e => setStockForm({ ...stockForm, quantity: e.target.value })} placeholder="e.g. 25 or -3" disabled={saving} /></div><div className="form-group"><label className="form-label">Reason</label><select className="form-input" value={stockForm.reason} onChange={e => setStockForm({ ...stockForm, reason: e.target.value })} disabled={saving}><option value="restock">Restock (shipment received)</option><option value="return">Return (from patient)</option><option value="adjustment">Adjustment (manual count)</option><option value="shrinkage">Shrinkage (lost/damaged)</option><option value="correction">Correction (fix error)</option></select></div><div className="form-group"><label className="form-label">Notes (optional)</label><input type="text" className="form-input" value={stockForm.notes} onChange={e => setStockForm({ ...stockForm, notes: e.target.value })} placeholder="e.g. Shipment #1234" disabled={saving} /></div><div className="modal-actions"><button className="btn btn-secondary" onClick={() => setStockAdjust(null)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleStockAdjust} disabled={saving || !stockForm.quantity}>{saving ? 'Saving...' : 'Apply'}</button></div></div></div>)}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminAuditLogPage() {
  const [logs, setLogs] = useState([]); const [loading, setLoading] = useState(true); const [filter, setFilter] = useState('');
  useEffect(() => { api.get('/api/admin/audit-log?limit=200').then(data => setLogs(data.logs || [])).catch(console.error).finally(() => setLoading(false)); }, []);
  const formatDateTime = (d) => new Date(d).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const getColor = (a) => { if (a.includes('LOGIN')) return '#3b82f6'; if (a.includes('CREATE')) return '#10b981'; if (a.includes('DELETE')) return '#ef4444'; if (a.includes('UPDATE')) return '#f59e0b'; if (a.includes('FUNDS')) return '#8b5cf6'; if (a.includes('DISPENSE')) return '#06b6d4'; return '#6b7280'; };
  const filtered = filter ? logs.filter(l => l.action.toLowerCase().includes(filter.toLowerCase()) || l.user_name?.toLowerCase().includes(filter.toLowerCase())) : logs;
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Audit Log</h1><p className="page-subtitle">HIPAA-compliant activity tracking</p></div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Activity</h2><input type="text" className="form-input" style={{ maxWidth: '200px' }} placeholder="Filter..." value={filter} onChange={e => setFilter(e.target.value)} /></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : filtered.length > 0 ? (
          <div className="audit-log-list">{filtered.map(log => (<div key={log.id} className="audit-log-item"><div className="audit-log-icon" style={{ background: getColor(log.action) }}>{log.action.charAt(0)}</div><div className="audit-log-content"><div className="audit-log-header"><span className="audit-action" style={{ color: getColor(log.action) }}>{log.action.replace(/_/g, ' ')}</span><span className="audit-time">{formatDateTime(log.created_at)}</span></div><div className="audit-log-details"><span>By: {log.user_name || 'System'}</span>{log.ip_address && <span> • IP: {log.ip_address}</span>}</div>{log.details && <div className="audit-log-data"><pre>{JSON.stringify(typeof log.details === 'string' ? JSON.parse(log.details) : log.details, null, 2)}</pre></div>}</div></div>))}</div>
        ) : <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No logs yet</h3></div>}
      </div>
    </div>
  );
}

function AdminSettingsPage() {
  const [ghlStatus, setGhlStatus] = useState({ connected: false }); const [loading, setLoading] = useState(true); const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [emailSettings, setEmailSettings] = useState({ doctorEmail: '', clinicEmail: '', fromEmail: '' });
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/ghl/status'),
      api.get('/api/admin/settings/email')
    ]).then(([ghl, email]) => {
      setGhlStatus(ghl);
      setEmailSettings({ doctorEmail: email.doctorEmail || '', clinicEmail: email.clinicEmail || '', fromEmail: email.fromEmail || '' });
    }).catch(console.error).finally(() => setLoading(false));
    const params = new URLSearchParams(window.location.search);
    if (params.get('ghl_success')) { setToast({ message: 'Connected!', type: 'success' }); window.history.replaceState({}, '', '/admin/settings'); }
    else if (params.get('ghl_error')) { setToast({ message: 'Failed', type: 'error' }); window.history.replaceState({}, '', '/admin/settings'); }
  }, []);

  const handleConnect = async () => { try { const { authUrl } = await api.get('/api/ghl/auth'); window.location.href = authUrl; } catch (error) { setToast({ message: 'Failed', type: 'error' }); } };

  const handleSaveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await api.put('/api/admin/settings/email', emailSettings);
      setToast({ message: 'Email settings saved!', type: 'success' });
    } catch (error) {
      setToast({ message: error.message || 'Failed to save', type: 'error' });
    } finally { setSavingEmail(false); }
  };

  if (loading) return <div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div>;
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Settings</h1><p className="page-subtitle">Integrations & Notifications</p></div>

      <div className="section-card" style={{ marginBottom: '24px' }}>
        <h2 className="section-title" style={{ marginBottom: '24px' }}>Email Notifications</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '20px', fontSize: '14px' }}>Configure email addresses for automated notifications. The clinic email receives intake form submissions and is CC'd on prescription signing requests.</p>
        <div className="admin-form-grid">
          <div className="form-group">
            <label className="form-label">Doctor Email</label>
            <input type="email" className="form-input" placeholder="doctor@example.com" value={emailSettings.doctorEmail} onChange={e => setEmailSettings({...emailSettings, doctorEmail: e.target.value})} disabled={savingEmail} />
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Receives prescription signing requests</p>
          </div>
          <div className="form-group">
            <label className="form-label">Clinic Email</label>
            <input type="email" className="form-input" placeholder="clinic@example.com" value={emailSettings.clinicEmail} onChange={e => setEmailSettings({...emailSettings, clinicEmail: e.target.value})} disabled={savingEmail} />
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Receives intake notifications, CC'd on signing requests</p>
          </div>
          <div className="form-group full-width">
            <label className="form-label">From Email (optional)</label>
            <input type="email" className="form-input" placeholder="noreply@ascenttrim.com" value={emailSettings.fromEmail} onChange={e => setEmailSettings({...emailSettings, fromEmail: e.target.value})} disabled={savingEmail} />
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Must be verified in SendGrid. Leave blank to use default.</p>
          </div>
        </div>
        <button className="btn btn-gold" onClick={handleSaveEmailSettings} disabled={savingEmail} style={{ marginTop: '16px' }}>{savingEmail ? 'Saving...' : 'Save Email Settings'}</button>
      </div>

      <div className="section-card"><h2 className="section-title" style={{ marginBottom: '24px' }}>GoHighLevel</h2><div className="integration-status"><div className="integration-info"><div className={`status-indicator ${ghlStatus.connected ? 'connected' : 'disconnected'}`}>{ghlStatus.connected ? '✓' : '○'}</div><div><h4>GHL CRM</h4><p>{ghlStatus.connected ? `Location: ${ghlStatus.locationId}` : 'Not connected'}</p></div></div><button className={`btn ${ghlStatus.connected ? 'btn-secondary' : 'btn-gold'}`} onClick={handleConnect}>{ghlStatus.connected ? 'Reconnect' : 'Connect'}</button></div></div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}


function AdminGuidePage() {
  const [activeSection, setActiveSection] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const sections = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'patients', label: 'Patient Management', icon: '👥' },
    { id: 'scheduling', label: 'Scheduling', icon: '📅' },
    { id: 'reminders', label: 'Reminders & Notifications', icon: '🔔' },
    { id: 'ghl', label: 'GoHighLevel Integration', icon: '🔗' },
    { id: 'commerce', label: 'Shop & Payments', icon: '💳' },
    { id: 'analytics', label: 'Analytics & Reports', icon: '📊' },
    { id: 'nutrition', label: 'Nutrition Roadmaps', icon: '🥗' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'api_keys', label: 'API Keys & Integrations', icon: '🔑' },
  ];

  const guideContent = {
    overview: {
      title: 'Portal Overview',
      content: (
        <>
          <p>Welcome to the Ascent Trim Portal admin guide. This portal manages patient registration, scheduling, payments, and clinic operations.</p>

          <h3>Quick Links</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', margin: '16px 0' }}>
            {[
              { label: 'Patient Status Board', path: '/admin/patient-board', desc: 'See all patients by stage' },
              { label: 'Calendar', path: '/admin/calendar', desc: 'View & manage appointments' },
              { label: 'Quick Book', path: '/admin/quick-book', desc: 'Book for a patient' },
              { label: 'Intake Reviews', path: '/admin/intake-reviews', desc: 'Review patient forms' },
              { label: 'Nutrition Roadmaps', path: '/admin/nutrition-roadmaps', desc: 'Generate nutrition PDFs' },
            ].map(link => (
              <a key={link.path} href={link.path} style={{
                display: 'block', padding: '16px', background: 'var(--cream)', borderRadius: '8px',
                textDecoration: 'none', color: 'inherit', border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>{link.label}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{link.desc}</div>
              </a>
            ))}
          </div>

          <h3>System Architecture</h3>
          <ul>
            <li><strong>Patients</strong> register and complete intake forms</li>
            <li><strong>Appointments</strong> are booked via portal or by staff</li>
            <li><strong>Reminders</strong> are sent automatically via GoHighLevel</li>
            <li><strong>Payments</strong> are processed through Stripe</li>
            <li><strong>Google Calendar</strong> syncs appointments both ways</li>
          </ul>
        </>
      )
    },
    patients: {
      title: 'Patient Management',
      content: (
        <>
          <h3>Patient Status Board</h3>
          <p><strong>Location:</strong> Patients → Status Board</p>
          <p>The Kanban board shows all patients organized by their journey stage:</p>
          <ul>
            <li><strong>Lead</strong> - New registration, no intake yet</li>
            <li><strong>Intake Pending</strong> - Registered but intake not complete</li>
            <li><strong>Scheduled</strong> - Has an upcoming appointment</li>
            <li><strong>Active</strong> - Recently seen (within 30 days)</li>
            <li><strong>Follow-up Needed</strong> - No appointment in 30-90 days</li>
            <li><strong>Inactive</strong> - No activity in 90+ days</li>
          </ul>
          <p><strong>To change status:</strong> Drag a patient card to a different column, or click the card and use the dropdown.</p>
          <p><strong>Admin Notes:</strong> Click any patient card to view/edit private notes visible only to staff.</p>

          <h3>Manage Users</h3>
          <p><strong>Location:</strong> Patients → Manage Users</p>
          <ul>
            <li>View all patients and their details</li>
            <li>Click a patient to see full profile</li>
            <li>Link patients to GHL contacts manually</li>
            <li>View intake form, appointments, and prescriptions</li>
          </ul>

          <h3>Intake Reviews</h3>
          <p><strong>Location:</strong> Patients → Intake Reviews</p>
          <p>Shows all submitted intake forms. Click to review full health history, medications, and goals.</p>

          <h3>Doctor Queue</h3>
          <p><strong>Location:</strong> Patients → Doctor Queue</p>
          <p>Prescription requests waiting for doctor review and signature.</p>
        </>
      )
    },
    scheduling: {
      title: 'Scheduling System',
      content: (
        <>
          <h3>Admin Calendar</h3>
          <p><strong>Location:</strong> Scheduling → Calendar</p>
          <ul>
            <li><strong>View modes:</strong> Month, Week, Day, List</li>
            <li><strong>Filters:</strong> By staff member, service type, or room</li>
            <li><strong>Color coding:</strong> Each service has a distinct color</li>
            <li><strong>Gray events (📅):</strong> External Google Calendar events (read-only)</li>
            <li><strong>Sync button:</strong> "Sync Google Calendars" pulls latest external events</li>
          </ul>
          <p>Click any appointment to view details, cancel, or reschedule.</p>

          <h3>Needs Review (post-appointment outcome)</h3>
          <p><strong>Location:</strong> Scheduling → Needs Review</p>
          <p>After every past appointment, the assigned provider should record what happened. The sidebar badge shows how many appointments are pending review for you.</p>
          <ul>
            <li><strong>Outcome:</strong> Showed, No-show, Cancelled (within 24h), or Rescheduled on-site</li>
            <li><strong>Notes:</strong> Free-form notes about what happened</li>
            <li><strong>Enrollment intent</strong> (Free In Clinic Assessment only): Enrolled, Interested, Undecided, or Not interested</li>
            <li><strong>Default view:</strong> Only appointments assigned to you. Toggle "Show all providers" to see everyone's.</li>
            <li>You can also record an outcome directly from the calendar — click a past appointment and use the Appointment Outcome section in the modal.</li>
            <li>Outcomes can be edited after saving; the original and updated values are recorded in the audit log.</li>
          </ul>
          <p><em>Why this matters:</em> No-show and "not interested" outcomes are what trigger the S6 and S8 recovery sequences in GoHighLevel (once that webhook wiring is enabled).</p>

          <h3>Quick Book (Staff Booking)</h3>
          <p><strong>Location:</strong> Scheduling → Quick Book</p>
          <p>Book appointments on behalf of patients:</p>
          <ol>
            <li>Select a patient from the dropdown</li>
            <li>Choose a service</li>
            <li>Pick a provider (or "Any Available")</li>
            <li>Select date and available time slot</li>
            <li>Confirm booking</li>
          </ol>

          <h3>Services</h3>
          <p><strong>Location:</strong> Scheduling → Services</p>
          <p>Configure appointment types offered:</p>
          <ul>
            <li><strong>Duration:</strong> Length of appointment</li>
            <li><strong>Buffer:</strong> Time between appointments</li>
            <li><strong>Public:</strong> Whether patients can self-book</li>
            <li><strong>GHL Webhook:</strong> URL to trigger on booking (for automations)</li>
          </ul>

          <h3>Availability</h3>
          <p><strong>Location:</strong> Scheduling → Availability</p>
          <p>Set recurring weekly schedules for each staff member. Example: "Available Mon-Fri 9am-5pm"</p>

          <h3>Time Blocks</h3>
          <p><strong>Location:</strong> Scheduling → Time Blocks</p>
          <p>Block off time that shouldn't be bookable:</p>
          <ul>
            <li>PTO / vacation days</li>
            <li>Lunch breaks</li>
            <li>Meetings</li>
            <li>Can apply to one staff member or all staff</li>
          </ul>

          <h3>Rooms</h3>
          <p><strong>Location:</strong> Scheduling → Rooms</p>
          <p>Configure treatment rooms. Services can require specific rooms to prevent double-booking.</p>

          <h3>Google Calendar Sync</h3>
          <p>Two-way sync is enabled:</p>
          <ul>
            <li><strong>Outbound:</strong> Portal appointments → Staff's Google Calendar</li>
            <li><strong>Inbound:</strong> Staff's external events → Block portal slots (every 10 min)</li>
          </ul>
          <p>External events appear gray on the admin calendar and automatically prevent double-booking.</p>

          <h3>Public Booking Pages</h3>
          <p>Patients can self-book via these public URLs:</p>
          <ul>
            <li><strong>/book</strong> - Full booking flow (select service, provider, time)</li>
            <li><strong>/schedule-assessment</strong> - Dedicated landing page for Free In Clinic Assessment only</li>
          </ul>
          <p>Share <code>portal.ascenttrim.com/schedule-assessment</code> for marketing the free consultation.</p>

          <h3>Embeddable Booking Widget</h3>
          <p>The booking page can be embedded on external websites (e.g., WordPress):</p>
          <ul>
            <li>Add this to any webpage: <code>{'<div id="ascent-booking"></div><script src="https://portal.ascenttrim.com/embed/booking.js"></script>'}</code></li>
            <li>The widget auto-resizes and matches the host page</li>
            <li>Allowed origins are configured in the server environment</li>
          </ul>
        </>
      )
    },
    reminders: {
      title: 'Reminders & Notifications',
      content: (
        <>
          <h3>How Reminders Work</h3>
          <p>When an appointment is booked, the system automatically schedules reminders:</p>
          <ol>
            <li><strong>Confirmation</strong> - Sent immediately on booking</li>
            <li><strong>24-hour reminder</strong> - Day before appointment</li>
            <li><strong>1-hour reminder</strong> - Hour before appointment</li>
            <li><strong>Follow-up</strong> - Day after appointment (optional)</li>
          </ol>

          <h3>Reminder Templates</h3>
          <p><strong>Location:</strong> Scheduling → Reminders</p>
          <p>Each service can have custom message templates. Available variables:</p>
          <ul>
            <li><code>{'{{first_name}}'}</code> - Patient's first name</li>
            <li><code>{'{{service_name}}'}</code> - Appointment type</li>
            <li><code>{'{{provider_name}}'}</code> - Staff member name</li>
            <li><code>{'{{appointment_display}}'}</code> - "Tuesday, March 5th at 2:30 PM"</li>
            <li><code>{'{{manage_url}}'}</code> - Link to cancel/reschedule</li>
          </ul>

          <h3>Delivery</h3>
          <p><strong>Email reminders</strong> are sent directly from the portal via SendGrid — no GHL involvement.</p>
          <p><strong>SMS reminders</strong> are currently sent via GHL webhook (the portal fires a webhook with <code>event: reminder_sms</code> and the pre-rendered message body at the correct time). When Twilio A2P verification is complete, SMS will send directly from the portal too.</p>
          <p>The portal handles all timing — GHL just delivers the text message.</p>

          <h3>Magic Links</h3>
          <p>Patients receive a unique link in reminders that lets them cancel or reschedule without logging in.</p>

          <h3>Webhook Logging</h3>
          <p><strong>Location:</strong> Settings → Webhook Logs</p>
          <p>Every reminder and webhook fire is logged with:</p>
          <ul>
            <li>Timestamp, type, service name, appointment ID</li>
            <li>Provider used: <strong>sendgrid-direct</strong> (email), <strong>twilio-direct</strong> (SMS), or <strong>ghl-webhook</strong> (SMS via GHL)</li>
            <li>HTTP status code and success/failure</li>
            <li>Error details if delivery failed</li>
          </ul>
          <p>Use the filters to narrow by type (GHL Appointment, Reminder 24h, etc.) or status (Success/Failed).</p>
        </>
      )
    },
    ghl: {
      title: 'GoHighLevel Integration',
      content: (
        <>
          <h3>Connection Status</h3>
          <p><strong>Location:</strong> Settings → GoHighLevel</p>
          <p>Shows whether GHL is connected and which Location (sub-account) is linked.</p>

          <h3>What Syncs from GHL</h3>
          <ul>
            <li><strong>Contact Source</strong> - Where the lead came from (e.g., "Facebook Ad")</li>
            <li><strong>Contact Tags</strong> - Tags applied in GHL</li>
            <li><strong>Pipeline/Opportunities</strong> - View GHL pipeline stages</li>
          </ul>

          <h3>Patient Sources Dashboard</h3>
          <p><strong>Location:</strong> Analytics → Patient Sources</p>
          <ul>
            <li>Charts showing where patients come from</li>
            <li>"Sync from GHL" pulls latest source data</li>
            <li>"Bulk Link by Email" matches patients to GHL contacts</li>
          </ul>

          <h3>GHL Pipelines</h3>
          <p><strong>Location:</strong> Analytics → Campaigns (GHL Pipelines tab)</p>
          <p>View live pipeline data from GHL - see contacts in each stage.</p>

          <h3>Auto-Sync</h3>
          <p>Source and tag data syncs automatically every 3 hours.</p>

          <h3>Webhook Configuration</h3>
          <p>All 18 services share a single GHL webhook URL. When an appointment is booked, the webhook payload includes:</p>
          <ul>
            <li><code>service_name</code> and <code>service_id</code> — to branch workflows by service type</li>
            <li><code>is_existing_patient</code> — whether the patient email exists in the portal</li>
            <li><code>has_completed_intake</code> — whether they have submitted an intake form</li>
          </ul>
          <p>Use these fields in GHL to create different confirmation paths for new vs. existing patients.</p>

          <h3>Troubleshooting</h3>
          <ul>
            <li><strong>"GHL not connected"</strong> - Go to Settings and click Connect</li>
            <li><strong>Must be logged into GHL</strong> in another tab before connecting</li>
            <li><strong>Use incognito</strong> if connection loops</li>
          </ul>
        </>
      )
    },
    commerce: {
      title: 'Shop & Payments',
      content: (
        <>
          <h3>Products</h3>
          <p><strong>Location:</strong> Commerce → Products</p>
          <p>Manage items available in the patient shop. Set prices, descriptions, and availability.</p>

          <h3>Orders</h3>
          <p><strong>Location:</strong> Commerce → Orders</p>
          <p>View and manage patient orders. Mark as fulfilled, view payment status.</p>
          <p>The <strong>Paid via</strong> column shows which processor took the money:</p>
          <ul>
            <li><strong>Stripe</strong> — appears in the Stripe Dashboard.</li>
            <li><strong>NMI</strong> — processed by NMI/LuQra. <strong>These will never appear in Stripe.</strong> Reconcile them in the NMI portal instead.</li>
            <li><strong>Clinic cash / paid other / split cards</strong> — no card processor was involved; settled outside the gateway.</li>
          </ul>
          <p>A red <strong>Not settled</strong> or <strong>Unverified</strong> tag means the gateway approved the charge but <strong>the money has not actually arrived</strong>. A banner at the top of the page totals these. Do not treat a flagged order as collected — check the processor account first.</p>

          <h3>Referral Discount (first purchase)</h3>
          <p><strong>Location:</strong> applied automatically on Commerce → Orders → Create Order</p>
          <p><strong>The discount goes to the referred patient, not the person who referred them.</strong> Referrers earn no credit, no Clinic Cash and no cash — they are thanked in person. Do not tell a patient they will earn anything for referring.</p>
          <p>When a patient who was referred makes their <strong>first</strong> purchase:</p>
          <ul>
            <li>Under $250 — no discount, but the benefit is used up</li>
            <li>$250 to $499 — <strong>$25 off</strong></li>
            <li>$500 or more — <strong>$50 off</strong></li>
          </ul>
          <p>The bands are measured on the price <em>before</em> any discount, so a $250 purchase counts as $250 even though they pay $225. It is one time, at the first visit, and it does not stack — if a member discount or coupon applies, the referral benefit is held back for a later purchase.</p>
          <p><strong>Record the referral before you ring up their first purchase</strong> (Patients → patient → Referrals → Set). The discount only applies if the portal already knows they were referred. A name typed into the free-text box works the same as a linked patient.</p>
          <p>The order screen shows the discount as its own line with the referrer's name. If the purchase is under $250 it shows an amber warning instead — worth mentioning to the patient if they are close to the threshold, since another item could be worth $25 to them.</p>
          <p>Refunding an order in full releases the benefit, and the patient can use it again on a later first purchase. This is not retroactive: patients who bought before the programme started do not get a discount.</p>

          <h3>Medication Pickup (Signed Dispense)</h3>
          <p><strong>Location:</strong> Commerce → Orders → <em>Dispense</em> button on any order with a medication line item</p>
          <p>Medications (products flagged <strong>Medically Restricted</strong>) can only be handed to the patient through a signed pickup:</p>
          <ol>
            <li>Open the order on the Orders page and click <strong>Dispense</strong>. The button appears when the order contains at least one medication line item and has not yet been marked picked up.</li>
            <li>For each medication being handed over, confirm the quantity and enter the lot number printed on the vial or box. Adjust the quantity down if the patient is only picking up part of the order — remaining doses can be dispensed on a later visit.</li>
            <li>Click <strong>Present to Patient →</strong> and hand the tablet to the patient. They will see the itemized list of medications and lot numbers, sign once, and click <strong>Save Signature & Complete Pickup</strong>.</li>
            <li>The signed pickup is recorded atomically: one <em>dispense pickup</em> record with the signature, plus one dispense history row per medication line with its own lot number. Inventory is decremented exactly once per line.</li>
          </ol>
          <p><strong>Rules:</strong></p>
          <ul>
            <li>The patient signs <strong>once</strong>, even for multi-medication pickups. Lot numbers are still recorded per line.</li>
            <li>Completed pickups are immutable. If a mistake is discovered, contact the developer.</li>
            <li>Medications cannot be dispensed through the general Dispense modal on the Users page — they only appear as options through this order-based pickup workflow.</li>
            <li>Non-medication products (light therapy accessories, supplements, etc.) still use the legacy Dispense button on the Users page — no signature is required.</li>
            <li>The patient signs on the same tablet you're already logged into. There is no public URL, no email link, and no separate signing screen for the patient's own device.</li>
          </ul>

          <h3>Memberships</h3>
          <p><strong>Location:</strong> Commerce → Memberships</p>
          <p>Configure subscription plans. Patients can subscribe via Stripe.</p>

          <h3>Coupons</h3>
          <p><strong>Location:</strong> Commerce → Coupons</p>
          <p>Create discount codes for products and services.</p>

          <h3>Card Processing — Stripe and NMI</h3>
          <p><strong>The portal uses two card processors, not one.</strong> Which one handled a given order is shown in the <strong>Paid via</strong> column on the Orders page, and it is the single most important thing to check when a payment appears to be missing.</p>
          <ul>
            <li><strong>Stripe</strong> — view in the Stripe Dashboard.</li>
            <li><strong>NMI / LuQra</strong> — view in the NMI portal. An NMI sale is <em>invisible</em> in Stripe. An order missing from Stripe is not necessarily a missing payment; check <strong>Paid via</strong> first.</li>
          </ul>
          <p><strong>&quot;Paid&quot; does not mean &quot;money received.&quot;</strong> An order is marked paid as soon as the gateway approves the charge. The funds settle afterwards, normally within 24–48 hours. If a charge has not settled after 48 hours the Orders page flags it in red — that money has not arrived and may never arrive. This usually means the gateway account is in test mode or is not fully activated for settlement.</p>
          <p><strong>If a card payment is refused with &quot;Gateway is in TEST MODE&quot;</strong>, that is a deliberate safety block, not a card problem. It means the payment gateway approved the card without actually contacting the bank, so no money would have been taken. Do not retry and do not hand over product — the gateway needs switching out of Test Mode in the LuQra portal first.</p>
          <p>Patient clinic cash balances are stored in the portal and can be used for purchases.</p>
        </>
      )
    },
    analytics: {
      title: 'Analytics & Reports',
      content: (
        <>
          <h3>Metrics Dashboard</h3>
          <p><strong>Location:</strong> Analytics → Metrics</p>
          <p>Overview of portal activity, registrations, and revenue. Includes:</p>
          <ul>
            <li><strong>Booking Source</strong> — Bar chart showing Staff Booked vs Patient Self-Booked vs Public/Widget</li>
            <li><strong>Bookings by Source & Month</strong> — Stacked monthly bars (last 6 months, color-coded by source)</li>
            <li><strong>Recent Bookings</strong> — Table with patient, service, source badge, who booked, and date</li>
            <li><strong>Revenue, Top Products, Payment Methods</strong> — Sales analytics</li>
            <li><strong>Client Source & How Heard</strong> — Patient acquisition tracking</li>
          </ul>
          <p>Clicking an appointment on the calendar also shows its booking source, who created it, referring URL (if available), and creation date.</p>

          <h3>Patient Sources</h3>
          <p><strong>Location:</strong> Analytics → Patient Sources</p>
          <p>See where patients come from:</p>
          <ul>
            <li><strong>Staff Source</strong> - Which admin brought them</li>
            <li><strong>How Heard</strong> - From intake form</li>
            <li><strong>GHL Source</strong> - From GHL contact record</li>
            <li><strong>GHL Tags</strong> - Tags from GHL</li>
          </ul>

          <h3>Campaigns</h3>
          <p><strong>Location:</strong> Analytics → Campaigns</p>
          <p>Local campaigns and GHL pipeline integration.</p>

          <h3>Local Campaigns</h3>
          <p>Create and manage local marketing campaigns with stages, status indicators (green/yellow/red), and tasks.</p>
          <ul>
            <li><strong>Stage ordering</strong> — Reorder stages with up/down arrows</li>
            <li><strong>Tasks</strong> — Add tasks to stages with due dates and status (hold, in progress, blocked, completed)</li>
            <li><strong>Hide completed</strong> — Toggle to hide finished tasks</li>
            <li><strong>Clickable URLs</strong> — URLs in stage comments are automatically linked</li>
          </ul>

          <h3>Audit Log</h3>
          <p><strong>Location:</strong> Analytics → Audit Log</p>
          <p>Complete history of admin actions - logins, changes, deletions. Useful for compliance and troubleshooting.</p>
        </>
      )
    },
    nutrition: {
      title: 'Nutrition Roadmaps (Beta)',
      content: (
        <>
          <h3>Overview</h3>
          <p><strong>Location:</strong> Tools → Nutrition Roadmaps</p>
          <p>Generate personalized nutrition PDF roadmaps for patients using the ANA (Ascent Nutritional Architect) engine.</p>

          <h3>Generating a Roadmap</h3>
          <ol>
            <li>Go to the <strong>Generate</strong> tab</li>
            <li>Search and select a patient</li>
            <li>Enter body composition measurements: sex, current weight, goal weight, body fat %, visceral fat, BMI, SMM</li>
            <li>(Optional) Add clinical narrative notes about the patient</li>
            <li>Click <strong>Preview Macros</strong> to see calculated protein, carbs, fat, and calorie targets</li>
            <li>Click <strong>Generate Roadmap</strong> — takes about 30-60 seconds</li>
            <li>When ready, you'll be switched to the All Roadmaps tab</li>
          </ol>

          <h3>Managing Roadmaps</h3>
          <p>The <strong>All Roadmaps</strong> tab shows every generated roadmap with:</p>
          <ul>
            <li>Patient name and email</li>
            <li>Computed macro targets (protein, carbs, fat, calories)</li>
            <li>Status (generating, ready, or error)</li>
            <li>Whether it has been emailed to the patient</li>
          </ul>
          <p><strong>Actions:</strong></p>
          <ul>
            <li><strong>PDF</strong> — Download the roadmap PDF</li>
            <li><strong>Email</strong> — Send the PDF to the patient's email on file</li>
            <li><strong>Delete</strong> — Remove the roadmap permanently</li>
          </ul>
        </>
      )
    },
    settings: {
      title: 'Settings & Administration',
      content: (
        <>
          <h3>Email Notifications</h3>
          <p><strong>Location:</strong> Settings</p>
          <ul>
            <li><strong>Doctor Email</strong> - Receives prescription signing requests</li>
            <li><strong>Clinic Email</strong> - Receives intake notifications</li>
          </ul>

          <h3>GoHighLevel Connection</h3>
          <p><strong>Location:</strong> Settings</p>
          <p>Connect or reconnect to GHL. Must be logged into GHL first.</p>

          <h3>Admin Management</h3>
          <p><strong>Location:</strong> Settings → Admins</p>
          <p>Add, edit, or remove admin/staff accounts. Each admin is assigned a <strong>Role</strong> that controls which sections they can access.</p>
          <ul>
            <li>Change an admin's role using the dropdown in the Admins table</li>
            <li>New admins default to the "Admin" role (full access)</li>
          </ul>

          <h3>Role Management</h3>
          <p><strong>Location:</strong> Settings → Roles</p>
          <p>Create and manage roles to control what each admin can see and do.</p>
          <ul>
            <li><strong>Admin</strong> (built-in) - Full access to all sections</li>
            <li><strong>Marketer</strong> (built-in) - Commerce and Analytics only</li>
            <li>Create custom roles with any combination of sections: Patients, Scheduling, Reminders, Commerce, Analytics, GHL, Settings, Patient Board, Patient Sources, Audit Log, User Guide</li>
            <li>System roles (Admin, Marketer) cannot be deleted but their sections can be customized</li>
          </ul>

          <h3>This Guide</h3>
          <p><strong>Location:</strong> Settings → User Guide</p>
          <p>You're here! Reference documentation for portal features.</p>

          <h3>Webhook Logs</h3>
          <p><strong>Location:</strong> Settings → Webhook Logs</p>
          <p>View all webhook fires with filters for type and success/failure status. See <strong>Reminders & Notifications</strong> section for details.</p>

          <h3>System Health Check</h3>
          <p><strong>URL:</strong> <a href="/health-check" target="_blank">portal.ascenttrim.com/health-check</a></p>
          <p>Visual dashboard showing the status of all portal systems — database, scheduling, integrations, and external services.</p>
        </>
      )
    },
    api_keys: {
      title: 'API Keys & External Integrations',
      content: (
        <>
          <h3>Overview</h3>
          <p>The portal provides a secure API (<code>/api/v1/*</code>) for external systems like Retell Voice AI to book, cancel, and reschedule appointments programmatically.</p>

          <h3>Managing API Keys</h3>
          <p><strong>Location:</strong> Settings → API Keys</p>
          <ul>
            <li><strong>Create</strong> — Click "Create API Key", give it a name (e.g., "Retell Voice AI"), select scopes, and set a rate limit</li>
            <li><strong>Copy immediately</strong> — The key is shown once after creation and cannot be retrieved again</li>
            <li><strong>Revoke</strong> — Instantly disables a key. Any integration using it will stop working</li>
            <li><strong>View Log</strong> — See every request made with a key (endpoint, status, IP, timestamp)</li>
          </ul>

          <h3>Available Scopes</h3>
          <ul>
            <li><strong>appointments:read</strong> — List services, staff, availability, and look up appointments</li>
            <li><strong>appointments:write</strong> — Book, cancel, and reschedule appointments</li>
          </ul>

          <h3>API Endpoints</h3>
          <ul>
            <li><code>GET /api/v1/services</code> — List bookable services</li>
            <li><code>GET /api/v1/services/:id/staff</code> — List staff for a service</li>
            <li><code>GET /api/v1/availability?service_id=X&date=Y</code> — Get available time slots</li>
            <li><code>POST /api/v1/appointments</code> — Book an appointment (requires: service_id, staff_id, start_dt, patient_name, patient_email)</li>
            <li><code>GET /api/v1/appointments/lookup?phone=X</code> — Find appointments by phone or email</li>
            <li><code>PUT /api/v1/appointments/:id/cancel</code> — Cancel an appointment</li>
            <li><code>PUT /api/v1/appointments/:id/reschedule</code> — Reschedule (requires: start_dt)</li>
          </ul>

          <h3>Security</h3>
          <ul>
            <li>All requests require the <code>X-API-Key</code> header</li>
            <li>Keys are hashed (SHA-256) before storage — the raw key is never stored</li>
            <li>Rate limiting enforced per key (default: 60 requests/minute)</li>
            <li>Every request is logged with IP address and response status</li>
            <li>Appointments created via API are tagged with <code>created_by_type: api</code> for tracking</li>
          </ul>

          <h3>Booking Source Tracking</h3>
          <p>Appointments booked through the API show the integration name (e.g., "Retell Voice AI") as the booking source in the calendar and metrics dashboards.</p>
        </>
      )
    }
  };

  const filteredSections = searchTerm
    ? sections.filter(s =>
        s.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (guideContent[s.id]?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sections;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">User Guide</h1>
        <p className="page-subtitle">How to use the Ascent Trim Portal</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px' }}>
        {/* Sidebar */}
        <div>
          <input
            type="text"
            className="form-control"
            placeholder="Search guide..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            overflow: 'hidden'
          }}>
            {filteredSections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  borderBottom: '1px solid var(--border-color)',
                  background: activeSection === section.id ? 'var(--accent-gold)' : 'transparent',
                  color: activeSection === section.id ? 'white' : 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: activeSection === section.id ? 600 : 400
                }}
              >
                <span>{section.icon}</span>
                <span>{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="section-card" style={{ padding: '32px' }}>
          <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '24px' }}>
            {guideContent[activeSection]?.title}
          </h2>
          <div style={{
            lineHeight: 1.7,
            fontSize: '15px'
          }} className="guide-content">
            <style>{`
              .guide-content h3 { margin-top: 28px; margin-bottom: 12px; font-size: 18px; color: var(--accent-gold); }
              .guide-content h3:first-child { margin-top: 0; }
              .guide-content p { margin: 12px 0; color: var(--text-secondary); }
              .guide-content ul, .guide-content ol { margin: 12px 0; padding-left: 24px; color: var(--text-secondary); }
              .guide-content li { margin: 8px 0; }
              .guide-content strong { color: var(--text-primary); }
              .guide-content code { background: var(--cream); padding: 2px 6px; border-radius: 4px; font-size: 13px; }
            `}</style>
            {guideContent[activeSection]?.content}
          </div>
        </div>
      </div>
    </div>
  );
}


function AdminManagementPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]); const [roles, setRoles] = useState([]); const [loading, setLoading] = useState(true); const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null); const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '', role_id: '' });
  const [saving, setSaving] = useState(false); const [toast, setToast] = useState(null);
  const fetchAdmins = async () => { try { const [a, r] = await Promise.all([api.get('/api/admin/admins'), api.get('/api/admin/roles')]); setAdmins(a); setRoles(r); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { fetchAdmins(); }, []);
  const openEditModal = (a) => { setEditingAdmin(a); setFormData({ name: a.name, email: a.email, password: '', phone: a.phone || '', role_id: a.role_id || '' }); setShowModal(true); };
  const openNewModal = () => { setEditingAdmin(null); const adminRole = roles.find(r => r.name === 'Admin'); setFormData({ name: '', email: '', password: '', phone: '', role_id: adminRole?.id || '' }); setShowModal(true); };
  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingAdmin) {
        await api.put(`/api/admin/admins/${editingAdmin.id}`, { name: formData.name, phone: formData.phone, password: formData.password || undefined });
        if (formData.role_id !== (editingAdmin.role_id || '')) {
          await api.put(`/api/admin/admins/${editingAdmin.id}/role`, { role_id: formData.role_id || null });
        }
      } else {
        await api.post('/api/admin/admins', { ...formData, password: formData.password });
      }
      setToast({ message: editingAdmin ? 'Admin updated!' : 'Admin created!', type: 'success' }); setShowModal(false); fetchAdmins();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };
  const handleDelete = async (id) => { if (id === user.id) { setToast({ message: "Can't delete yourself!", type: 'error' }); return; } if (!window.confirm('Delete this admin?')) return; try { await api.delete(`/api/admin/admins/${id}`); setToast({ message: 'Admin deleted!', type: 'success' }); fetchAdmins(); } catch (error) { setToast({ message: error.message, type: 'error' }); } };
  const handleColorChange = async (adminId, color) => { try { await api.put(`/api/admin/admins/${adminId}/color`, { color }); setAdmins(admins.map(a => a.id === adminId ? { ...a, calendar_color: color } : a)); setToast({ message: 'Calendar color updated!', type: 'success' }); } catch (error) { setToast({ message: error.message, type: 'error' }); } };
  const handleRoleChange = async (adminId, roleId) => {
    try { await api.put(`/api/admin/admins/${adminId}/role`, { role_id: roleId || null }); setToast({ message: 'Role updated!', type: 'success' }); fetchAdmins(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' });
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Admin Management</h1><p className="page-subtitle">Manage administrator accounts and their roles</p></div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">All Admins</h2><button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add Admin</button></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : admins.length > 0 ? (
          <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Admin</th><th>Role</th><th>Phone</th><th>Calendar Color</th><th>Created</th><th>Actions</th></tr></thead><tbody>
            {admins.map(a => (<tr key={a.id}><td><div className="user-cell"><div className="user-cell-avatar" style={a.calendar_color ? { backgroundColor: a.calendar_color, color: '#fff' } : {}}>{a.name?.split(' ').map(n => n[0]).join('')}</div><div className="user-cell-info"><h4>{a.name}{a.id === user.id && <span style={{ color: 'var(--gold)', marginLeft: '8px' }}>(You)</span>}</h4><p>{a.email}</p></div></div></td><td><select value={a.role_id || ''} onChange={e => handleRoleChange(a.id, e.target.value)} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', background: 'var(--cream)' }}>{roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}<option value="">No Role</option></select></td><td>{a.phone || '-'}</td><td><input type="color" value={a.calendar_color || '#3788d8'} onChange={(e) => handleColorChange(a.id, e.target.value)} style={{ width: '40px', height: '30px', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: 0 }} title="Click to change calendar color" /></td><td>{formatDate(a.created_at)}</td><td><div className="action-btns"><button className="action-btn edit" onClick={() => openEditModal(a)}>Edit</button>{a.id !== user.id && <button className="action-btn delete" onClick={() => handleDelete(a.id)}>Delete</button>}</div></td></tr>))}
          </tbody></table></div>
        ) : <div className="empty-state"><div className="empty-state-icon">👤</div><h3>No admins yet</h3></div>}
      </div>
      {showModal && (<div className="modal-overlay" onClick={() => !saving && setShowModal(false)}><div className="modal" onClick={e => e.stopPropagation()}><div className="modal-header"><h2 className="modal-title">{editingAdmin ? 'Edit Admin' : 'Add Admin'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div><div className="admin-form-grid"><div className="form-group"><label className="form-label">Full Name</label><input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} disabled={saving} /></div><div className="form-group"><label className="form-label">Phone</label><input type="tel" className="form-input" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} disabled={saving} /></div><div className="form-group full-width"><label className="form-label">Email</label><input type="email" className="form-input" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} disabled={editingAdmin || saving} /></div><div className="form-group full-width"><label className="form-label">{editingAdmin ? 'New Password (leave blank to keep)' : 'Password'}</label><input type="password" className="form-input" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} disabled={saving} /></div><div className="form-group full-width"><label className="form-label">Role</label><select className="form-input" value={formData.role_id} onChange={e => setFormData({ ...formData, role_id: e.target.value })} disabled={saving}>{roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div></div><div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div></div></div>)}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const SECTION_LABELS = {
  patients: 'Patients', scheduling: 'Scheduling', reminders: 'Reminders', commerce: 'Commerce',
  analytics: 'Analytics', ghl: 'GHL', settings: 'Settings', patient_board: 'Patient Board',
  patient_sources: 'Patient Sources', audit_log: 'Audit Log', guide: 'User Guide', content: 'Website'
};

function AdminRolesPage() {
  const [roles, setRoles] = useState([]); const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false); const [editingRole, setEditingRole] = useState(null);
  const [formName, setFormName] = useState(''); const [formSections, setFormSections] = useState([]);
  const [saving, setSaving] = useState(false); const [toast, setToast] = useState(null);

  const fetchRoles = async () => { try { setRoles(await api.get('/api/admin/roles')); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { fetchRoles(); }, []);

  const openNewModal = () => { setEditingRole(null); setFormName(''); setFormSections([]); setShowModal(true); };
  const openEditModal = (role) => { setEditingRole(role); setFormName(role.name); setFormSections(role.allowed_sections || []); setShowModal(true); };

  const toggleSection = (section) => {
    setFormSections(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingRole) {
        await api.put(`/api/admin/roles/${editingRole.id}`, { name: formName, allowed_sections: formSections });
      } else {
        await api.post('/api/admin/roles', { name: formName, allowed_sections: formSections });
      }
      setToast({ message: editingRole ? 'Role updated!' : 'Role created!', type: 'success' });
      setShowModal(false); fetchRoles();
    } catch (error) { setToast({ message: error.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try { await api.delete(`/api/admin/roles/${role.id}`); setToast({ message: 'Role deleted!', type: 'success' }); fetchRoles(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Role Management</h1><p className="page-subtitle">Control what each admin role can access</p></div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">All Roles</h2><button className="btn btn-gold btn-sm" onClick={openNewModal}>+ New Role</button></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : roles.length > 0 ? (
          <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Role</th><th>Sections</th><th>Users</th><th>Actions</th></tr></thead><tbody>
            {roles.map(r => (
              <tr key={r.id}>
                <td><div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><strong>{r.name}</strong>{r.is_system && <span style={{ fontSize: '11px', background: 'var(--cream-dark)', padding: '2px 8px', borderRadius: '10px', color: 'var(--text-muted)' }}>System</span>}</div></td>
                <td><div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{(r.allowed_sections || []).map(s => <span key={s} style={{ fontSize: '11px', background: 'var(--accent-gold)', color: '#fff', padding: '2px 8px', borderRadius: '10px' }}>{SECTION_LABELS[s] || s}</span>)}</div></td>
                <td>{r.user_count || 0}</td>
                <td><div className="action-btns">
                  <button className="action-btn edit" onClick={() => openEditModal(r)}>Edit</button>
                  {!r.is_system && <button className="action-btn delete" onClick={() => handleDelete(r)}>Delete</button>}
                </div></td>
              </tr>
            ))}
          </tbody></table></div>
        ) : <div className="empty-state"><h3>No roles yet</h3></div>}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header"><h2 className="modal-title">{editingRole ? 'Edit Role' : 'New Role'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div>
            <div className="form-group">
              <label className="form-label">Role Name</label>
              <input type="text" className="form-input" value={formName} onChange={e => setFormName(e.target.value)} disabled={saving || (editingRole && editingRole.is_system)} placeholder="e.g. Marketer, Front Desk" />
            </div>
            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">Allowed Sections</label>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>Choose which parts of the portal this role can access.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {Object.entries(SECTION_LABELS).map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: formSections.includes(key) ? 'var(--gold-light)' : 'var(--cream)', borderRadius: '8px', cursor: 'pointer', border: formSections.includes(key) ? '2px solid var(--accent-gold)' : '2px solid transparent', transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={formSections.includes(key)} onChange={() => toggleSection(key)} style={{ accentColor: 'var(--accent-gold)' }} />
                    <span style={{ fontSize: '13px', fontWeight: formSections.includes(key) ? '600' : '400' }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSave} disabled={saving || !formName.trim()}>{saving ? 'Saving...' : 'Save Role'}</button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function IntakeReviewsPage() {
  const [intakes, setIntakes] = useState([]); const [loading, setLoading] = useState(true); const [selectedIntake, setSelectedIntake] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false); const [showViewModal, setShowViewModal] = useState(false); const [saving, setSaving] = useState(false); const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [patientIntakeForms, setPatientIntakeForms] = useState([]);
  const signatureRef = useRef(null); const [signatureEmpty, setSignatureEmpty] = useState(true);
  const [prescriptionData, setPrescriptionData] = useState({
    currentWeight: '', heightInches: '', bloodPressure: '', bmi: '', pulse: '',
    hasAllergies: false, allergyList: '', clinicalReasoning: ''
  });
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [nutritionMode, setNutritionMode] = useState(null); // 'email' or 'now'
  const [nutritionData, setNutritionData] = useState({
    mealsPerDay: '', feelAfterEating: '', energyCrashes: '', mealsOftenMissed: '', reasonMealsMissed: '',
    eatHomeOrAway: '', exerciseRoutine: '', dailyRoutineConsistency: '', mealTimesConsistent: '',
    hungerCravingsChanges: '', favoriteFoods: '', foodsCraveMost: '', hardestAboutNutrition: '',
    successWithNutrition: '', stressLevel: 5, gallbladderRemoval: false, hysterectomy: false,
    thyroidCondition: false, fattyLiver: false, digestiveIssues: false, foodAllergies: false,
    otherMedicalHistory: false, medicalHistoryDetails: '', additionalNotes: ''
  });

  const fetchIntakes = async () => { try { const data = await api.get('/api/admin/intake-forms'); setIntakes(data); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { fetchIntakes(); }, []);

  const openViewModal = async (intake) => {
    setSelectedIntake(intake);
    setShowViewModal(true);
    // Fetch all intake forms for this patient
    try {
      const forms = await api.get(`/api/admin/users/${intake.user_id}/intake-forms`);
      setPatientIntakeForms(forms);
    } catch (e) { setPatientIntakeForms([]); }
  };

  const viewOtherIntakeForm = async (formId) => {
    try {
      const form = await api.get(`/api/admin/intake-forms/${formId}`);
      setSelectedIntake(form);
    } catch (e) { setToast({ message: 'Failed to load intake form', type: 'error' }); }
  };


  const openPrescriptionModal = async (intake) => {
    setSelectedIntake(intake);
    // Try to load existing vitals for this patient
    try {
      const vitals = await api.get(`/api/admin/vitals/${intake.user_id}`);
      if (vitals) {
        setPrescriptionData({
          currentWeight: vitals.current_weight || '',
          heightInches: vitals.height_inches || '',
          bloodPressure: vitals.blood_pressure || '',
          bmi: vitals.bmi || '',
          pulse: vitals.pulse || '',
          hasAllergies: vitals.has_allergies || false,
          allergyList: vitals.allergy_list || '',
          clinicalReasoning: vitals.clinical_reasoning || ''
        });
      } else {
        setPrescriptionData({
          currentWeight: '', heightInches: '', bloodPressure: '', bmi: '', pulse: '',
          hasAllergies: intake.allergies_list ? true : false, allergyList: intake.allergies_list || '', clinicalReasoning: ''
        });
      }
    } catch (e) {
      setPrescriptionData({
        currentWeight: '', heightInches: '', bloodPressure: '', bmi: '', pulse: '',
        hasAllergies: intake.allergies_list ? true : false, allergyList: intake.allergies_list || '', clinicalReasoning: ''
      });
    }
    setShowPrescriptionModal(true);
  };

  const handleSaveVitals = async () => {
    setSaving(true);
    try {
      await api.post('/api/admin/vitals', {
        userId: selectedIntake.user_id,
        intakeId: selectedIntake.id,
        ...prescriptionData
      });
      setToast({ message: 'Vitals saved successfully', type: 'success' });
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally { setSaving(false); }
  };

  const handleCreatePrescription = async () => {
    if (!prescriptionData.currentWeight || !prescriptionData.bloodPressure) {
      setToast({ message: 'Weight and blood pressure are required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/admin/prescriptions', {
        intakeId: selectedIntake.id,
        userId: selectedIntake.user_id,
        ...prescriptionData
      });
      setToast({ message: 'Prescription created and sent to doctor queue', type: 'success' });
      setShowPrescriptionModal(false);
      fetchIntakes();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally { setSaving(false); }
  };

  const openNutritionModal = (intake) => {
    setSelectedIntake(intake);
    setNutritionMode(null);
    setNutritionData({
      mealsPerDay: '', feelAfterEating: '', energyCrashes: '', mealsOftenMissed: '', reasonMealsMissed: '',
      eatHomeOrAway: '', exerciseRoutine: '', dailyRoutineConsistency: '', mealTimesConsistent: '',
      hungerCravingsChanges: '', favoriteFoods: '', foodsCraveMost: '', hardestAboutNutrition: '',
      successWithNutrition: '', stressLevel: 5, gallbladderRemoval: false, hysterectomy: false,
      thyroidCondition: false, fattyLiver: false, digestiveIssues: false, foodAllergies: false,
      otherMedicalHistory: false, medicalHistoryDetails: '', additionalNotes: ''
    });
    setShowNutritionModal(true);
  };

  const handleNutritionSubmit = async () => {
    setSaving(true);
    try {
      if (nutritionMode === 'email') {
        await api.post('/api/admin/nutrition', { userId: selectedIntake.user_id, sendEmail: true });
        setToast({ message: 'Nutrition form email sent to patient', type: 'success' });
      } else {
        await api.post('/api/admin/nutrition', { userId: selectedIntake.user_id, sendEmail: false, formData: nutritionData });
        setToast({ message: 'Nutrition form saved successfully', type: 'success' });
      }
      setShowNutritionModal(false);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    } finally { setSaving(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' }) : '-';
  const getStatusBadge = (status) => {
    const styles = {
      pending_review: { bg: '#fef3c7', color: '#92400e', label: 'Pending Review' },
      prescription_created: { bg: '#d1fae5', color: '#065f46', label: 'Prescription Created' }
    };
    const s = styles[status] || { bg: '#e5e7eb', color: '#374151', label: status };
    return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
  };


  return (
    <div>
      <div className="page-header"><h1 className="page-title">Intake Reviews</h1><p className="page-subtitle">Review patient intake forms and create prescriptions</p></div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Patient Intakes</h2></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : intakes.length > 0 ? (
          <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Patient</th><th>DOB</th><th>Submitted</th><th>Status</th><th>Actions</th></tr></thead><tbody>
            {intakes.map(i => (<tr key={i.id}>
              <td><div className="user-cell"><div className="user-cell-avatar">{i.name?.split(' ').map(n => n[0]).join('')}</div><div className="user-cell-info"><h4>{i.name}</h4><p>{i.email}</p></div></div></td>
              <td>{formatDate(i.date_of_birth)}</td>
              <td>{formatDate(i.created_at)}</td>
              <td>{getStatusBadge(i.status)}</td>
              <td><div className="action-btns">
                {i.status === 'pending_review' && <button className="action-btn edit" onClick={() => openPrescriptionModal(i)}>Create Prescription</button>}
                <button className="action-btn edit" onClick={() => openNutritionModal(i)}>Create Nutrition</button>
                <button className="action-btn edit" onClick={() => openViewModal(i)}>View</button>
              </div></td>
            </tr>))}
          </tbody></table></div>
        ) : <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No intake forms yet</h3></div>}
      </div>

      {showPrescriptionModal && selectedIntake && (
        <div className="modal-overlay" onClick={() => !saving && setShowPrescriptionModal(false)}>
          <div className="modal prescription-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Create Prescription for {selectedIntake.name}</h2><button className="modal-close" onClick={() => setShowPrescriptionModal(false)} disabled={saving}>×</button></div>
            <div className="prescription-form">
              <div className="prescription-section">
                <h4>Patient Vitals</h4>
                <div className="admin-form-grid">
                  <div className="form-group"><label className="form-label">Current Weight (lbs) *</label><input type="number" className="form-input" value={prescriptionData.currentWeight} onChange={e => setPrescriptionData({...prescriptionData, currentWeight: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Height (inches)</label><input type="number" className="form-input" value={prescriptionData.heightInches} onChange={e => setPrescriptionData({...prescriptionData, heightInches: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Blood Pressure *</label><input type="text" className="form-input" placeholder="120/80" value={prescriptionData.bloodPressure} onChange={e => setPrescriptionData({...prescriptionData, bloodPressure: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">BMI</label><input type="text" className="form-input" value={prescriptionData.bmi} onChange={e => setPrescriptionData({...prescriptionData, bmi: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Pulse</label><input type="number" className="form-input" value={prescriptionData.pulse} onChange={e => setPrescriptionData({...prescriptionData, pulse: e.target.value})} /></div>
                </div>
              </div>

              <div className="prescription-section">
                <h4>Allergies</h4>
                <div className="form-group">
                  <label className="consent-label"><input type="checkbox" checked={prescriptionData.hasAllergies} onChange={e => setPrescriptionData({...prescriptionData, hasAllergies: e.target.checked})} /><span>Patient has food or drug allergies</span></label>
                </div>
                {prescriptionData.hasAllergies && <div className="form-group"><label className="form-label">List allergies</label><textarea className="form-input" value={prescriptionData.allergyList} onChange={e => setPrescriptionData({...prescriptionData, allergyList: e.target.value})} rows={2} /></div>}
              </div>

              <div className="prescription-section">
                <h4>Clinical Reasoning / Notes</h4>
                <div className="form-group"><textarea className="form-input" value={prescriptionData.clinicalReasoning} onChange={e => setPrescriptionData({...prescriptionData, clinicalReasoning: e.target.value})} rows={3} placeholder="Add clinical reasoning or other notes for the doctor..." /></div>
              </div>
            </div>
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowPrescriptionModal(false)} disabled={saving}>Cancel</button><button className="btn btn-secondary" onClick={handleSaveVitals} disabled={saving}>{saving ? 'Saving...' : 'Save Vitals'}</button><button className="btn btn-gold" onClick={handleCreatePrescription} disabled={saving}>{saving ? 'Sending...' : 'Send to Doctor Queue'}</button></div>
          </div>
        </div>
      )}

      {showViewModal && selectedIntake && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal prescription-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Intake Form: {selectedIntake.first_name} {selectedIntake.last_name}</h2><button className="modal-close" onClick={() => setShowViewModal(false)}>×</button></div>
            <div className="prescription-review">
              <div className="review-section"><h4>Contact Information</h4>
                <div className="review-grid">
                  <div><span>Name:</span> {selectedIntake.first_name} {selectedIntake.last_name}</div>
                  <div><span>Email:</span> {selectedIntake.email}</div>
                  <div><span>Phone:</span> {selectedIntake.phone || '-'}</div>
                  <div><span>DOB:</span> {formatDate(selectedIntake.date_of_birth)}</div>
                  <div><span>Gender:</span> {selectedIntake.gender || '-'}</div>
                </div>
                {selectedIntake.street_address && <p><span>Address:</span> {selectedIntake.street_address}, {selectedIntake.city}, {selectedIntake.state} {selectedIntake.postal_code}</p>}
              </div>
              <div className="review-section"><h4>Physical & Emergency</h4>
                <div className="review-grid">
                  <div><span>Weight:</span> {selectedIntake.current_weight || '-'} lbs</div>
                  <div><span>Height:</span> {selectedIntake.height_inches || '-'}"</div>
                  <div><span>Emergency Contact:</span> {selectedIntake.emergency_contact_name || '-'}</div>
                  <div><span>Emergency Phone:</span> {selectedIntake.emergency_contact_phone || '-'}</div>
                </div>
              </div>
              {selectedIntake.what_brings_you && <div className="review-section"><h4>What Brings You In</h4><p>{(Array.isArray(selectedIntake.what_brings_you) ? selectedIntake.what_brings_you : JSON.parse(selectedIntake.what_brings_you || '[]')).join(', ') || '-'}</p></div>}
              {selectedIntake.primary_goal && <div className="review-section"><h4>Primary Goal</h4><p>{selectedIntake.primary_goal}</p></div>}
              {selectedIntake.weight_loss_challenges && <div className="review-section"><h4>Weight Loss Challenges</h4><p>{(Array.isArray(selectedIntake.weight_loss_challenges) ? selectedIntake.weight_loss_challenges : JSON.parse(selectedIntake.weight_loss_challenges || '[]')).join(', ') || '-'}</p></div>}
              <div className="review-section"><h4>Lifestyle</h4>
                <div className="review-grid">
                  <div><span>Meals/day:</span> {selectedIntake.meals_per_day || '-'}</div>
                  <div><span>Water:</span> {selectedIntake.daily_water_intake || '-'}</div>
                  <div><span>Activity:</span> {selectedIntake.physical_activity_level || '-'}</div>
                  <div><span>Sleep:</span> {selectedIntake.sleep_quality || '-'} ({selectedIntake.avg_sleep_hours || '-'} hrs)</div>
                  <div><span>Stress:</span> {selectedIntake.stress_level || '-'}</div>
                  <div><span>Alcohol:</span> {selectedIntake.alcohol_use || '-'}</div>
                </div>
              </div>
              {selectedIntake.metabolic_health && <div className="review-section"><h4>Medical History - Metabolic</h4><p>{(Array.isArray(selectedIntake.metabolic_health) ? selectedIntake.metabolic_health : JSON.parse(selectedIntake.metabolic_health || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.cardiovascular && <div className="review-section"><h4>Medical History - Cardiovascular</h4><p>{(Array.isArray(selectedIntake.cardiovascular) ? selectedIntake.cardiovascular : JSON.parse(selectedIntake.cardiovascular || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.gi_conditions && <div className="review-section"><h4>Medical History - GI Conditions</h4><p>{(Array.isArray(selectedIntake.gi_conditions) ? selectedIntake.gi_conditions : JSON.parse(selectedIntake.gi_conditions || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.mental_health && <div className="review-section"><h4>Medical History - Mental Health</h4><p>{(Array.isArray(selectedIntake.mental_health) ? selectedIntake.mental_health : JSON.parse(selectedIntake.mental_health || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.surgical_history && <div className="review-section"><h4>Surgical History</h4><p>{(Array.isArray(selectedIntake.surgical_history) ? selectedIntake.surgical_history : JSON.parse(selectedIntake.surgical_history || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.current_medications && <div className="review-section"><h4>Current Medications</h4><p>{(Array.isArray(selectedIntake.current_medications) ? selectedIntake.current_medications : JSON.parse(selectedIntake.current_medications || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.allergies_list && <div className="review-section"><h4>Allergies</h4><p>{selectedIntake.allergies_list}</p></div>}
              {selectedIntake.family_history && <div className="review-section"><h4>Family History</h4><p>{(Array.isArray(selectedIntake.family_history) ? selectedIntake.family_history : JSON.parse(selectedIntake.family_history || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.pregnancy_status && <div className="review-section"><h4>Pregnancy Status</h4><p>{selectedIntake.pregnancy_status}</p></div>}
              {selectedIntake.treatment_interests && <div className="review-section"><h4>Treatment Interests</h4><p>{(Array.isArray(selectedIntake.treatment_interests) ? selectedIntake.treatment_interests : JSON.parse(selectedIntake.treatment_interests || '[]')).join(', ') || 'None'}</p></div>}
              {selectedIntake.signature_data && <div className="review-section"><h4>Patient Signature</h4><img src={selectedIntake.signature_data} alt="Patient Signature" style={{maxWidth: '300px', border: '1px solid var(--cream-dark)', borderRadius: '8px'}} /><p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px'}}>Signed: {new Date(selectedIntake.consent_timestamp).toLocaleString('en-US', { timeZone: 'America/Denver' })}</p></div>}
              {patientIntakeForms.length > 1 && (
                <div className="review-section">
                  <h4>Previous Intake Forms ({patientIntakeForms.length - 1})</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {patientIntakeForms.filter(f => f.id !== selectedIntake.id).map(f => (
                      <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--cream-light)', borderRadius: '6px' }}>
                        <div>
                          <span style={{ fontWeight: 500 }}>{formatDate(f.created_at)}</span>
                          <span style={{ marginLeft: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>{f.first_name} {f.last_name}</span>
                          {f.has_signature && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--accent-gold)' }}>Signed</span>}
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => viewOtherIntakeForm(f.id)}>View</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
              <button className="btn btn-secondary" onClick={() => window.open(`/api/admin/intake-forms/${selectedIntake.id}/pdf`, '_blank')}>Download PDF</button>
              {selectedIntake.status === 'pending_review' && <button className="btn btn-gold" onClick={() => { setShowViewModal(false); openPrescriptionModal(selectedIntake); }}>Create Prescription</button>}
            </div>
          </div>
        </div>
      )}

      {showNutritionModal && selectedIntake && (
        <div className="modal-overlay" onClick={() => !saving && setShowNutritionModal(false)}>
          <div className="modal prescription-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: nutritionMode === 'now' ? '800px' : '500px' }}>
            <div className="modal-header"><h2 className="modal-title">Nutrition Intake for {selectedIntake.name}</h2><button className="modal-close" onClick={() => setShowNutritionModal(false)} disabled={saving}>×</button></div>

            {!nutritionMode ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <p style={{ marginBottom: '24px', color: 'var(--text-muted)' }}>How would you like to complete this nutrition intake form?</p>
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                  <button className="btn btn-secondary" onClick={() => setNutritionMode('email')} style={{ flex: 1, maxWidth: '200px' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#9993;</div>
                    <div>Email to Patient</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Patient completes at home</div>
                  </button>
                  <button className="btn btn-gold" onClick={() => setNutritionMode('now')} style={{ flex: 1, maxWidth: '200px' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#9998;</div>
                    <div>Complete Now</div>
                    <div style={{ fontSize: '12px', color: 'var(--cream-dark)', marginTop: '4px' }}>Fill out during visit</div>
                  </button>
                </div>
              </div>
            ) : nutritionMode === 'email' ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9993;</div>
                <p style={{ marginBottom: '8px' }}>Send nutrition intake form to:</p>
                <p style={{ fontWeight: 600, marginBottom: '24px' }}>{selectedIntake.email}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>The patient will receive an email with a link to complete the form. You'll be notified when they submit it.</p>
              </div>
            ) : (
              <div className="prescription-form" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <div className="prescription-section">
                  <h4>Eating Habits</h4>
                  <div className="form-group"><label className="form-label">1. How many times per day do you typically eat?</label><input type="text" className="form-input" value={nutritionData.mealsPerDay} onChange={e => setNutritionData({...nutritionData, mealsPerDay: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">2. Do you generally feel better or worse after eating?</label><textarea className="form-input" rows={2} value={nutritionData.feelAfterEating} onChange={e => setNutritionData({...nutritionData, feelAfterEating: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">3. Do you experience energy crashes?</label><textarea className="form-input" rows={2} value={nutritionData.energyCrashes} onChange={e => setNutritionData({...nutritionData, energyCrashes: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">4. Which meals do you most often miss?</label><select className="form-input" value={nutritionData.mealsOftenMissed} onChange={e => setNutritionData({...nutritionData, mealsOftenMissed: e.target.value})}><option value="">Select...</option><option value="Breakfast">Breakfast</option><option value="Lunch">Lunch</option><option value="Dinner">Dinner</option><option value="Snacks">Snacks</option><option value="None">None</option></select></div>
                  <div className="form-group"><label className="form-label">5. If meals are missed, why?</label><select className="form-input" value={nutritionData.reasonMealsMissed} onChange={e => setNutritionData({...nutritionData, reasonMealsMissed: e.target.value})}><option value="">Select...</option><option value="Time">Time</option><option value="Appetite">Appetite</option><option value="Stress">Stress</option><option value="Forgetting">Forgetting</option><option value="Other">Other</option><option value="N/A">N/A</option></select></div>
                  <div className="form-group"><label className="form-label">6. Eat at home or away?</label><select className="form-input" value={nutritionData.eatHomeOrAway} onChange={e => setNutritionData({...nutritionData, eatHomeOrAway: e.target.value})}><option value="">Select...</option><option value="Mostly at home">Mostly at home</option><option value="Mostly away">Mostly away</option><option value="About equal">About equal</option></select></div>
                </div>
                <div className="prescription-section">
                  <h4>Lifestyle & Routines</h4>
                  <div className="form-group"><label className="form-label">7. Exercise/movement routine?</label><textarea className="form-input" rows={2} value={nutritionData.exerciseRoutine} onChange={e => setNutritionData({...nutritionData, exerciseRoutine: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">8. Daily routines - consistent or chaotic?</label><select className="form-input" value={nutritionData.dailyRoutineConsistency} onChange={e => setNutritionData({...nutritionData, dailyRoutineConsistency: e.target.value})}><option value="">Select...</option><option value="Very consistent">Very consistent</option><option value="Mostly consistent">Mostly consistent</option><option value="Somewhat chaotic">Somewhat chaotic</option><option value="Very chaotic">Very chaotic</option></select></div>
                  <div className="form-group"><label className="form-label">9. Meal times consistent?</label><select className="form-input" value={nutritionData.mealTimesConsistent} onChange={e => setNutritionData({...nutritionData, mealTimesConsistent: e.target.value})}><option value="">Select...</option><option value="Very consistent">Very consistent</option><option value="Mostly consistent">Mostly consistent</option><option value="Vary somewhat">Vary somewhat</option><option value="Vary a lot">Vary a lot</option></select></div>
                  <div className="form-group"><label className="form-label">10. Hunger/cravings changes?</label><textarea className="form-input" rows={2} value={nutritionData.hungerCravingsChanges} onChange={e => setNutritionData({...nutritionData, hungerCravingsChanges: e.target.value})} /></div>
                </div>
                <div className="prescription-section">
                  <h4>Food Preferences & Goals</h4>
                  <div className="form-group"><label className="form-label">11. Favorite meals/foods?</label><textarea className="form-input" rows={2} value={nutritionData.favoriteFoods} onChange={e => setNutritionData({...nutritionData, favoriteFoods: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">12. Foods you crave most?</label><textarea className="form-input" rows={2} value={nutritionData.foodsCraveMost} onChange={e => setNutritionData({...nutritionData, foodsCraveMost: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">13. What feels hardest about nutrition?</label><textarea className="form-input" rows={2} value={nutritionData.hardestAboutNutrition} onChange={e => setNutritionData({...nutritionData, hardestAboutNutrition: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">14. What does success look like?</label><textarea className="form-input" rows={2} value={nutritionData.successWithNutrition} onChange={e => setNutritionData({...nutritionData, successWithNutrition: e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">15. Stress level (1-10)</label><div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><input type="range" min="1" max="10" value={nutritionData.stressLevel} onChange={e => setNutritionData({...nutritionData, stressLevel: parseInt(e.target.value)})} style={{ flex: 1 }} /><span style={{ fontWeight: 600 }}>{nutritionData.stressLevel}</span></div></div>
                </div>
                <div className="prescription-section">
                  <h4>Medical & Digestive History</h4>
                  <div className="checkbox-group">
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.gallbladderRemoval} onChange={e => setNutritionData({...nutritionData, gallbladderRemoval: e.target.checked})} /><span>Gallbladder removal</span></label>
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.hysterectomy} onChange={e => setNutritionData({...nutritionData, hysterectomy: e.target.checked})} /><span>Hysterectomy</span></label>
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.thyroidCondition} onChange={e => setNutritionData({...nutritionData, thyroidCondition: e.target.checked})} /><span>Thyroid condition</span></label>
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.fattyLiver} onChange={e => setNutritionData({...nutritionData, fattyLiver: e.target.checked})} /><span>Fatty liver</span></label>
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.digestiveIssues} onChange={e => setNutritionData({...nutritionData, digestiveIssues: e.target.checked})} /><span>Digestive issues</span></label>
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.foodAllergies} onChange={e => setNutritionData({...nutritionData, foodAllergies: e.target.checked})} /><span>Food allergies</span></label>
                    <label className="consent-label"><input type="checkbox" checked={nutritionData.otherMedicalHistory} onChange={e => setNutritionData({...nutritionData, otherMedicalHistory: e.target.checked})} /><span>Other medical history</span></label>
                  </div>
                  {(nutritionData.gallbladderRemoval || nutritionData.hysterectomy || nutritionData.thyroidCondition || nutritionData.fattyLiver || nutritionData.digestiveIssues || nutritionData.foodAllergies || nutritionData.otherMedicalHistory) && (
                    <div className="form-group" style={{ marginTop: '16px' }}><label className="form-label">Details:</label><textarea className="form-input" rows={2} value={nutritionData.medicalHistoryDetails} onChange={e => setNutritionData({...nutritionData, medicalHistoryDetails: e.target.value})} /></div>
                  )}
                </div>
                <div className="prescription-section">
                  <h4>Additional Notes</h4>
                  <div className="form-group"><textarea className="form-input" rows={2} value={nutritionData.additionalNotes} onChange={e => setNutritionData({...nutritionData, additionalNotes: e.target.value})} /></div>
                </div>
              </div>
            )}

            {nutritionMode && (
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setNutritionMode(null)} disabled={saving}>Back</button>
                <button className="btn btn-gold" onClick={handleNutritionSubmit} disabled={saving}>
                  {saving ? 'Saving...' : nutritionMode === 'email' ? 'Send Email' : 'Save Nutrition Form'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function DoctorQueuePage() {
  const [prescriptions, setPrescriptions] = useState([]); const [loading, setLoading] = useState(true); const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false); const [saving, setSaving] = useState(false); const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const signatureRef = useRef(null); const [signatureEmpty, setSignatureEmpty] = useState(true); const [physicianNotes, setPhysicianNotes] = useState('');
  const [patientIntakeForms, setPatientIntakeForms] = useState([]); const [showIntakeFormModal, setShowIntakeFormModal] = useState(false); const [viewingIntakeForm, setViewingIntakeForm] = useState(null);
  const [rxData, setRxData] = useState({
    prescriberDetermination: '', semaglutideType: '', semaglutideConcentration: '', semaglutideDosing: '',
    tirzepatideType: '', tirzepatideConcentration: '', tirzepatideDosing: ''
  });

  const semaglutideOptions = {
    '2.5mg-2mg/mL': ['8 units (0.2mg) sub-q every week for 4 weeks #1mL vial', 'Inject 16 units (0.4mg) sub-q every week for 4 weeks #1mL vial'],
    '5mg-2mg/mL': ['Inject 16 units (0.8mg) sub-q every week for 4 weeks #1mL vial', 'Inject 28 units (1.4mg) sub-q every week for 4 weeks #1.5mL vial', 'Inject 30 units (1.5mg) sub-q every week for 4 weeks #1.5mL vial', 'Inject 43 units (2.15mg) sub-q every week for 4 weeks #2mL vial', 'Inject 54 units (2.7mg) sub-q every week for 4 weeks #2.5mL vial']
  };
  const tirzepatideOptions = {
    '8.5mg-100mcg/mL': ['Inject 25 units (2.125mg) sub-q every week for 4 weeks #1mL vial', 'Inject 50 units (4.25mg) sub-q every week for 4 weeks #2mL Vial', 'Inject 75 units (6.375mg) sub-q every week for 4 weeks #3mL Vial'],
    '5mg-2mg/mL (copy)': ['Inject 50 units (8.5mg) sub-q every week for 4 weeks #2mL Vial (28-day)', 'Inject 64 units (11mg) sub-q every week for 4 weeks #3mL Vial', 'Inject 80 units (13.5mg) sub-q every week for 4 weeks #3.5mL Vial', 'Inject 100 units (17mg) sub-q every week for 4 weeks #4mL Vial']
  };

  const fetchPrescriptions = async () => { try { const data = await api.get('/api/admin/prescriptions'); setPrescriptions(data); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { fetchPrescriptions(); }, []);

  const clearSignature = () => { if (signatureRef.current) { signatureRef.current.clear(); setSignatureEmpty(true); } };
  const handleSignatureEnd = () => { if (signatureRef.current && !signatureRef.current.isEmpty()) { setSignatureEmpty(false); } };

  const openSignModal = async (prescription) => {
    try {
      const fullPrescription = await api.get(`/api/admin/prescriptions/${prescription.id}`);
      setSelectedPrescription(fullPrescription);
      setPhysicianNotes(fullPrescription.physician_notes || '');
      setRxData({
        prescriberDetermination: fullPrescription.prescriber_determination || '',
        semaglutideType: fullPrescription.semaglutide_type || '', semaglutideConcentration: fullPrescription.semaglutide_concentration || '', semaglutideDosing: fullPrescription.semaglutide_dosing || '',
        tirzepatideType: fullPrescription.tirzepatide_type || '', tirzepatideConcentration: fullPrescription.tirzepatide_concentration || '', tirzepatideDosing: fullPrescription.tirzepatide_dosing || ''
      });
      setSignatureEmpty(true);
      setShowSignModal(true);
      // Fetch patient's intake form history
      try {
        const forms = await api.get(`/api/admin/users/${fullPrescription.user_id}/intake-forms`);
        setPatientIntakeForms(forms);
      } catch (e) { setPatientIntakeForms([]); }
    } catch (e) { setToast({ message: 'Failed to load prescription', type: 'error' }); }
  };

  const viewIntakeForm = async (formId) => {
    try {
      const form = await api.get(`/api/admin/intake-forms/${formId}`);
      setViewingIntakeForm(form);
      setShowIntakeFormModal(true);
    } catch (e) { setToast({ message: 'Failed to load intake form', type: 'error' }); }
  };

  const handleSign = async () => {
    if (signatureEmpty) { setToast({ message: 'Please provide your signature', type: 'error' }); return; }
    if (!rxData.semaglutideDosing && !rxData.tirzepatideDosing) { setToast({ message: 'Please select at least one medication', type: 'error' }); return; }
    setSaving(true);
    try {
      const signatureData = signatureRef.current.toDataURL('image/png');
      await api.put(`/api/admin/prescriptions/${selectedPrescription.id}/sign`, { signatureData, physicianNotes, ...rxData });
      setToast({ message: 'Prescription signed successfully', type: 'success' });
      setShowSignModal(false);
      fetchPrescriptions();
    } catch (error) { setToast({ message: error.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleResend = async (prescriptionId) => {
    setResendingId(prescriptionId);
    try {
      await api.post(`/api/admin/prescriptions/${prescriptionId}/resend`);
      setToast({ message: 'Signing link resent to doctor', type: 'success' });
    } catch (error) { setToast({ message: error.message || 'Failed to resend', type: 'error' }); }
    finally { setResendingId(null); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' }) : '-';
  const getStatusBadge = (status) => {
    const styles = {
      pending_signature: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting Signature' },
      approved: { bg: '#d1fae5', color: '#065f46', label: 'Signed' }
    };
    const s = styles[status] || { bg: '#e5e7eb', color: '#374151', label: status };
    return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
  };

  const pendingCount = prescriptions.filter(p => p.status === 'pending_signature').length;

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Doctor Queue</h1><p className="page-subtitle">Review and sign prescriptions {pendingCount > 0 && <span className="queue-badge">{pendingCount} awaiting signature</span>}</p></div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Prescriptions</h2></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : prescriptions.length > 0 ? (
          <div className="admin-table-wrapper"><table className="admin-table"><thead><tr><th>Patient</th><th>Medications</th><th>Created By</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead><tbody>
            {prescriptions.map(p => (<tr key={p.id} className={p.status === 'pending_signature' ? 'highlight-row' : ''}>
              <td><div className="user-cell"><div className="user-cell-avatar">{p.patient_name?.split(' ').map(n => n[0]).join('')}</div><div className="user-cell-info"><h4>{p.patient_name}</h4><p>{p.patient_email}</p></div></div></td>
              <td><div style={{fontSize: '13px'}}>{p.semaglutide_dosing && <div>Semaglutide</div>}{p.tirzepatide_dosing && <div>Tirzepatide</div>}{!p.semaglutide_dosing && !p.tirzepatide_dosing && (p.status === 'pending_signature' ? <span style={{color: 'var(--text-muted)'}}>Pending</span> : '-')}</div></td>
              <td>{p.created_by_name}</td>
              <td>{formatDate(p.created_at)}</td>
              <td>{getStatusBadge(p.status)}</td>
              <td><div className="action-btns">
                {p.status === 'pending_signature' && <><button className="action-btn edit" style={{background: 'var(--accent-gold)', color: 'white'}} onClick={() => openSignModal(p)}>Review & Sign</button><button className="action-btn" style={{marginLeft: '8px'}} onClick={() => handleResend(p.id)} disabled={resendingId === p.id}>{resendingId === p.id ? 'Sending...' : 'Resend Link'}</button></>}
                {p.status === 'approved' && <button className="action-btn edit" onClick={() => openSignModal(p)}>View</button>}
              </div></td>
            </tr>))}
          </tbody></table></div>
        ) : <div className="empty-state"><div className="empty-state-icon">📋</div><h3>No prescriptions yet</h3></div>}
      </div>

      {showSignModal && selectedPrescription && (
        <div className="modal-overlay" onClick={() => !saving && setShowSignModal(false)}>
          <div className="modal prescription-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Prescription Review</h2><button className="modal-close" onClick={() => setShowSignModal(false)} disabled={saving}>×</button></div>
            <div className="prescription-review">
              <div className="review-section"><h4>Patient Information</h4>
                <div className="review-grid">
                  <div><span>Name:</span> {selectedPrescription.patient_name}</div>
                  <div><span>DOB:</span> {formatDate(selectedPrescription.date_of_birth)}</div>
                  <div><span>Phone:</span> {selectedPrescription.patient_phone || '-'}</div>
                </div>
              </div>
              <div className="review-section"><h4>Vitals</h4>
                <div className="review-grid">
                  <div><span>Weight:</span> {selectedPrescription.current_weight} lbs</div>
                  <div><span>Height:</span> {selectedPrescription.height_inches}"</div>
                  <div><span>BMI:</span> {selectedPrescription.bmi}</div>
                  <div><span>BP:</span> {selectedPrescription.blood_pressure}</div>
                  <div><span>Pulse:</span> {selectedPrescription.pulse || '-'}</div>
                </div>
              </div>
              {selectedPrescription.has_allergies && <div className="review-section"><h4>Allergies</h4><p>{selectedPrescription.allergy_list}</p></div>}
              <div className="review-section"><h4>Medical History (from intake)</h4>
                {selectedPrescription.current_medications && <p><span>Medications:</span> {(Array.isArray(selectedPrescription.current_medications) ? selectedPrescription.current_medications : []).join(', ') || 'None'}</p>}
                {selectedPrescription.metabolic_health && <p><span>Metabolic:</span> {(Array.isArray(selectedPrescription.metabolic_health) ? selectedPrescription.metabolic_health : []).join(', ') || 'None'}</p>}
                {selectedPrescription.cardiovascular && <p><span>Cardiovascular:</span> {(Array.isArray(selectedPrescription.cardiovascular) ? selectedPrescription.cardiovascular : []).join(', ') || 'None'}</p>}
                {selectedPrescription.primary_goal && <p><span>Goals:</span> {selectedPrescription.primary_goal}</p>}
              </div>
              {selectedPrescription.clinical_reasoning && <div className="review-section"><h4>Clinical Reasoning (from clinic)</h4><p>{selectedPrescription.clinical_reasoning}</p></div>}
              {patientIntakeForms.length > 0 && (
                <div className="review-section">
                  <h4>Patient Intake Forms ({patientIntakeForms.length})</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {patientIntakeForms.map((f, idx) => (
                      <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: idx === 0 ? 'var(--cream)' : 'var(--cream-light)', borderRadius: '6px', border: idx === 0 ? '2px solid var(--accent-gold)' : 'none' }}>
                        <div>
                          <span style={{ fontWeight: 500 }}>{formatDate(f.created_at)}</span>
                          {idx === 0 && <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 600, color: 'var(--accent-gold)' }}>CURRENT</span>}
                          {f.has_signature && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>Signed</span>}
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => viewIntakeForm(f.id)}>View Form</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedPrescription.status === 'pending_signature' ? (
                <>
                  <div className="prescription-section">
                    <h4>Prescriber Determination</h4>
                    <div className="form-group"><select className="form-input" value={rxData.prescriberDetermination} onChange={e => setRxData({...rxData, prescriberDetermination: e.target.value})}><option value="">Select...</option><option value="approved">Approved for Treatment</option><option value="needs_review">Needs Further Review</option><option value="not_candidate">Not a Candidate</option></select></div>
                  </div>

                  <div className="prescription-section">
                    <h4>SEMAGLUTIDE</h4>
                    <div className="form-group"><label className="form-label">Type</label><select className="form-input" value={rxData.semaglutideType} onChange={e => setRxData({...rxData, semaglutideType: e.target.value})}><option value="">Select...</option><option value="Glycine">Glycine</option><option value="Methylcobalamin">Methylcobalamin</option></select></div>
                    <div className="form-group"><label className="form-label">Concentration</label><select className="form-input" value={rxData.semaglutideConcentration} onChange={e => setRxData({...rxData, semaglutideConcentration: e.target.value, semaglutideDosing: ''})}><option value="">Select...</option>{Object.keys(semaglutideOptions).map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                    {rxData.semaglutideConcentration && <div className="form-group"><label className="form-label">Dosing</label>{semaglutideOptions[rxData.semaglutideConcentration]?.map(d => (<label key={d} className="radio-label"><input type="radio" name="semaglutideDosing" value={d} checked={rxData.semaglutideDosing === d} onChange={e => setRxData({...rxData, semaglutideDosing: e.target.value})} /><span>{d}</span></label>))}</div>}
                  </div>

                  <div className="prescription-section">
                    <h4>TIRZEPATIDE</h4>
                    <div className="form-group"><label className="form-label">Type</label><select className="form-input" value={rxData.tirzepatideType} onChange={e => setRxData({...rxData, tirzepatideType: e.target.value})}><option value="">Select...</option><option value="Glycine">Glycine</option><option value="Methylcobalamin">Methylcobalamin</option></select></div>
                    <div className="form-group"><label className="form-label">Concentration</label><select className="form-input" value={rxData.tirzepatideConcentration} onChange={e => setRxData({...rxData, tirzepatideConcentration: e.target.value, tirzepatideDosing: ''})}><option value="">Select...</option>{Object.keys(tirzepatideOptions).map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                    {rxData.tirzepatideConcentration && <div className="form-group"><label className="form-label">Dosing</label>{tirzepatideOptions[rxData.tirzepatideConcentration]?.map(d => (<label key={d} className="radio-label"><input type="radio" name="tirzepatideDosing" value={d} checked={rxData.tirzepatideDosing === d} onChange={e => setRxData({...rxData, tirzepatideDosing: e.target.value})} /><span>{d}</span></label>))}</div>}
                  </div>

                  <div className="prescription-section">
                    <h4>Physician Notes</h4>
                    <textarea className="form-input" value={physicianNotes} onChange={e => setPhysicianNotes(e.target.value)} rows={2} placeholder="Additional notes..." />
                  </div>

                  <div className="signature-section">
                    <div className="signature-header"><label className="form-label">Physician Signature *</label><button type="button" className="btn btn-ghost btn-sm" onClick={clearSignature}>Clear</button></div>
                    <div className="signature-pad-wrapper"><SignatureCanvas ref={signatureRef} canvasProps={{ className: 'signature-canvas' }} onEnd={handleSignatureEnd} backgroundColor="white" /></div>
                    {signatureEmpty && <p className="signature-hint">Please sign above to approve this prescription</p>}
                  </div>
                </>
              ) : (
                <>
                  {selectedPrescription.semaglutide_dosing && <div className="review-section"><h4>Semaglutide Prescription</h4><p><span>Type:</span> {selectedPrescription.semaglutide_type}</p><p><span>Concentration:</span> {selectedPrescription.semaglutide_concentration}</p><p><span>Dosing:</span> {selectedPrescription.semaglutide_dosing}</p></div>}
                  {selectedPrescription.tirzepatide_dosing && <div className="review-section"><h4>Tirzepatide Prescription</h4><p><span>Type:</span> {selectedPrescription.tirzepatide_type}</p><p><span>Concentration:</span> {selectedPrescription.tirzepatide_concentration}</p><p><span>Dosing:</span> {selectedPrescription.tirzepatide_dosing}</p></div>}
                  {selectedPrescription.clinical_reasoning && <div className="review-section"><h4>Clinical Reasoning</h4><p>{selectedPrescription.clinical_reasoning}</p></div>}
                  <div className="review-section"><h4>Physician Signature</h4>
                    <p><span>Signed by:</span> {selectedPrescription.physician_name}</p>
                    <p><span>Signed at:</span> {new Date(selectedPrescription.signed_at).toLocaleString('en-US', { timeZone: 'America/Denver' })}</p>
                    {selectedPrescription.physician_signature && <img src={selectedPrescription.physician_signature} alt="Physician Signature" style={{maxWidth: '300px', border: '1px solid var(--cream-dark)', borderRadius: '8px', marginTop: '8px'}} />}
                  </div>
                </>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowSignModal(false)} disabled={saving}>Close</button>
              <button className="btn btn-secondary" onClick={() => window.open(`/api/admin/prescriptions/${selectedPrescription.id}/pdf`, '_blank')}>Download PDF</button>
              {selectedPrescription.status === 'pending_signature' && <button className="btn btn-gold" onClick={handleSign} disabled={saving || signatureEmpty}>{saving ? 'Signing...' : 'Sign & Approve'}</button>}
            </div>
          </div>
        </div>
      )}

      {showIntakeFormModal && viewingIntakeForm && (
        <div className="modal-overlay" onClick={() => setShowIntakeFormModal(false)}>
          <div className="modal prescription-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Intake Form: {viewingIntakeForm.first_name} {viewingIntakeForm.last_name}</h2><button className="modal-close" onClick={() => setShowIntakeFormModal(false)}>×</button></div>
            <div className="prescription-review" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="review-section"><h4>Contact Information</h4>
                <div className="review-grid">
                  <div><span>Name:</span> {viewingIntakeForm.first_name} {viewingIntakeForm.last_name}</div>
                  <div><span>Email:</span> {viewingIntakeForm.email}</div>
                  <div><span>Phone:</span> {viewingIntakeForm.phone || '-'}</div>
                  <div><span>DOB:</span> {formatDate(viewingIntakeForm.date_of_birth)}</div>
                  <div><span>Gender:</span> {viewingIntakeForm.gender || '-'}</div>
                </div>
              </div>
              <div className="review-section"><h4>Physical & Emergency</h4>
                <div className="review-grid">
                  <div><span>Weight:</span> {viewingIntakeForm.current_weight || '-'} lbs</div>
                  <div><span>Height:</span> {viewingIntakeForm.height_inches || '-'}"</div>
                  <div><span>Emergency Contact:</span> {viewingIntakeForm.emergency_contact_name || '-'}</div>
                  <div><span>Emergency Phone:</span> {viewingIntakeForm.emergency_contact_phone || '-'}</div>
                </div>
              </div>
              {viewingIntakeForm.what_brings_you && <div className="review-section"><h4>What Brings You In</h4><p>{(Array.isArray(viewingIntakeForm.what_brings_you) ? viewingIntakeForm.what_brings_you : JSON.parse(viewingIntakeForm.what_brings_you || '[]')).join(', ') || '-'}</p></div>}
              {viewingIntakeForm.primary_goal && <div className="review-section"><h4>Primary Goal</h4><p>{viewingIntakeForm.primary_goal}</p></div>}
              {viewingIntakeForm.metabolic_health && <div className="review-section"><h4>Medical History - Metabolic</h4><p>{(Array.isArray(viewingIntakeForm.metabolic_health) ? viewingIntakeForm.metabolic_health : JSON.parse(viewingIntakeForm.metabolic_health || '[]')).join(', ') || 'None'}</p></div>}
              {viewingIntakeForm.cardiovascular && <div className="review-section"><h4>Medical History - Cardiovascular</h4><p>{(Array.isArray(viewingIntakeForm.cardiovascular) ? viewingIntakeForm.cardiovascular : JSON.parse(viewingIntakeForm.cardiovascular || '[]')).join(', ') || 'None'}</p></div>}
              {viewingIntakeForm.current_medications && <div className="review-section"><h4>Current Medications</h4><p>{(Array.isArray(viewingIntakeForm.current_medications) ? viewingIntakeForm.current_medications : JSON.parse(viewingIntakeForm.current_medications || '[]')).join(', ') || 'None'}</p></div>}
              {viewingIntakeForm.allergies_list && <div className="review-section"><h4>Allergies</h4><p>{viewingIntakeForm.allergies_list}</p></div>}
              {viewingIntakeForm.signature_data && <div className="review-section"><h4>Patient Signature</h4><img src={viewingIntakeForm.signature_data} alt="Patient Signature" style={{maxWidth: '300px', border: '1px solid var(--cream-dark)', borderRadius: '8px'}} /><p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px'}}>Signed: {new Date(viewingIntakeForm.consent_timestamp).toLocaleString('en-US', { timeZone: 'America/Denver' })}</p></div>}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowIntakeFormModal(false)}>Close</button>
              <button className="btn btn-secondary" onClick={() => window.open(`/api/admin/intake-forms/${viewingIntakeForm.id}/pdf`, '_blank')}>Download PDF</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function PublicSigningPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const [success, setSuccess] = useState(false);
  const [physicianNotes, setPhysicianNotes] = useState('');
  const [rxData, setRxData] = useState({
    prescriberDetermination: '', semaglutideType: '', semaglutideConcentration: '', semaglutideDosing: '',
    tirzepatideType: '', tirzepatideConcentration: '', tirzepatideDosing: ''
  });

  const semaglutideOptions = {
    '2.5mg-2mg/mL': ['8 units (0.2mg) sub-q every week for 4 weeks #1mL vial', 'Inject 16 units (0.4mg) sub-q every week for 4 weeks #1mL vial'],
    '5mg-2mg/mL': ['Inject 16 units (0.8mg) sub-q every week for 4 weeks #1mL vial', 'Inject 28 units (1.4mg) sub-q every week for 4 weeks #1.5mL vial', 'Inject 30 units (1.5mg) sub-q every week for 4 weeks #1.5mL vial', 'Inject 43 units (2.15mg) sub-q every week for 4 weeks #2mL vial', 'Inject 54 units (2.7mg) sub-q every week for 4 weeks #2.5mL vial']
  };
  const tirzepatideOptions = {
    '8.5mg-100mcg/mL': ['Inject 25 units (2.125mg) sub-q every week for 4 weeks #1mL vial', 'Inject 50 units (4.25mg) sub-q every week for 4 weeks #2mL Vial', 'Inject 75 units (6.375mg) sub-q every week for 4 weeks #3mL Vial'],
    '5mg-2mg/mL (copy)': ['Inject 50 units (8.5mg) sub-q every week for 4 weeks #2mL Vial (28-day)', 'Inject 64 units (11mg) sub-q every week for 4 weeks #3mL Vial', 'Inject 80 units (13.5mg) sub-q every week for 4 weeks #3.5mL Vial', 'Inject 100 units (17mg) sub-q every week for 4 weeks #4mL Vial']
  };

  useEffect(() => {
    const fetchPrescription = async () => {
      try {
        const response = await fetch(`/api/public/sign/${token}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load prescription');
        setPrescription(data);
        setPhysicianNotes(data.physician_notes || '');
        setRxData({
          prescriberDetermination: data.prescriber_determination || '',
          semaglutideType: data.semaglutide_type || '', semaglutideConcentration: data.semaglutide_concentration || '', semaglutideDosing: data.semaglutide_dosing || '',
          tirzepatideType: data.tirzepatide_type || '', tirzepatideConcentration: data.tirzepatide_concentration || '', tirzepatideDosing: data.tirzepatide_dosing || ''
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchPrescription();
  }, [token]);

  const clearSignature = () => { if (signatureRef.current) { signatureRef.current.clear(); setSignatureEmpty(true); } };
  const handleSignatureEnd = () => { if (signatureRef.current && !signatureRef.current.isEmpty()) { setSignatureEmpty(false); } };

  const handleSign = async () => {
    if (signatureEmpty) { setToast({ message: 'Please provide your signature', type: 'error' }); return; }
    if (!rxData.semaglutideDosing && !rxData.tirzepatideDosing) { setToast({ message: 'Please select at least one medication', type: 'error' }); return; }
    setSaving(true);
    try {
      const signatureData = signatureRef.current.toDataURL('image/png');
      const response = await fetch(`/api/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signatureData, physicianNotes, ...rxData })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to sign');
      setSuccess(true);
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  if (loading) return <div className="login-page"><div className="loading-spinner"><div className="spinner"></div></div></div>;

  if (error) return (
    <div className="login-page">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>!</div>
        <h2 style={{ marginBottom: '8px' }}>Unable to Load Prescription</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>{error}</p>
        <button className="btn btn-gold" onClick={() => navigate('/login')}>Go to Login</button>
      </div>
    </div>
  );

  if (success) return (
    <div className="login-page">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
        <h2 style={{ marginBottom: '8px' }}>Prescription Signed!</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>The prescription has been signed and approved successfully.</p>
      </div>
    </div>
  );

  if (prescription?.status === 'approved') return (
    <div className="login-page">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
        <h2 style={{ marginBottom: '8px' }}>Already Signed</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>This prescription has already been signed.</p>
      </div>
    </div>
  );

  return (
    <div className="login-page" style={{ padding: '20px', minHeight: '100vh', overflowY: 'auto' }}>
      <div className="intake-card" style={{ maxWidth: '800px' }}>
        <div className="login-logo"><h1>Ascent Trim</h1><span>Prescription Review & Signature</span></div>

        <div className="prescription-review">
          <div className="review-section"><h4>Patient Information</h4>
            <div className="review-grid">
              <div><span>Name:</span> {prescription.patient_name}</div>
              <div><span>DOB:</span> {formatDate(prescription.date_of_birth)}</div>
              <div><span>Phone:</span> {prescription.patient_phone || '-'}</div>
            </div>
          </div>

          <div className="review-section"><h4>Vitals</h4>
            <div className="review-grid">
              <div><span>Weight:</span> {prescription.current_weight} lbs</div>
              <div><span>Height:</span> {prescription.height_inches}"</div>
              <div><span>BMI:</span> {prescription.bmi}</div>
              <div><span>BP:</span> {prescription.blood_pressure}</div>
              <div><span>Pulse:</span> {prescription.pulse || '-'}</div>
            </div>
          </div>

          {prescription.has_allergies && <div className="review-section"><h4>Allergies</h4><p style={{color: '#dc2626', fontWeight: 600}}>{prescription.allergy_list}</p></div>}

          <div className="review-section"><h4>Medical History</h4>
            {prescription.intake_medications && <p><span>Medications:</span> {(Array.isArray(prescription.intake_medications) ? prescription.intake_medications : []).join(', ') || 'None'}</p>}
            {prescription.metabolic_health && <p><span>Metabolic:</span> {(Array.isArray(prescription.metabolic_health) ? prescription.metabolic_health : []).join(', ') || 'None'}</p>}
            {prescription.cardiovascular && <p><span>Cardiovascular:</span> {(Array.isArray(prescription.cardiovascular) ? prescription.cardiovascular : []).join(', ') || 'None'}</p>}
            {prescription.primary_goal && <p><span>Goals:</span> {prescription.primary_goal}</p>}
          </div>

          {prescription.clinical_reasoning && <div className="review-section"><h4>Clinical Reasoning (from clinic)</h4><p>{prescription.clinical_reasoning}</p></div>}

          <div className="prescription-section">
            <h4>Prescriber Determination</h4>
            <div className="form-group"><select className="form-input" value={rxData.prescriberDetermination} onChange={e => setRxData({...rxData, prescriberDetermination: e.target.value})}><option value="">Select...</option><option value="approved">Approved for Treatment</option><option value="needs_review">Needs Further Review</option><option value="not_candidate">Not a Candidate</option></select></div>
          </div>

          <div className="prescription-section">
            <h4>SEMAGLUTIDE</h4>
            <div className="form-group"><label className="form-label">Type</label><select className="form-input" value={rxData.semaglutideType} onChange={e => setRxData({...rxData, semaglutideType: e.target.value})}><option value="">Select...</option><option value="Glycine">Glycine</option><option value="Methylcobalamin">Methylcobalamin</option></select></div>
            <div className="form-group"><label className="form-label">Concentration</label><select className="form-input" value={rxData.semaglutideConcentration} onChange={e => setRxData({...rxData, semaglutideConcentration: e.target.value, semaglutideDosing: ''})}><option value="">Select...</option>{Object.keys(semaglutideOptions).map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            {rxData.semaglutideConcentration && <div className="form-group"><label className="form-label">Dosing</label>{semaglutideOptions[rxData.semaglutideConcentration]?.map(d => (<label key={d} className="radio-label"><input type="radio" name="semaglutideDosing" value={d} checked={rxData.semaglutideDosing === d} onChange={e => setRxData({...rxData, semaglutideDosing: e.target.value})} /><span>{d}</span></label>))}</div>}
          </div>

          <div className="prescription-section">
            <h4>TIRZEPATIDE</h4>
            <div className="form-group"><label className="form-label">Type</label><select className="form-input" value={rxData.tirzepatideType} onChange={e => setRxData({...rxData, tirzepatideType: e.target.value})}><option value="">Select...</option><option value="Glycine">Glycine</option><option value="Methylcobalamin">Methylcobalamin</option></select></div>
            <div className="form-group"><label className="form-label">Concentration</label><select className="form-input" value={rxData.tirzepatideConcentration} onChange={e => setRxData({...rxData, tirzepatideConcentration: e.target.value, tirzepatideDosing: ''})}><option value="">Select...</option>{Object.keys(tirzepatideOptions).map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            {rxData.tirzepatideConcentration && <div className="form-group"><label className="form-label">Dosing</label>{tirzepatideOptions[rxData.tirzepatideConcentration]?.map(d => (<label key={d} className="radio-label"><input type="radio" name="tirzepatideDosing" value={d} checked={rxData.tirzepatideDosing === d} onChange={e => setRxData({...rxData, tirzepatideDosing: e.target.value})} /><span>{d}</span></label>))}</div>}
          </div>

          <div className="prescription-section">
            <h4>Physician Notes</h4>
            <textarea className="form-input" value={physicianNotes} onChange={e => setPhysicianNotes(e.target.value)} rows={2} placeholder="Additional notes..." />
          </div>

          <div className="signature-section">
            <div className="signature-header"><label className="form-label">Physician Signature *</label><button type="button" className="btn btn-ghost btn-sm" onClick={clearSignature}>Clear</button></div>
            <div className="signature-pad-wrapper"><SignatureCanvas ref={signatureRef} canvasProps={{ className: 'signature-canvas' }} onEnd={handleSignatureEnd} backgroundColor="white" /></div>
            {signatureEmpty && <p className="signature-hint">Please sign above to approve this prescription</p>}
          </div>
        </div>

        <div className="intake-actions" style={{ marginTop: '24px' }}>
          <button className="btn btn-gold" onClick={handleSign} disabled={saving || signatureEmpty}>{saving ? 'Signing...' : 'Sign & Approve Prescription'}</button>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function PublicNutritionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [patientName, setPatientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    mealsPerDay: '', feelAfterEating: '', energyCrashes: '', mealsOftenMissed: '', reasonMealsMissed: '',
    eatHomeOrAway: '', exerciseRoutine: '', dailyRoutineConsistency: '', mealTimesConsistent: '',
    hungerCravingsChanges: '', favoriteFoods: '', foodsCraveMost: '', hardestAboutNutrition: '',
    successWithNutrition: '', stressLevel: 5, gallbladderRemoval: false, hysterectomy: false,
    thyroidCondition: false, fattyLiver: false, digestiveIssues: false, foodAllergies: false,
    otherMedicalHistory: false, medicalHistoryDetails: '', additionalNotes: ''
  });

  useEffect(() => {
    const fetchNutrition = async () => {
      try {
        const response = await fetch(`/api/public/nutrition/${token}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load form');
        setPatientName(data.patientName);
      } catch (err) { setError(err.message); } finally { setLoading(false); }
    };
    if (token) fetchNutrition();
  }, [token]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const response = await fetch(`/api/public/nutrition/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to submit');
      setSuccess(true);
    } catch (err) { setToast({ message: err.message, type: 'error' }); } finally { setSaving(false); }
  };

  if (loading) return <div className="login-page"><div className="loading-spinner"><div className="spinner"></div></div></div>;

  if (error) return (
    <div className="login-page">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>!</div>
        <h2 style={{ marginBottom: '8px' }}>Unable to Load Form</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>{error}</p>
      </div>
    </div>
  );

  if (success) return (
    <div className="login-page">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
        <h2 style={{ marginBottom: '8px' }}>Form Submitted!</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Thank you for completing your nutrition intake form.</p>
      </div>
    </div>
  );

  return (
    <div className="login-page" style={{ padding: '20px', minHeight: '100vh', overflowY: 'auto' }}>
      <div className="intake-card" style={{ maxWidth: '800px' }}>
        <div className="login-logo"><h1>Ascent Trim</h1><span>Nutrition Intake - Initial Assessment</span></div>
        <p style={{ textAlign: 'center', marginBottom: '24px', color: 'var(--text-muted)' }}>Hello {patientName}, please answer the questions below as honestly as possible. There are no right or wrong answers. This information helps us personalize your nutrition recommendations.</p>

        <div className="prescription-form">
          <div className="prescription-section">
            <h4>Eating Habits</h4>
            <div className="form-group"><label className="form-label">1. How many times per day do you typically eat? (Include meals and snacks)</label><input type="text" className="form-input" value={formData.mealsPerDay} onChange={e => setFormData({...formData, mealsPerDay: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">2. Do you generally feel better or worse after eating? Please explain.</label><textarea className="form-input" rows={2} value={formData.feelAfterEating} onChange={e => setFormData({...formData, feelAfterEating: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">3. Do you experience energy crashes at any point during the day? If yes, when?</label><textarea className="form-input" rows={2} value={formData.energyCrashes} onChange={e => setFormData({...formData, energyCrashes: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">4. Which meals do you most often miss?</label><select className="form-input" value={formData.mealsOftenMissed} onChange={e => setFormData({...formData, mealsOftenMissed: e.target.value})}><option value="">Select...</option><option value="Breakfast">Breakfast</option><option value="Lunch">Lunch</option><option value="Dinner">Dinner</option><option value="Snacks">Snacks</option><option value="None">None - I rarely miss meals</option></select></div>
            <div className="form-group"><label className="form-label">5. If meals are missed, what is the main reason?</label><select className="form-input" value={formData.reasonMealsMissed} onChange={e => setFormData({...formData, reasonMealsMissed: e.target.value})}><option value="">Select...</option><option value="Time">Time</option><option value="Appetite">Appetite</option><option value="Stress">Stress</option><option value="Forgetting">Forgetting</option><option value="Other">Other</option><option value="N/A">N/A</option></select></div>
            <div className="form-group"><label className="form-label">6. Do you eat most meals at home or away from home?</label><select className="form-input" value={formData.eatHomeOrAway} onChange={e => setFormData({...formData, eatHomeOrAway: e.target.value})}><option value="">Select...</option><option value="Mostly at home">Mostly at home</option><option value="Mostly away">Mostly away from home</option><option value="About equal">About equal</option></select></div>
          </div>

          <div className="prescription-section">
            <h4>Lifestyle & Routines</h4>
            <div className="form-group"><label className="form-label">7. What is your current exercise or movement routine?</label><textarea className="form-input" rows={2} value={formData.exerciseRoutine} onChange={e => setFormData({...formData, exerciseRoutine: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">8. Do your daily routines feel more consistent or chaotic?</label><select className="form-input" value={formData.dailyRoutineConsistency} onChange={e => setFormData({...formData, dailyRoutineConsistency: e.target.value})}><option value="">Select...</option><option value="Very consistent">Very consistent</option><option value="Mostly consistent">Mostly consistent</option><option value="Somewhat chaotic">Somewhat chaotic</option><option value="Very chaotic">Very chaotic</option></select></div>
            <div className="form-group"><label className="form-label">9. Are your meal times consistent, or do they vary day to day?</label><select className="form-input" value={formData.mealTimesConsistent} onChange={e => setFormData({...formData, mealTimesConsistent: e.target.value})}><option value="">Select...</option><option value="Very consistent">Very consistent</option><option value="Mostly consistent">Mostly consistent</option><option value="Vary somewhat">Vary somewhat</option><option value="Vary a lot">Vary a lot</option></select></div>
            <div className="form-group"><label className="form-label">10. Do you notice changes in hunger or cravings at certain times of the day?</label><textarea className="form-input" rows={2} value={formData.hungerCravingsChanges} onChange={e => setFormData({...formData, hungerCravingsChanges: e.target.value})} /></div>
          </div>

          <div className="prescription-section">
            <h4>Food Preferences & Goals</h4>
            <div className="form-group"><label className="form-label">11. What are your favorite meals or foods?</label><textarea className="form-input" rows={2} value={formData.favoriteFoods} onChange={e => setFormData({...formData, favoriteFoods: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">12. What foods do you crave most often?</label><textarea className="form-input" rows={2} value={formData.foodsCraveMost} onChange={e => setFormData({...formData, foodsCraveMost: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">13. What feels hardest about nutrition right now?</label><textarea className="form-input" rows={2} value={formData.hardestAboutNutrition} onChange={e => setFormData({...formData, hardestAboutNutrition: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">14. What does success with nutrition look like to you personally?</label><textarea className="form-input" rows={2} value={formData.successWithNutrition} onChange={e => setFormData({...formData, successWithNutrition: e.target.value})} /></div>
            <div className="form-group"><label className="form-label">15. On a scale of 1-10, how would you rate your current stress level?</label><div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><input type="range" min="1" max="10" value={formData.stressLevel} onChange={e => setFormData({...formData, stressLevel: parseInt(e.target.value)})} style={{ flex: 1 }} /><span style={{ fontWeight: 600, minWidth: '30px' }}>{formData.stressLevel}</span></div></div>
          </div>

          <div className="prescription-section">
            <h4>Medical & Digestive History</h4>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>Please check any that apply:</p>
            <div className="checkbox-group">
              <label className="consent-label"><input type="checkbox" checked={formData.gallbladderRemoval} onChange={e => setFormData({...formData, gallbladderRemoval: e.target.checked})} /><span>Gallbladder removal</span></label>
              <label className="consent-label"><input type="checkbox" checked={formData.hysterectomy} onChange={e => setFormData({...formData, hysterectomy: e.target.checked})} /><span>Hysterectomy</span></label>
              <label className="consent-label"><input type="checkbox" checked={formData.thyroidCondition} onChange={e => setFormData({...formData, thyroidCondition: e.target.checked})} /><span>Thyroid condition</span></label>
              <label className="consent-label"><input type="checkbox" checked={formData.fattyLiver} onChange={e => setFormData({...formData, fattyLiver: e.target.checked})} /><span>Fatty liver diagnosis</span></label>
              <label className="consent-label"><input type="checkbox" checked={formData.digestiveIssues} onChange={e => setFormData({...formData, digestiveIssues: e.target.checked})} /><span>Digestive issues (bloating, reflux, constipation, diarrhea, etc.)</span></label>
              <label className="consent-label"><input type="checkbox" checked={formData.foodAllergies} onChange={e => setFormData({...formData, foodAllergies: e.target.checked})} /><span>Food allergies or sensitivities</span></label>
              <label className="consent-label"><input type="checkbox" checked={formData.otherMedicalHistory} onChange={e => setFormData({...formData, otherMedicalHistory: e.target.checked})} /><span>Other relevant medical history</span></label>
            </div>
            {(formData.gallbladderRemoval || formData.hysterectomy || formData.thyroidCondition || formData.fattyLiver || formData.digestiveIssues || formData.foodAllergies || formData.otherMedicalHistory) && (
              <div className="form-group" style={{ marginTop: '16px' }}><label className="form-label">Please provide details:</label><textarea className="form-input" rows={3} value={formData.medicalHistoryDetails} onChange={e => setFormData({...formData, medicalHistoryDetails: e.target.value})} placeholder="Provide any additional details about the conditions selected above..." /></div>
            )}
          </div>

          <div className="prescription-section">
            <h4>Additional Notes</h4>
            <div className="form-group"><textarea className="form-input" rows={3} value={formData.additionalNotes} onChange={e => setFormData({...formData, additionalNotes: e.target.value})} placeholder="Any other information you'd like to share..." /></div>
          </div>
        </div>

        <div className="intake-actions" style={{ marginTop: '24px' }}>
          <button className="btn btn-gold" onClick={handleSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit Nutrition Form'}</button>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============ ECOMMERCE PAGES ============

function ShopPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/api/store/products'), api.get('/api/cart'), api.get('/api/my-membership')])
      .then(([prods, cartItems, mem]) => { setProducts(prods); setCart(cartItems); setMembership(mem); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const discountPercent = membership?.discount_percent || 0;
  const getDiscountedPrice = (price) => discountPercent > 0 ? Math.round(price * (1 - discountPercent / 100) * 100) / 100 : price;

  const addToCart = async (product) => {
    try {
      await api.post('/api/cart', { productId: product.id, quantity: 1 });
      const updatedCart = await api.get('/api/cart');
      setCart(updatedCart);
      setToast({ message: `${product.name} added to cart`, type: 'success' });
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const getCartQuantity = (productId) => {
    const item = cart.find(c => c.product_id === productId);
    return item ? item.quantity : 0;
  };

  const cartTotal = cart.reduce((sum, c) => sum + (c.price * c.quantity), 0);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);
  const grouped = products.reduce((acc, p) => { if (!acc[p.category]) acc[p.category] = []; acc[p.category].push(p); return acc; }, {});

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Shop</h1><p className="page-subtitle">Browse and purchase products</p></div>
        <Link to="/cart" className="btn btn-gold">{cartCount > 0 ? `Cart (${cartCount}) - $${cartTotal.toFixed(2)}` : 'Cart'}</Link>
      </div>
      <div className="balance-card" style={{ marginBottom: '24px' }}>
        <div className="balance-amount">${parseFloat(user?.balance || 0).toFixed(2)}</div>
        <div className="balance-label">Clinic Cash Balance</div>
        {discountPercent > 0 && <div style={{ marginTop: '8px', padding: '6px 12px', background: 'rgba(5, 150, 105, 0.2)', borderRadius: '8px', color: '#059669', fontSize: '14px', fontWeight: 600 }}>{membership?.plan_name}: {discountPercent}% off all products</div>}
      </div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : products.length > 0 ? (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="section-card" style={{ marginBottom: '24px' }}>
            <div className="section-header"><h2 className="section-title">{cat}</h2></div>
            <div className="products-grid">
              {items.map(p => (
                <div key={p.id} className="product-card">
                  <div className="product-card-icon">{p.icon || '📦'}</div>
                  <div className="product-card-info">
                    <h4>{p.name}</h4>
                    {discountPercent > 0 ? (
                      <p><span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', marginRight: '8px' }}>${p.price.toFixed(2)}</span><span style={{ color: '#059669', fontWeight: 600 }}>${getDiscountedPrice(p.price).toFixed(2)}</span></p>
                    ) : (
                      <p style={{ color: 'var(--accent-gold)', fontWeight: 600 }}>${p.price.toFixed(2)}</p>
                    )}
                    {p.medically_restricted && !isAdmin && <p style={{ color: '#dc2626', fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>Rx Required</p>}
                  </div>
                  {p.medically_restricted && !isAdmin ? (
                    <button className="btn btn-secondary btn-sm" disabled style={{ opacity: 0.6 }}>Prescription Required</button>
                  ) : (
                    <button className="btn btn-gold btn-sm" onClick={() => addToCart(p)}>
                      {getCartQuantity(p.id) > 0 ? `Add More (${getCartQuantity(p.id)})` : 'Add to Cart'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      ) : <div className="section-card"><div className="empty-state"><div className="empty-state-icon">🛒</div><h3>No products available</h3><p>Check back soon!</p></div></div>}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function CartPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [membership, setMembership] = useState(null);

  const fetchCart = () => Promise.all([api.get('/api/cart'), api.get('/api/my-membership')])
    .then(([c, m]) => { setCart(c); setMembership(m); })
    .catch(console.error).finally(() => setLoading(false));
  useEffect(() => { fetchCart(); }, []);

  const updateQuantity = async (itemId, quantity) => {
    try {
      await api.put(`/api/cart/${itemId}`, { quantity });
      const updatedCart = await api.get('/api/cart');
      setCart(updatedCart);
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/api/cart/${itemId}`);
      const updatedCart = await api.get('/api/cart');
      setCart(updatedCart);
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const discountPercent = membership?.discount_percent || 0;
  const subtotal = cart.reduce((sum, c) => sum + (c.price * c.quantity), 0);
  const discountAmount = discountPercent > 0 ? Math.round(subtotal * (discountPercent / 100) * 100) / 100 : 0;
  const total = subtotal - discountAmount;
  const balance = parseFloat(user?.balance || 0);

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Shopping Cart</h1><p className="page-subtitle">Review your items</p></div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : cart.length > 0 ? (
        <>
          <div className="section-card">
            {cart.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--cream-dark)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '24px' }}>{item.icon || '📦'}</span>
                  <div><h4>{item.name}</h4><p style={{ color: 'var(--text-muted)' }}>${item.price.toFixed(2)} each</p></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                    <span style={{ minWidth: '30px', textAlign: 'center' }}>{item.quantity}</span>
                    <button className="btn btn-secondary btn-sm" onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                  </div>
                  <span style={{ fontWeight: 600, minWidth: '70px', textAlign: 'right' }}>${(item.price * item.quantity).toFixed(2)}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => removeItem(item.id)} style={{ color: '#dc2626' }}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          <div className="section-card" style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--cream-dark)' }}>
              <span>Subtotal</span><span style={{ fontWeight: 600 }}>${subtotal.toFixed(2)}</span>
            </div>
            {discountPercent > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--cream-dark)', background: 'rgba(5, 150, 105, 0.1)' }}>
                <span style={{ color: '#059669' }}>Member Discount ({discountPercent}%)</span><span style={{ color: '#059669', fontWeight: 600 }}>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--cream-dark)' }}>
              <span style={{ fontWeight: 600 }}>Total</span><span style={{ fontWeight: 600, fontSize: '18px' }}>${total.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--cream-dark)' }}>
              <span>Clinic Cash Available</span><span style={{ color: 'var(--accent-gold)', fontWeight: 600 }}>${balance.toFixed(2)}</span>
            </div>
            <div style={{ padding: '16px', display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
              <Link to="/shop" className="btn btn-secondary">Continue Shopping</Link>
              <button className="btn btn-gold" onClick={() => navigate('/checkout')}>Proceed to Checkout</button>
            </div>
          </div>
        </>
      ) : (
        <div className="section-card"><div className="empty-state"><div className="empty-state-icon">🛒</div><h3>Your cart is empty</h3><p>Browse our shop to add items</p><Link to="/shop" className="btn btn-gold" style={{ marginTop: '16px' }}>Go to Shop</Link></div></div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Stripe CardElement styling
const cardElementStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a1a',
      fontFamily: '"Inter", system-ui, sans-serif',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

// CheckoutForm - internal component that uses Stripe hooks
function CheckoutForm({ cart, membership, onSuccess }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('clinic_cash');
  const [useClinicCash, setUseClinicCash] = useState(parseFloat(user?.balance || 0));
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const discountPercent = membership?.discount_percent || 0;
  const subtotal = cart.reduce((sum, c) => sum + (c.price * c.quantity), 0);
  const memberDiscountAmount = discountPercent > 0 ? Math.round(subtotal * (discountPercent / 100) * 100) / 100 : 0;
  const afterMemberDiscount = subtotal - memberDiscountAmount;
  const couponDiscount = appliedCoupon ? appliedCoupon.discount : 0;
  const discountAmount = memberDiscountAmount + couponDiscount;
  const total = subtotal - discountAmount;
  const balance = parseFloat(user?.balance || 0);
  const clinicCashUsed = paymentMethod === 'clinic_cash' ? Math.min(balance, total) : (paymentMethod === 'split' ? Math.min(useClinicCash, balance, total) : 0);
  const cardAmount = total - clinicCashUsed;
  const requiresCard = cardAmount > 0;

  // Which processor is active? Defaults to Stripe until the config call returns,
  // so the existing Stripe experience is what renders first in every case.
  const [paymentConfig, setPaymentConfig] = useState({ provider: 'stripe' });
  const nmiCardRef = useRef(null);
  // Billing address — only used by NMI (its gateway account enforces AVS).
  // Prefilled from the patient's intake form; always editable, because AVS
  // validates the address the card issuer holds, not the patient's home address.
  const [billing, setBilling] = useState({ firstName: '', lastName: '', address1: '', city: '', state: '', zip: '', country: 'US' });
  useEffect(() => {
    let cancelled = false;
    api.get('/api/payment-config')
      .then((cfg) => {
        if (cancelled || !cfg?.provider) return;
        setPaymentConfig(cfg);
        if (cfg.billingPrefill) setBilling((b) => ({ ...b, ...cfg.billingPrefill }));
      })
      .catch(() => { /* keep the Stripe default */ });
    return () => { cancelled = true; };
  }, []);
  const setBillingField = (k) => (e) => setBilling((b) => ({ ...b, [k]: e.target.value }));

  // Which gateway this checkout will use. The server decides and reports it via
  // /api/payment-config; the browser never chooses.
  const isNmi = paymentConfig.provider === 'nmi';
  const stripeAvailable = !!stripePromise && !!stripe && !!elements;
  // Card entry is usable when the ACTIVE processor can render a form.
  const cardAvailable = isNmi ? !!paymentConfig.tokenizationKey : stripeAvailable;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const result = await api.post('/api/coupons/validate', { code: couponCode.trim(), orderAmount: afterMemberDiscount });
      setAppliedCoupon({ code: result.code, discount: result.discount, discountType: result.discountType, discountValue: result.discountValue });
      setToast({ message: `Coupon applied! You save $${result.discount.toFixed(2)}`, type: 'success' });
    } catch (e) {
      setToast({ message: e.message || 'Invalid coupon code', type: 'error' });
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleCheckout = async () => {
    if (paymentMethod === 'clinic_cash' && balance < total) {
      setToast({ message: 'Insufficient Clinic Cash balance', type: 'error' });
      return;
    }

    // Validate card if needed
    if (requiresCard && !cardAvailable) {
      setToast({ message: 'Card payments are not configured. Please contact support.', type: 'error' });
      return;
    }

    // NMI's gateway account enforces AVS — catch a missing address here so the
    // patient gets a clear message instead of an opaque gateway decline.
    if (requiresCard && isNmi && paymentConfig.billingRequired) {
      const missing = ['address1', 'city', 'state', 'zip'].filter((k) => !String(billing[k] || '').trim());
      if (missing.length) {
        setToast({ message: 'Please complete your billing address before paying by card.', type: 'error' });
        return;
      }
    }

    setProcessing(true);
    setCardError(null);

    try {
      // TOKENIZE BEFORE CREATING THE ORDER (NMI only).
      //
      // Collect.js needs no order id — the order id is attached later, on the
      // sale. Creating the order first meant a card that failed validation
      // left an orphan pending_payment order behind every single time, which
      // is how #896 and #897 appeared with no gateway transaction at all.
      // Validate and tokenise first, and nothing is created unless we hold a
      // real token.
      //
      // Stripe is deliberately NOT reordered: it needs the PaymentIntent (and
      // therefore the order) to exist before the card can be confirmed.
      let nmiToken = null;
      if (requiresCard && isNmi) {
        try {
          nmiToken = await nmiCardRef.current.tokenize();
        } catch (tokErr) {
          // A validation failure, not a decline: no order, no charge.
          setCardError(tokErr.message);
          setToast({ message: tokErr.message, type: 'error' });
          setProcessing(false);
          return;
        }
      }

      // Create the order on the backend
      // One key per checkout attempt, held across retries of that attempt, so a
      // refresh or a network retry resolves to the SAME order instead of a
      // second one. Cleared only once the payment itself has succeeded, so a
      // user-initiated retry reuses the order rather than opening another.
      let attemptKey = sessionStorage.getItem('checkoutAttemptKey');
      if (!attemptKey) {
        attemptKey = 'co-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
        try { sessionStorage.setItem('checkoutAttemptKey', attemptKey); } catch (_) {}
      }
      const result = await api.post(
        '/api/checkout',
        { paymentMethod, useClinicCash: clinicCashUsed, couponCode: appliedCoupon?.code || null },
        { 'Idempotency-Key': attemptKey }
      );

      // Smart-route: if the cart contained a membership, server returns a Stripe Checkout subscription URL
      if (result.isSubscription && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }

      if (result.requiresPayment && result.clientSecret) {
        // Confirm the payment with Stripe
        const cardElement = elements.getElement(CardElement);
        const { error, paymentIntent } = await stripe.confirmCardPayment(result.clientSecret, {
          payment_method: { card: cardElement },
        });

        if (error) {
          setCardError(error.message);
          setToast({ message: error.message, type: 'error' });
          setProcessing(false);
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          // Confirm payment on backend
          await api.post(`/api/orders/${result.orderId}/confirm-payment`);
        }
      } else if (result.requiresPayment && result.deferredCapture) {
        // The token was obtained BEFORE the order existed. Only the opaque
        // single-use token crosses the wire — never card data.
        await api.post(`/api/orders/${result.orderId}/confirm-payment`,
          { paymentToken: nmiToken, billing });
      }

      // Payment is done. Only now release the attempt key, so any earlier
      // failure is retried against the SAME order instead of opening another.
      try { sessionStorage.removeItem('checkoutAttemptKey'); } catch (_) {}

      await refreshUser();
      navigate('/my-orders');
    } catch (e) {
      // Two new server outcomes from the Stripe migration need their own words.
      // Both mean "no payment was taken", and the cart is left intact so the
      // patient can act on the message instead of re-entering everything.
      const code = e && (e.code || (e.body && e.body.code));
      if (code === 'product_not_eligible_for_card') {
        const msg = (e.body && e.body.error) || e.message
          || 'One or more items in your cart cannot be paid for online. Please contact the clinic.';
        setCardError(msg);
        setToast({ message: msg, type: 'error' });
      } else if (code === 'payment_provider_unavailable') {
        const msg = 'Card payments are temporarily unavailable. Nothing was charged — please try again shortly or call the clinic.';
        setCardError(msg);
        setToast({ message: msg, type: 'error' });
      } else if (code === 'capture_not_verified') {
        // APPROVED BUT UNCONFIRMED. The worst thing a patient can do here is
        // pay again, so this must never read like a plain failure.
        const msg = (e.body && e.body.chargeMayHaveSucceeded)
          ? 'Your card was approved but we could not confirm it. DO NOT pay again — please contact the clinic so we can finish this order.'
          : 'We could not confirm this payment with the card processor, so the order was not marked paid. Please contact the clinic before trying again.';
        setCardError(msg);
        setToast({ message: msg, type: 'error' });
      } else if (code === 'possible_duplicate_order') {
        const msg = (e.body && e.body.error) || 'This looks like a duplicate of an order already paid. Nothing was charged.';
        setCardError(msg);
        setToast({ message: msg, type: 'error' });
      } else {
        // A processor DECLINE lands here with the gateway's own words.
        const msg = e.message || 'Payment was not completed.';
        setCardError(msg);
        setToast({ message: msg, type: 'error' });
      }
      // Busy state always clears, so the button never strands on "Processing".
      setProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
  };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Checkout</h1><p className="page-subtitle">Complete your order</p></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Order Summary</h2></div>
          {cart.map(item => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--cream-dark)' }}>
              <span>{item.name} x{item.quantity}</span>
              <span>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--cream-dark)' }}>
            <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
          </div>
          {discountPercent > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--cream-dark)', color: '#059669' }}>
              <span>Member Discount ({discountPercent}%)</span><span>-${memberDiscountAmount.toFixed(2)}</span>
            </div>
          )}
          {appliedCoupon && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--cream-dark)', color: '#059669' }}>
              <span>Coupon ({appliedCoupon.code})</span><span>-${couponDiscount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', fontWeight: 600, fontSize: '18px' }}>
            <span>Total</span><span>${total.toFixed(2)}</span>
          </div>
          {/* Coupon Code Input */}
          <div style={{ padding: '16px 0', borderTop: '1px solid var(--cream-dark)' }}>
            {appliedCoupon ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#d1fae5', borderRadius: '8px' }}>
                <span style={{ color: '#065f46' }}>Coupon "{appliedCoupon.code}" applied</span>
                <button onClick={handleRemoveCoupon} style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '14px' }}>Remove</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Coupon code"
                  value={couponCode}
                  onChange={e => setCouponCode(e.target.value.toUpperCase())}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {couponLoading ? '...' : 'Apply'}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Payment Method</h2></div>
          <div className="form-group">
            <label className="radio-label" style={{ display: 'flex', alignItems: 'center', padding: '12px', background: paymentMethod === 'clinic_cash' ? 'var(--cream)' : 'transparent', borderRadius: '8px', cursor: 'pointer' }}>
              <input type="radio" name="payment" checked={paymentMethod === 'clinic_cash'} onChange={() => setPaymentMethod('clinic_cash')} />
              <span style={{ marginLeft: '12px' }}>Pay with Clinic Cash <span style={{ color: 'var(--accent-gold)' }}>(${balance.toFixed(2)} available)</span></span>
            </label>
            <label className="radio-label" style={{ display: 'flex', alignItems: 'center', padding: '12px', background: paymentMethod === 'card' ? 'var(--cream)' : 'transparent', borderRadius: '8px', cursor: 'pointer', marginTop: '8px' }}>
              <input type="radio" name="payment" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
              <span style={{ marginLeft: '12px' }}>Pay with Card</span>
            </label>
            {balance > 0 && balance < total && (
              <label className="radio-label" style={{ display: 'flex', alignItems: 'center', padding: '12px', background: paymentMethod === 'split' ? 'var(--cream)' : 'transparent', borderRadius: '8px', cursor: 'pointer', marginTop: '8px' }}>
                <input type="radio" name="payment" checked={paymentMethod === 'split'} onChange={() => setPaymentMethod('split')} />
                <span style={{ marginLeft: '12px' }}>Split Payment</span>
              </label>
            )}
          </div>
          {paymentMethod === 'split' && (
            <div className="form-group" style={{ marginTop: '16px' }}>
              <label className="form-label">Use Clinic Cash: ${clinicCashUsed.toFixed(2)}</label>
              <input type="range" min="0" max={Math.min(balance, total)} step="0.01" value={useClinicCash} onChange={e => setUseClinicCash(parseFloat(e.target.value))} style={{ width: '100%' }} />
              <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Remaining ${cardAmount.toFixed(2)} will be charged to card</p>
            </div>
          )}

          {/* Card Input Section */}
          {requiresCard && (
            <div style={{ marginTop: '20px' }}>
              <label className="form-label">Card Details</label>
              {isNmi && paymentConfig.tokenizationKey ? (
                <div style={{ padding: '14px', border: '1px solid var(--cream-dark)', borderRadius: '8px', background: 'white', minHeight: '44px' }}>
                  <NmiCardForm
                    ref={nmiCardRef}
                    tokenizationKey={paymentConfig.tokenizationKey}
                    onError={setCardError}
                  />
                </div>
              ) : stripeAvailable ? (
                <div style={{ padding: '14px', border: '1px solid var(--cream-dark)', borderRadius: '8px', background: 'white', minHeight: '44px' }}>
                  <CardElement options={cardElementStyle} onChange={handleCardChange} />
                </div>
              ) : (
                <div style={{ padding: '14px', border: '1px solid #fecaca', borderRadius: '8px', background: '#fef2f2', color: '#991b1b', fontSize: '14px' }}>
                  Card payments are not yet configured. Please use Clinic Cash or contact support.
                </div>
              )}
              {cardError && <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>{cardError}</p>}

              {/* Billing address — shown only when NMI is the active processor.
                  Stripe checkout is unchanged and never renders this block. */}
              {isNmi && paymentConfig.billingRequired && (
                <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid var(--cream-dark)' }}>
                  <label className="form-label">Billing Address</label>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px' }}>
                    Must match the address on file with your card issuer.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input className="form-input" style={{ flex: 1 }} placeholder="First name"
                      value={billing.firstName} onChange={setBillingField('firstName')} />
                    <input className="form-input" style={{ flex: 1 }} placeholder="Last name"
                      value={billing.lastName} onChange={setBillingField('lastName')} />
                  </div>
                  <input className="form-input" style={{ width: '100%', marginBottom: '10px' }} placeholder="Street address"
                    value={billing.address1} onChange={setBillingField('address1')} />
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input className="form-input" style={{ flex: 2 }} placeholder="City"
                      value={billing.city} onChange={setBillingField('city')} />
                    <input className="form-input" style={{ flex: 1 }} placeholder="State"
                      value={billing.state} onChange={setBillingField('state')} />
                    <input className="form-input" style={{ flex: 1 }} placeholder="ZIP"
                      value={billing.zip} onChange={setBillingField('zip')} />
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: '24px', padding: '16px', background: 'var(--cream)', borderRadius: '8px' }}>
            {clinicCashUsed > 0 && <p>Clinic Cash: -${clinicCashUsed.toFixed(2)}</p>}
            {cardAmount > 0 && <p>Card: ${cardAmount.toFixed(2)}</p>}
            <p style={{ fontWeight: 600, marginTop: '8px' }}>Total: ${total.toFixed(2)}</p>
          </div>
          <button
            className="btn btn-gold"
            style={{ width: '100%', marginTop: '24px' }}
            onClick={handleCheckout}
            disabled={processing || (requiresCard && !cardAvailable)}
          >
            {processing ? 'Processing...' : `Pay ${cardAmount > 0 ? `$${cardAmount.toFixed(2)} by Card` : `$${total.toFixed(2)}`}`}
          </button>
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// CheckoutPage - wrapper that provides Stripe Elements context
function CheckoutPage() {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/api/cart'), api.get('/api/my-membership')])
      .then(([c, m]) => { setCart(c); setMembership(m); })
      .catch(console.error).finally(() => setLoading(false));
  }, [user]);

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;
  if (cart.length === 0) return <Navigate to="/cart" replace />;

  // Wrap with Stripe Elements if Stripe is configured
  if (stripePromise) {
    return (
      <Elements stripe={stripePromise}>
        <CheckoutForm cart={cart} membership={membership} />
      </Elements>
    );
  }

  // Fallback without Stripe (clinic cash only, or NMI is the active processor).
  // Still wrapped in <Elements stripe={null}> — a supported pattern — because
  // CheckoutForm calls useStripe()/useElements(), which THROW outside an Elements
  // provider. With stripe={null} both hooks return null and the form degrades to
  // its non-Stripe path instead of crashing the page.
  return (
    <Elements stripe={null}>
      <CheckoutForm cart={cart} membership={membership} />
    </Elements>
  );
}

/**
 * Pay an order that is already sitting in `pending_payment`.
 *
 * Needed because the shop only pays for a cart at the moment it is created.
 * An order that was opened and then left unpaid — or whose earlier attempt on
 * another processor was voided — had no route back to payment inside the
 * portal at all.
 *
 * Duplicate safety is mostly server-side (one PaymentIntent per order, reused;
 * a succeeded one returns 409). Here we add the two client-side halves: the
 * button disables itself for the whole round trip, and a success refreshes the
 * list from the server rather than guessing the new state.
 */
function PayPendingOrderInner({ order, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // This order is bound to the gateway that opened it, so the form follows the
  // ORDER, never the clinic's current toggle. Config supplies the NMI
  // tokenization key; `order.payment_provider` decides which form to show.
  const [cfg, setCfg] = useState({ provider: 'stripe' });
  const nmiRef = useRef(null);
  const [billing, setBilling] = useState({ firstName: '', lastName: '', address1: '', city: '', state: '', zip: '', country: 'US' });
  const setBf = (k) => (e) => setBilling((b) => ({ ...b, [k]: e.target.value }));
  useEffect(() => {
    let dead = false;
    api.get('/api/payment-config')
      .then((c) => { if (!dead && c?.provider) { setCfg(c); if (c.billingPrefill) setBilling((b) => ({ ...b, ...c.billingPrefill })); } })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  const amount = Number(order.stripe_amount || 0);
  const isNmiOrder = order.payment_provider === 'nmi';
  const ready = isNmiOrder ? !!cfg.tokenizationKey : (!!stripePromise && !!stripe && !!elements);

  const pay = async () => {
    if (busy) return;              // second click during the round trip
    if (isNmiOrder) {
      const missing = ['address1', 'city', 'state', 'zip'].filter((k) => !String(billing[k] || '').trim());
      if (missing.length) { setError('Billing address is required by the card processor (street, city, state, ZIP).'); return; }
    }
    setBusy(true); setError(null);
    try {
      const intent = await api.post(`/api/orders/${order.id}/payment-intent`);

      // Branch on what the SERVER returned, not on a client guess.
      if (intent.deferredCapture || (!intent.clientSecret && isNmiOrder)) {
        let paymentToken;
        try {
          paymentToken = await nmiRef.current.tokenize();
        } catch (tokErr) { setError(tokErr.message); setBusy(false); return; }
        await api.post(`/api/orders/${order.id}/confirm-payment`, { paymentToken, billing });
      } else {
        const { error: stripeError } = await stripe.confirmCardPayment(intent.clientSecret, {
          payment_method: { card: elements.getElement(CardElement) },
        });
        if (stripeError) { setError(stripeError.message); setBusy(false); return; }

        // The server re-verifies with Stripe before it will mark anything paid.
        await api.post(`/api/orders/${order.id}/confirm-payment`, {});
      }
      setOpen(false);
      await onPaid();
    } catch (e) {
      const code = e && e.code;
      if (code === 'possible_duplicate_order') {
        setError((e.body && e.body.error) || 'This looks like a duplicate of an order already paid. Nothing was charged.');
        await onPaid();
      } else if (code === 'already_paid') {
        setError('This order has already been paid. Refreshing…');
        await onPaid();
      } else if (code === 'payment_provider_unavailable') {
        setError('Card payments are temporarily unavailable. Nothing was charged — please try again shortly.');
      } else if (code === 'capture_not_verified') {
        setError('We could not confirm this payment with the card processor, so the order was not marked paid. Please contact the clinic before trying again.');
      } else {
        setError(e.message || 'Payment failed.');
      }
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn btn-gold btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setOpen(true)}>
        Pay ${amount.toFixed(2)}
      </button>
    );
  }

  return (
    <div style={{ width: '100%', marginTop: '12px', padding: '14px', border: '1px solid var(--cream-dark)', borderRadius: '8px', background: 'white' }}>
      <div style={{ fontWeight: 600, marginBottom: '10px' }}>Pay ${amount.toFixed(2)} for order #{order.id}</div>
      {ready ? (
        <>
          {isNmiOrder
            ? <NmiCardForm ref={nmiRef} tokenizationKey={cfg.tokenizationKey} onError={setError} />
            : <CardElement options={cardElementStyle} onChange={() => setError(null)} />}
          {/* AVS is mandatory on the LuQra gateway account. Prefilled from the
              patient's own intake here (unlike the front desk, where the card
              may not be the patient's) and always editable. */}
          {isNmiOrder && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <input className="form-input" placeholder="First name" value={billing.firstName} onChange={setBf('firstName')} />
                <input className="form-input" placeholder="Last name" value={billing.lastName} onChange={setBf('lastName')} />
              </div>
              <input className="form-input" style={{ marginTop: '8px' }} placeholder="Street address" value={billing.address1} onChange={setBf('address1')} />
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginTop: '8px' }}>
                <input className="form-input" placeholder="City" value={billing.city} onChange={setBf('city')} />
                <input className="form-input" placeholder="State" value={billing.state} onChange={setBf('state')} />
                <input className="form-input" placeholder="ZIP" value={billing.zip} onChange={setBf('zip')} />
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: '14px', color: '#991b1b' }}>Card payments are not available right now. Please contact the clinic.</div>
      )}
      {error && <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button className="btn btn-gold btn-sm" disabled={!ready || busy} onClick={pay}>
          {busy ? 'Processing…' : `Pay $${amount.toFixed(2)}`}
        </button>
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setOpen(false); setError(null); }}>
          Cancel
        </button>
      </div>
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '10px' }}>
        Do not refresh while this is processing.
      </p>
    </div>
  );
}

function PayPendingOrder(props) {
  // CheckoutPage wraps itself the same way; MyOrdersPage is not inside an
  // Elements provider, and useStripe()/useElements() throw outside one.
  return <Elements stripe={stripePromise || null}><PayPendingOrderInner {...props} /></Elements>;
}

function MyOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/api/orders').then(setOrders).catch(console.error);
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const getStatusBadge = (status) => {
    const styles = { pending_payment: { bg: '#fef3c7', color: '#92400e' }, paid: { bg: '#dbeafe', color: '#1e40af' }, ready: { bg: '#d1fae5', color: '#065f46' }, picked_up: { bg: '#e5e7eb', color: '#374151' }, cancelled: { bg: '#f3f4f6', color: '#6b7280' }, refunded: { bg: '#fee2e2', color: '#991b1b' } };
    const s = styles[status] || styles.paid;
    return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color }}>{status.replace('_', ' ').toUpperCase()}</span>;
  };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">My Orders</h1><p className="page-subtitle">View your order history</p></div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : orders.length > 0 ? (
        <div className="section-card">
          {orders.map(order => (
            <div key={order.id} style={{ padding: '20px', borderBottom: '1px solid var(--cream-dark)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div><strong>Order #{order.id}</strong><span style={{ color: 'var(--text-muted)', marginLeft: '12px' }}>{formatDate(order.created_at)}</span></div>
                {getStatusBadge(order.status)}
              </div>
              <div style={{ marginBottom: '12px' }}>
                {order.items?.filter(i => i.name).map((item, idx) => (
                  <span key={idx} style={{ marginRight: '16px' }}>{item.name} x{item.quantity}</span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '16px', color: 'var(--text-muted)', fontSize: '14px' }}>
                <span>Total: <strong style={{ color: 'var(--text-primary)' }}>${order.total.toFixed(2)}</strong></span>
                {order.clinic_cash_used > 0 && <span>Clinic Cash: ${order.clinic_cash_used.toFixed(2)}</span>}
                {order.stripe_amount > 0 && <span>Card: ${order.stripe_amount.toFixed(2)}</span>}
                {["paid", "ready", "picked_up"].includes(order.status) && <a href={"/api/orders/" + order.id + "/receipt"} className="btn btn-secondary btn-sm" style={{marginLeft: "auto"}} target="_blank" rel="noreferrer">Download Receipt</a>}
                {order.status === 'pending_payment' && order.stripe_amount > 0 && (
                  order.possible_duplicate_of ? (
                    // This order mirrors one already paid — almost always the
                    // abandoned half of a double-submit. Offering a Pay button
                    // here would charge for goods already bought.
                    <span style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-muted)' }}
                          title={`Appears to duplicate order #${order.possible_duplicate_of}`}>
                      Already covered by order #{order.possible_duplicate_of} — contact the clinic
                    </span>
                  ) : (
                    <PayPendingOrder order={order} onPaid={load} />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="section-card"><div className="empty-state"><div className="empty-state-icon">📦</div><h3>No orders yet</h3><p>Your orders will appear here</p><Link to="/shop" className="btn btn-gold" style={{ marginTop: '16px' }}>Start Shopping</Link></div></div>
      )}
    </div>
  );
}

function MembershipsPage() {
  const [plans, setPlans] = useState([]);
  const [myMembership, setMyMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const location = useLocation();

  useEffect(() => {
    Promise.all([api.get('/api/membership-plans'), api.get('/api/my-membership')])
      .then(([p, m]) => { setPlans(p); setMyMembership(m); })
      .catch(console.error).finally(() => setLoading(false));
    const params = new URLSearchParams(location.search);
    if (params.get('success')) setToast({ message: 'Membership activated successfully!', type: 'success' });
    if (params.get('canceled')) setToast({ message: 'Membership signup canceled', type: 'error' });
  }, [location]);

  const handleSubscribe = async (planId) => {
    try {
      const result = await api.post('/api/subscribe', { planId });
      if (result.sessionUrl) window.location.href = result.sessionUrl;
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your membership? It will remain active until the end of the billing period.')) return;
    try {
      await api.post('/api/cancel-membership');
      setToast({ message: 'Membership will cancel at end of billing period', type: 'success' });
      const m = await api.get('/api/my-membership');
      setMyMembership(m);
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'long', day: 'numeric' }) : '-';

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Memberships</h1><p className="page-subtitle">Subscribe for monthly Clinic Cash credits</p></div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <>
          {myMembership && (
            <div className="section-card" style={{ marginBottom: '24px', background: 'linear-gradient(135deg, var(--accent-gold) 0%, #d4a84b 100%)', color: 'white' }}>
              <div className="section-header"><h2 className="section-title" style={{ color: 'white' }}>Your Membership</h2></div>
              <div style={{ padding: '0 0 20px 0' }}>
                <h3 style={{ marginBottom: '8px' }}>{myMembership.plan_name}</h3>
                <p>${parseFloat(myMembership.price).toFixed(2)}/{myMembership.interval} → ${parseFloat(myMembership.clinic_cash_amount).toFixed(2)} Clinic Cash</p>
                <p style={{ marginTop: '12px', opacity: 0.9 }}>
                  {myMembership.status === 'canceling' ? 'Cancels on: ' : 'Renews on: '}{formatDate(myMembership.current_period_end)}
                </p>
                {myMembership.status === 'active' && (
                  <button className="btn" style={{ marginTop: '16px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }} onClick={handleCancel}>Cancel Membership</button>
                )}
                {myMembership.status === 'canceling' && <p style={{ marginTop: '12px', fontStyle: 'italic' }}>Membership will end after current period</p>}
              </div>
            </div>
          )}
          <div className="section-card">
            <div className="section-header"><h2 className="section-title">{myMembership ? 'Other Plans' : 'Available Plans'}</h2></div>
            {plans.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', padding: '20px 0' }}>
                {plans.filter(p => !myMembership || p.id !== myMembership.plan_id).map(plan => (
                  <div key={plan.id} style={{ padding: '24px', border: '2px solid var(--cream-dark)', borderRadius: '12px', textAlign: 'center' }}>
                    <h3 style={{ marginBottom: '8px' }}>{plan.name}</h3>
                    <p style={{ fontSize: '32px', fontWeight: 700, color: 'var(--accent-gold)', margin: '16px 0' }}>${plan.price.toFixed(2)}<span style={{ fontSize: '16px', fontWeight: 400, color: 'var(--text-muted)' }}>/{plan.interval}</span></p>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>{plan.description || `Get $${plan.clinic_cash_amount.toFixed(2)} Clinic Cash every ${plan.interval}`}</p>
                    <div style={{ background: 'var(--cream)', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                      <span style={{ fontSize: '24px', fontWeight: 700, color: 'var(--accent-gold)' }}>${plan.clinic_cash_amount.toFixed(2)}</span>
                      <span style={{ color: 'var(--text-muted)' }}> Clinic Cash/{plan.interval}</span>
                    </div>
                    <button className="btn btn-gold" style={{ width: '100%' }} onClick={() => handleSubscribe(plan.id)} disabled={!!myMembership}>
                      {myMembership ? 'Already Subscribed' : 'Subscribe'}
                    </button>
                  </div>
                ))}
              </div>
            ) : <p style={{ color: 'var(--text-muted)', padding: '20px 0' }}>No membership plans available</p>}
          </div>
        </>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// Helper component for admin order card payment (needs to be inside Elements)
function AdminOrderCardPayment({ total, cardAmount, clinicCashUsed, creating, cardError, setCardError, selectedPatient, orderItems, onCancel, onSubmit, paymentMethod, patientBalance, paymentConfig = { provider: 'stripe' }, nmiCardRef, billing, setBillingField }) {
  const stripe = useStripe();
  const elements = useElements();
  const stripeReady = !!(stripe && elements);
  // Which gateway is taking this card is decided by the server's processor
  // toggle (/api/payment-config). The browser never chooses.
  const isNmi = paymentConfig.provider === 'nmi' && !!paymentConfig.tokenizationKey;
  // NMI tokenises through Collect.js, so Stripe.js being unready must not gate
  // the panel — otherwise the front desk sees a permanent spinner whenever the
  // clinic is routed to LuQra.
  const cardReady = isNmi || stripeReady;

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : null);
  };

  const isDisabled = creating || !selectedPatient || orderItems.length === 0 || (paymentMethod === 'clinic_cash' && patientBalance < total);

  // While Stripe.js/Elements is initializing, show a clear loading state so the
  // panel is never silently blank. Historically this was invisible: staff saw
  // an empty card area and assumed the whole ring-up flow was broken.
  if (!cardReady) {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center' }}>
        <div className="loading-spinner" style={{ margin: '0 auto 12px' }}><div className="spinner"></div></div>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>Loading secure card form…</p>
        <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>If this doesn't clear, refresh the page or check your network connection.</p>
        <div className="modal-actions" style={{ marginTop: '16px' }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={creating}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '0 24px 16px' }}>
        <label className="form-label">Card Details</label>
        <div style={{
          padding: '14px',
          border: '1px solid var(--cream-dark)',
          borderRadius: '8px',
          background: 'white',
          minHeight: '44px',
          display: 'block',       // iOS Safari: force block layout so the Stripe iframe isn't sized by an inline/flex parent
          width: '100%',          // iOS Safari: avoid the iframe collapsing when the modal grid squeezes width
          position: 'relative'    // iOS Safari: create a positioning context — fixes several known iframe render bugs
        }}>
          {isNmi
            ? <NmiCardForm ref={nmiCardRef} tokenizationKey={paymentConfig.tokenizationKey} onError={setCardError} />
            : <CardElement options={cardElementStyle} onChange={handleCardChange} />}
        </div>
        {cardError && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px' }}>{cardError}</p>}

        {/* AVS. The LuQra gateway account REQUIRES a billing address: a sale
            without address1 is declined outright. This is the CARDHOLDER's
            billing address as the issuer holds it — deliberately NOT prefilled
            from the patient's intake, because at the front desk the card may
            not be the patient's and a wrong address reads as fraud. */}
        {isNmi && (
          <div style={{ marginTop: '12px' }}>
            <label className="form-label">Cardholder Billing Address <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <input className="form-input" placeholder="First name" value={billing?.firstName || ''} onChange={setBillingField('firstName')} />
              <input className="form-input" placeholder="Last name" value={billing?.lastName || ''} onChange={setBillingField('lastName')} />
            </div>
            <input className="form-input" style={{ marginTop: '8px' }} placeholder="Street address" value={billing?.address1 || ''} onChange={setBillingField('address1')} />
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginTop: '8px' }}>
              <input className="form-input" placeholder="City" value={billing?.city || ''} onChange={setBillingField('city')} />
              <input className="form-input" placeholder="State" value={billing?.state || ''} onChange={setBillingField('state')} />
              <input className="form-input" placeholder="ZIP" value={billing?.zip || ''} onChange={setBillingField('zip')} />
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px' }}>
              Required by the card processor. Use the address on the card's statement.
            </p>
          </div>
        )}

        <div style={{ marginTop: '12px', padding: '12px', background: 'var(--cream)', borderRadius: '8px', fontSize: '14px' }}>
          {clinicCashUsed > 0 && <div>Clinic Cash: ${clinicCashUsed.toFixed(2)}</div>}
          <div style={{ fontWeight: 600 }}>Card Amount: ${cardAmount.toFixed(2)}</div>
        </div>
      </div>
      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onCancel} disabled={creating}>Cancel</button>
        <button className="btn btn-gold" onClick={() => onSubmit(stripe, elements)} disabled={isDisabled || (!isNmi && !stripe)}>
          {creating ? 'Processing Payment...' : `Charge $${cardAmount.toFixed(2)}`}
        </button>
      </div>
    </>
  );
}

// Phase 2: sequential card-entry component for split-card orders.
// Reuses one CardElement, clears it between cards. Each Stripe confirm is followed by
// a server-side /confirm call that verifies the PaymentIntent and rolls up order status.
function AdminSplitCardPayment({ orderId, payments, currentIndex, onPaymentConfirmed, onComplete, onCancel, total }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const currentPayment = payments[currentIndex];
  const allDone = currentIndex >= payments.length;

  const handleChargeCard = async () => {
    if (!currentPayment || !stripe || !elements) return;
    setProcessing(true);
    setCardError(null);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(currentPayment.clientSecret, {
        payment_method: { card: cardElement }
      });
      if (error) {
        setCardError(error.message);
        setProcessing(false);
        return;
      }
      if (paymentIntent.status === 'succeeded') {
        const result = await api.post(`/api/admin/orders/${orderId}/payments/${currentPayment.paymentId}/confirm`);
        if (result.success || result.paymentStatus === 'succeeded') {
          // Clear card field for next card
          try { cardElement.clear(); } catch (_) {}
          const nextIndex = currentIndex + 1;
          onPaymentConfirmed(nextIndex);
          if (nextIndex >= payments.length) {
            onComplete();
          }
        } else {
          setCardError('Server did not confirm the payment. Please retry.');
        }
      } else {
        setCardError('Payment did not succeed (status: ' + paymentIntent.status + ')');
      }
    } catch (e) {
      setCardError(e.message || 'Unexpected error');
    } finally {
      setProcessing(false);
    }
  };

  if (allDone) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
        <h3>All payments confirmed</h3>
        <p style={{ color: 'var(--text-muted)' }}>Order paid. You can close this window.</p>
        <button className="btn btn-gold" onClick={onComplete} style={{ marginTop: '12px' }}>Done</button>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '0 24px 12px' }}>
        <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '6px' }}>Order #{orderId} — total ${total.toFixed(2)}</div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {payments.map((p, i) => (
              <div key={p.paymentId} style={{
                flex: 1,
                padding: '8px 10px',
                border: '1px solid ' + (i < currentIndex ? '#059669' : i === currentIndex ? 'var(--accent-gold)' : 'var(--cream-dark)'),
                borderRadius: '6px',
                background: i < currentIndex ? '#ecfdf5' : i === currentIndex ? 'white' : 'transparent',
                fontSize: '13px'
              }}>
                <div style={{ fontWeight: 600 }}>
                  {i < currentIndex ? '✓ ' : i === currentIndex ? '→ ' : ''}Card {i + 1}: ${parseFloat(p.amount).toFixed(2)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {i < currentIndex ? 'paid' : i === currentIndex ? 'enter card below' : 'pending'}
                </div>
              </div>
            ))}
          </div>
        </div>
        <label className="form-label">Card {currentIndex + 1} Details — Charging ${parseFloat(currentPayment.amount).toFixed(2)}</label>
        <div style={{ padding: '14px', border: '1px solid var(--cream-dark)', borderRadius: '8px', background: 'white', minHeight: '44px' }}>
          <CardElement options={cardElementStyle} onChange={(e) => setCardError(e.error ? e.error.message : null)} />
        </div>
        {cardError && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '8px' }}>{cardError}</p>}
        {currentIndex > 0 && (
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
            ⚠️ Earlier charges have succeeded. If you cancel now, the order will be in <code>partial_paid</code> status — you can finish or refund from the orders list.
          </p>
        )}
      </div>
      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onCancel} disabled={processing}>Cancel</button>
        <button className="btn btn-gold" onClick={handleChargeCard} disabled={processing || !stripe}>
          {processing ? 'Processing...' : `Charge $${parseFloat(currentPayment.amount).toFixed(2)}`}
        </button>
      </div>
    </>
  );
}

// ==================== Signed Medication Pickup Modal ====================
// Two-phase in-modal UX (no public route, no token). Staff opens from an order,
// enters per-line lot + quantity, clicks "Present to Patient", swaps to a
// full-screen signature step showing the itemized handoff, patient signs once
// covering every line, submits. Idempotent via client_request_id.
function generatePickupClientRequestId() {
  try {
    if (typeof crypto !== 'undefined' && crypto && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
    }
  } catch (_) {}
  // Fallback: timestamp + random suffix; length + charset match server regex
  return 'p-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
}

function PickupModal({ orderId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [lines, setLines] = useState([]); // [{ order_item_id, product_id, product_name, ordered_qty, dispensed_qty, remaining_qty, includeQty, lotNumber }]
  const [phase, setPhase] = useState('collect'); // 'collect' | 'signature'
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const signatureRef = useRef(null);
  // Why the dispense list is empty — drives the staff-facing explanation.
  const [emptyReason, setEmptyReason] = useState(null);
  const [alreadyDispensed, setAlreadyDispensed] = useState([]);
  const clientRequestIdRef = useRef(generatePickupClientRequestId());

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.get(`/api/admin/orders/${orderId}/dispense-lines`);
        if (!mounted) return;
        setOrderInfo(data.order);
        setEmptyReason(data.emptyReason || null);
        setAlreadyDispensed(data.alreadyDispensed || []);
        setLines((data.lines || []).map(l => ({
          ...l,
          includeQty: l.remaining_qty,
          lotNumber: ''
        })));
      } catch (e) {
        if (!mounted) return;
        setLoadError(e.message || 'Failed to load order');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [orderId]);

  const includedLines = lines.filter(l => l.includeQty > 0);
  const canProceed = includedLines.length > 0 && includedLines.every(l =>
    l.lotNumber.trim().length > 0 && l.includeQty > 0 && l.includeQty <= l.remaining_qty
  );

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) setSignatureEmpty(false);
  };
  const clearSignature = () => {
    if (signatureRef.current) signatureRef.current.clear();
    setSignatureEmpty(true);
  };

  const handleSubmit = async () => {
    if (saving) return;
    if (signatureEmpty || !signatureRef.current || signatureRef.current.isEmpty()) {
      setToast({ message: 'Patient signature required', type: 'error' });
      return;
    }
    const signature = signatureRef.current.toDataURL('image/png');
    setSaving(true);
    try {
      // Minimum-payload contract: only user-entered data. Server derives
      // userId (from orders.user_id), product_id + product_name (from
      // order_items), and medically_restricted (from products).
      const result = await api.post('/api/admin/pickups', {
        orderId,
        patientSignature: signature,
        clientRequestId: clientRequestIdRef.current,
        lines: includedLines.map(l => ({
          orderItemId: l.order_item_id,
          quantity: l.includeQty,
          lotNumber: l.lotNumber.trim()
        }))
      });
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (e) {
      setToast({ message: e.message || 'Failed to complete pickup', type: 'error' });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: '480px' }}>
          <div className="loading-spinner"><div className="spinner"></div></div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
          <div className="modal-header">
            <h2 className="modal-title">Dispense Medication</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <p style={{ color: 'crimson' }}>{loadError}</p>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
          <div className="modal-header">
            <h2 className="modal-title">Dispense Medication — Order #{orderId}</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          {/* The old copy here said only "No medication line items remain to be
              dispensed" for BOTH causes, which staff read as the form being
              broken. Each cause now gets its own explanation and next action. */}
          {emptyReason === 'no_restricted_items' ? (
            <>
              <p style={{ fontWeight: 600, marginBottom: '8px' }}>This order has no prescription medications to dispense.</p>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Only products flagged <strong>Medically Restricted</strong> require a signed pickup.
                Everything on this order is a regular retail item, so the patient can be handed it
                without this form — nothing is broken and no signature is needed.
              </p>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.6, marginTop: '10px' }}>
                If a medication <em>should</em> appear here, open <strong>Products</strong>, edit that
                product, and tick <strong>Medically Restricted</strong>. It will then appear on new orders.
              </p>
            </>
          ) : emptyReason === 'all_dispensed' ? (
            <>
              <p style={{ fontWeight: 600, marginBottom: '8px' }}>All medications on this order have already been dispensed.</p>
              {alreadyDispensed.length > 0 && (
                <ul style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.7, paddingLeft: '18px' }}>
                  {alreadyDispensed.map((d, i) => (
                    <li key={i}>{d.product_name} — {d.dispensed_qty} of {d.ordered_qty} dispensed</li>
                  ))}
                </ul>
              )}
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '10px' }}>
                Completed pickups are permanent. If something was recorded in error, contact the developer.
              </p>
            </>
          ) : emptyReason === 'no_items' ? (
            <p>This order has no line items.</p>
          ) : (
            <p>No medication line items remain to be dispensed for this order.</p>
          )}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'collect') {
    return (
      <div className="modal-overlay" onClick={() => !saving && onClose()}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px' }}>
          <div className="modal-header">
            <h2 className="modal-title">Dispense Medication — Order #{orderId}</h2>
            <button className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '12px' }}>
            Enter the lot number for each medication being handed to the patient. Adjust the quantity if the patient is only picking up part of the order. The patient will sign <strong>once</strong> for the entire handoff on the next screen.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {lines.map((l, idx) => (
              <div key={l.order_item_id} style={{
                padding: '12px',
                border: '1px solid var(--border-color, var(--cream-dark))',
                borderRadius: '8px',
                background: 'var(--cream)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong>{l.product_name}</strong>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    Remaining: {l.remaining_qty} of {l.ordered_qty}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      className="form-input"
                      min="0"
                      max={l.remaining_qty}
                      value={l.includeQty}
                      onChange={e => {
                        const raw = parseInt(e.target.value, 10);
                        const q = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(l.remaining_qty, raw));
                        setLines(prev => prev.map((x, i) => i === idx ? { ...x, includeQty: q } : x));
                      }}
                      disabled={saving}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Lot Number *</label>
                    <input
                      type="text"
                      className="form-input"
                      maxLength={100}
                      value={l.lotNumber}
                      placeholder="e.g. SG-2026-08-A"
                      onChange={e => setLines(prev => prev.map((x, i) => i === idx ? { ...x, lotNumber: e.target.value } : x))}
                      disabled={saving || l.includeQty <= 0}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-gold" onClick={() => setPhase('signature')} disabled={saving || !canProceed}>
              Present to Patient →
            </button>
          </div>
        </div>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    );
  }

  // phase === 'signature'
  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.9)' }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '760px', width: '100%' }}>
        <div className="modal-header">
          <h2 className="modal-title">Patient Signature Required</h2>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>
            You are receiving the following medication{includedLines.length > 1 ? 's' : ''} today:
          </p>
          <ul style={{ margin: '0 0 16px 20px', fontSize: '15px', lineHeight: '1.8' }}>
            {includedLines.map(l => (
              <li key={l.order_item_id}>
                <strong>{l.product_name}</strong> × {l.includeQty} — Lot: <code>{l.lotNumber.trim()}</code>
              </li>
            ))}
          </ul>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            By signing below, you confirm you have received the medication{includedLines.length > 1 ? 's' : ''} listed above.
          </p>
        </div>
        <div className="signature-pad-wrapper" style={{
          border: '2px solid var(--cream-dark)',
          borderRadius: '8px',
          background: 'white'
        }}>
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            canvasProps={{ className: 'signature-canvas', style: { width: '100%', height: '220px', display: 'block' } }}
            onEnd={handleSignatureEnd}
            backgroundColor="white"
          />
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setPhase('collect')} disabled={saving}>← Back</button>
          <button className="btn btn-secondary" onClick={clearSignature} disabled={saving}>Clear</button>
          <button className="btn btn-gold" onClick={handleSubmit} disabled={saving || signatureEmpty}>
            {saving ? 'Recording…' : 'Save Signature & Complete Pickup'}
          </button>
        </div>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}
// ==================== END Signed Medication Pickup Modal ====================

/**
 * Which processor actually took the money, and whether it arrived.
 *
 * Added 2026-09-21 after three NMI payments totalling $498.98 sat unsettled for
 * ten days while the portal displayed them as `paid`. The data was always
 * correct — orders.payment_provider recorded the processor from the start —
 * but no screen showed it, so staff reconciling against Stripe saw only an
 * unexplained absence.
 *
 * Legacy orders predating the column are inferred from the Stripe payment
 * intent. Non-card orders (clinic cash, comped, split) have no processor at
 * all and say so, rather than being silently labelled Stripe.
 */
function renderPaymentRoute(order, settlement) {
  const provider = order.payment_provider || (order.stripe_payment_intent ? 'stripe' : null);

  const chip = (label, bg, fg, title) => (
    <span title={title || undefined} style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: '4px',
      fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
      textTransform: 'uppercase', background: bg, color: fg, whiteSpace: 'nowrap'
    }}>{label}</span>
  );

  let main;
  if (provider === 'stripe') {
    main = chip('Stripe', '#eef2ff', '#3730a3',
      order.payment_provider ? 'Processed by Stripe' : 'Legacy order — inferred from the Stripe payment intent');
  } else if (provider === 'nmi') {
    main = chip('NMI', '#ccfbf1', '#115e59', 'Processed by NMI / LuQra — not visible in Stripe');
  } else if (order.payment_method) {
    const label = String(order.payment_method).replace(/_/g, ' ');
    main = chip(label, '#f3f4f6', '#4b5563', 'No card processor — settled outside the gateway');
  } else {
    main = <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>—</span>;
  }

  // The processor's own reference, so staff can find the transaction in the
  // Stripe dashboard or the LuQra portal without opening the order.
  const txnId = order.provider_transaction_id || order.stripe_payment_intent || null;
  const txnLine = txnId ? (
    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'ui-monospace, monospace', marginTop: '2px' }}
         title={txnId}>{txnId.length > 20 ? txnId.slice(0, 18) + '…' : txnId}</div>
  ) : null;

  // A payment the gateway approved while in Test Mode never reached the
  // processor. It is not money, regardless of what the order status says.
  if (order.gateway_verification === 'test_mode') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
        {main}
        {chip('Test mode', '#fef3c7', '#92400e', 'Gateway was in Test Mode — the processor was never contacted and no money moved')}
        {txnLine}
      </div>
    );
  }

  if (!settlement || !settlement.needs_attention) {
    return txnLine
      ? <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>{main}{txnLine}</div>
      : main;
  }

  // The money has not arrived. This is the annotation that would have caught
  // the September incident on day two instead of day ten.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
      {main}
      {chip(settlement.state === 'unknown' ? 'Unverified' : 'Not settled', '#fee2e2', '#991b1b', settlement.reason)}
      {settlement.age_hours != null && (
        <span style={{ fontSize: '10px', color: '#991b1b' }}>{settlement.age_hours}h</span>
      )}
      {txnLine}
    </div>
  );
}

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Settlement audit, keyed by order id. Answers "did the money actually
  // arrive", which order.status does not: `paid` only ever meant the gateway
  // approved the charge. Loaded separately so a slow or unreachable gateway
  // never delays the orders list itself.
  const [settlementByOrder, setSettlementByOrder] = useState({});
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pickupOrderId, setPickupOrderId] = useState(null);
  const [patients, setPatients] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('clinic_cash');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [patientMembership, setPatientMembership] = useState(null);
  // Referral first-purchase discount. Display only — the server owns the rules
  // and recomputes the amount when the order is created, so a stale preview
  // here can never set the price.
  const [referralPreview, setReferralPreview] = useState(null);
  const [patientOrders, setPatientOrders] = useState([]);
  const [useClinicCash, setUseClinicCash] = useState(0);
  const [cardError, setCardError] = useState(null);
  // Split-card state (Phase 2 of split-payment feature)
  const [splitAmount1, setSplitAmount1] = useState(0);
  const [splitAmount2, setSplitAmount2] = useState(0);
  const [splitOrderId, setSplitOrderId] = useState(null);
  const [splitPayments, setSplitPayments] = useState([]); // [{ paymentId, amount, paymentIntentId, clientSecret, status }]
  const [splitCurrentIndex, setSplitCurrentIndex] = useState(0);
  // Search state
  const [orderSearch, setOrderSearch] = useState(''); // searches order # or customer name/email in the list
  const [patientSearch, setPatientSearch] = useState(''); // filters the patient picker in the create modal
  // Refund state. The refund endpoint already existed and is unchanged; this is
  // purely the UI that calls it, so staff no longer need a developer to refund.
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundPayments, setRefundPayments] = useState([]);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundBusyId, setRefundBusyId] = useState(null);  // payment id currently in flight
  const [refundConfirmId, setRefundConfirmId] = useState(null); // payment id awaiting confirm

  const fetchOrders = () => api.get('/api/admin/orders').then(setOrders).catch(console.error).finally(() => setLoading(false));
  // Deliberately NOT chained to fetchOrders: this calls out to the payment
  // gateway, so it must never be able to delay or fail the orders list. If it
  // errors the table still renders, just without settlement annotations.
  const fetchSettlement = () => api.get('/api/admin/orders/settlement-audit')
    .then(res => {
      const byId = {};
      (res?.orders || []).forEach(r => { byId[r.order_id] = r; });
      setSettlementByOrder(byId);
      setSettlementSummary(res || null);
    })
    .catch(() => { /* non-fatal: list renders unannotated */ });
  useEffect(() => { fetchOrders(); fetchSettlement(); }, []);

  // Which processor takes front-desk card payments. Defaults to Stripe until
  // the config call returns, so the existing Stripe panel is what renders
  // first in every case. `billingPrefill` is deliberately IGNORED here: it is
  // keyed to the signed-in staff member, not the cardholder.
  const [paymentConfig, setPaymentConfig] = useState({ provider: 'stripe' });
  const nmiCardRef = useRef(null);
  const [billing, setBilling] = useState({ firstName: '', lastName: '', address1: '', city: '', state: '', zip: '', country: 'US' });
  const setBillingField = (k) => (e) => setBilling((b) => ({ ...b, [k]: e.target.value }));
  useEffect(() => {
    let cancelled = false;
    api.get('/api/payment-config')
      .then((cfg) => { if (!cancelled && cfg?.provider) setPaymentConfig(cfg); })
      .catch(() => { /* keep the Stripe default */ });
    return () => { cancelled = true; };
  }, []);

  const openCreateModal = async () => {
    try {
      const [pats, prods] = await Promise.all([api.get('/api/admin/users'), api.get('/api/products')]);
      setPatients(pats);
      setProducts(prods);
      setSelectedPatient(null);
      setOrderItems([]);
      setPaymentMethod('clinic_cash');
      setNotes('');
      setPatientMembership(null);
      setPatientOrders([]);
      setUseClinicCash(0);
      setCardError(null);
      setSplitAmount1(0);
      setSplitAmount2(0);
      setSplitOrderId(null);
      setSplitPayments([]);
      setSplitCurrentIndex(0);
      setPatientSearch('');
      setShowCreateModal(true);
    } catch (e) { setToast({ message: 'Failed to load data', type: 'error' }); }
  };

  const handlePatientChange = async (patientId) => {
    const patient = patients.find(p => p.id === parseInt(patientId));
    setSelectedPatient(patient);
    setUseClinicCash(0);
    if (patient) {
      try {
        const [membership, orders] = await Promise.all([
          api.get(`/api/admin/users/${patient.id}/membership`),
          api.get(`/api/admin/users/${patient.id}/orders`)
        ]);
        setPatientMembership(membership);
        setPatientOrders(orders || []);
      } catch (e) {
        setPatientMembership(null);
        setPatientOrders([]);
      }
    } else {
      setPatientMembership(null);
      setPatientOrders([]);
    }
  };

  const addProduct = (product) => {
    const existing = orderItems.find(i => i.productId === product.id);
    if (existing) {
      setOrderItems(orderItems.map(i => i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setOrderItems([...orderItems, { productId: product.id, name: product.name, price: parseFloat(product.price), quantity: 1 }]);
    }
  };

  const updateItemQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setOrderItems(orderItems.filter(i => i.productId !== productId));
    } else {
      setOrderItems(orderItems.map(i => i.productId === productId ? { ...i, quantity } : i));
    }
  };

  const discountPercent = patientMembership?.discount_percent || 0;
  const subtotalForPreview = orderItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);
  const memberDiscountForPreview = discountPercent > 0 ? Math.round(subtotalForPreview * (discountPercent / 100) * 100) / 100 : 0;

  // Ask the server what this patient qualifies for whenever the cart or the
  // patient changes. Read-only — nothing is consumed until the order is created.
  useEffect(() => {
    let cancelled = false;
    if (!selectedPatient || subtotalForPreview <= 0) { setReferralPreview(null); return; }
    api.get(`/api/admin/users/${selectedPatient.id}/referral-discount?subtotal=${subtotalForPreview}&has_other_offer=${memberDiscountForPreview > 0}`)
      .then(r => { if (!cancelled) setReferralPreview(r); })
      .catch(() => { if (!cancelled) setReferralPreview(null); });
    return () => { cancelled = true; };
  }, [selectedPatient, subtotalForPreview, memberDiscountForPreview]);

  const subtotal = orderItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);
  const discountAmount = discountPercent > 0 ? Math.round(subtotal * (discountPercent / 100) * 100) / 100 : 0;
  // The server decides the referral discount and owns the rules; this is only
  // what staff are shown before the order exists. The amount is recomputed
  // server-side on submit, so a stale preview can never set the price.
  const referralAmount = (referralPreview && referralPreview.amount) || 0;
  const total = subtotal - discountAmount - referralAmount;
  const patientBalance = parseFloat(selectedPatient?.balance || 0);
  const clinicCashUsed = paymentMethod === 'clinic_cash' ? Math.min(patientBalance, total) :
                         paymentMethod === 'split' ? Math.min(useClinicCash, patientBalance, total) : 0;
  const cardAmount = paymentMethod === 'card' ? total : paymentMethod === 'split' ? total - clinicCashUsed : 0;
  const requiresCard = paymentMethod === 'card' || (paymentMethod === 'split' && cardAmount > 0);

  const handleCreateOrder = async (stripe, elements) => {
    if (!selectedPatient) { setToast({ message: 'Please select a patient', type: 'error' }); return; }
    if (orderItems.length === 0) { setToast({ message: 'Please add items to the order', type: 'error' }); return; }
    if (paymentMethod === 'clinic_cash' && patientBalance < total) { setToast({ message: 'Insufficient Clinic Cash', type: 'error' }); return; }
    // Stripe.js is only required when Stripe is the active processor. Under NMI
    // the card is tokenised by Collect.js, so demanding Stripe here would make
    // the Charge button permanently dead whenever the clinic routes to LuQra.
    const nmiActive = paymentConfig.provider === 'nmi' && !!paymentConfig.tokenizationKey;
    if (requiresCard && !nmiActive && (!stripe || !elements)) { setToast({ message: 'Card payment not available', type: 'error' }); return; }
    // AVS is mandatory on the LuQra gateway account: a sale with no address1 is
    // declined with an opaque gateway error. Catch it here so staff get a
    // sentence they can act on instead of "transaction declined".
    if (requiresCard && nmiActive) {
      const missing = ['address1', 'city', 'state', 'zip'].filter((k) => !String(billing[k] || '').trim());
      if (missing.length) {
        setToast({ message: 'Cardholder billing address is required by the card processor (street, city, state, ZIP).', type: 'error' });
        return;
      }
    }

    setCreating(true);
    setCardError(null);

    try {
      // TOKENIZE BEFORE CREATING THE ORDER (NMI only) — same reason as the
      // patient shop: a card that fails validation must not leave an orphan
      // pending_payment order behind. Stripe keeps create-first because it
      // needs the PaymentIntent before the card can be confirmed.
      let nmiToken = null;
      if (requiresCard && nmiActive) {
        try {
          nmiToken = await nmiCardRef.current.tokenize();
        } catch (tokErr) {
          setCardError(tokErr.message);
          setToast({ message: tokErr.message, type: 'error' });
          setCreating(false);
          return;
        }
      }

      const result = await api.post('/api/admin/orders', {
        userId: selectedPatient.id,
        items: orderItems,
        paymentMethod,
        useClinicCash: clinicCashUsed,
        notes
      });

      // Smart-route: if a membership product is in the order, server returns a Stripe Checkout subscription URL
      // Staff sends this URL to the patient to complete the subscription setup.
      if (result.isSubscription && result.checkoutUrl) {
        const copied = await navigator.clipboard.writeText(result.checkoutUrl).then(() => true).catch(() => false);
        const msg = `This order contains a membership, so a recurring subscription link was created.\n\n${copied ? 'The link has been copied to your clipboard.' : 'Copy the link below:'}\n\n${result.checkoutUrl}\n\nSend this to the patient. No order is created until they complete checkout.`;
        window.alert(msg);
        setCreating(false);
        return;
      }

      if (result.requiresPayment && result.clientSecret) {
        // Process card payment with Stripe
        const cardElement = elements.getElement(CardElement);
        const { error, paymentIntent } = await stripe.confirmCardPayment(result.clientSecret, {
          payment_method: { card: cardElement }
        });

        if (error) {
          setCardError(error.message);
          setToast({ message: error.message, type: 'error' });
          setCreating(false);
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          await api.post(`/api/admin/orders/${result.orderId}/confirm-payment`);
        }
      } else if (result.requiresPayment && result.deferredCapture) {
        // The token was obtained BEFORE the order existed. Only the opaque
        // single-use token crosses the wire — never card data.
        await api.post(`/api/admin/orders/${result.orderId}/confirm-payment`,
          { paymentToken: nmiToken, billing });
      }

      setToast({ message: 'Order created successfully!', type: 'success' });
      setShowCreateModal(false);
      fetchOrders();
    } catch (e) {
      const code = e && (e.code || (e.body && e.body.code));
      let msg;
      if (code === 'capture_not_verified') {
        // APPROVED BUT UNCONFIRMED — retrying here is how a patient gets
        // charged twice.
        msg = (e.body && e.body.chargeMayHaveSucceeded)
          ? 'The card was APPROVED but the portal could not confirm it. DO NOT charge again — contact support to reconcile this order.'
          : 'The payment could not be confirmed with the processor, so the order was not marked paid.';
      } else if (code === 'payment_provider_unavailable') {
        msg = 'Card payments are temporarily unavailable. Nothing was charged.';
      } else if (code === 'possible_duplicate_order') {
        msg = (e.body && e.body.error) || 'This looks like a duplicate of an order already paid. Nothing was charged.';
      } else {
        msg = e.message || 'Payment was not completed.';   // processor decline
      }
      setCardError(msg);                 // persistent, inline, next to the card
      setToast({ message: msg, type: 'error' });
    }
    finally { setCreating(false); }      // busy always clears
  };

  // Phase 2: create a split-card order and transition the modal to sequential card-entry mode
  const handleSplitOrderStart = async () => {
    if (!selectedPatient) { setToast({ message: 'Select a patient', type: 'error' }); return; }
    if (orderItems.length === 0) { setToast({ message: 'Add items to the order', type: 'error' }); return; }
    if (Math.abs((splitAmount1 + splitAmount2) - total) >= 0.02) { setToast({ message: 'Card amounts must sum to order total', type: 'error' }); return; }
    if (splitAmount1 <= 0 || splitAmount2 <= 0) { setToast({ message: 'Both card amounts must be greater than zero', type: 'error' }); return; }
    setCreating(true);
    try {
      const result = await api.post('/api/admin/orders/split', {
        userId: selectedPatient.id,
        items: orderItems,
        splitAmounts: [splitAmount1, splitAmount2],
        notes
      });
      setSplitOrderId(result.orderId);
      setSplitPayments(result.payments || []);
      setSplitCurrentIndex(0);
    } catch (e) {
      setToast({ message: e.message || 'Failed to create split order', type: 'error' });
    } finally { setCreating(false); }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/api/admin/orders/${orderId}`, { status });
      setToast({ message: 'Order updated', type: 'success' });
      fetchOrders();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  // ---- Refunds -------------------------------------------------------------
  // Refunds go through POST /api/admin/orders/:orderId/payments/:paymentId/refund,
  // which is authenticated by the admin's session cookie. Sending no amount refunds
  // the full REMAINING balance — the server owns all refund maths and provider
  // routing (a Stripe charge still refunds via Stripe after the processor switch).
  const openRefundModal = async (order) => {
    setRefundOrder(order);
    setRefundPayments([]);
    setRefundConfirmId(null);
    setRefundLoading(true);
    try {
      setRefundPayments(await api.get(`/api/admin/orders/${order.id}/payments`));
    } catch (e) {
      setToast({ message: e.message || 'Could not load payments', type: 'error' });
    } finally { setRefundLoading(false); }
  };

  const doRefund = async (payment) => {
    if (refundBusyId) return;           // guard against a double-click double-refunding
    setRefundBusyId(payment.id);
    try {
      const r = await api.post(`/api/admin/orders/${refundOrder.id}/payments/${payment.id}/refund`, {});
      setToast({ message: `Refunded $${Number(r.amount).toFixed(2)} — ${r.refundId}`, type: 'success' });
      setRefundConfirmId(null);
      // Re-read from the server rather than patching local state, so what's shown
      // is the authoritative post-refund record.
      try { setRefundPayments(await api.get(`/api/admin/orders/${refundOrder.id}/payments`)); } catch (_) {}
      fetchOrders();
    } catch (e) {
      setToast({ message: e.message || 'Refund failed', type: 'error' });
    } finally { setRefundBusyId(null); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const getStatusBadge = (status) => {
    const styles = { pending_payment: { bg: '#fef3c7', color: '#92400e', label: 'Pending Payment' }, paid: { bg: '#dbeafe', color: '#1e40af', label: 'Paid' }, ready: { bg: '#d1fae5', color: '#065f46', label: 'Ready' }, picked_up: { bg: '#e5e7eb', color: '#374151', label: 'Picked Up' }, comped: { bg: '#f3e8ff', color: '#7c3aed', label: 'Comped' }, refunded: { bg: '#fee2e2', color: '#991b1b', label: 'Refunded' }, partially_refunded: { bg: '#ffedd5', color: '#9a3412', label: 'Partially Refunded' }, partial_paid: { bg: '#fef3c7', color: '#92400e', label: 'Partially Paid' }, cancelled: { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' } };
    const s = styles[status] || { bg: '#e5e7eb', color: '#374151', label: status };
    return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
  };

  // Combine status filter + free-text search (order #, customer name, email)
  const filteredOrders = orders.filter(o => {
    if (filter !== 'all' && o.status !== filter) return false;
    const q = orderSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(o.id).includes(q) ||
      (o.customer_name && o.customer_name.toLowerCase().includes(q)) ||
      (o.customer_email && o.customer_email.toLowerCase().includes(q))
    );
  });
  const groupedProducts = products.reduce((acc, p) => { if (!acc[p.category]) acc[p.category] = []; acc[p.category].push(p); return acc; }, {});
  // Alphabetically sorted + search-filtered patients for the Create Order modal picker
  const sortedPatients = [...patients].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const filteredPatients = patientSearch.trim()
    ? sortedPatients.filter(p => {
        const q = patientSearch.trim().toLowerCase();
        return (p.name && p.name.toLowerCase().includes(q)) || (p.email && p.email.toLowerCase().includes(q));
      })
    : sortedPatients;

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Orders</h1><p className="page-subtitle">Manage customer orders</p></div>
        <button className="btn btn-gold" onClick={openCreateModal}>+ Create Order</button>
      </div>
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Order Queue</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="search"
              className="form-input"
              style={{ width: '260px' }}
              placeholder="Search order # or customer..."
              value={orderSearch}
              onChange={e => setOrderSearch(e.target.value)}
            />
            <select className="form-input" style={{ width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="all">All Orders</option>
              <option value="pending_payment">Pending Payment</option>
              <option value="partial_paid">Partial Paid</option>
              <option value="paid">Paid</option>
              <option value="ready">Ready</option>
              <option value="picked_up">Picked Up</option>
              <option value="comped">Comped</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
        {(orderSearch || filter !== 'all') && (
          <div style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--text-muted)' }}>
            Showing {filteredOrders.length} of {orders.length} orders
            {orderSearch && <span> matching <strong>"{orderSearch}"</strong></span>}
            {filter !== 'all' && <span> (status: <strong>{filter.replace('_', ' ')}</strong>)</span>}
          </div>
        )}
        {settlementSummary && settlementSummary.needsAttention > 0 && (
          <div style={{
            margin: '0 0 14px', padding: '11px 14px', borderRadius: '6px',
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '13px'
          }}>
            <strong>
              {settlementSummary.needsAttention === 1
                ? '1 order is marked paid but has not settled at the processor'
                : `${settlementSummary.needsAttention} orders are marked paid but have not settled at the processor`}
              {settlementSummary.unsettledTotal > 0 && ` — $${settlementSummary.unsettledTotal.toFixed(2)}`}.
            </strong>
            <div style={{ marginTop: '3px', fontSize: '12px' }}>
              The gateway approved these charges but the funds have not batched out. Check the
              processor account before treating them as collected.
            </div>
          </div>
        )}
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : filteredOrders.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Paid via</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.id}>
                    <td><strong>#{order.id}</strong><br /><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatDate(order.created_at)}</span></td>
                    <td><strong>{order.customer_name}</strong><br /><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{order.customer_email}</span></td>
                    <td>{order.items?.filter(i => i.name).map((i, idx) => <div key={idx}>{i.name} x{i.quantity}</div>)}</td>
                    <td>${parseFloat(order.total).toFixed(2)}{order.discount_amount > 0 && <span style={{ fontSize: '11px', color: '#059669', display: 'block' }}>({order.discount_percent}% off)</span>}</td>
                    <td>{renderPaymentRoute(order, settlementByOrder[order.id])}</td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td>
                      <div className="action-btns">
                        {order.status === 'paid' && <button className="action-btn edit" onClick={() => updateStatus(order.id, 'ready')}>Mark Ready</button>}
                        {order.status === 'ready' && <button className="action-btn edit" onClick={() => updateStatus(order.id, 'picked_up')}>Mark Picked Up</button>}
                        {order.has_remaining_restricted_items && <button className="action-btn edit" style={{background: '#0369a1', color: 'white'}} onClick={() => setPickupOrderId(order.id)}>Dispense</button>}
                        {['paid', 'ready', 'picked_up'].includes(order.status) && <a href={"/api/orders/" + order.id + "/receipt"} className="action-btn" style={{background: 'var(--accent-gold)', color: 'white'}} target="_blank" rel="noreferrer">Receipt</a>}
                        {['paid', 'ready', 'picked_up', 'partial_paid', 'partially_refunded', 'refunded'].includes(order.status) && <button className="action-btn" style={{background: '#b91c1c', color: 'white'}} onClick={() => openRefundModal(order)}>Refund</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">📦</div><h3>No orders</h3></div>}
      </div>

      {refundOrder && (
        <div className="modal-overlay" onClick={() => !refundBusyId && setRefundOrder(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Refund — Order #{refundOrder.id}</h2>
              <button className="modal-close" onClick={() => setRefundOrder(null)} disabled={!!refundBusyId}>×</button>
            </div>
            <div style={{ padding: '0 4px 4px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: 0 }}>
                {refundOrder.customer_name} · order total ${parseFloat(refundOrder.total).toFixed(2)}
              </p>

              {refundLoading ? <div className="loading-spinner"><div className="spinner"></div></div>
                : refundPayments.length === 0 ? (
                  <div className="empty-state" style={{ padding: '24px 12px' }}>
                    <div className="empty-state-icon">💳</div>
                    <h3>No card payments on this order</h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Only card payments can be refunded here. Clinic Cash and comped orders
                      are adjusted from the patient's balance instead.
                    </p>
                  </div>
                ) : (
                  <>
                    <table className="admin-table" style={{ marginTop: '4px' }}>
                      <thead><tr><th>Payment</th><th>Paid</th><th>Refunded</th><th>Refundable</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>
                        {refundPayments.map(p => {
                          const remaining = Number(p.remaining_refundable);
                          const refundable = remaining > 0 && ['succeeded', 'partially_refunded'].includes(p.status);
                          const busy = refundBusyId === p.id;
                          return (
                            <tr key={p.id}>
                              <td>
                                <strong>#{p.id}</strong>
                                <br /><span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{p.payment_provider || 'stripe'}</span>
                              </td>
                              <td>${Number(p.amount).toFixed(2)}</td>
                              <td>${Number(p.refunded_amount).toFixed(2)}</td>
                              <td><strong>${remaining.toFixed(2)}</strong></td>
                              <td><span style={{ fontSize: '12px' }}>{String(p.status).replace(/_/g, ' ')}</span></td>
                              <td>
                                {!refundable ? <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>—</span>
                                  : refundConfirmId === p.id ? (
                                    <div className="action-btns">
                                      <button className="action-btn" style={{ background: '#b91c1c', color: 'white' }} disabled={busy} onClick={() => doRefund(p)}>
                                        {busy ? 'Refunding…' : `Confirm $${remaining.toFixed(2)}`}
                                      </button>
                                      <button className="action-btn" disabled={busy} onClick={() => setRefundConfirmId(null)}>Cancel</button>
                                    </div>
                                  ) : (
                                    <button className="action-btn" style={{ background: '#b91c1c', color: 'white' }} disabled={!!refundBusyId} onClick={() => setRefundConfirmId(p.id)}>
                                      Refund ${remaining.toFixed(2)}
                                    </button>
                                  )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {refundConfirmId && (
                      <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b' }}>
                        ⚠️ This sends a real refund to the card through the processor that originally
                        took the payment. It cannot be undone from the portal.
                      </div>
                    )}
                  </>
                )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setRefundOrder(null)} disabled={!!refundBusyId}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header"><h2 className="modal-title">Create Order for Patient</h2><button className="modal-close" onClick={() => setShowCreateModal(false)} disabled={creating}>×</button></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <div className="form-group">
                  <label className="form-label">Select Patient *</label>
                  {selectedPatient ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid var(--cream-dark)', borderRadius: '8px', background: 'white' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{selectedPatient.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{selectedPatient.email}</div>
                      </div>
                      <button className="btn btn-secondary btn-sm" onClick={() => { handlePatientChange(''); setPatientSearch(''); }}>Change</button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="search"
                        className="form-input"
                        placeholder="Search by name or email..."
                        value={patientSearch}
                        onChange={e => setPatientSearch(e.target.value)}
                        autoFocus
                      />
                      <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--cream-dark)', borderRadius: '8px' }}>
                        {filteredPatients.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                            {patientSearch ? 'No patients match your search' : 'No patients available'}
                          </div>
                        ) : (
                          filteredPatients.slice(0, 50).map(p => (
                            <div
                              key={p.id}
                              onClick={() => { handlePatientChange(p.id); setPatientSearch(''); }}
                              style={{ padding: '10px 12px', borderBottom: '1px solid var(--cream-dark)', cursor: 'pointer' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <div style={{ fontWeight: 500 }}>{p.name || '(no name)'}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.email}</div>
                            </div>
                          ))
                        )}
                        {filteredPatients.length > 50 && (
                          <div style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', background: 'var(--cream)' }}>
                            Showing first 50 of {filteredPatients.length} — refine search to narrow down
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {selectedPatient && (
                  <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Clinic Cash Balance:</span>
                      <span style={{ fontWeight: 600, color: 'var(--accent-gold)' }}>${patientBalance.toFixed(2)}</span>
                    </div>
                    {patientMembership && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                        <span>Membership:</span>
                        <span style={{ fontWeight: 600, color: '#059669' }}>{patientMembership.plan_name} ({discountPercent}% off)</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Add Products</label>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    {Object.entries(groupedProducts).map(([cat, items]) => (
                      <div key={cat}>
                        <div style={{ padding: '8px 12px', background: 'var(--cream)', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}>{cat}</div>
                        {items.map(p => (
                          <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--cream-dark)' }}>
                            <div>
                              <div>{p.name}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>${parseFloat(p.price).toFixed(2)}</div>
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={() => addProduct(p)}>+ Add</button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="form-group">
                  <label className="form-label">Order Items</label>
                  <div style={{ border: '1px solid var(--border)', borderRadius: '8px', minHeight: '150px' }}>
                    {orderItems.length > 0 ? orderItems.map(item => (
                      <div key={item.productId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid var(--cream-dark)' }}>
                        <div>
                          <div>{item.name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>${item.price.toFixed(2)} each</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => updateItemQuantity(item.productId, item.quantity - 1)}>-</button>
                          <span>{item.quantity}</span>
                          <button className="btn btn-secondary btn-sm" onClick={() => updateItemQuantity(item.productId, item.quantity + 1)}>+</button>
                          <span style={{ marginLeft: '8px', fontWeight: 600 }}>${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    )) : <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>No items added</div>}
                  </div>
                </div>
                {orderItems.length > 0 && (
                  <div style={{ padding: '16px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span>Subtotal:</span><span>${subtotal.toFixed(2)}</span></div>
                    {discountPercent > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#059669' }}><span>Member Discount ({discountPercent}%):</span><span>-${discountAmount.toFixed(2)}</span></div>}
                    {referralAmount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#059669' }}>
                        <span>Referral Discount:</span><span>-${referralAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {referralPreview && referralPreview.consumes && referralAmount === 0 && (
                      // They are referred and this is their first purchase, but it
                      // is under the threshold. Say so before the sale, because the
                      // benefit is spent either way.
                      <div style={{ marginBottom: '8px', padding: '8px 10px', borderRadius: '6px', background: '#fff7ed', border: '1px solid #fed7aa', fontSize: '12px', color: '#9a3412' }}>
                        Referred patient. A first purchase of ${referralPreview.tiers?.tier1?.min ?? 250} or more would earn ${referralPreview.tiers?.tier1?.amount ?? 25} off — this one uses up the referral benefit without a discount.
                      </div>
                    )}
                    {referralPreview && referralPreview.consumes && referralAmount > 0 && referralPreview.referrer?.name && (
                      <div style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                        First purchase · referred by {referralPreview.referrer.name}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '18px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}><span>Total:</span><span>${total.toFixed(2)}</span></div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-input" value={paymentMethod} onChange={e => { setPaymentMethod(e.target.value); setCardError(null); if (e.target.value === 'split_cards' && total > 0) { setSplitAmount1(Math.round(total * 50) / 100); setSplitAmount2(Math.round(total * 100 - Math.round(total * 50)) / 100); } }}>
                    <option value="clinic_cash">Clinic Cash {selectedPatient && `($${patientBalance.toFixed(2)} available)`}</option>
                    <option value="card">Pay by Card</option>
                    {patientBalance > 0 && <option value="split">Split (Clinic Cash + Card)</option>}
                    <option value="split_cards">Split Across Two Cards</option>
                    <option value="paid_other">Already Paid (External)</option>
                    <option value="comped">Comped (Free)</option>
                  </select>
                  {paymentMethod === 'clinic_cash' && patientBalance < total && <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>Insufficient balance</p>}
                </div>
                {paymentMethod === 'split' && (
                  <div className="form-group">
                    <label className="form-label">Use Clinic Cash: ${clinicCashUsed.toFixed(2)}</label>
                    <input type="range" min="0" max={Math.min(patientBalance, total)} step="0.01" value={useClinicCash} onChange={e => setUseClinicCash(parseFloat(e.target.value))} style={{ width: '100%' }} />
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Remaining ${cardAmount.toFixed(2)} to card</p>
                  </div>
                )}
                {paymentMethod === 'split_cards' && !splitOrderId && (
                  <div className="form-group" style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                    <label className="form-label" style={{ marginBottom: '8px' }}>Card 1 amount</label>
                    <input type="number" step="0.01" min="0" max={total} value={splitAmount1} onChange={e => setSplitAmount1(parseFloat(e.target.value) || 0)} className="form-input" />
                    <label className="form-label" style={{ marginTop: '12px', marginBottom: '8px' }}>Card 2 amount</label>
                    <input type="number" step="0.01" min="0" max={total} value={splitAmount2} onChange={e => setSplitAmount2(parseFloat(e.target.value) || 0)} className="form-input" />
                    <div style={{ marginTop: '8px', fontSize: '13px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Sum:</span>
                      <span style={{ fontWeight: 600, color: Math.abs((splitAmount1 + splitAmount2) - total) < 0.02 ? '#059669' : '#dc2626' }}>
                        ${(splitAmount1 + splitAmount2).toFixed(2)} / ${total.toFixed(2)}
                      </span>
                    </div>
                    {Math.abs((splitAmount1 + splitAmount2) - total) >= 0.02 && (
                      <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>Amounts must sum to order total</p>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Notes (optional)</label>
                  <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Internal notes about this order..." />
                </div>
                {/* Patient Order History */}
                {selectedPatient && patientOrders.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <label className="form-label">Recent Orders</label>
                    <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}>
                      {patientOrders.slice(0, 10).map(o => (
                        <div key={o.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', justifyContent: 'space-between' }}>
                          <span>#{o.id} - {new Date(o.created_at).toLocaleDateString('en-US', { timeZone: 'America/Denver' })}</span>
                          <span style={{ color: o.status === 'paid' ? '#059669' : '#d97706' }}>${o.total.toFixed(2)} ({o.status})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Payment / Action Section */}
            {splitOrderId && stripePromise ? (
              // Phase 2: split-card flow in progress — sequential card entry
              <Elements stripe={stripePromise}>
                <AdminSplitCardPayment
                  orderId={splitOrderId}
                  payments={splitPayments}
                  currentIndex={splitCurrentIndex}
                  onPaymentConfirmed={(idx) => setSplitCurrentIndex(idx)}
                  onComplete={() => { setToast({ message: 'Order paid in full!', type: 'success' }); setShowCreateModal(false); fetchOrders(); }}
                  onCancel={() => { setShowCreateModal(false); fetchOrders(); }}
                  total={total}
                />
              </Elements>
            ) : paymentMethod === 'split_cards' ? (
              // Phase 2: pre-flight — staff sets amounts, clicks Continue to start the flow
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>Cancel</button>
                <button
                  className="btn btn-gold"
                  onClick={handleSplitOrderStart}
                  disabled={creating || !selectedPatient || orderItems.length === 0 || total <= 0 || Math.abs((splitAmount1 + splitAmount2) - total) >= 0.02 || splitAmount1 <= 0 || splitAmount2 <= 0 || !stripePromise}
                >
                  {creating ? 'Creating Order...' : 'Continue to Card Entry'}
                </button>
              </div>
            ) : requiresCard && (stripePromise || (paymentConfig.provider === 'nmi' && paymentConfig.tokenizationKey)) ? (
              <Elements stripe={stripePromise}>
                <AdminOrderCardPayment
                  total={total}
                  cardAmount={cardAmount}
                  clinicCashUsed={clinicCashUsed}
                  creating={creating}
                  cardError={cardError}
                  setCardError={setCardError}
                  selectedPatient={selectedPatient}
                  orderItems={orderItems}
                  onCancel={() => setShowCreateModal(false)}
                  onSubmit={handleCreateOrder}
                  paymentMethod={paymentMethod}
                  patientBalance={patientBalance}
                  paymentConfig={paymentConfig}
                  nmiCardRef={nmiCardRef}
                  billing={billing}
                  setBillingField={setBillingField}
                />
              </Elements>
            ) : requiresCard && !stripePromise && paymentConfig.provider !== 'nmi' ? (
              // Explicit failure UI. Previously this branch was silent — the
              // else below would render just the Create Order buttons with no
              // card field, and staff assumed the whole flow was broken.
              // With this, misconfig is loud: they see exactly what to fix.
              <div style={{ padding: '16px 24px 24px' }}>
                <div style={{ padding: '14px 16px', background: '#fef3cd', border: '1px solid #f2d38c', borderRadius: '8px', color: '#7c5f18', fontSize: '13px' }}>
                  <strong>Card payments are not configured on this environment.</strong>
                  <div style={{ marginTop: '6px' }}>The Stripe publishable key (<code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code>) was not present when this bundle was built. Pick <em>Clinic Cash</em>, <em>Comped</em>, or <em>Paid Other</em> to complete the order, or contact admin to configure Stripe.</div>
                </div>
                <div className="modal-actions" style={{ marginTop: '16px' }}>
                  <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>Cancel</button>
                <button className="btn btn-gold" onClick={() => handleCreateOrder(null, null)} disabled={creating || !selectedPatient || orderItems.length === 0 || (paymentMethod === 'clinic_cash' && patientBalance < total)}>
                  {creating ? 'Processing...' : 'Create Order'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {pickupOrderId != null && (
        <PickupModal
          orderId={pickupOrderId}
          onClose={() => setPickupOrderId(null)}
          onSuccess={(result) => {
            const suffix = result && result.idempotentReplay ? ' (already recorded)' : '';
            setToast({ message: 'Medication pickup recorded' + suffix, type: 'success' });
            fetchOrders();
          }}
        />
      )}
    </div>
  );
}

function AdminNutritionReviewsPage() {
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => { api.get('/api/admin/nutrition-intakes').then(setIntakes).catch(console.error).finally(() => setLoading(false)); }, []);

  const viewDetails = async (intake) => {
    setDetailsLoading(true);
    try {
      const details = await api.get(`/api/admin/nutrition-intakes/${intake.id}`);
      setSelectedIntake(details);
    } catch (e) { console.error(e); }
    finally { setDetailsLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const filteredIntakes = filter === 'all' ? intakes : intakes.filter(i => i.status === filter);

  const questions = [
    { key: 'meals_per_day', label: 'How many meals do you eat per day?' },
    { key: 'feel_after_eating', label: 'How do you feel after eating?' },
    { key: 'energy_crashes', label: 'Do you experience energy crashes?' },
    { key: 'meals_often_missed', label: 'Which meals do you often miss?' },
    { key: 'reason_meals_missed', label: 'Why do you miss meals?' },
    { key: 'eat_home_or_away', label: 'Do you eat mostly at home or away?' },
    { key: 'exercise_routine', label: 'What is your exercise routine?' },
    { key: 'daily_routine_consistency', label: 'How consistent is your daily routine?' },
    { key: 'meal_times_consistent', label: 'Are your meal times consistent?' },
    { key: 'hunger_cravings_changes', label: 'Have you noticed changes in hunger or cravings?' },
    { key: 'favorite_foods', label: 'What are your favorite foods?' },
    { key: 'foods_crave_most', label: 'What foods do you crave most?' },
    { key: 'hardest_about_nutrition', label: 'What is hardest about nutrition for you?' },
    { key: 'success_with_nutrition', label: 'What success have you had with nutrition?' },
    { key: 'stress_level', label: 'Stress level (1-10)' },
  ];

  const medicalHistory = [
    { key: 'gallbladder_removal', label: 'Gallbladder Removal' },
    { key: 'hysterectomy', label: 'Hysterectomy' },
    { key: 'thyroid_condition', label: 'Thyroid Condition' },
    { key: 'fatty_liver', label: 'Fatty Liver' },
    { key: 'digestive_issues', label: 'Digestive Issues' },
    { key: 'food_allergies', label: 'Food Allergies' },
    { key: 'other_medical_history', label: 'Other Medical History' },
  ];

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Nutrition Reviews</h1><p className="page-subtitle">Review patient nutrition intake forms</p></div>
      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Submissions</h2>
          <select className="form-input" style={{ width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All ({intakes.length})</option>
            <option value="completed">Completed ({intakes.filter(i => i.status === 'completed').length})</option>
            <option value="pending">Pending ({intakes.filter(i => i.status === 'pending').length})</option>
          </select>
        </div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : filteredIntakes.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>Patient</th><th>Status</th><th>Submitted</th><th>Initiated By</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredIntakes.map(intake => (
                  <tr key={intake.id}>
                    <td><strong>{intake.patient_name}</strong><br /><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{intake.patient_email}</span></td>
                    <td><span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: intake.status === 'completed' ? '#d1fae5' : '#fef3c7', color: intake.status === 'completed' ? '#065f46' : '#92400e' }}>{intake.status === 'completed' ? 'Completed' : 'Pending'}</span></td>
                    <td>{intake.status === 'completed' ? formatDate(intake.completed_at) : <span style={{ color: 'var(--text-muted)' }}>Not yet</span>}</td>
                    <td>{intake.initiated_by_name || '-'}</td>
                    <td>
                      {intake.status === 'completed' && <button className="action-btn edit" onClick={() => viewDetails(intake)}>View Responses</button>}
                      {intake.status === 'pending' && <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Awaiting patient</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">🥗</div><h3>No nutrition intakes yet</h3></div>}
      </div>

      {selectedIntake && (
        <div className="modal-overlay" onClick={() => setSelectedIntake(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">Nutrition Intake: {selectedIntake.patient_name}</h2>
              <button className="modal-close" onClick={() => setSelectedIntake(null)}>×</button>
            </div>
            {detailsLoading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
              <div style={{ padding: '16px' }}>
                <div style={{ marginBottom: '24px', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                    <div><strong>Patient:</strong> {selectedIntake.patient_name}</div>
                    <div><strong>Email:</strong> {selectedIntake.patient_email}</div>
                    <div><strong>Completed:</strong> {formatDate(selectedIntake.completed_at)}</div>
                    <div><strong>Initiated By:</strong> {selectedIntake.initiated_by_name || 'Patient'}</div>
                  </div>
                </div>

                <h4 style={{ color: 'var(--gold)', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>Questions & Answers</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                  {questions.map(q => (
                    <div key={q.key} style={{ borderBottom: '1px solid var(--cream-dark)', paddingBottom: '12px' }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>{q.label}</div>
                      <div style={{ fontSize: '15px' }}>{selectedIntake[q.key] || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No answer</span>}</div>
                    </div>
                  ))}
                </div>

                <h4 style={{ color: 'var(--gold)', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>Medical & Digestive History</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  {medicalHistory.map(m => (
                    <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '20px', height: '20px', borderRadius: '4px', background: selectedIntake[m.key] ? '#d1fae5' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                        {selectedIntake[m.key] ? '✓' : '✗'}
                      </span>
                      <span>{m.label}</span>
                    </div>
                  ))}
                </div>
                {selectedIntake.medical_history_details && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Medical History Details</div>
                    <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>{selectedIntake.medical_history_details}</div>
                  </div>
                )}

                {selectedIntake.additional_notes && (
                  <>
                    <h4 style={{ color: 'var(--gold)', marginBottom: '16px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>Additional Notes</h4>
                    <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>{selectedIntake.additional_notes}</div>
                  </>
                )}
              </div>
            )}
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setSelectedIntake(null)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminMetricsPage() {
  const [clientSources, setClientSources] = useState([]);
  const [howHeard, setHowHeard] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [salesByMonth, setSalesByMonth] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bookingSources, setBookingSources] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/metrics/client-sources'),
      api.get('/api/admin/metrics/how-heard'),
      api.get('/api/admin/metrics/sales-summary'),
      api.get('/api/admin/metrics/sales-by-month'),
      api.get('/api/admin/metrics/top-products'),
      api.get('/api/admin/metrics/payment-methods'),
      api.get('/api/admin/metrics/booking-sources')
    ])
      .then(([cs, hh, ss, sm, tp, pm, bs]) => {
        setClientSources(cs);
        setHowHeard(hh);
        setSalesSummary(ss);
        setSalesByMonth(sm);
        setTopProducts(tp);
        setPaymentMethods(pm);
        setBookingSources(bs);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, parseInt(month) - 1).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', year: 'numeric' });
  };

  const getBarColor = (index, scheme = 'gold') => {
    const colors = {
      gold: ['#b8860b', '#d4a84b', '#8b6914', '#c9a227', '#a67c00', '#e5c158', '#9b7615', '#d4b44a', '#7a5d0a', '#c4a030'],
      blue: ['#1e40af', '#3b82f6', '#2563eb', '#60a5fa', '#1d4ed8', '#93c5fd', '#1e3a8a', '#3b82f6', '#1e40af', '#60a5fa'],
      green: ['#166534', '#22c55e', '#16a34a', '#4ade80', '#15803d', '#86efac', '#14532d', '#22c55e', '#166534', '#4ade80'],
      purple: ['#7c3aed', '#a78bfa', '#6d28d9', '#c4b5fd', '#5b21b6', '#ddd6fe', '#4c1d95', '#8b5cf6', '#7c3aed', '#a78bfa']
    };
    return colors[scheme][index % colors[scheme].length];
  };

  const SummaryCard = ({ label, value, subtext }) => (
    <div style={{ background: 'var(--card-bg)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
      <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent-gold)', marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>{label}</div>
      {subtext && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{subtext}</div>}
    </div>
  );

  const BarChart = ({ data, title, subtitle, colorScheme = 'gold', valueKey = 'count', labelKey = 'source', formatValue }) => {
    const total = data.reduce((sum, s) => sum + (s[valueKey] || 0), 0);
    const maxVal = Math.max(...data.map(s => s[valueKey] || 0), 1);
    return (
      <div className="section-card" style={{ marginBottom: '24px' }}>
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <span style={{ color: 'var(--text-muted)' }}>{subtitle}</span>
        </div>
        {data.length > 0 ? (
          <div style={{ padding: '16px 0' }}>
            {data.map((item, index) => (
              <div key={item[labelKey]} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500 }}>{item[labelKey]}</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {formatValue ? formatValue(item[valueKey]) : item[valueKey]}
                    {total > 0 && !formatValue && ` (${Math.round((item[valueKey] / total) * 100)}%)`}
                  </span>
                </div>
                <div style={{ height: '24px', background: 'var(--cream)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(item[valueKey] / maxVal) * 100}%`,
                    height: '100%',
                    background: getBarColor(index, colorScheme),
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            ))}
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No data yet</h3></div>}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Metrics</h1><p className="page-subtitle">Sales analytics and reports</p></div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <>
          {salesSummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <SummaryCard label="Total Revenue" value={formatCurrency(salesSummary.totalRevenue)} />
              <SummaryCard label="Total Orders" value={salesSummary.totalOrders} subtext="Paid/Picked Up/Ready" />
              <SummaryCard label="Avg Order Value" value={formatCurrency(salesSummary.avgOrderValue)} />
              <SummaryCard label="Clinic Cash Used" value={formatCurrency(salesSummary.totalClinicCashUsed)} />
            </div>
          )}
          <BarChart
            data={salesByMonth.map(m => ({ month: formatMonth(m.month), revenue: m.revenue, orderCount: m.orderCount }))}
            title="Revenue by Month"
            subtitle={salesByMonth.length + ' months shown'}
            colorScheme="green"
            valueKey="revenue"
            labelKey="month"
            formatValue={formatCurrency}
          />
          <BarChart
            data={topProducts.map(p => ({ name: p.productName, revenue: p.revenue, units: p.unitsSold }))}
            title="Top Products"
            subtitle="By Revenue"
            colorScheme="gold"
            valueKey="revenue"
            labelKey="name"
            formatValue={formatCurrency}
          />
          <BarChart
            data={paymentMethods.map(p => ({ method: p.method, revenue: p.revenue, count: p.orderCount }))}
            title="Payment Methods"
            subtitle="By Revenue"
            colorScheme="blue"
            valueKey="revenue"
            labelKey="method"
            formatValue={formatCurrency}
          />
          {bookingSources && (
            <>
              <BarChart
                data={bookingSources.bySource}
                title="Booking Source"
                subtitle={'Total: ' + bookingSources.bySource.reduce((s,c) => s + c.count, 0) + ' appointments'}
                colorScheme="green"
              />

              {/* Booking source by month */}
              {bookingSources.byMonth.length > 0 && (() => {
                const months = [...new Set(bookingSources.byMonth.map(r => r.month))].sort();
                const sourceLabels = { admin: 'Staff', patient: 'Patient', public: 'Public' };
                const sourceColors = { admin: '#b8860b', patient: '#3b82f6', public: '#22c55e' };
                return (
                  <div className="section-card" style={{ marginBottom: '24px' }}>
                    <div className="section-header">
                      <h2 className="section-title">Bookings by Source & Month</h2>
                    </div>
                    <div style={{ padding: '16px 0' }}>
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        {Object.entries(sourceLabels).map(([key, label]) => (
                          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                            <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: sourceColors[key], display: 'inline-block' }} />
                            {label}
                          </span>
                        ))}
                      </div>
                      {months.map(month => {
                        const monthData = bookingSources.byMonth.filter(r => r.month === month);
                        const total = monthData.reduce((s, r) => s + r.count, 0);
                        const [y, m] = month.split('-');
                        const label = new Date(y, parseInt(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                        return (
                          <div key={month} style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontWeight: 500 }}>{label}</span>
                              <span style={{ color: 'var(--text-muted)' }}>{total} total</span>
                            </div>
                            <div style={{ display: 'flex', height: '24px', borderRadius: '4px', overflow: 'hidden', background: 'var(--cream)' }}>
                              {monthData.map(r => (
                                <div key={r.source} title={`${sourceLabels[r.source] || r.source}: ${r.count}`} style={{
                                  width: total > 0 ? `${(r.count / total) * 100}%` : '0%',
                                  height: '100%',
                                  background: sourceColors[r.source] || '#999',
                                  transition: 'width 0.5s ease'
                                }} />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Recent bookings table */}
              <div className="section-card" style={{ marginBottom: '24px' }}>
                <div className="section-header">
                  <h2 className="section-title">Recent Bookings</h2>
                  <span style={{ color: 'var(--text-muted)' }}>Last 25 appointments</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th>Service</th>
                        <th>Source</th>
                        <th>Booked By</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingSources.recent.map(appt => (
                        <tr key={appt.id}>
                          <td>{appt.patientName}</td>
                          <td>{appt.serviceName}</td>
                          <td>
                            <span className={`status-badge ${appt.source === 'admin' ? 'status-active' : appt.source === 'patient' ? 'status-pending' : 'status-info'}`}>
                              {appt.source === 'admin' ? 'Staff' : appt.source === 'patient' ? 'Patient' : appt.source === 'public' ? 'Public' : appt.source}
                            </span>
                          </td>
                          <td>{appt.createdByName || '—'}</td>
                          <td>{new Date(appt.startDt).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <BarChart data={clientSources} title="Client Source (Staff)" subtitle={'Total: ' + clientSources.reduce((s,c) => s + c.count, 0)} colorScheme="gold" />
          <BarChart data={howHeard} title="How Did You Hear About Us?" subtitle={'Total: ' + howHeard.reduce((s,c) => s + c.count, 0)} colorScheme="blue" />
        </>
      )}
    </div>
  );
}


function AdminPatientSourcesPage() {
  const [patients, setPatients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const patientsPerPage = 25;

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/patient-sources'),
      api.get('/api/admin/patient-sources/summary')
    ])
      .then(([patientsData, summaryData]) => {
        setPatients(patientsData.patients || []);
        setSummary(summaryData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post('/api/admin/ghl/sync-sources');
      setToast({ message: `Synced ${result.successCount} of ${result.total} contacts`, type: 'success' });
      const [patientsData, summaryData] = await Promise.all([
        api.get('/api/admin/patient-sources'),
        api.get('/api/admin/patient-sources/summary')
      ]);
      setPatients(patientsData.patients || []);
      setSummary(summaryData);
    } catch (error) {
      setToast({ message: error.message || 'Sync failed', type: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleBulkLink = async () => {
    setLinking(true);
    try {
      const result = await api.post('/api/admin/ghl/bulk-link');
      setToast({ message: `Linked ${result.linkedCount} patients to GHL (${result.notFoundCount} not found in GHL)`, type: 'success' });
      const [patientsData, summaryData] = await Promise.all([
        api.get('/api/admin/patient-sources'),
        api.get('/api/admin/patient-sources/summary')
      ]);
      setPatients(patientsData.patients || []);
      setSummary(summaryData);
    } catch (error) {
      setToast({ message: error.message || 'Bulk link failed', type: 'error' });
    } finally {
      setLinking(false);
    }
  };

  const getBarColor = (index, scheme = 'gold') => {
    const colors = {
      gold: ['#b8860b', '#d4a84b', '#8b6914', '#c9a227', '#a67c00', '#e5c158', '#9b7615', '#d4b44a', '#7a5d0a', '#c4a030'],
      blue: ['#1e40af', '#3b82f6', '#2563eb', '#60a5fa', '#1d4ed8', '#93c5fd', '#1e3a8a', '#3b82f6', '#1e40af', '#60a5fa'],
      green: ['#166534', '#22c55e', '#16a34a', '#4ade80', '#15803d', '#86efac', '#14532d', '#22c55e', '#166534', '#4ade80'],
      purple: ['#7c3aed', '#a78bfa', '#6d28d9', '#c4b5fd', '#5b21b6', '#ddd6fe', '#4c1d95', '#8b5cf6', '#7c3aed', '#a78bfa']
    };
    return colors[scheme][index % colors[scheme].length];
  };

  const BarChart = ({ data, title, subtitle, colorScheme = 'gold' }) => {
    const total = data.reduce((sum, s) => sum + s.count, 0);
    const maxCount = Math.max(...data.map(s => s.count), 1);
    return (
      <div className="section-card" style={{ marginBottom: '24px' }}>
        <div className="section-header"><h2 className="section-title">{title}</h2><span style={{ color: 'var(--text-muted)' }}>{subtitle}: {total}</span></div>
        {data.length > 0 ? (
          <div style={{ padding: '16px 0' }}>
            {data.map((source, index) => (
              <div key={source.source} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500 }}>{source.source}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{source.count} ({total > 0 ? Math.round((source.count / total) * 100) : 0}%)</span>
                </div>
                <div style={{ height: '24px', background: 'var(--cream)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(source.count / maxCount) * 100}%`,
                    height: '100%',
                    background: getBarColor(index, colorScheme),
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            ))}
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No data yet</h3></div>}
      </div>
    );
  };

  const filteredPatients = patients.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.email.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (sourceFilter === 'all') return true;
    if (sourceFilter === 'ghl-linked') return p.ghlLinked;
    if (sourceFilter === 'no-source') return !p.clientSource && !p.howHeard && !p.ghlSource;
    return true;
  });

  // Reset to page 1 when filter/search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm, sourceFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / patientsPerPage);
  const startIndex = (currentPage - 1) * patientsPerPage;
  const paginatedPatients = filteredPatients.slice(startIndex, startIndex + patientsPerPage);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' });

  // Calculate registration stats
  const stats = {
    total: patients.length,
    withStaffSource: patients.filter(p => p.clientSource).length,
    withHowHeard: patients.filter(p => p.howHeard).length,
    ghlLinked: patients.filter(p => p.ghlLinked).length,
    withAnySource: patients.filter(p => p.clientSource || p.howHeard || p.ghlSource).length
  };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1 className="page-title">Patient Sources</h1>
        <p className="page-subtitle">Track where patients come from</p>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--card-bg)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent-gold)' }}>{stats.total}</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Total Patients</div>
            </div>
            <div style={{ background: 'var(--card-bg)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#22c55e' }}>{stats.ghlLinked}</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>GHL Linked</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stats.total > 0 ? Math.round((stats.ghlLinked / stats.total) * 100) : 0}%</div>
            </div>
            <div style={{ background: 'var(--card-bg)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#3b82f6' }}>{stats.withHowHeard}</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Intake Completed</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stats.total > 0 ? Math.round((stats.withHowHeard / stats.total) * 100) : 0}%</div>
            </div>
            <div style={{ background: 'var(--card-bg)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--accent-gold)' }}>{stats.withStaffSource}</div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Staff Assigned</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stats.total > 0 ? Math.round((stats.withStaffSource / stats.total) * 100) : 0}%</div>
            </div>
          </div>

          {/* Registration Funnel */}
          <div className="section-card" style={{ marginBottom: '24px' }}>
            <div className="section-header"><h2 className="section-title">Patient Registration Overview</h2></div>
            <div style={{ padding: '16px 0' }}>
              {[
                { label: 'Registered in Portal', count: stats.total, color: '#b8860b' },
                { label: 'Completed Intake Form', count: stats.withHowHeard, color: '#3b82f6' },
                { label: 'Linked to GHL', count: stats.ghlLinked, color: '#22c55e' },
                { label: 'Has Source Data', count: stats.withAnySource, color: '#8b5cf6' }
              ].map((item, index) => (
                <div key={item.label} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 500 }}>{item.label}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{item.count} ({stats.total > 0 ? Math.round((item.count / stats.total) * 100) : 0}%)</span>
                  </div>
                  <div style={{ height: '24px', background: 'var(--cream)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${stats.total > 0 ? (item.count / stats.total) * 100 : 0}%`,
                      height: '100%',
                      background: item.color,
                      borderRadius: '4px',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {summary && (
            <>
              <BarChart data={summary.byClientSource.filter(s => s.source !== 'Unknown')} title="Client Source (Staff)" subtitle="Total Patients" colorScheme="gold" />
              <BarChart data={summary.byHowHeard.filter(s => s.source !== 'Unknown')} title="How Did You Hear About Us? (Intake)" subtitle="Total Responses" colorScheme="blue" />
              <BarChart data={summary.byGhlSource.filter(s => s.source !== 'Unknown')} title="GHL Contact Source" subtitle="Total Linked" colorScheme="green" />
              <BarChart data={(summary.byGhlTags || []).filter(s => s.source !== 'Unknown')} title="GHL Tags" subtitle="Tag Occurrences" colorScheme="purple" />
            </>
          )}

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">All Patients</h2>
              <button className="btn btn-secondary btn-sm" onClick={handleBulkLink} disabled={linking} style={{ marginRight: '8px' }}>{linking ? 'Linking...' : 'Bulk Link by Email'}</button><button className="btn btn-gold btn-sm" onClick={handleSync} disabled={syncing}>{syncing ? 'Syncing...' : 'Sync from GHL'}</button>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px', maxWidth: '300px' }}>
                <input type="text" className="form-input" placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ margin: 0 }} />
              </div>
              <select className="form-input" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={{ width: 'auto', margin: 0 }}>
                <option value="all">All Patients</option>
                <option value="ghl-linked">GHL Linked</option>
                <option value="no-source">No Source Data</option>
              </select>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
                {totalPages > 1 && ` (page ${currentPage} of ${totalPages})`}
              </span>
            </div>

            {paginatedPatients.length > 0 ? (
              <>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th>Staff Source</th>
                        <th>How Heard</th>
                        <th>GHL Source</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPatients.map(p => (
                        <tr key={p.id}>
                          <td>
                            <div className="user-cell">
                              <div className="user-cell-avatar">{p.name?.split(' ').map(n => n[0]).join('')}</div>
                              <div className="user-cell-info"><h4>{p.name}</h4><p>{p.email}</p></div>
                            </div>
                          </td>
                          <td>{p.clientSource || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                          <td>{p.howHeard || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                          <td>
                            {p.ghlSource || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            {p.ghlLinked && !p.ghlSource && <span className="status-badge success" style={{ marginLeft: '6px' }}>Linked</span>}
                          </td>
                          <td style={{ color: 'var(--text-muted)' }}>{formatDate(p.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
                    <button 
                      className="btn btn-secondary btn-sm" 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1}
                    >«</button>
                    <button 
                      className="btn btn-secondary btn-sm" 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                      disabled={currentPage === 1}
                    >‹ Prev</button>
                    
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Show first, last, current, and adjacent pages
                      if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            className={`btn btn-sm ${currentPage === page ? 'btn-gold' : 'btn-secondary'}`}
                            onClick={() => setCurrentPage(page)}
                          >{page}</button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} style={{ color: 'var(--text-muted)' }}>...</span>;
                      }
                      return null;
                    })}
                    
                    <button 
                      className="btn btn-secondary btn-sm" 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                      disabled={currentPage === totalPages}
                    >Next ›</button>
                    <button 
                      className="btn btn-secondary btn-sm" 
                      onClick={() => setCurrentPage(totalPages)} 
                      disabled={currentPage === totalPages}
                    >»</button>
                  </div>
                )}
              </>
            ) : searchTerm ? (
              <div className="empty-state"><div className="empty-state-icon">🔍</div><h3>No patients found</h3><p>Try a different search term</p></div>
            ) : (
              <div className="empty-state"><div className="empty-state-icon">👥</div><h3>No patients yet</h3></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function AdminPatientBoardPage() {
  const [data, setData] = useState({ stages: [], patients: [] });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [draggedPatient, setDraggedPatient] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    api.get('/api/admin/patient-board')
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDragStart = (e, patient) => {
    setDraggedPatient(patient);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedPatient || draggedPatient.status === newStatus) {
      setDraggedPatient(null);
      return;
    }

    const oldStatus = draggedPatient.status;
    // Optimistic update
    setData(prev => ({
      ...prev,
      patients: prev.patients.map(p =>
        p.id === draggedPatient.id ? { ...p, status: newStatus } : p
      )
    }));

    try {
      await api.put(`/api/admin/patients/${draggedPatient.id}/status`, { status: newStatus });
      setToast({ message: `Moved ${draggedPatient.name} to ${newStatus}`, type: 'success' });
    } catch (error) {
      // Revert on error
      setData(prev => ({
        ...prev,
        patients: prev.patients.map(p =>
          p.id === draggedPatient.id ? { ...p, status: oldStatus } : p
        )
      }));
      setToast({ message: error.message || 'Failed to update status', type: 'error' });
    }
    setDraggedPatient(null);
  };

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setNotesValue(patient.notes || '');
    setEditingNotes(false);
  };

  const handleSaveNotes = async () => {
    if (!selectedPatient) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/patients/${selectedPatient.id}/notes`, { notes: notesValue });
      setData(prev => ({
        ...prev,
        patients: prev.patients.map(p =>
          p.id === selectedPatient.id ? { ...p, notes: notesValue } : p
        )
      }));
      setSelectedPatient(prev => ({ ...prev, notes: notesValue }));
      setEditingNotes(false);
      setToast({ message: 'Notes saved', type: 'success' });
    } catch (error) {
      setToast({ message: error.message || 'Failed to save notes', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedPatient) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/patients/${selectedPatient.id}/status`, { status: newStatus });
      setData(prev => ({
        ...prev,
        patients: prev.patients.map(p =>
          p.id === selectedPatient.id ? { ...p, status: newStatus } : p
        )
      }));
      setSelectedPatient(prev => ({ ...prev, status: newStatus }));
      setToast({ message: `Status updated to ${newStatus}`, type: 'success' });
    } catch (error) {
      setToast({ message: error.message || 'Failed to update status', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const filteredPatients = searchTerm
    ? data.patients.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : data.patients;

  const getPatientsByStage = (stageId) => filteredPatients.filter(p => p.status === stageId);

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1 className="page-title">Patient Status Board</h1>
        <p className="page-subtitle">Track patient journey through the clinic</p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search patients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            {data.stages.map(stage => (
              <div key={stage.id} style={{
                background: 'var(--card-bg)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid var(--border-color)',
                borderTop: `3px solid ${stage.color}`
              }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: stage.color }}>{getPatientsByStage(stage.id).length}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{stage.label}</div>
              </div>
            ))}
          </div>

          {/* Kanban Board */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${data.stages.length}, minmax(200px, 1fr))`,
            gap: '16px',
            overflowX: 'auto',
            paddingBottom: '20px'
          }}>
            {data.stages.map(stage => (
              <div
                key={stage.id}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
                style={{
                  background: dragOverStage === stage.id ? 'var(--cream)' : 'var(--card-bg)',
                  borderRadius: '12px',
                  border: `1px solid ${dragOverStage === stage.id ? stage.color : 'var(--border-color)'}`,
                  minHeight: '400px',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Stage Header */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  background: stage.color + '15',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: stage.color
                    }} />
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>{stage.label}</span>
                    <span style={{
                      marginLeft: 'auto',
                      background: stage.color,
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>{getPatientsByStage(stage.id).length}</span>
                  </div>
                </div>

                {/* Patient Cards */}
                <div style={{ padding: '8px', maxHeight: '500px', overflowY: 'auto' }}>
                  {getPatientsByStage(stage.id).map(patient => (
                    <div
                      key={patient.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, patient)}
                      onClick={() => handlePatientClick(patient)}
                      style={{
                        background: 'white',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '8px',
                        cursor: 'grab',
                        transition: 'all 0.2s ease',
                        opacity: draggedPatient?.id === patient.id ? 0.5 : 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'var(--accent-gold)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}>
                          {patient.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{patient.name}</div>
                        </div>
                      </div>

                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {patient.nextAppointment ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>📅</span>
                            <span>Next: {formatDateTime(patient.nextAppointment)}</span>
                          </div>
                        ) : patient.lastAppointment ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>⏱️</span>
                            <span>Last: {formatDate(patient.lastAppointment)}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>{patient.hasIntake ? '✓' : '○'}</span>
                            <span>{patient.hasIntake ? 'Intake complete' : 'No intake yet'}</span>
                          </div>
                        )}
                      </div>

                      {patient.notes && (
                        <div style={{
                          marginTop: '8px',
                          padding: '6px 8px',
                          background: 'var(--cream)',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: 'var(--text-secondary)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          📝 {patient.notes}
                        </div>
                      )}
                    </div>
                  ))}

                  {getPatientsByStage(stage.id).length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: 'var(--text-muted)',
                      fontSize: '13px'
                    }}>
                      {searchTerm ? 'No matches' : 'No patients'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Patient Detail Modal */}
      {selectedPatient && (
        <div className="modal-overlay" onClick={() => setSelectedPatient(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Patient Details</h2>
              <button className="modal-close" onClick={() => setSelectedPatient(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'var(--accent-gold)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '20px'
                }}>
                  {selectedPatient.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px' }}>{selectedPatient.name}</h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>{selectedPatient.email}</p>
                  {selectedPatient.phone && (
                    <p style={{ margin: '2px 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>{selectedPatient.phone}</p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={selectedPatient.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={saving}
                >
                  {data.stages.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', margin: '20px 0' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Joined</div>
                  <div style={{ fontWeight: 500 }}>{formatDate(selectedPatient.createdAt)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Intake</div>
                  <div style={{ fontWeight: 500 }}>{selectedPatient.hasIntake ? '✅ Complete' : '⏳ Pending'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Next Appointment</div>
                  <div style={{ fontWeight: 500 }}>{formatDateTime(selectedPatient.nextAppointment)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Last Visit</div>
                  <div style={{ fontWeight: 500 }}>{formatDate(selectedPatient.lastAppointment)}</div>
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Admin Notes</span>
                  {!editingNotes && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditingNotes(true)}
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >Edit</button>
                  )}
                </label>
                {editingNotes ? (
                  <>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      placeholder="Add notes about this patient..."
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button className="btn btn-gold" onClick={handleSaveNotes} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button className="btn btn-secondary" onClick={() => { setEditingNotes(false); setNotesValue(selectedPatient.notes || ''); }}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{
                    padding: '12px',
                    background: 'var(--cream)',
                    borderRadius: '8px',
                    minHeight: '60px',
                    fontSize: '14px',
                    color: selectedPatient.notes ? 'var(--text-primary)' : 'var(--text-muted)'
                  }}>
                    {selectedPatient.notes || 'No notes yet'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AdminCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [campaignForm, setCampaignForm] = useState({ name: '', status: 'active' });
  const [stageForm, setStageForm] = useState({ name: '', location: '', comments: '', status: 'green', due_date: '', patient_id: '' });
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskCampaignId, setSelectedTaskCampaignId] = useState(null);
  const [selectedTaskStageId, setSelectedTaskStageId] = useState(null);
  const [taskForm, setTaskForm] = useState({ name: '', due_date: '', status: 'hold' });
  const [hideCompleted, setHideCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const data = await api.get('/api/admin/campaigns');
      setCampaigns(data);
      setExpandedCampaigns(prev => {
        const expanded = { ...prev };
        data.forEach(c => { if (!(c.id in expanded)) expanded[c.id] = false; });
        return expanded;
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPipelines = async () => {
    setLoadingPipelines(true);
    try {
      const data = await api.get('/api/admin/ghl/pipelines');
      setPipelines(data.pipelines || []);
      if (data.pipelines?.length > 0 && !selectedPipeline) {
        setSelectedPipeline(data.pipelines[0].id);
      }
    } catch (e) { console.error('Error fetching pipelines:', e); }
    finally { setLoadingPipelines(false); }
  };

  const fetchPipelineSummary = async (pipelineId) => {
    if (!pipelineId) return;
    try {
      const [summaryData, oppData] = await Promise.all([
        api.get(`/api/admin/ghl/pipelines/${pipelineId}/summary`),
        api.get(`/api/admin/ghl/pipelines/${pipelineId}/opportunities`)
      ]);
      setPipelineSummary(summaryData);
      setOpportunities(oppData.opportunities || []);
      const expanded = {};
      (summaryData.stages || []).forEach(s => { expanded[s.id] = false; });
      setExpandedStages(expanded);
    } catch (e) { console.error('Error fetching pipeline data:', e); }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchPipelines();
    api.get('/api/admin/users').then(data => setPatients((Array.isArray(data) ? data : []).filter(u => u.role === 'patient'))).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedPipeline) {
      fetchPipelineSummary(selectedPipeline);
    }
  }, [selectedPipeline]);

  const openCampaignModal = (campaign = null) => {
    setEditingCampaign(campaign);
    setCampaignForm(campaign ? { name: campaign.name, status: campaign.status } : { name: '', status: 'active' });
    setShowCampaignModal(true);
  };

  const openStageModal = (campaignId, stage = null) => {
    setSelectedCampaignId(campaignId);
    setEditingStage(stage);
    setStageForm(stage ? { name: stage.name, location: stage.location || '', comments: stage.comments || '', status: stage.status || 'green', due_date: stage.due_date ? stage.due_date.split('T')[0] : '', patient_id: stage.patient_id || '' } : { name: '', location: '', comments: '', status: 'green', due_date: '', patient_id: '' });
    setPatientSearch(stage?.patient_name || '');
    setShowStageModal(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) { setToast({ message: 'Name is required', type: 'error' }); return; }
    setSaving(true);
    try {
      if (editingCampaign) {
        await api.put(`/api/admin/campaigns/${editingCampaign.id}`, campaignForm);
        setToast({ message: 'Campaign updated!', type: 'success' });
      } else {
        await api.post('/api/admin/campaigns', campaignForm);
        setToast({ message: 'Campaign created!', type: 'success' });
      }
      setShowCampaignModal(false);
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign and all its stages?')) return;
    try {
      await api.delete(`/api/admin/campaigns/${id}`);
      setToast({ message: 'Campaign deleted!', type: 'success' });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleSaveStage = async () => {
    if (!stageForm.name.trim()) { setToast({ message: 'Name is required', type: 'error' }); return; }
    setSaving(true);
    try {
      if (editingStage) {
        await api.put(`/api/admin/campaigns/${selectedCampaignId}/stages/${editingStage.id}`, stageForm);
        setToast({ message: 'Stage updated!', type: 'success' });
      } else {
        await api.post(`/api/admin/campaigns/${selectedCampaignId}/stages`, stageForm);
        setToast({ message: 'Stage added!', type: 'success' });
      }
      setShowStageModal(false);
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDeleteStage = async (campaignId, stageId) => {
    if (!window.confirm('Delete this stage?')) return;
    try {
      await api.delete(`/api/admin/campaigns/${campaignId}/stages/${stageId}`);
      setToast({ message: 'Stage deleted!', type: 'success' });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleMoveStage = async (campaign, stageIndex, direction) => {
    const stages = [...campaign.stages];
    const newIndex = stageIndex + direction;
    if (newIndex < 0 || newIndex >= stages.length) return;
    [stages[stageIndex], stages[newIndex]] = [stages[newIndex], stages[stageIndex]];
    try {
      await api.put(`/api/admin/campaigns/${campaign.id}/stages-reorder`, { stageIds: stages.map(s => s.id) });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleStageStatusChange = async (campaignId, stage, newStatus) => {
    try {
      await api.put(`/api/admin/campaigns/${campaignId}/stages/${stage.id}`, { status: newStatus });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const getStageStatusColor = (status) => {
    switch (status) {
      case 'green': return '#22c55e';
      case 'yellow': return '#eab308';
      case 'red': return '#ef4444';
      default: return '#22c55e';
    }
  };

  const getTaskStatusStyle = (status) => {
    switch (status) {
      case 'hold': return { bg: '#6b7280', label: 'Hold' };
      case 'in_progress': return { bg: '#3b82f6', label: 'In Progress' };
      case 'blocked': return { bg: '#ef4444', label: 'Blocked' };
      case 'completed': return { bg: '#22c55e', label: 'Completed' };
      default: return { bg: '#6b7280', label: 'Hold' };
    }
  };

  const openTaskModal = (campaignId, stageId, task = null) => {
    setSelectedTaskCampaignId(campaignId);
    setSelectedTaskStageId(stageId);
    setEditingTask(task);
    setTaskForm(task ? { name: task.name, due_date: task.due_date ? task.due_date.slice(0, 10) : '', status: task.status || 'hold' } : { name: '', due_date: '', status: 'hold' });
    setShowTaskModal(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.name.trim()) { setToast({ message: 'Task name is required', type: 'error' }); return; }
    setSaving(true);
    try {
      if (editingTask) {
        await api.put(`/api/admin/campaigns/${selectedTaskCampaignId}/stages/${selectedTaskStageId}/tasks/${editingTask.id}`, taskForm);
        setToast({ message: 'Task updated!', type: 'success' });
      } else {
        await api.post(`/api/admin/campaigns/${selectedTaskCampaignId}/stages/${selectedTaskStageId}/tasks`, taskForm);
        setToast({ message: 'Task added!', type: 'success' });
      }
      setShowTaskModal(false);
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDeleteTask = async (campaignId, stageId, taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/api/admin/campaigns/${campaignId}/stages/${stageId}/tasks/${taskId}`);
      setToast({ message: 'Task deleted!', type: 'success' });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleTaskStatusChange = async (campaignId, stageId, task, newStatus) => {
    try {
      await api.put(`/api/admin/campaigns/${campaignId}/stages/${stageId}/tasks/${task.id}`, { status: newStatus });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleStatusChange = async (campaign, newStatus) => {
    try {
      await api.put(`/api/admin/campaigns/${campaign.id}`, { status: newStatus });
      fetchCampaigns();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const toggleExpand = (id) => {
    setExpandedCampaigns(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'var(--success)';
      case 'paused': return 'var(--warning, #f59e0b)';
      case 'completed': return 'var(--text-muted)';
      default: return 'var(--text-muted)';
    }
  };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="page-header">
        <h1 className="page-title">Campaigns</h1>
        <p className="page-subtitle">Track marketing campaigns and pipeline stages</p>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <button className={activeTab === 'ghl' ? 'btn btn-gold' : 'btn btn-secondary'} onClick={() => setActiveTab('ghl')}>GHL Pipelines</button>
        <button className={activeTab === 'local' ? 'btn btn-gold' : 'btn btn-secondary'} onClick={() => setActiveTab('local')}>Local Campaigns</button>
      </div>

      {activeTab === 'ghl' && (
        <div className="section-card" style={{ marginBottom: '24px' }}>
          <div className="section-header">
            <h2 className="section-title">GHL Pipeline</h2>
            {pipelines.length > 0 && (
              <select style={{ width: 'auto', border: 'none', borderRadius: '8px', padding: '10px 16px', background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-dark, #a67c00) 100%)', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '14px', appearance: 'none', WebkitAppearance: 'none', paddingRight: '32px', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'white\' d=\'M6 8L2 4h8z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }} value={selectedPipeline || ''} onChange={(e) => setSelectedPipeline(e.target.value)}>
                {pipelines.map(p => <option key={p.id} value={p.id} style={{ background: 'white', color: 'var(--text-primary)' }}>{p.name}</option>)}
              </select>
            )}
          </div>
          {loadingPipelines ? (
            <div className="loading-spinner"><div className="spinner"></div></div>
          ) : pipelines.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon">📊</div><h3>No pipelines found</h3><p>Make sure opportunities.readonly scope is enabled</p></div>
          ) : pipelineSummary ? (
            <>
              <div className="stats-row" style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <div className="stat-card"><div className="stat-value">{pipelineSummary.totalOpportunities}</div><div className="stat-label">Total Contacts</div></div>
                <div className="stat-card" style={{ background: '#dcfce7' }}><div className="stat-value" style={{ color: '#16a34a' }}>${(pipelineSummary.totalValue || 0).toLocaleString()}</div><div className="stat-label">Pipeline Value</div></div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pipelineSummary.stages.map((stage, i) => {
                  const stageOpps = opportunities.filter(o => o.stageId === stage.id);
                  const isExpanded = expandedStages[stage.id];
                  return (
                    <div key={stage.id} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                      <div onClick={() => setExpandedStages(prev => ({ ...prev, [stage.id]: !prev[stage.id] }))} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: 'var(--cream)', cursor: 'pointer', gap: '12px' }}>
                        <span style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: '0.2s' }}>▶</span>
                        <span style={{ flex: 1, fontWeight: '600' }}>{stage.name}</span>
                        <span className="badge" style={{ background: 'var(--gold)', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '12px' }}>{stage.count}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>${(stage.value || 0).toLocaleString()}</span>
                      </div>
                      {isExpanded && stageOpps.length > 0 && (
                        <table className="admin-table" style={{ margin: 0 }}>
                          <thead><tr><th>Contact</th><th>Value</th><th>Portal Patient</th></tr></thead>
                          <tbody>
                            {stageOpps.map(opp => (
                              <tr key={opp.id}>
                                <td style={{ fontWeight: '500' }}>{opp.name}</td>
                                <td>${(opp.monetaryValue || 0).toLocaleString()}</td>
                                <td>{opp.patient ? <span style={{ color: 'var(--success)' }}>{opp.patient.name}</span> : <span style={{ color: 'var(--text-muted)' }}>Not linked</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {isExpanded && stageOpps.length === 0 && (
                        <div style={{ padding: '16px', color: 'var(--text-muted)', textAlign: 'center', fontSize: '13px' }}>No contacts in this stage</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      )}


      {activeTab === 'local' && (
      <>
      <div className="section-card" style={{ marginBottom: '24px' }}>
        <div className="section-header">
          <h2 className="section-title">All Campaigns</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)} />
              Hide completed tasks
            </label>
            <button className="btn btn-gold btn-sm" onClick={() => openCampaignModal()}>+ Add Campaign</button>
          </div>
        </div>

        {/* Overdue summary by campaign */}
        {!loading && (() => {
          const overdueByCampaign = campaigns.map(c => {
            const count = (c.stages || []).filter(s => s.due_date && new Date(s.due_date.split('T')[0] + 'T23:59:59') < new Date()).length;
            return { name: c.name, count };
          }).filter(c => c.count > 0);
          const totalOverdue = overdueByCampaign.reduce((sum, c) => sum + c.count, 0);
          if (totalOverdue === 0) return null;
          return (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '14px 18px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>&#9888;</span>
                <span style={{ fontWeight: 600, color: '#991b1b', fontSize: '14px' }}>{totalOverdue} overdue item{totalOverdue !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {overdueByCampaign.map(c => (
                  <span key={c.name} style={{ background: '#fee2e2', color: '#dc2626', padding: '3px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: 500 }}>
                    {c.name}: {c.count}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : campaigns.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {campaigns.map(campaign => (
              <div key={campaign.id} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                <div 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    padding: '16px', 
                    background: 'var(--cream)',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleExpand(campaign.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '20px' }}>{expandedCampaigns[campaign.id] ? '▼' : '▶'}</span>
                    <span style={{ fontSize: '18px' }}>📢</span>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{campaign.name}</h3>
                    <span style={{ 
                      fontSize: '12px', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      background: getStatusColor(campaign.status),
                      color: 'white',
                      textTransform: 'capitalize'
                    }}>{campaign.status}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={e => e.stopPropagation()}>
                    <select 
                      className="form-input" 
                      value={campaign.status} 
                      onChange={e => handleStatusChange(campaign, e.target.value)}
                      style={{ width: 'auto', margin: 0, padding: '4px 8px', fontSize: '12px' }}
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button className="btn btn-secondary btn-sm" onClick={() => openStageModal(campaign.id)}>+ Stage</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => openCampaignModal(campaign)}>Edit</button>
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDeleteCampaign(campaign.id)}>Delete</button>
                  </div>
                </div>
                
                {expandedCampaigns[campaign.id] && (
                  <div style={{ padding: '16px' }}>
                    {campaign.stages && campaign.stages.length > 0 ? (
                      <div className="admin-table-wrapper">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th style={{ width: '3%' }}>#</th>
                              <th style={{ width: '6%' }}>Status</th>
                              <th style={{ width: '17%' }}>Stage Name</th>
                              <th style={{ width: '12%' }}>Client</th>
                              <th style={{ width: '12%' }}>Location</th>
                              <th style={{ width: '20%' }}>Comments</th>
                              <th style={{ width: '8%' }}>Due Date</th>
                              <th style={{ width: '8%' }}>Updated</th>
                              <th style={{ width: '14%' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaign.stages.map((stage, idx) => {
                              const visibleTasks = hideCompleted
                                ? (stage.tasks || []).filter(t => t.status !== 'completed')
                                : (stage.tasks || []);
                              const totalTasks = (stage.tasks || []).length;
                              const completedTasks = (stage.tasks || []).filter(t => t.status === 'completed').length;
                              return (
                              <React.Fragment key={stage.id}>
                              <tr>
                                <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{idx + 1}</td>
                                <td>
                                  <select
                                    value={stage.status || 'green'}
                                    onChange={e => handleStageStatusChange(campaign.id, stage, e.target.value)}
                                    style={{
                                      width: '36px', height: '28px', padding: '0', border: '2px solid var(--border-color)', borderRadius: '6px',
                                      backgroundColor: getStageStatusColor(stage.status || 'green'), color: 'transparent', cursor: 'pointer',
                                      appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none'
                                    }}
                                    title={`Status: ${stage.status || 'green'}`}
                                  >
                                    <option value="green">Green</option>
                                    <option value="yellow">Yellow</option>
                                    <option value="red">Red</option>
                                  </select>
                                </td>
                                <td style={{ fontWeight: 500 }}>
                                  {stage.name}
                                  {totalTasks > 0 && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>({completedTasks}/{totalTasks} done)</span>}
                                </td>
                                <td style={{ fontSize: '13px' }}>
                                  {stage.patient_name ? (
                                    <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{stage.patient_name}</span>
                                  ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                </td>
                                <td>{stage.location || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{stage.comments ? stage.comments.split(/(https?:\/\/[^\s]+)/g).map((part, i) => /^https?:\/\//.test(part) ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>{part}</a> : part) : '—'}</td>
                                <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                                  {stage.due_date ? (() => { const dd = stage.due_date.split('T')[0]; const isOverdue = new Date(dd + 'T23:59:59') < new Date(); return <span style={{ color: isOverdue ? '#dc2626' : 'inherit', fontWeight: isOverdue ? 600 : 400 }}>{new Date(dd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{isOverdue ? ' (overdue)' : ''}</span>; })() : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                                </td>
                                <td style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                  {stage.updated_at ? new Date(stage.updated_at).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric' }) : '—'}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <button
                                      className="action-btn"
                                      onClick={() => handleMoveStage(campaign, idx, -1)}
                                      disabled={idx === 0}
                                      style={{ opacity: idx === 0 ? 0.3 : 1, padding: '2px 6px', fontSize: '14px' }}
                                      title="Move up"
                                    >&#9650;</button>
                                    <button
                                      className="action-btn"
                                      onClick={() => handleMoveStage(campaign, idx, 1)}
                                      disabled={idx === campaign.stages.length - 1}
                                      style={{ opacity: idx === campaign.stages.length - 1 ? 0.3 : 1, padding: '2px 6px', fontSize: '14px' }}
                                      title="Move down"
                                    >&#9660;</button>
                                    <button className="action-btn" onClick={() => openTaskModal(campaign.id, stage.id)} title="Add task" style={{ fontSize: '11px', padding: '2px 6px' }}>+ Task</button>
                                    <button className="action-btn edit" onClick={() => openStageModal(campaign.id, stage)}>Edit</button>
                                    <button className="action-btn delete" onClick={() => handleDeleteStage(campaign.id, stage.id)}>Del</button>
                                  </div>
                                </td>
                              </tr>
                              {visibleTasks.length > 0 && (
                                <tr>
                                  <td colSpan={9} style={{ padding: '0', background: '#fafaf8' }}>
                                    <div style={{ paddingLeft: '48px' }}>
                                      {visibleTasks.map(task => {
                                        const ts = getTaskStatusStyle(task.status);
                                        const isOverdue = task.due_date && task.status !== 'completed' && new Date(task.due_date + 'T23:59:59') < new Date();
                                        return (
                                          <div key={task.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', gap: '10px', borderBottom: '1px solid var(--border-color)', fontSize: '13px' }}>
                                            <select
                                              value={task.status}
                                              onChange={e => handleTaskStatusChange(campaign.id, stage.id, task, e.target.value)}
                                              style={{ padding: '2px 4px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border-color)', backgroundColor: ts.bg, color: 'white', cursor: 'pointer', fontWeight: 600 }}
                                            >
                                              <option value="hold">Hold</option>
                                              <option value="in_progress">In Progress</option>
                                              <option value="blocked">Blocked</option>
                                              <option value="completed">Completed</option>
                                            </select>
                                            <span style={{ flex: 1, textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? 'var(--text-muted)' : 'var(--text-primary)' }}>{task.name}</span>
                                            {task.due_date && (
                                              <span style={{ fontSize: '12px', color: isOverdue ? '#ef4444' : 'var(--text-muted)', fontWeight: isOverdue ? 600 : 400 }}>
                                                {isOverdue ? 'Overdue: ' : 'Due: '}{new Date(task.due_date + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric' })}
                                              </span>
                                            )}
                                            <button className="action-btn edit" onClick={() => openTaskModal(campaign.id, stage.id, task)} style={{ fontSize: '11px', padding: '1px 5px' }}>Edit</button>
                                            <button className="action-btn delete" onClick={() => handleDeleteTask(campaign.id, stage.id, task.id)} style={{ fontSize: '11px', padding: '1px 5px' }}>Del</button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                        No stages yet. <button className="btn btn-ghost btn-sm" onClick={() => openStageModal(campaign.id)}>Add first stage</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📢</div>
            <h3>No campaigns yet</h3>
            <p>Create your first campaign to start tracking marketing efforts.</p>
            <button className="btn btn-gold" onClick={() => openCampaignModal()}>+ Add Campaign</button>
          </div>
        )}
      </div>

      {showCampaignModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowCampaignModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingCampaign ? 'Edit Campaign' : 'Add Campaign'}</h2>
              <button className="modal-close" onClick={() => setShowCampaignModal(false)} disabled={saving}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">Campaign Name *</label>
              <input 
                type="text" 
                className="form-input" 
                value={campaignForm.name} 
                onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="e.g., Facebook Spring 2026"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select 
                className="form-input" 
                value={campaignForm.status} 
                onChange={e => setCampaignForm({ ...campaignForm, status: e.target.value })}
                disabled={saving}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowCampaignModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSaveCampaign} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {showStageModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowStageModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingStage ? 'Edit Stage' : 'Add Stage'}</h2>
              <button className="modal-close" onClick={() => setShowStageModal(false)} disabled={saving}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">Stage Name *</label>
              <input
                type="text"
                className="form-input"
                value={stageForm.name}
                onChange={e => setStageForm({ ...stageForm, name: e.target.value })}
                placeholder="e.g., Lead Capture"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[{ value: 'green', label: 'Green', color: '#22c55e' }, { value: 'yellow', label: 'Yellow', color: '#eab308' }, { value: 'red', label: 'Red', color: '#ef4444' }].map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input type="radio" name="stageStatus" value={opt.value} checked={stageForm.status === opt.value} onChange={e => setStageForm({ ...stageForm, status: e.target.value })} disabled={saving} />
                    <span style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: opt.color, display: 'inline-block' }}></span>
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={stageForm.due_date}
                  onChange={e => setStageForm({ ...stageForm, due_date: e.target.value })}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search clients..."
                  value={patientSearch}
                  onChange={e => { setPatientSearch(e.target.value); if (!e.target.value) setStageForm({ ...stageForm, patient_id: '' }); }}
                  disabled={saving}
                  style={{ marginBottom: '4px' }}
                />
                <select
                  className="form-input"
                  value={stageForm.patient_id}
                  onChange={e => {
                    const pid = e.target.value ? parseInt(e.target.value) : '';
                    setStageForm({ ...stageForm, patient_id: pid });
                    if (pid) { const p = patients.find(p => p.id === pid); if (p) setPatientSearch(p.name); }
                    else setPatientSearch('');
                  }}
                  disabled={saving}
                  size={patientSearch ? Math.min(6, patients.filter(p => p.name?.toLowerCase().includes(patientSearch.toLowerCase()) || p.email?.toLowerCase().includes(patientSearch.toLowerCase())).length + 1) : 1}
                >
                  <option value="">None</option>
                  {patients
                    .filter(p => !patientSearch || p.name?.toLowerCase().includes(patientSearch.toLowerCase()) || p.email?.toLowerCase().includes(patientSearch.toLowerCase()))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(p => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input
                type="text"
                className="form-input"
                value={stageForm.location}
                onChange={e => setStageForm({ ...stageForm, location: e.target.value })}
                placeholder="e.g., FB Lead Form, GHL Workflow, Calendly"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Comments</label>
              <textarea
                className="form-input"
                value={stageForm.comments}
                onChange={e => setStageForm({ ...stageForm, comments: e.target.value })}
                placeholder="Notes about this stage..."
                rows={3}
                disabled={saving}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowStageModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSaveStage} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowTaskModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingTask ? 'Edit Task' : 'Add Task'}</h2>
              <button className="modal-close" onClick={() => setShowTaskModal(false)} disabled={saving}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">Task Name *</label>
              <input
                type="text"
                className="form-input"
                value={taskForm.name}
                onChange={e => setTaskForm({ ...taskForm, name: e.target.value })}
                placeholder="e.g., Create landing page"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="date"
                className="form-input"
                value={taskForm.due_date}
                onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={taskForm.status}
                onChange={e => setTaskForm({ ...taskForm, status: e.target.value })}
                disabled={saving}
              >
                <option value="hold">Hold</option>
                <option value="in_progress">In Progress</option>
                <option value="blocked">Blocked</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowTaskModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSaveTask} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

// Admin Nutrition Roadmaps Page (ANA Integration - Beta)
function AdminNutritionRoadmapsPage() {
  const [tab, setTab] = useState('generate');
  const [users, setUsers] = useState([]);
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Generate tab state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [formData, setFormData] = useState({
    sex: 'Female',
    current_weight: '',
    goal_weight: '',
    body_fat_pct: '',
    visceral_fat: '',
    bmi: '',
    smm: '',
    narrative: '',
    patient_name: '',
  });
  const [macrosPreview, setMacrosPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);

  // Fetch users for patient selector
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await api.get('/api/admin/users');
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Fetch roadmaps when on roadmaps tab
  const fetchRoadmaps = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/admin/nutrition-roadmaps');
      setRoadmaps(data);
    } catch (error) {
      console.error('Error fetching roadmaps:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'roadmaps') {
      fetchRoadmaps();
    }
  }, [tab, fetchRoadmaps]);

  // Poll for generating status
  useEffect(() => {
    if (!generatingId) return;
    const interval = setInterval(async () => {
      try {
        const status = await api.get(`/api/admin/nutrition-roadmaps/${generatingId}/status`);
        if (status.status === 'ready') {
          clearInterval(interval);
          setGenerating(false);
          setGeneratingId(null);
          setToast({ message: 'Roadmap generated successfully!', type: 'success' });
          setTab('roadmaps');
          fetchRoadmaps();
        } else if (status.status === 'error') {
          clearInterval(interval);
          setGenerating(false);
          setGeneratingId(null);
          setToast({ message: status.error || 'Generation failed', type: 'error' });
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [generatingId, fetchRoadmaps]);

  const handleSelectPatient = async (userId) => {
    setSelectedUserId(userId);
    setMacrosPreview(null);
    if (!userId) return;

    try {
      const prefill = await api.get(`/api/admin/nutrition-roadmaps/patient/${userId}/prefill`);
      setFormData(prev => ({
        ...prev,
        patient_name: prefill.patient_name || '',
      }));
    } catch (error) {
      console.error('Prefill error:', error);
    }
  };

  const handlePreviewMacros = async () => {
    if (!formData.current_weight || !formData.goal_weight) {
      setToast({ message: 'Current weight and goal weight are required', type: 'error' });
      return;
    }
    try {
      const macros = await api.post('/api/admin/nutrition-roadmaps/compute-macros', {
        sex: formData.sex,
        current_weight: parseFloat(formData.current_weight),
        goal_weight: parseFloat(formData.goal_weight),
        body_fat_pct: formData.body_fat_pct ? parseFloat(formData.body_fat_pct) : null,
        conditions: '',
      });
      setMacrosPreview(macros);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    }
  };

  const handleGenerate = async () => {
    if (!selectedUserId) {
      setToast({ message: 'Please select a patient', type: 'error' });
      return;
    }
    const required = ['sex', 'current_weight', 'goal_weight', 'body_fat_pct', 'visceral_fat', 'bmi', 'smm'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length > 0) {
      setToast({ message: `Missing required fields: ${missing.join(', ')}`, type: 'error' });
      return;
    }

    setGenerating(true);
    try {
      const patientData = {
        patient_name: formData.patient_name,
        sex: formData.sex,
        current_weight: parseFloat(formData.current_weight),
        goal_weight: parseFloat(formData.goal_weight),
        body_fat_pct: parseFloat(formData.body_fat_pct),
        visceral_fat: parseFloat(formData.visceral_fat),
        bmi: parseFloat(formData.bmi),
        smm: parseFloat(formData.smm),
      };

      const result = await api.post('/api/admin/nutrition-roadmaps/generate', {
        user_id: parseInt(selectedUserId),
        patient_data: patientData,
        narrative: formData.narrative || '',
      });

      setGeneratingId(result.id);
      setToast({ message: 'Generation started — this takes about 30-60 seconds...', type: 'success' });
    } catch (error) {
      setGenerating(false);
      setToast({ message: error.message, type: 'error' });
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await window.fetch(`/api/admin/nutrition-roadmaps/${id}/pdf`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nutrition_roadmap.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    }
  };

  const handleEmail = async (id) => {
    try {
      const result = await api.post(`/api/admin/nutrition-roadmaps/${id}/email`);
      setToast({ message: result.message, type: 'success' });
      fetchRoadmaps();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this roadmap? This cannot be undone.')) return;
    try {
      await api.delete(`/api/admin/nutrition-roadmaps/${id}`);
      setToast({ message: 'Roadmap deleted', type: 'success' });
      fetchRoadmaps();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
    }
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Nutrition Roadmaps</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className={`btn ${tab === 'generate' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('generate')}
          >
            Generate
          </button>
          <button
            className={`btn ${tab === 'roadmaps' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab('roadmaps')}
          >
            All Roadmaps
          </button>
        </div>
      </div>

      {tab === 'generate' && (
        <div style={{ maxWidth: '700px' }}>
          {/* Patient Selector */}
          <div className="form-group">
            <label className="form-label">Patient</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search patients..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
            />
            {userSearch && !selectedUserId && (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '4px', maxHeight: '200px', overflow: 'auto' }}>
                {filteredUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => { handleSelectPatient(u.id); setUserSearch(u.name); }}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.target.style.background = '#f8fafc'}
                    onMouseLeave={e => e.target.style.background = 'white'}
                  >
                    <strong>{u.name}</strong> <span style={{ color: '#94a3b8' }}>{u.email}</span>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div style={{ padding: '10px 14px', color: '#94a3b8' }}>No patients found</div>
                )}
              </div>
            )}
            {selectedUserId && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: '4px' }}
                onClick={() => { setSelectedUserId(''); setUserSearch(''); setFormData(prev => ({ ...prev, patient_name: '' })); setMacrosPreview(null); }}>
                Clear selection
              </button>
            )}
          </div>

          {/* Form Fields */}
          <div className="admin-form-grid" style={{ marginTop: '16px' }}>
            <div className="form-group">
              <label className="form-label">Sex</label>
              <select className="form-input" value={formData.sex} onChange={e => setFormData(prev => ({ ...prev, sex: e.target.value }))}>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Current Weight (lbs)</label>
              <input type="number" className="form-input" value={formData.current_weight}
                onChange={e => setFormData(prev => ({ ...prev, current_weight: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Goal Weight (lbs)</label>
              <input type="number" className="form-input" value={formData.goal_weight}
                onChange={e => setFormData(prev => ({ ...prev, goal_weight: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Body Fat %</label>
              <input type="number" className="form-input" value={formData.body_fat_pct}
                onChange={e => setFormData(prev => ({ ...prev, body_fat_pct: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Visceral Fat</label>
              <input type="number" className="form-input" value={formData.visceral_fat}
                onChange={e => setFormData(prev => ({ ...prev, visceral_fat: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">BMI</label>
              <input type="number" className="form-input" step="0.1" value={formData.bmi}
                onChange={e => setFormData(prev => ({ ...prev, bmi: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">SMM</label>
              <input type="number" className="form-input" step="0.1" value={formData.smm}
                onChange={e => setFormData(prev => ({ ...prev, smm: e.target.value }))} />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '16px' }}>
            <label className="form-label">Clinical Narrative (optional)</label>
            <textarea className="form-input" rows={4} placeholder="Free-text clinical notes about the patient..."
              value={formData.narrative}
              onChange={e => setFormData(prev => ({ ...prev, narrative: e.target.value }))}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <button className="btn btn-secondary" onClick={handlePreviewMacros}
              disabled={!formData.current_weight || !formData.goal_weight}>
              Preview Macros
            </button>
            <button className="btn btn-primary" onClick={handleGenerate}
              disabled={generating || !selectedUserId}>
              {generating ? 'Generating...' : 'Generate Roadmap'}
            </button>
          </div>

          {/* Macros Preview */}
          {macrosPreview && (
            <div style={{ marginTop: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 12px 0' }}>Macro Preview</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#c8a97e' }}>{macrosPreview.protein}g</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Protein</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#c8a97e' }}>{macrosPreview.carbs}g</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Carbs</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#c8a97e' }}>{macrosPreview.fat}g</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Fat</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#c8a97e' }}>{macrosPreview.calories}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>Calories</div>
                </div>
              </div>
              {macrosPreview.lean_mode && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#f59e0b' }}>Lean Mode Active</div>
              )}
              {macrosPreview.overrides?.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#94a3b8' }}>
                  {macrosPreview.overrides.join(' | ')}
                </div>
              )}
            </div>
          )}

          {generating && (
            <div style={{ marginTop: '20px', textAlign: 'center', padding: '24px' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
              <p style={{ color: '#94a3b8' }}>Generating roadmap... this takes about 30-60 seconds</p>
            </div>
          )}
        </div>
      )}

      {tab === 'roadmaps' && (
        <div className="admin-table-wrapper">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          ) : roadmaps.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <h3>No roadmaps yet</h3>
              <p>Generate your first nutrition roadmap from the Generate tab.</p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Date</th>
                  <th>Macros</th>
                  <th>Status</th>
                  <th>Emailed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roadmaps.map(r => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.patient_name}</strong>
                      <br /><span style={{ fontSize: '12px', color: '#94a3b8' }}>{r.patient_email}</span>
                    </td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      {r.macros ? (
                        <span style={{ fontSize: '13px' }}>
                          P:{r.macros.protein}g C:{r.macros.carbs}g F:{r.macros.fat}g = {r.macros.calories}cal
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <span className={`status-badge ${r.status === 'ready' ? 'status-active' : r.status === 'error' ? 'status-error' : 'status-pending'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td>{r.emailed_at ? new Date(r.emailed_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {r.status === 'ready' && (
                          <>
                            <button className="btn btn-sm btn-primary" onClick={() => handleDownload(r.id)}>PDF</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => handleEmail(r.id)}>Email</button>
                          </>
                        )}
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(r.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

function AdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({ code: '', discountType: 'percent', discountValue: '', minOrderAmount: '', maxUses: '', expiresAt: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);

  const fetchCoupons = () => {
    api.get('/api/admin/coupons').then(setCoupons).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchCoupons(); }, []);

  const openModal = (coupon = null) => {
    setEditingCoupon(coupon);
    if (coupon) {
      setFormData({
        code: coupon.code,
        discountType: coupon.discount_type,
        discountValue: coupon.discount_value,
        minOrderAmount: coupon.min_order_amount || '',
        maxUses: coupon.max_uses || '',
        expiresAt: coupon.expires_at ? new Date(coupon.expires_at).toISOString().split('T')[0] : ''
      });
    } else {
      setFormData({ code: '', discountType: 'percent', discountValue: '', minOrderAmount: '', maxUses: '', expiresAt: '' });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.discountValue) {
      setToast({ message: 'Code and discount value are required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editingCoupon) {
        await api.put(`/api/admin/coupons/${editingCoupon.id}`, formData);
      } else {
        await api.post('/api/admin/coupons', formData);
      }
      setToast({ message: editingCoupon ? 'Coupon updated' : 'Coupon created', type: 'success' });
      setShowModal(false);
      fetchCoupons();
    } catch (e) { setToast({ message: e.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await api.put(`/api/admin/coupons/${coupon.id}`, { ...coupon, active: !coupon.active, discountType: coupon.discount_type, discountValue: coupon.discount_value, minOrderAmount: coupon.min_order_amount, maxUses: coupon.max_uses, expiresAt: coupon.expires_at });
      fetchCoupons();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await api.delete(`/api/admin/coupons/${id}`);
      setToast({ message: 'Coupon deleted', type: 'success' });
      fetchCoupons();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' }) : 'Never';

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Coupon Codes</h1><p className="page-subtitle">Create and manage discount coupons</p></div>
        <button className="btn btn-gold" onClick={() => openModal()}>+ New Coupon</button>
      </div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="section-card">
          {coupons.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Min Order</th>
                  <th>Uses</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(coupon => (
                  <tr key={coupon.id}>
                    <td><strong>{coupon.code}</strong></td>
                    <td>{coupon.discount_type === 'percent' ? `${coupon.discount_value}%` : `$${parseFloat(coupon.discount_value).toFixed(2)}`}</td>
                    <td>{coupon.min_order_amount ? `$${parseFloat(coupon.min_order_amount).toFixed(2)}` : '-'}</td>
                    <td>{coupon.uses_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ''}</td>
                    <td>{formatDate(coupon.expires_at)}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: coupon.active ? '#d1fae5' : '#fee2e2',
                        color: coupon.active ? '#065f46' : '#991b1b'
                      }}>
                        {coupon.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-small" onClick={() => openModal(coupon)}>Edit</button>
                        <button className="btn btn-small" onClick={() => handleToggleActive(coupon)}>{coupon.active ? 'Disable' : 'Enable'}</button>
                        <button className="btn btn-small btn-danger" onClick={() => handleDelete(coupon.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🎟️</div>
              <h3>No coupons yet</h3>
              <p>Create your first coupon code</p>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingCoupon ? 'Edit Coupon' : 'New Coupon'}</h2>
            <div className="form-group">
              <label className="form-label">Code</label>
              <input
                className="form-input"
                value={formData.code}
                onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SUMMER20"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Discount Type</label>
              <select className="form-input" value={formData.discountType} onChange={e => setFormData({ ...formData, discountType: e.target.value })}>
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Discount Value</label>
              <input
                className="form-input"
                type="number"
                step={formData.discountType === 'percent' ? '1' : '0.01'}
                value={formData.discountValue}
                onChange={e => setFormData({ ...formData, discountValue: e.target.value })}
                placeholder={formData.discountType === 'percent' ? 'e.g., 20' : 'e.g., 10.00'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Minimum Order Amount (optional)</label>
              <input
                className="form-input"
                type="number"
                step="0.01"
                value={formData.minOrderAmount}
                onChange={e => setFormData({ ...formData, minOrderAmount: e.target.value })}
                placeholder="e.g., 50.00"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Maximum Uses (optional)</label>
              <input
                className="form-input"
                type="number"
                value={formData.maxUses}
                onChange={e => setFormData({ ...formData, maxUses: e.target.value })}
                placeholder="Leave empty for unlimited"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Expires On (optional)</label>
              <input
                className="form-input"
                type="date"
                value={formData.expiresAt}
                onChange={e => setFormData({ ...formData, expiresAt: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminMembershipPlansPage() {
  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', price: '', interval: 'month', clinicCashAmount: '', discountPercent: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);
  const [tab, setTab] = useState('plans');

  const fetchData = () => {
    Promise.all([api.get('/api/admin/membership-plans'), api.get('/api/admin/memberships')])
      .then(([p, m]) => { setPlans(p); setMemberships(m); })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const openModal = (plan = null) => {
    setEditingPlan(plan);
    setFormData(plan ? { name: plan.name, description: plan.description || '', price: plan.price, interval: plan.interval, clinicCashAmount: plan.clinic_cash_amount, discountPercent: plan.discount_percent || '' } : { name: '', description: '', price: '', interval: 'month', clinicCashAmount: '', discountPercent: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.price || !formData.clinicCashAmount) {
      setToast({ message: 'Name, price, and Clinic Cash amount are required', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editingPlan) {
        await api.put(`/api/admin/membership-plans/${editingPlan.id}`, { ...formData, active: true });
      } else {
        await api.post('/api/admin/membership-plans', formData);
      }
      setToast({ message: editingPlan ? 'Plan updated' : 'Plan created', type: 'success' });
      setShowModal(false);
      fetchData();
    } catch (e) { setToast({ message: e.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this plan?')) return;
    try {
      await api.delete(`/api/admin/membership-plans/${id}`);
      setToast({ message: 'Plan deactivated', type: 'success' });
      fetchData();
    } catch (e) { setToast({ message: e.message, type: 'error' }); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Denver', year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Memberships</h1><p className="page-subtitle">Manage membership plans and subscribers</p></div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <button className={`btn ${tab === 'plans' ? 'btn-gold' : 'btn-secondary'}`} onClick={() => setTab('plans')}>Plans</button>
        <button className={`btn ${tab === 'subscribers' ? 'btn-gold' : 'btn-secondary'}`} onClick={() => setTab('subscribers')}>Subscribers ({memberships.filter(m => m.status === 'active').length})</button>
      </div>
      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : tab === 'plans' ? (
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Membership Plans</h2><button className="btn btn-gold btn-sm" onClick={() => openModal()}>+ Add Plan</button></div>
          {plans.length > 0 ? (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Price</th><th>Clinic Cash</th><th>Discount</th><th>Interval</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {plans.map(plan => (
                    <tr key={plan.id}>
                      <td><strong>{plan.name}</strong>{plan.description && <br />}{plan.description && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{plan.description}</span>}</td>
                      <td>${plan.price.toFixed(2)}</td>
                      <td style={{ color: 'var(--accent-gold)', fontWeight: 600 }}>${plan.clinic_cash_amount.toFixed(2)}</td>
                      <td style={{ color: plan.discount_percent > 0 ? '#059669' : 'var(--text-muted)' }}>{plan.discount_percent > 0 ? `${plan.discount_percent}%` : '-'}</td>
                      <td>{({month:"Monthly",six_week:"Every 6 Weeks",quarter:"Quarterly",year:"Yearly"})[plan.interval]}</td>
                      <td><span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '12px', background: plan.active ? '#d1fae5' : '#fee2e2', color: plan.active ? '#065f46' : '#991b1b' }}>{plan.active ? 'Active' : 'Inactive'}</span></td>
                      <td><div className="action-btns"><button className="action-btn edit" onClick={() => openModal(plan)}>Edit</button>{plan.active && <button className="action-btn delete" onClick={() => handleDelete(plan.id)}>Deactivate</button>}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="empty-state"><h3>No plans yet</h3><button className="btn btn-gold" onClick={() => openModal()}>Create First Plan</button></div>}
        </div>
      ) : (
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Active Subscribers</h2></div>
          {memberships.length > 0 ? (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead><tr><th>Member</th><th>Plan</th><th>Status</th><th>Renews</th></tr></thead>
                <tbody>
                  {memberships.map(m => (
                    <tr key={m.id}>
                      <td><strong>{m.user_name}</strong><br /><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{m.user_email}</span></td>
                      <td>{m.plan_name}<br /><span style={{ fontSize: '12px', color: 'var(--accent-gold)' }}>${parseFloat(m.clinic_cash_amount).toFixed(2)}/mo</span></td>
                      <td><span style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '12px', background: m.status === 'active' ? '#d1fae5' : '#fef3c7', color: m.status === 'active' ? '#065f46' : '#92400e' }}>{m.status}</span></td>
                      <td>{formatDate(m.current_period_end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="empty-state"><h3>No subscribers yet</h3></div>}
        </div>
      )}
      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">{editingPlan ? 'Edit' : 'Create'} Membership Plan</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div>
            <div className="admin-form-grid">
              <div className="form-group full-width"><label className="form-label">Plan Name *</label><input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Gold Membership" /></div>
              <div className="form-group full-width"><label className="form-label">Description</label><textarea className="form-input" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} placeholder="Optional description" /></div>
              <div className="form-group"><label className="form-label">Price ($) *</label><input type="number" step="0.01" className="form-input" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} placeholder="99.00" /></div>
              <div className="form-group"><label className="form-label">Billing Interval</label><select className="form-input" value={formData.interval} onChange={e => setFormData({ ...formData, interval: e.target.value })}><option value="month">Monthly</option><option value="six_week">Every 6 Weeks</option><option value="quarter">Quarterly</option><option value="year">Yearly</option></select></div>
              <div className="form-group"><label className="form-label">Clinic Cash Amount ($) *</label><input type="number" step="0.01" className="form-input" value={formData.clinicCashAmount} onChange={e => setFormData({ ...formData, clinicCashAmount: e.target.value })} placeholder="120.00" /><p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Amount credited each billing cycle</p></div>
              <div className="form-group"><label className="form-label">Discount on Products (%)</label><input type="number" step="1" min="0" max="100" className="form-input" value={formData.discountPercent} onChange={e => setFormData({ ...formData, discountPercent: e.target.value })} placeholder="10" /><p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Percent off products & services</p></div>
            </div>
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Plan'}</button></div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ==================== SCHEDULING SYSTEM COMPONENTS ====================

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function SchedulingTabs({ active }) {
  const tabs = [
    { path: '/admin/scheduling', label: 'Services' },
    { path: '/admin/scheduling/availability', label: 'Availability' },
    { path: '/admin/scheduling/time-blocks', label: 'Time Blocks' },
    { path: '/admin/scheduling/rooms', label: 'Rooms' },
  ];
  return (
    <div className="scheduling-tabs">
      {tabs.map(tab => (
        <Link key={tab.path} to={tab.path} className={`scheduling-tab ${active === tab.path ? 'active' : ''}`}>{tab.label}</Link>
      ))}
    </div>
  );
}

function AdminWebhookLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [filterSuccess, setFilterSuccess] = useState('');
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/admin/webhook-logs?limit=${limit}&offset=${page * limit}`;
      if (filterType) url += `&type=${filterType}`;
      if (filterSuccess !== '') url += `&success=${filterSuccess}`;
      const data = await api.get(url);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterSuccess]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Webhook Logs</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select className="form-input" style={{ width: 'auto', minWidth: '150px' }} value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0); }}>
            <option value="">All Types</option>
            <option value="ghl_appointment">GHL Appointment</option>
            <option value="reminder_24h_before">Reminder 24h</option>
            <option value="reminder_1h_before">Reminder 1h</option>
            <option value="reminder_follow_up">Reminder Follow-up</option>
          </select>
          <select className="form-input" style={{ width: 'auto', minWidth: '120px' }} value={filterSuccess} onChange={e => { setFilterSuccess(e.target.value); setPage(0); }}>
            <option value="">All Status</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={fetchLogs}>Refresh</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📡</div>
          <h3>No webhook logs yet</h3>
          <p>Webhook fires will appear here as appointments are booked and reminders are sent.</p>
        </div>
      ) : (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Service</th>
                  <th>Appt ID</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                      {new Date(log.created_at).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                    </td>
                    <td>
                      <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '12px', background: log.webhook_type.startsWith('ghl') ? '#ede9fe' : '#e0f2fe', color: log.webhook_type.startsWith('ghl') ? '#7c3aed' : '#0369a1' }}>
                        {log.webhook_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: '13px' }}>{log.service_name || '—'}</td>
                    <td style={{ fontSize: '13px' }}>{log.appointment_id || '—'}</td>
                    <td>
                      <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '12px', background: log.success ? '#d4edda' : '#f8d7da', color: log.success ? '#155724' : '#721c24' }}>
                        {log.success ? `${log.status_code || 'OK'}` : `Failed${log.status_code ? ` (${log.status_code})` : ''}`}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#64748b', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.error_message || (log.payload_summary ? JSON.stringify(log.payload_summary) : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', fontSize: '13px', color: '#64748b' }}>
            <span>Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
              <button className="btn btn-secondary btn-sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AdminApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['appointments:read', 'appointments:write']);
  const [newKeyRateLimit, setNewKeyRateLimit] = useState(60);
  const [createdKey, setCreatedKey] = useState(null);
  const [selectedKeyLog, setSelectedKeyLog] = useState(null);
  const [accessLog, setAccessLog] = useState({ log: [], total: 0, page: 1, totalPages: 1 });
  const [toast, setToast] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/admin/api-keys');
      setKeys(data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    try {
      const data = await api.post('/api/admin/api-keys', { name: newKeyName.trim(), scopes: newKeyScopes, rate_limit: newKeyRateLimit });
      setCreatedKey(data);
      setShowCreate(false);
      setNewKeyName('');
      fetchKeys();
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to create API key' });
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Revoke API key "${name}"? Any integration using this key will stop working.`)) return;
    try {
      await api.put(`/api/admin/api-keys/${id}/revoke`);
      setToast({ type: 'success', message: `Key "${name}" revoked` });
      fetchKeys();
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to revoke key' });
    }
  };

  const handleActivate = async (id, name) => {
    try {
      await api.put(`/api/admin/api-keys/${id}/activate`);
      setToast({ type: 'success', message: `Key "${name}" activated` });
      fetchKeys();
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to activate key' });
    }
  };

  const viewLog = async (keyId, page = 1) => {
    try {
      const data = await api.get(`/api/admin/api-keys/${keyId}/log?page=${page}&limit=50`);
      setAccessLog(data);
      setSelectedKeyLog(keyId);
    } catch (error) {
      console.error('Error fetching access log:', error);
    }
  };

  const toggleScope = (scope) => {
    setNewKeyScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setToast({ type: 'success', message: 'Copied to clipboard' });
    });
  };

  return (
    <div className="admin-page">
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: '8px', background: toast.type === 'success' ? '#d4edda' : '#f8d7da', color: toast.type === 'success' ? '#155724' : '#721c24', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: '14px' }} onClick={() => setToast(null)}>
          {toast.message}
        </div>
      )}

      {/* Created Key Modal — shown once after creation */}
      {createdKey && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '32px', maxWidth: '600px', width: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#059669' }}>API Key Created</h3>
            <p style={{ color: '#dc2626', fontWeight: 600 }}>Copy this key now. It will not be shown again.</p>
            <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all', marginBottom: '16px' }}>
              {createdKey.key}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary" onClick={() => copyToClipboard(createdKey.key)}>Copy Key</button>
              <button className="btn btn-secondary" onClick={() => setCreatedKey(null)}>Done</button>
            </div>
            <div style={{ marginTop: '16px', fontSize: '13px', color: '#64748b' }}>
              <strong>Name:</strong> {createdKey.name}<br />
              <strong>Scopes:</strong> {createdKey.scopes?.join(', ')}<br />
              <strong>Rate Limit:</strong> {createdKey.rate_limit} requests/minute
            </div>
          </div>
        </div>
      )}

      {/* Access Log Modal */}
      {selectedKeyLog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '800px', width: '95%', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Access Log ({accessLog.total} requests)</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedKeyLog(null)}>Close</button>
            </div>
            {accessLog.log.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>No requests logged yet.</p>
            ) : (
              <>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead><tr><th>Time</th><th>Method</th><th>Endpoint</th><th>Status</th><th>IP</th></tr></thead>
                    <tbody>
                      {accessLog.log.map((entry, i) => (
                        <tr key={i}>
                          <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{new Date(entry.created_at).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</td>
                          <td><span style={{ fontSize: '12px', fontWeight: 600, color: entry.method === 'GET' ? '#059669' : entry.method === 'PUT' ? '#d97706' : '#2563eb' }}>{entry.method}</span></td>
                          <td style={{ fontSize: '13px', fontFamily: 'monospace' }}>{entry.endpoint}</td>
                          <td><span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '12px', background: entry.status_code < 400 ? '#d4edda' : '#f8d7da', color: entry.status_code < 400 ? '#155724' : '#721c24' }}>{entry.status_code}</span></td>
                          <td style={{ fontSize: '12px', color: '#64748b' }}>{entry.ip_address}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {accessLog.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                    <button className="btn btn-secondary btn-sm" disabled={accessLog.page <= 1} onClick={() => viewLog(selectedKeyLog, accessLog.page - 1)}>Previous</button>
                    <span style={{ fontSize: '13px', color: '#64748b', padding: '4px 8px' }}>Page {accessLog.page} of {accessLog.totalPages}</span>
                    <button className="btn btn-secondary btn-sm" disabled={accessLog.page >= accessLog.totalPages} onClick={() => viewLog(selectedKeyLog, accessLog.page + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="page-header">
        <h2>API Keys</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create API Key</button>
      </div>

      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
        Manage API keys for external integrations (Retell Voice AI, partner systems). Each key provides authenticated access to the scheduling API.
      </p>

      {/* Create Key Form */}
      {showCreate && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ marginTop: 0 }}>Create New API Key</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px' }}>
            <div>
              <label className="form-label">Name (e.g., "Retell Voice AI")</label>
              <input className="form-input" value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Integration name" />
            </div>
            <div>
              <label className="form-label">Scopes</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newKeyScopes.includes('appointments:read')} onChange={() => toggleScope('appointments:read')} /> Read appointments
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newKeyScopes.includes('appointments:write')} onChange={() => toggleScope('appointments:write')} /> Write appointments
                </label>
              </div>
            </div>
            <div>
              <label className="form-label">Rate Limit (requests/minute)</label>
              <input className="form-input" type="number" value={newKeyRateLimit} onChange={e => setNewKeyRateLimit(parseInt(e.target.value) || 60)} style={{ width: '120px' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!newKeyName.trim() || newKeyScopes.length === 0}>Create Key</button>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Keys Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
      ) : keys.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔑</div>
          <h3>No API keys yet</h3>
          <p>Create an API key to allow external systems like Retell Voice AI to access the scheduling API.</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key Prefix</th>
                <th>Scopes</th>
                <th>Rate Limit</th>
                <th>Status</th>
                <th>Requests</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id}>
                  <td style={{ fontWeight: 500 }}>{k.name}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{k.key_prefix}...</td>
                  <td>
                    {(k.scopes || []).map(s => (
                      <span key={s} style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: s.includes('write') ? '#fef3c7' : '#e0f2fe', color: s.includes('write') ? '#92400e' : '#0369a1', marginRight: '4px' }}>{s}</span>
                    ))}
                  </td>
                  <td style={{ fontSize: '13px' }}>{k.rate_limit}/min</td>
                  <td>
                    <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '12px', background: k.active ? '#d4edda' : '#f8d7da', color: k.active ? '#155724' : '#721c24' }}>
                      {k.active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td style={{ fontSize: '13px' }}>{k.total_requests || 0}</td>
                  <td style={{ fontSize: '13px', color: '#64748b' }}>
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : 'Never'}
                  </td>
                  <td style={{ fontSize: '13px', color: '#64748b' }}>
                    {new Date(k.created_at).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => viewLog(k.id)}>Log</button>
                      {k.active ? (
                        <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }} onClick={() => handleRevoke(k.id, k.name)}>Revoke</button>
                      ) : (
                        <button className="btn btn-sm" style={{ background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' }} onClick={() => handleActivate(k.id, k.name)}>Activate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* API Documentation Reference */}
      <div style={{ marginTop: '32px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
        <h3 style={{ marginTop: 0 }}>API Endpoint Reference</h3>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>All endpoints require the <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px' }}>X-API-Key</code> header.</p>
        <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
          {[
            { method: 'GET', path: '/api/v1/services', scope: 'read', desc: 'List bookable services' },
            { method: 'GET', path: '/api/v1/services/:id/staff', scope: 'read', desc: 'List staff for a service' },
            { method: 'GET', path: '/api/v1/availability?service_id=X&date=Y', scope: 'read', desc: 'Get available time slots' },
            { method: 'POST', path: '/api/v1/appointments', scope: 'write', desc: 'Book an appointment' },
            { method: 'GET', path: '/api/v1/appointments/lookup?phone=X', scope: 'read', desc: 'Find appointments by phone/email' },
            { method: 'PUT', path: '/api/v1/appointments/:id/cancel', scope: 'write', desc: 'Cancel an appointment' },
            { method: 'PUT', path: '/api/v1/appointments/:id/reschedule', scope: 'write', desc: 'Reschedule an appointment' },
          ].map((ep, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontWeight: 600, fontSize: '11px', width: '40px', color: ep.method === 'GET' ? '#059669' : ep.method === 'PUT' ? '#d97706' : '#2563eb' }}>{ep.method}</span>
              <code style={{ fontFamily: 'monospace', flex: 1 }}>{ep.path}</code>
              <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: ep.scope === 'write' ? '#fef3c7' : '#e0f2fe', color: ep.scope === 'write' ? '#92400e' : '#0369a1' }}>{ep.scope}</span>
              <span style={{ color: '#64748b', fontSize: '12px' }}>{ep.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminReportIssuePage() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [toast, setToast] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState('submit');

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const url = filterStatus ? `/api/admin/submissions?status=${filterStatus}` : '/api/admin/submissions';
      const [subsData, reviewsData] = await Promise.all([
        api.get(url),
        api.get('/api/admin/submission-reviews').catch(() => [])
      ]);
      setSubmissions(Array.isArray(subsData) ? subsData : subsData.submissions || []);
      setReviews(Array.isArray(reviewsData) ? reviewsData : []);
    } catch (e) { console.error('Fetch submissions error:', e); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setToast({ type: 'error', message: 'Title is required' }); return; }
    setSubmitting(true);
    try {
      await api.post('/api/admin/submissions', { title: title.trim(), description: description.trim() });
      setToast({ type: 'success', message: 'Issue submitted successfully!' });
      setTitle(''); setDescription('');
      fetchSubmissions();
    } catch (e) { setToast({ type: 'error', message: e.message || 'Failed to submit' }); }
    finally { setSubmitting(false); }
  };

  const statusBadge = (status) => {
    const colors = { pending: { bg: '#fef3c7', color: '#92400e' }, in_progress: { bg: '#dbeafe', color: '#1e40af' }, resolved: { bg: '#d1fae5', color: '#065f46' }, closed: { bg: '#f3f4f6', color: '#6b7280' } };
    const c = colors[status] || colors.pending;
    return <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: c.bg, color: c.color, fontWeight: 600 }}>{(status || 'pending').replace('_', ' ')}</span>;
  };

  return (
    <div className="admin-page">
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px', borderRadius: '8px', background: toast.type === 'success' ? '#d4edda' : '#f8d7da', color: toast.type === 'success' ? '#155724' : '#721c24', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: '14px', cursor: 'pointer' }} onClick={() => setToast(null)}>
          {toast.message}
        </div>
      )}

      <div className="page-header">
        <h2>Report an Issue</h2>
      </div>

      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--border-color)', marginBottom: '20px' }}>
        {[{ id: 'submit', label: 'Submit & Recent' }, { id: 'all', label: 'All Issues & Statuses' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: activeTab === tab.id ? 600 : 400, color: activeTab === tab.id ? 'var(--accent-gold)' : 'var(--text-muted)', borderBottom: activeTab === tab.id ? '2px solid var(--accent-gold)' : '2px solid transparent', marginBottom: '-2px' }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'submit' && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Submit Form */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '16px' }}>New Issue</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Submitted by</label>
              <input className="form-input" value={user?.name || user?.email || ''} disabled style={{ background: '#f8fafc' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief summary of the issue" disabled={submitting} />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Steps to reproduce, what you expected, what happened instead..." rows={5} disabled={submitting} style={{ resize: 'vertical' }} />
            </div>
            <button className="btn btn-primary" type="submit" disabled={submitting || !title.trim()}>
              {submitting ? 'Submitting...' : 'Submit Issue'}
            </button>
          </form>
        </div>

        {/* Submissions List */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Past Submissions</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select className="form-input" style={{ width: 'auto', minWidth: '120px', margin: 0 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
              <button className="btn btn-secondary btn-sm" onClick={fetchSubmissions}>Refresh</button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
          ) : submissions.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>No submissions yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '500px', overflowY: 'auto' }}>
              {submissions.map(s => (
                <div key={s.id || s._id} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <strong style={{ fontSize: '14px' }}>{s.title}</strong>
                    {statusBadge(s.status)}
                  </div>
                  {s.description && <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 6px' }}>{s.description}</p>}
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {s.name && <span>{s.name} · </span>}
                    {s.createdAt || s.created_at ? new Date(s.createdAt || s.created_at).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>}

      {activeTab === 'all' && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>All Issues</h3>
            <button className="btn btn-secondary btn-sm" onClick={fetchSubmissions}>Refresh</button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Submitted By</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Response</th>
                    <th>Reviewed</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(s => {
                    const review = reviews.find(r => r.submission_id === (s.id || s._id));
                    const status = review?.status || s.status || 'pending';
                    const priorityColors = { high: { bg: '#fee2e2', color: '#dc2626' }, medium: { bg: '#fef3c7', color: '#92400e' }, low: { bg: '#e0f2fe', color: '#0369a1' } };
                    const statusColors = { pending: { bg: '#fef3c7', color: '#92400e' }, approved: { bg: '#d1fae5', color: '#065f46' }, rejected: { bg: '#fee2e2', color: '#dc2626' }, in_progress: { bg: '#dbeafe', color: '#1e40af' }, resolved: { bg: '#d1fae5', color: '#065f46' } };
                    const sc = statusColors[status] || statusColors.pending;
                    const pc = review?.priority ? (priorityColors[review.priority] || {}) : {};
                    return (
                      <tr key={s.id || s._id}>
                        <td style={{ fontWeight: 500 }}>{s.title}</td>
                        <td style={{ fontSize: '13px' }}>{s.name || '—'}</td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{s.createdAt || s.created_at ? new Date(s.createdAt || s.created_at).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric' }) : '—'}</td>
                        <td><span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: sc.bg, color: sc.color, fontWeight: 600 }}>{status.replace('_', ' ')}</span></td>
                        <td>{review?.priority ? <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: pc.bg, color: pc.color, fontWeight: 600 }}>{review.priority}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{review?.response_message || s.description || '—'}</td>
                        <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{review?.reviewed_at ? new Date(review.reviewed_at).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric' }) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminRemindersPage() {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/scheduling-services'),
      api.get('/api/admin/reminder-stats')
    ]).then(([servicesData, statsData]) => {
      setServices(servicesData.filter(s => s.active));
      setStats(statsData);
      if (servicesData.length > 0) {
        setSelectedService(servicesData[0].id);
      }
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedService) {
      Promise.all([
        api.get(`/api/admin/reminder-templates/${selectedService}`),
        api.get(`/api/admin/reminder-config/${selectedService}`)
      ]).then(([templatesData, configData]) => {
        setTemplates(templatesData);
        setConfig(configData);
      }).catch(console.error);
    }
  }, [selectedService]);

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/reminder-templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        body: editingTemplate.body,
        active: editingTemplate.active
      });
      setToast({ message: 'Template saved', type: 'success' });
      setTemplates(templates.map(t => t.id === editingTemplate.id ? editingTemplate : t));
      setEditingTemplate(null);
    } catch (e) {
      setToast({ message: 'Failed to save template', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/api/admin/reminder-templates/${template.id}`, { active: !template.active });
      setTemplates(templates.map(t => t.id === template.id ? { ...t, active: !t.active } : t));
      setToast({ message: `Template ${!template.active ? 'enabled' : 'disabled'}`, type: 'success' });
    } catch (e) {
      setToast({ message: 'Failed to update template', type: 'error' });
    }
  };

  const handleUpdateConfig = async (cfg, field, value) => {
    try {
      await api.put(`/api/admin/reminder-config/${cfg.id}`, { [field]: value });
      setConfig(config.map(c => c.id === cfg.id ? { ...c, [field]: value } : c));
      setToast({ message: 'Config updated', type: 'success' });
    } catch (e) {
      setToast({ message: 'Failed to update config', type: 'error' });
    }
  };

  const handlePreview = async (template) => {
    try {
      const result = await api.post('/api/admin/reminder-templates/preview', {
        subject: template.subject,
        body: template.body
      });
      setPreview(result);
    } catch (e) {
      setToast({ message: 'Failed to generate preview', type: 'error' });
    }
  };

  const messageTypes = ['confirmation', '24hr_reminder', '1hr_reminder', 'followup', 'cancellation'];
  const messageTypeLabels = {
    'confirmation': 'Confirmation',
    '24hr_reminder': '24-Hour Reminder',
    '1hr_reminder': '1-Hour Reminder',
    'followup': 'Follow-up',
    'cancellation': 'Cancellation'
  };

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Appointment Reminders</h1>
        <p className="page-subtitle">Manage reminder templates and timing</p>
      </div>

      {stats && (
        <div className="stats-grid" style={{ marginBottom: '20px' }}>
          <div className="stat-card">
            <div className="stat-value">{stats.pending_count}</div>
            <div className="stat-label">Pending Reminders</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.by_status?.find(s => s.status === 'sent')?.count || 0}</div>
            <div className="stat-label">Sent (30 days)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.by_status?.find(s => s.status === 'failed')?.count || 0}</div>
            <div className="stat-label">Failed (30 days)</div>
          </div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">
          <h2 className="section-title">Templates</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select className="form-input" value={selectedService || ''} onChange={e => setSelectedService(e.target.value)} style={{ maxWidth: '250px' }}>
              {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button
              className="btn btn-secondary btn-sm"
              title="Add any missing reminder templates and configs for this service. Does NOT send any emails."
              onClick={async () => {
                if (!selectedService) return;
                if (!window.confirm('Reseed default reminder templates and configs for this service? This is safe — no emails will be sent. Existing custom templates will not be overwritten.')) return;
                try {
                  const result = await api.post(`/api/admin/scheduling-services/${selectedService}/reseed-reminders`);
                  setToast({ message: result.message || 'Reseeded successfully', type: 'success' });
                  // Reload templates and config
                  const [t, c] = await Promise.all([
                    api.get(`/api/admin/reminder-templates/${selectedService}`),
                    api.get(`/api/admin/reminder-config/${selectedService}`)
                  ]);
                  setTemplates(t);
                  setConfig(c);
                } catch (e) {
                  setToast({ message: e.message || 'Reseed failed', type: 'error' });
                }
              }}
            >Reseed Defaults</button>
          </div>
        </div>

        <div className="reminders-grid">
          {messageTypes.map(msgType => {
            const smsTemplate = templates.find(t => t.message_type === msgType && t.channel === 'sms');
            const emailTemplate = templates.find(t => t.message_type === msgType && t.channel === 'email');
            const cfg = config.find(c => c.message_type === msgType && c.channel === 'sms');

            return (
              <div key={msgType} className="reminder-type-card">
                <div className="reminder-type-header">
                  <h3>{messageTypeLabels[msgType]}</h3>
                  {cfg && (
                    <div className="timing-badge">
                      {cfg.offset_hours === 0 ? 'Immediate' :
                       cfg.offset_hours > 0 ? `+${cfg.offset_hours}h after` :
                       `${Math.abs(cfg.offset_hours)}h before`}
                    </div>
                  )}
                </div>

                <div className="template-channels">
                  {smsTemplate && (
                    <div className={`template-item ${!smsTemplate.active ? 'inactive' : ''}`}>
                      <div className="template-item-header">
                        <span className="channel-badge sms">SMS</span>
                        <div className="template-actions">
                          <button className="btn-icon" onClick={() => handlePreview(smsTemplate)} title="Preview">👁</button>
                          <button className="btn-icon" onClick={() => setEditingTemplate(smsTemplate)} title="Edit">✏️</button>
                          <button className="btn-icon" onClick={() => handleToggleActive(smsTemplate)} title={smsTemplate.active ? 'Disable' : 'Enable'}>
                            {smsTemplate.active ? '✓' : '○'}
                          </button>
                        </div>
                      </div>
                      <div className="template-preview">{smsTemplate.body?.substring(0, 80)}...</div>
                    </div>
                  )}

                  {emailTemplate && (
                    <div className={`template-item ${!emailTemplate.active ? 'inactive' : ''}`}>
                      <div className="template-item-header">
                        <span className="channel-badge email">Email</span>
                        <div className="template-actions">
                          <button className="btn-icon" onClick={() => handlePreview(emailTemplate)} title="Preview">👁</button>
                          <button className="btn-icon" onClick={() => setEditingTemplate(emailTemplate)} title="Edit">✏️</button>
                          <button className="btn-icon" onClick={() => handleToggleActive(emailTemplate)} title={emailTemplate.active ? 'Disable' : 'Enable'}>
                            {emailTemplate.active ? '✓' : '○'}
                          </button>
                        </div>
                      </div>
                      <div className="template-preview">{emailTemplate.subject}</div>
                    </div>
                  )}
                </div>

                {cfg && (
                  <div className="timing-config">
                    <label>Timing:</label>
                    <select value={cfg.offset_hours} onChange={e => handleUpdateConfig(cfg, 'offset_hours', parseInt(e.target.value))}>
                      <option value="0">Immediate</option>
                      <option value="-1">1 hour before</option>
                      <option value="-2">2 hours before</option>
                      <option value="-4">4 hours before</option>
                      <option value="-24">24 hours before</option>
                      <option value="-48">48 hours before</option>
                      <option value="1">1 hour after</option>
                      <option value="24">24 hours after</option>
                      <option value="48">48 hours after</option>
                    </select>
                    <label className="toggle-label">
                      <input type="checkbox" checked={cfg.active} onChange={e => handleUpdateConfig(cfg, 'active', e.target.checked)} />
                      Active
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {editingTemplate && (
        <div className="modal-overlay" onClick={() => !saving && setEditingTemplate(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Edit {editingTemplate.channel.toUpperCase()} Template</h2>
              <button className="modal-close" onClick={() => setEditingTemplate(null)}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">Message Type</label>
              <input type="text" className="form-input" value={messageTypeLabels[editingTemplate.message_type]} disabled />
            </div>
            {editingTemplate.channel === 'email' && (
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input type="text" className="form-input" value={editingTemplate.subject || ''} onChange={e => setEditingTemplate({ ...editingTemplate, subject: e.target.value })} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Body</label>
              <textarea className="form-input" rows={editingTemplate.channel === 'email' ? 10 : 4} value={editingTemplate.body || ''} onChange={e => setEditingTemplate({ ...editingTemplate, body: e.target.value })} />
            </div>
            <div className="template-variables">
              <strong>Available Variables:</strong> {"{{first_name}}"}, {"{{patient_name}}"}, {"{{service_name}}"}, {"{{provider_name}}"}, {"{{appointment_display}}"}, {"{{appointment_date}}"}, {"{{appointment_time}}"}, {"{{manage_url}}"}, {"{{location}}"}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => handlePreview(editingTemplate)}>Preview</button>
              <button className="btn btn-secondary" onClick={() => setEditingTemplate(null)}>Cancel</button>
              <button className="btn btn-gold" onClick={handleSaveTemplate} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Template Preview</h2>
              <button className="modal-close" onClick={() => setPreview(null)}>×</button>
            </div>
            {preview.subject && (
              <div className="form-group">
                <label className="form-label">Subject</label>
                <div className="preview-content">{preview.subject}</div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Body</label>
              <div className="preview-content" style={{ whiteSpace: 'pre-wrap' }}>{preview.body}</div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}


// Patient Call Reminder Form — scaffolded route. When Bree/Anshul provide the hosted form URL,
// set PATIENT_REMINDER_FORM_URL below and redeploy the frontend. No other change required.
const PATIENT_REMINDER_FORM_URL = ''; // e.g., 'https://reminder.ascenttrim.com/'

function AdminPatientReminderPage() {
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Patient Call Reminders</h1>
          <p className="page-subtitle">Log a callback request or follow-up for a patient</p>
        </div>
      </div>
      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        {PATIENT_REMINDER_FORM_URL ? (
          <iframe
            src={PATIENT_REMINDER_FORM_URL}
            title="Patient Call Reminder Form"
            style={{ width: '100%', height: '80vh', border: 'none', display: 'block' }}
            sandbox="allow-forms allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <h3 style={{ margin: 0, marginBottom: '8px' }}>Form embed pending</h3>
            <p style={{ color: 'var(--text-muted)', maxWidth: '520px', margin: '0 auto', lineHeight: '1.5' }}>
              This page is wired up and ready. The patient call reminder form will appear here once the hosted URL is provided.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: '12px', maxWidth: '520px', margin: '16px auto 0', lineHeight: '1.5' }}>
              <strong>To activate:</strong> set <code style={{ background: 'var(--cream)', padding: '2px 6px', borderRadius: '4px' }}>PATIENT_REMINDER_FORM_URL</code> in <code style={{ background: 'var(--cream)', padding: '2px 6px', borderRadius: '4px' }}>client/src/App.js</code> to the hosted form URL, then rebuild and restart the portal frontend. The route, nav entry, and permissions are already in place.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


// Pick the most readable calendar view for the current viewport.
// Phones: list (easy vertical scan). Tablets: single-day grid (full time detail per day).
// Desktops: week grid (current default).
// Use the minimum of three width sources to handle mobile Safari's quirks where one
// can report stale values during the first paint.
function pickCalendarView() {
  if (typeof window === 'undefined') return 'timeGridWeek';
  const inner = window.innerWidth || 9999;
  const client = (document && document.documentElement && document.documentElement.clientWidth) || 9999;
  const w = Math.min(inner, client);
  if (w < 768) return 'listWeek';
  if (w < 1024) return 'timeGridDay';
  return 'timeGridWeek';
}

function AdminCalendarPage() {
  const calendarRef = useRef(null);
  const [calendarView, setCalendarView] = useState(pickCalendarView);

  // Defense-in-depth: after the calendar mounts, explicitly call changeView()
  // with the correct viewport view. Some browsers (mobile Safari especially) can
  // ignore or race the initialView prop on the first render.
  useEffect(() => {
    const apply = () => {
      const target = pickCalendarView();
      if (calendarRef.current) {
        try { calendarRef.current.getApi().changeView(target); } catch (_) {}
      }
      setCalendarView(target);
    };
    apply();
    const t1 = setTimeout(apply, 50);
    const t2 = setTimeout(apply, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    let timer = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const next = pickCalendarView();
        setCalendarView(prev => {
          if (prev !== next && calendarRef.current) {
            try { calendarRef.current.getApi().changeView(next); } catch (_) {}
          }
          return next;
        });
      }, 200);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      clearTimeout(timer);
    };
  }, []);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [patients, setPatients] = useState([]);
  const [formData, setFormData] = useState({ patient_id: '', patient_name: '', patient_email: '', patient_phone: '', staff_id: '', service_id: '', start_dt: '', notes: '', internal_notes: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [outcomeData, setOutcomeData] = useState({ outcome: '', outcome_notes: '', enrollment_intent: '' });
  const [savingOutcome, setSavingOutcome] = useState(false);
  // Recurring-appointment UI state (create-only; edit path never uses these).
  const [recurEnabled, setRecurEnabled] = useState(false);
  const [recurPattern, setRecurPattern] = useState('weekly');
  const [recurCount, setRecurCount] = useState(4);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const fetchEvents = async (start, end) => {
    try {
      let url = `/api/admin/calendar/events?start=${start}&end=${end}`;
      if (selectedStaff) url += `&staff_id=${selectedStaff}`;
      const data = await api.get(url);
      setEvents(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/staff'),
      api.get('/api/admin/scheduling-services'),
      api.get('/api/admin/users')
    ]).then(([staffData, servicesData, usersData]) => {
      setStaff(staffData);
      setServices(servicesData.filter(s => s.active));
      setPatients(usersData.filter(u => u.role === 'patient'));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const start = calendarApi.view.activeStart.toISOString();
      const end = calendarApi.view.activeEnd.toISOString();
      fetchEvents(start, end);
    }
  }, [selectedStaff]);

  const handleDateClick = (info) => {
    const clickedDate = new Date(info.dateStr);
    const now = new Date();
    if (clickedDate < now.setHours(0,0,0,0)) return;
    setSelectedEvent(null);
    setSelectedDate(info.dateStr);
    const dateTime = info.dateStr.includes('T') ? info.dateStr.slice(0, 16) : `${info.dateStr}T09:00`;
    setFormData({ patient_id: '', patient_name: '', patient_email: '', patient_phone: '', staff_id: selectedStaff || '', service_id: '', start_dt: dateTime, notes: '', internal_notes: '' });
    setRecurEnabled(false); setRecurPattern('weekly'); setRecurCount(4);
    setPreviewData(null); setPreviewError('');
    setShowModal(true);
  };

  const handleEventClick = async (info) => {
    const eventId = info.event.id;
    // External Google Calendar events are read-only
    if (eventId.startsWith('gcal-')) {
      setToast({ message: `Google Calendar: ${info.event.title.replace('📅 ', '')} (${info.event.extendedProps.staffName})`, type: 'info' });
      return;
    }
    if (eventId.startsWith('apt-')) {
      const aptId = eventId.replace('apt-', '');
      try {
        const appointments = await api.get(`/api/admin/appointments?start_date=${info.event.startStr}&end_date=${info.event.endStr}`);
        const apt = appointments.find(a => a.id === parseInt(aptId));
        if (apt) {
          setSelectedEvent(apt);
          setFormData({
            patient_id: apt.patient_id || '',
            patient_name: apt.patient_name,
            patient_email: apt.patient_email || '',
            patient_phone: apt.patient_phone || '',
            staff_id: apt.staff_id,
            service_id: apt.service_id,
            start_dt: apt.start_dt.slice(0, 16),
            notes: apt.notes || '',
            internal_notes: apt.internal_notes || ''
          });
          setOutcomeData({
            outcome: apt.outcome || '',
            outcome_notes: apt.outcome_notes || '',
            enrollment_intent: apt.enrollment_intent || ''
          });
          setRecurEnabled(false); setRecurPattern('weekly'); setRecurCount(4);
          setPreviewData(null); setPreviewError('');
          setShowModal(true);
        }
      } catch (e) { console.error(e); }
    }
  };

  const handlePatientSelect = (patientId, patient) => {
    if (patientId && patient) {
      setFormData(prev => ({ ...prev, patient_id: patientId, patient_name: patient.name, patient_email: patient.email, patient_phone: patient.phone || '' }));
    } else {
      setFormData(prev => ({ ...prev, patient_id: '', patient_name: '', patient_email: '', patient_phone: '' }));
    }
  };

  const handlePreviewSeries = async () => {
    setPreviewError('');
    setPreviewData(null);
    if (!formData.staff_id || !formData.service_id || !formData.start_dt) {
      setPreviewError('Fill staff, service, and date/time before previewing.');
      return;
    }
    const parsedCount = Math.max(1, Math.min(26, parseInt(recurCount, 10) || 1));
    setPreviewing(true);
    try {
      const data = await api.post('/api/admin/appointments/preview-series', {
        staff_id: parseInt(formData.staff_id, 10),
        service_id: parseInt(formData.service_id, 10),
        start_dt: formData.start_dt,
        recurrence: { pattern: recurPattern, count: parsedCount }
      });
      setPreviewData(data);
    } catch (error) {
      setPreviewError(error.message || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  /**
   * Whether this appointment can reach the patient at all.
   *
   * Staff-booked appointments could be saved with a name and nothing else, and
   * 58% of them were: of 1,309 admin bookings in a 90-day sample, 761 had
   * neither an email nor a phone. Those cannot send a confirmation, a 24-hour
   * reminder or a 1-hour reminder — the send is skipped and the row is marked
   * failed, silently. It is the single largest cause of the ~40% reminder
   * failure rate, and from the front desk it reads as "reminders are broken".
   *
   * ONE contact method is enough — either channel reaches the patient. Notably
   * zero appointments were missing a phone while having an email, so requiring
   * "at least one" blocks essentially the whole failing population without
   * obstructing the 64 phone-only bookings that do work.
   *
   * Internal/admin service types are exempt: they have no patient to notify and
   * their reminder_config is already deactivated.
   */
  const INTERNAL_SERVICE_IDS = new Set(['15', '18', '19', '20', '21', '25']);
  const contactGap = () => {
    const isInternal = INTERNAL_SERVICE_IDS.has(String(formData.service_id));
    const missingPhone = !String(formData.patient_phone || '').trim();
    const missingEmail = !String(formData.patient_email || '').trim();
    return {
      isInternal,
      missingPhone,
      missingEmail,
      // Unreachable only when BOTH are absent.
      unreachable: !isInternal && missingPhone && missingEmail
    };
  };

  const handleSave = async () => {
    if (!formData.patient_name || !formData.staff_id || !formData.service_id || !formData.start_dt) {
      setToast({ message: 'Please fill in required fields', type: 'error' }); return;
    }
    // Block CREATE without any contact method. Deliberately not blocking EDIT:
    // the 155 existing unreachable appointments still need to be cancellable and
    // editable, and blocking there would trap staff in a form they cannot leave.
    // The banner below flags them instead.
    if (!selectedEvent && contactGap().unreachable) {
      setToast({
        message: 'Add an email or phone — without one this patient gets no confirmation and no reminders.',
        type: 'error'
      });
      return;
    }
    // Recurring create requires a successful preview with zero conflicts.
    if (!selectedEvent && recurEnabled) {
      if (!previewData) {
        setToast({ message: 'Click Preview Series before creating.', type: 'error' }); return;
      }
      if (previewData.any_conflict) {
        setToast({ message: 'Series has conflicts — resolve before creating.', type: 'error' }); return;
      }
    }
    setSaving(true);
    try {
      if (selectedEvent) {
        await api.put(`/api/admin/appointments/${selectedEvent.id}`, formData);
        setToast({ message: 'Appointment updated!', type: 'success' });
      } else if (recurEnabled) {
        const parsedCount = Math.max(1, Math.min(26, parseInt(recurCount, 10) || 1));
        const res = await api.post('/api/admin/appointments', {
          ...formData,
          recurrence: { pattern: recurPattern, count: parsedCount }
        });
        setToast({ message: `Created series of ${res.total} appointments`, type: 'success' });
      } else {
        await api.post('/api/admin/appointments', formData);
        setToast({ message: 'Appointment created!', type: 'success' });
      }
      setShowModal(false);
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        fetchEvents(calendarApi.view.activeStart.toISOString(), calendarApi.view.activeEnd.toISOString());
      }
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleSaveOutcome = async () => {
    if (!selectedEvent || !outcomeData.outcome) return;
    setSavingOutcome(true);
    try {
      const payload = {
        outcome: outcomeData.outcome,
        outcome_notes: outcomeData.outcome_notes || null,
        enrollment_intent: outcomeData.outcome === 'showed' ? (outcomeData.enrollment_intent || null) : null
      };
      const method = selectedEvent.outcome ? 'patch' : 'post';
      await api[method](`/api/admin/appointments/${selectedEvent.id}/outcome`, payload);
      setToast({ message: 'Outcome saved!', type: 'success' });
      setSelectedEvent({ ...selectedEvent, ...payload, outcome_recorded_at: new Date().toISOString() });
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        fetchEvents(calendarApi.view.activeStart.toISOString(), calendarApi.view.activeEnd.toISOString());
      }
    } catch (error) {
      setToast({ message: error.message || 'Failed to save outcome', type: 'error' });
    } finally {
      setSavingOutcome(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedEvent) return;
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await api.put(`/api/admin/appointments/${selectedEvent.id}/status`, { status: 'cancelled' });
      setToast({ message: 'Appointment cancelled!', type: 'success' });
      setShowModal(false);
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        fetchEvents(calendarApi.view.activeStart.toISOString(), calendarApi.view.activeEnd.toISOString());
      }
    } catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  const handleDatesSet = (info) => { fetchEvents(info.startStr, info.endStr); };

  const handleSyncCalendars = async () => {
    setSyncing(true);
    try {
      const result = await api.post('/api/admin/calendar/sync');
      setToast({ message: `Synced ${result.synced} events from ${result.staff} staff calendars`, type: 'success' });
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        fetchEvents(calendarApi.view.activeStart.toISOString(), calendarApi.view.activeEnd.toISOString());
      }
    } catch (error) { setToast({ message: error.message || 'Sync failed', type: 'error' }); }
    finally { setSyncing(false); }
  };

  if (loading) return <div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div>;

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Calendar</h1><p className="page-subtitle">Manage appointments and schedules</p></div>
      <div className="section-card">
        <div className="section-header">
          <div className="calendar-filters">
            <select className="form-input" value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} style={{ maxWidth: '200px' }}>
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '15px', fontSize: '14px', cursor: 'pointer' }}>
              <input type="checkbox" checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)} />
              Show cancelled
            </label>
          </div>
          <button className="btn btn-secondary" onClick={handleSyncCalendars} disabled={syncing} style={{ marginLeft: 'auto' }}>
            {syncing ? 'Syncing...' : 'Sync Google Calendars'}
          </button>
        </div>
        <div className="calendar-wrapper">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={calendarView}
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }}
            events={showCancelled ? events : events.filter(e => e.extendedProps?.status !== "cancelled")}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            slotMinTime="07:00:00"
            slotMaxTime="20:00:00"
            allDaySlot={false}
            height="auto"
            nowIndicator={true}
            timeZone="America/Denver"
            noEventsContent="No appointments to display."
          />
        </div>
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{selectedEvent ? 'Edit Appointment' : 'New Appointment'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button>
            </div>
            {selectedEvent && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '10px 0 16px', borderBottom: '1px solid var(--border-color)', marginBottom: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                <span>
                  <strong>Booked via:</strong>{' '}
                  <span className={`status-badge ${selectedEvent.created_by_type === 'admin' ? 'status-active' : selectedEvent.created_by_type === 'patient' ? 'status-pending' : 'status-info'}`}>
                    {selectedEvent.created_by_type === 'admin' ? 'Staff' : selectedEvent.created_by_type === 'patient' ? 'Patient' : selectedEvent.created_by_type === 'public' ? 'Public' : selectedEvent.created_by_type || 'Unknown'}
                  </span>
                  {selectedEvent.created_by_name && ` by ${selectedEvent.created_by_name}`}
                </span>
                {selectedEvent.referrer_url && (
                  <span><strong>From:</strong> {(() => { try { return new URL(selectedEvent.referrer_url).hostname + new URL(selectedEvent.referrer_url).pathname; } catch { return selectedEvent.referrer_url; } })()}</span>
                )}
                <span><strong>Created:</strong> {new Date(selectedEvent.created_at).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </div>
            )}
            <div className="admin-form-grid">
              <div className="form-group full-width">
                <label className="form-label">Select Patient (optional)</label>
                <SearchableSelect
                  options={patients}
                  value={formData.patient_id}
                  onChange={handlePatientSelect}
                  placeholder="Search patients or leave blank for walk-in..."
                  disabled={saving}
                  displayKey="name"
                  valueKey="id"
                />
              </div>
              <div className="form-group"><label className="form-label">Patient Name *</label><input type="text" className="form-input" value={formData.patient_name} onChange={e => setFormData({ ...formData, patient_name: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={formData.patient_email} onChange={e => setFormData({ ...formData, patient_email: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input type="tel" className="form-input" value={formData.patient_phone} onChange={e => setFormData({ ...formData, patient_phone: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Staff *</label><select className="form-input" value={formData.staff_id} onChange={e => setFormData({ ...formData, staff_id: e.target.value })} disabled={saving}><option value="">Select...</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "15px", fontSize: "14px", cursor: "pointer" }}><input type="checkbox" checked={showCancelled} onChange={e => setShowCancelled(e.target.checked)} /> Show cancelled</label></div>
              <div className="form-group"><label className="form-label">Service *</label><select className="form-input" value={formData.service_id} onChange={e => setFormData({ ...formData, service_id: e.target.value })} disabled={saving}><option value="">Select...</option>{services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>)}</select></div>
              <div className="form-group"><label className="form-label">Date/Time *</label><input type="datetime-local" className="form-input" value={formData.start_dt} onChange={e => setFormData({ ...formData, start_dt: e.target.value })} disabled={saving} /></div>
              <div className="form-group full-width"><label className="form-label">Notes (visible to patient)</label><textarea className="form-input" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} disabled={saving} rows={2} /></div>
              <div className="form-group full-width"><label className="form-label">Internal Notes (staff only)</label><textarea className="form-input" value={formData.internal_notes} onChange={e => setFormData({ ...formData, internal_notes: e.target.value })} disabled={saving} rows={2} /></div>
            </div>
            {/* Fix B — Soft warning when a new booking will fail to reach the patient.
                Skipped for the 6 internal-only services (id 15/18/19/20/21/25) whose
                reminder_config is already deactivated, since the warning would be
                redundant noise there. */}
            {(() => {
              // Shown on the EDIT path too. It used to bail on `selectedEvent`,
              // which is why 155 already-booked patients sat unreachable with
              // nothing on screen ever saying so — staff only saw the warning
              // while creating, and only until they clicked past it.
              const gap = contactGap();
              if (gap.isInternal) return null;
              if (!selectedEvent && (!formData.staff_id || !formData.service_id)) return null;
              if (!gap.missingPhone && !gap.missingEmail) return null;

              // Both missing = the patient cannot be reached at all. Red, and on
              // create the Save button refuses. One missing = one channel is
              // still working, so amber and informational.
              const red = gap.unreachable;
              const msg = gap.unreachable
                ? (selectedEvent
                    ? 'This patient will get nothing — no confirmation and no reminders. Add an email or phone to fix it.'
                    : 'Add an email or phone. Without one, this patient gets no confirmation and no reminders.')
                : gap.missingPhone
                  ? 'SMS reminders will be skipped — no phone number.'
                  : 'Email reminders will be skipped — no email address.';
              return (
                <div style={{
                  marginTop: '16px', padding: '10px 12px', borderRadius: '4px', fontSize: '13px',
                  background: red ? '#fef2f2' : '#fef3cd',
                  border: '1px solid ' + (red ? '#fca5a5' : '#f2d38c'),
                  color: red ? '#991b1b' : '#7c5f18',
                  fontWeight: red ? 600 : 400
                }}>
                  ⚠ {msg}
                </div>
              );
            })()}
            {/* Read-only series info shown on the edit path when this appointment
                belongs to a series. Edits stay single-instance in this MVP. */}
            {selectedEvent && selectedEvent.recurring_group_id && (
              <div style={{ marginTop: '16px', padding: '10px 12px', background: 'var(--bg-tertiary, #f7f7f9)', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '13px' }}>
                <strong>Recurring series</strong>
                <div style={{ marginTop: '4px', color: 'var(--text-muted)' }}>
                  Occurrence {(selectedEvent.recurrence_index ?? 0) + 1}
                  {selectedEvent.recurrence_total ? ` of ${selectedEvent.recurrence_total}` : ''}
                  {selectedEvent.recurrence_pattern ? ` · ${selectedEvent.recurrence_pattern}` : ''}
                </div>
                <div style={{ marginTop: '4px', color: 'var(--text-muted)', fontSize: '12px' }}>
                  Edits apply to this occurrence only.
                </div>
              </div>
            )}
            {/* Recurring controls — create-only, admin-calendar-only. */}
            {!selectedEvent && (
              <div style={{ marginTop: '16px', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 500 }}>
                  <input type="checkbox" checked={recurEnabled}
                    onChange={e => { setRecurEnabled(e.target.checked); setPreviewData(null); setPreviewError(''); }}
                    disabled={saving} />
                  Repeat this appointment
                </label>
                {recurEnabled && (
                  <div style={{ marginTop: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ margin: 0, flex: '0 0 140px' }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>Repeats</label>
                      <select className="form-input" value={recurPattern}
                        onChange={e => { setRecurPattern(e.target.value); setPreviewData(null); }}
                        disabled={saving || previewing}>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Every 2 weeks</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '0 0 120px' }}>
                      <label className="form-label" style={{ fontSize: '12px' }}>Occurrences (max 26)</label>
                      <input type="number" className="form-input" min={1} max={26} value={recurCount}
                        onChange={e => { setRecurCount(e.target.value); setPreviewData(null); }}
                        disabled={saving || previewing} />
                    </div>
                    <button type="button" className="btn btn-secondary" onClick={handlePreviewSeries}
                      disabled={saving || previewing} style={{ marginBottom: '2px' }}>
                      {previewing ? 'Checking…' : 'Preview Series'}
                    </button>
                  </div>
                )}
                {recurEnabled && previewError && (
                  <div style={{ marginTop: '10px', padding: '8px 10px', background: '#fdecea', color: '#a72c1e', borderRadius: '4px', fontSize: '13px' }}>
                    {previewError}
                  </div>
                )}
                {recurEnabled && previewData && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                      <strong>{previewData.effective_count}</strong> occurrence{previewData.effective_count === 1 ? '' : 's'} · {previewData.service_name} · {previewData.service_duration_min} min
                      {previewData.capped_to_max && (
                        <span style={{ marginLeft: '8px', color: '#8a6d1a' }}>(capped from {previewData.requested_count} to max {previewData.max_allowed})</span>
                      )}
                    </div>
                    {previewData.any_conflict && (
                      <div style={{ marginBottom: '8px', padding: '8px 10px', background: '#fdecea', color: '#a72c1e', borderRadius: '4px', fontSize: '13px' }}>
                        <strong>{previewData.conflict_count} conflict{previewData.conflict_count === 1 ? '' : 's'}</strong> — pick a different time or staff member. The series will not be created while any occurrence conflicts.
                      </div>
                    )}
                    <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-tertiary, #f7f7f9)' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px' }}>#</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Date / Time</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.occurrences.map(o => (
                            <tr key={o.index} style={{ borderTop: '1px solid var(--border-color)', background: o.conflict ? '#fdecea' : 'transparent' }}>
                              <td style={{ padding: '6px 8px' }}>{o.index + 1}</td>
                              <td style={{ padding: '6px 8px' }}>
                                {new Date(o.start_dt).toLocaleString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </td>
                              <td style={{ padding: '6px 8px', color: o.conflict ? '#a72c1e' : '#1e6b1e' }}>
                                {o.conflict ? `Conflict with appt #${o.conflict_appointment_id}` : 'Available'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {selectedEvent && new Date(selectedEvent.start_dt) < new Date() && selectedEvent.status !== 'cancelled' && (
              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '15px' }}>
                  Appointment Outcome
                  {selectedEvent.outcome && <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'normal' }}>(recorded {selectedEvent.outcome_recorded_at ? new Date(selectedEvent.outcome_recorded_at).toLocaleDateString() : ''})</span>}
                </h4>
                <div className="admin-form-grid">
                  <div className="form-group">
                    <label className="form-label">Outcome *</label>
                    <select className="form-input" value={outcomeData.outcome} onChange={e => setOutcomeData({ ...outcomeData, outcome: e.target.value, enrollment_intent: e.target.value === 'showed' ? outcomeData.enrollment_intent : '' })} disabled={savingOutcome}>
                      <option value="">Select...</option>
                      <option value="showed">Showed</option>
                      <option value="no_show">No-show</option>
                      <option value="cancelled_late">Cancelled (within 24h)</option>
                      <option value="rescheduled_onsite">Rescheduled on-site</option>
                    </select>
                  </div>
                  {outcomeData.outcome === 'showed' && /assessment/i.test(selectedEvent.service_name || '') && (
                    <div className="form-group">
                      <label className="form-label">Enrollment Intent</label>
                      <select className="form-input" value={outcomeData.enrollment_intent} onChange={e => setOutcomeData({ ...outcomeData, enrollment_intent: e.target.value })} disabled={savingOutcome}>
                        <option value="">Select...</option>
                        <option value="enrolled">Enrolled</option>
                        <option value="interested">Interested</option>
                        <option value="undecided">Undecided</option>
                        <option value="not_interested">Not interested</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group full-width">
                    <label className="form-label">Outcome Notes</label>
                    <textarea className="form-input" value={outcomeData.outcome_notes} onChange={e => setOutcomeData({ ...outcomeData, outcome_notes: e.target.value })} disabled={savingOutcome} rows={2} />
                  </div>
                </div>
                <button className="btn btn-gold" onClick={handleSaveOutcome} disabled={savingOutcome || !outcomeData.outcome} style={{ marginTop: '8px' }}>
                  {savingOutcome ? 'Saving...' : selectedEvent.outcome ? 'Update Outcome' : 'Save Outcome'}
                </button>
              </div>
            )}
            <div className="modal-actions">
              {selectedEvent && selectedEvent.status !== 'cancelled' && <button className="btn btn-danger" onClick={handleCancel} disabled={saving} style={{ marginRight: 'auto' }}>Cancel Appointment</button>}
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Close</button>
              <button
                className="btn btn-gold"
                onClick={handleSave}
                disabled={
                  saving ||
                  (!selectedEvent && recurEnabled && (!previewData || previewData.any_conflict))
                }
              >
                {saving
                  ? 'Saving...'
                  : selectedEvent
                    ? 'Save'
                    : recurEnabled
                      ? (previewData ? `Create Series (${previewData.effective_count})` : 'Preview required')
                      : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminNeedsReviewPage() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [toast, setToast] = useState(null);

  const fetchAppts = () => {
    setLoading(true);
    api.get(`/api/admin/appointments/needs-review${showAll ? '?all=true' : ''}`)
      .then(data => setAppts(Array.isArray(data) ? data : []))
      .catch(e => setToast({ message: e.message || 'Failed to load', type: 'error' }))
      .finally(() => setLoading(false));
  };
  useEffect(fetchAppts, [showAll]);

  const updateDraft = (id, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [id]: {
        outcome: prev[id]?.outcome || '',
        outcome_notes: prev[id]?.outcome_notes || '',
        enrollment_intent: prev[id]?.enrollment_intent || '',
        ...prev[id],
        [field]: value,
        ...(field === 'outcome' && value !== 'showed' ? { enrollment_intent: '' } : {})
      }
    }));
  };

  const handleSave = async (apt) => {
    const draft = drafts[apt.id] || {};
    if (!draft.outcome) return;
    setSavingId(apt.id);
    try {
      const payload = {
        outcome: draft.outcome,
        outcome_notes: draft.outcome_notes || null,
        enrollment_intent: draft.outcome === 'showed' ? (draft.enrollment_intent || null) : null
      };
      await api.post(`/api/admin/appointments/${apt.id}/outcome`, payload);
      setToast({ message: `Saved outcome for ${apt.patient_name}`, type: 'success' });
      setAppts(prev => prev.filter(a => a.id !== apt.id));
      setDrafts(prev => { const { [apt.id]: _, ...rest } = prev; return rest; });
    } catch (e) {
      setToast({ message: e.message || 'Save failed', type: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const fmtDateTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Needs Review</h1>
        <p className="page-subtitle">Past appointments awaiting outcome from the assigned provider.</p>
      </div>
      <div className="section-card">
        <div className="section-header" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show all providers (not just mine)
          </label>
          <span style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-muted)' }}>{appts.length} pending</span>
        </div>
        {loading ? (
          <div className="loading-spinner" style={{ minHeight: '200px' }}><div className="spinner"></div></div>
        ) : appts.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
            All caught up. No past appointments awaiting outcome.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {appts.map(apt => {
              const draft = drafts[apt.id] || {};
              const isAssessment = /assessment/i.test(apt.service_name || '');
              return (
                <div key={apt.id} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px', background: 'var(--cream)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    <div>
                      <strong style={{ fontSize: '15px' }}>{apt.patient_name}</strong>
                      <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
                        {apt.service_name} · {apt.staff_name || 'Unassigned'}
                      </span>
                    </div>
                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{fmtDateTime(apt.start_dt)}</span>
                  </div>
                  <div className="admin-form-grid">
                    <div className="form-group">
                      <label className="form-label">Outcome *</label>
                      <select className="form-input" value={draft.outcome || ''} onChange={e => updateDraft(apt.id, 'outcome', e.target.value)} disabled={savingId === apt.id}>
                        <option value="">Select...</option>
                        <option value="showed">Showed</option>
                        <option value="no_show">No-show</option>
                        <option value="cancelled_late">Cancelled (within 24h)</option>
                        <option value="rescheduled_onsite">Rescheduled on-site</option>
                      </select>
                    </div>
                    {draft.outcome === 'showed' && isAssessment && (
                      <div className="form-group">
                        <label className="form-label">Enrollment Intent</label>
                        <select className="form-input" value={draft.enrollment_intent || ''} onChange={e => updateDraft(apt.id, 'enrollment_intent', e.target.value)} disabled={savingId === apt.id}>
                          <option value="">Select...</option>
                          <option value="enrolled">Enrolled</option>
                          <option value="interested">Interested</option>
                          <option value="undecided">Undecided</option>
                          <option value="not_interested">Not interested</option>
                        </select>
                      </div>
                    )}
                    <div className="form-group full-width">
                      <label className="form-label">Notes</label>
                      <textarea className="form-input" value={draft.outcome_notes || ''} onChange={e => updateDraft(apt.id, 'outcome_notes', e.target.value)} disabled={savingId === apt.id} rows={2} />
                    </div>
                  </div>
                  <button className="btn btn-gold" onClick={() => handleSave(apt)} disabled={savingId === apt.id || !draft.outcome} style={{ marginTop: '8px' }}>
                    {savingId === apt.id ? 'Saving...' : 'Save outcome'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminSchedulingServicesPage() {
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', duration_minutes: 30, buffer_minutes: 0, min_lead_hours: 24, max_advance_days: 30, patient_bookable: true, requires_room: false, price: 0, color: '#3788d8', ghl_webhook_url: '', staff_ids: [] });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);

  const fetchData = async () => {
    try {
      const [servicesData, staffData] = await Promise.all([
        api.get('/api/admin/scheduling-services'),
        api.get('/api/admin/staff')
      ]);
      setServices(servicesData);
      setStaff(staffData);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openEditModal = (s) => {
    setEditingService(s);
    setFormData({
      name: s.name, description: s.description || '', duration_minutes: s.duration_minutes,
      buffer_minutes: s.buffer_minutes || 0, min_lead_hours: s.min_lead_hours || 24,
      max_advance_days: s.max_advance_days || 30, patient_bookable: s.patient_bookable,
      requires_room: s.requires_room, price: s.price || 0, color: s.color || '#3788d8',
      ghl_webhook_url: s.ghl_webhook_url || '', staff_ids: (s.staff || []).map(st => st.id)
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    setEditingService(null);
    setFormData({ name: '', description: '', duration_minutes: 30, buffer_minutes: 0, min_lead_hours: 24, max_advance_days: 30, patient_bookable: true, requires_room: false, price: 0, color: '#3788d8', ghl_webhook_url: '', staff_ids: [] });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.duration_minutes) {
      setToast({ message: 'Name and duration are required', type: 'error' }); return;
    }
    setSaving(true);
    try {
      let serviceId;
      if (editingService) {
        await api.put(`/api/admin/scheduling-services/${editingService.id}`, formData);
        serviceId = editingService.id;
      } else {
        const result = await api.post('/api/admin/scheduling-services', formData);
        serviceId = result.id;
      }
      await api.put(`/api/admin/scheduling-services/${serviceId}/staff`, { staff_ids: formData.staff_ids });
      setToast({ message: editingService ? 'Service updated!' : 'Service created!', type: 'success' });
      setShowModal(false);
      fetchData();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this service?')) return;
    try { await api.delete(`/api/admin/scheduling-services/${id}`); setToast({ message: 'Service deactivated!', type: 'success' }); fetchData(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  const toggleStaff = (staffId) => {
    setFormData(prev => ({
      ...prev,
      staff_ids: prev.staff_ids.includes(staffId) ? prev.staff_ids.filter(id => id !== staffId) : [...prev.staff_ids, staffId]
    }));
  };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Scheduling</h1><p className="page-subtitle">Manage appointments and availability</p></div>
      <SchedulingTabs active="/admin/scheduling" />
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Services</h2><button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add Service</button></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : services.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>Service</th><th>Duration</th><th>Staff</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id} className={!s.active ? 'inactive-row' : ''}>
                    <td><div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: s.color || '#3788d8' }} /><div><strong>{s.name}</strong><br /><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>${parseFloat(s.price || 0).toFixed(2)} {s.patient_bookable ? '- Patient Bookable' : '- Admin Only'}</span></div></div></td>
                    <td>{s.duration_minutes} min</td>
                    <td>{(s.staff || []).map(st => st.name).join(', ') || '-'}</td>
                    <td><span className={`status-badge ${s.active ? 'active' : 'inactive'}`}>{s.active ? 'Active' : 'Inactive'}</span></td>
                    <td><div className="action-btns"><button className="action-btn edit" onClick={() => openEditModal(s)}>Edit</button>{s.active && <button className="action-btn delete" onClick={() => handleDelete(s.id)}>Deactivate</button>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">📅</div><h3>No services yet</h3><p>Create appointment types for your calendar.</p></div>}
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header"><h2 className="modal-title">{editingService ? 'Edit Service' : 'Add Service'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div>
            <div className="admin-form-grid">
              <div className="form-group"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Duration (min) *</label><input type="number" className="form-input" value={formData.duration_minutes} onChange={e => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 30 })} disabled={saving} /></div>
              <div className="form-group full-width"><label className="form-label">Description</label><textarea className="form-input" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} disabled={saving} rows={2} /></div>
              <div className="form-group"><label className="form-label">Buffer (min)</label><input type="number" className="form-input" value={formData.buffer_minutes} onChange={e => setFormData({ ...formData, buffer_minutes: parseInt(e.target.value) || 0 })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Min Lead (hours)</label><input type="number" className="form-input" value={formData.min_lead_hours} onChange={e => setFormData({ ...formData, min_lead_hours: parseInt(e.target.value) || 0 })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Max Advance (days)</label><input type="number" className="form-input" value={formData.max_advance_days} onChange={e => setFormData({ ...formData, max_advance_days: parseInt(e.target.value) || 30 })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Price ($)</label><input type="number" step="0.01" className="form-input" value={formData.price} onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Color</label><input type="color" className="form-input" value={formData.color} onChange={e => setFormData({ ...formData, color: e.target.value })} disabled={saving} style={{ height: '40px', padding: '4px' }} /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">GHL Webhook URL</label><input type="url" className="form-input" placeholder="https://services.leadconnectorhq.com/hooks/..." value={formData.ghl_webhook_url} onChange={e => setFormData({ ...formData, ghl_webhook_url: e.target.value })} disabled={saving} /><small style={{ color: '#6b7280', fontSize: '12px' }}>Paste GoHighLevel inbound webhook URL to enable appointment reminders</small></div>
              <div className="form-group"><label className="form-label">Options</label><label className="consent-label"><input type="checkbox" checked={formData.patient_bookable} onChange={e => setFormData({ ...formData, patient_bookable: e.target.checked })} disabled={saving} /><span>Patient can book online</span></label><label className="consent-label"><input type="checkbox" checked={formData.requires_room} onChange={e => setFormData({ ...formData, requires_room: e.target.checked })} disabled={saving} /><span>Requires treatment room</span></label></div>
              <div className="form-group full-width"><label className="form-label">Assigned Staff</label><div className="checkbox-group">{staff.map(s => (<label key={s.id} className="checkbox-label"><input type="checkbox" checked={formData.staff_ids.includes(s.id)} onChange={() => toggleStaff(s.id)} disabled={saving} /><span>{s.name}</span></label>))}</div></div>
            </div>
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminRoomsPage() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);

  const fetchRooms = async () => { try { setRooms(await api.get('/api/admin/rooms')); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { fetchRooms(); }, []);

  const openEditModal = (r) => { setEditingRoom(r); setFormData({ name: r.name, description: r.description || '' }); setShowModal(true); };
  const openNewModal = () => { setEditingRoom(null); setFormData({ name: '', description: '' }); setShowModal(true); };

  const handleSave = async () => {
    if (!formData.name) { setToast({ message: 'Name is required', type: 'error' }); return; }
    setSaving(true);
    try {
      if (editingRoom) { await api.put(`/api/admin/rooms/${editingRoom.id}`, formData); }
      else { await api.post('/api/admin/rooms', formData); }
      setToast({ message: editingRoom ? 'Room updated!' : 'Room created!', type: 'success' });
      setShowModal(false); fetchRooms();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this room?')) return;
    try { await api.delete(`/api/admin/rooms/${id}`); setToast({ message: 'Room deactivated!', type: 'success' }); fetchRooms(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Scheduling</h1><p className="page-subtitle">Manage appointments and availability</p></div>
      <SchedulingTabs active="/admin/scheduling/rooms" />
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Rooms</h2><button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add Room</button></div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : rooms.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>Room</th><th>Description</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {rooms.map(r => (
                  <tr key={r.id} className={!r.active ? 'inactive-row' : ''}>
                    <td><strong>{r.name}</strong></td>
                    <td>{r.description || '-'}</td>
                    <td><span className={`status-badge ${r.active ? 'active' : 'inactive'}`}>{r.active ? 'Active' : 'Inactive'}</span></td>
                    <td><div className="action-btns"><button className="action-btn edit" onClick={() => openEditModal(r)}>Edit</button>{r.active && <button className="action-btn delete" onClick={() => handleDelete(r.id)}>Deactivate</button>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">🏥</div><h3>No rooms yet</h3><p>Add treatment rooms for scheduling.</p></div>}
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">{editingRoom ? 'Edit Room' : 'Add Room'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div>
            <div className="admin-form-grid">
              <div className="form-group full-width"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} disabled={saving} /></div>
              <div className="form-group full-width"><label className="form-label">Description</label><textarea className="form-input" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} disabled={saving} rows={2} /></div>
            </div>
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminAvailabilityPage() {
  const [rules, setRules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({ staff_id: '', days_of_week: [], start_time: '09:00', end_time: '17:00', effective_from: '', effective_until: '' });

  // Convert 24hr time string (e.g., "09:00" or "17:00") to 12hr format (e.g., "9:00 AM" or "5:00 PM")
  const formatTime12 = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.slice(0, 5).split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');

  const fetchData = async () => {
    try {
      const [rulesData, staffData] = await Promise.all([api.get('/api/admin/availability'), api.get('/api/admin/staff')]);
      setRules(rulesData);
      setStaff(staffData);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openEditModal = (r) => {
    setEditingRule(r);
    setFormData({ staff_id: r.staff_id, days_of_week: [r.day_of_week], start_time: r.start_time.slice(0, 5), end_time: r.end_time.slice(0, 5), effective_from: r.effective_from ? r.effective_from.slice(0, 10) : '', effective_until: r.effective_until ? r.effective_until.slice(0, 10) : '' });
    setShowModal(true);
  };

  const openNewModal = () => {
    setEditingRule(null);
    setFormData({ staff_id: selectedStaff || '', days_of_week: [1, 2, 3, 4, 5], start_time: '09:00', end_time: '17:00', effective_from: '', effective_until: '' });
    setShowModal(true);
  };

  const toggleDay = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(dayIndex)
        ? prev.days_of_week.filter(d => d !== dayIndex)
        : [...prev.days_of_week, dayIndex].sort((a, b) => a - b)
    }));
  };

  const handleSave = async () => {
    if (!formData.staff_id || !formData.start_time || !formData.end_time) {
      setToast({ message: 'Staff, start time, and end time are required', type: 'error' }); return;
    }
    if (!editingRule && formData.days_of_week.length === 0) {
      setToast({ message: 'Please select at least one day', type: 'error' }); return;
    }
    setSaving(true);
    try {
      if (editingRule) {
        await api.put(`/api/admin/availability/${editingRule.id}`, { ...formData, day_of_week: formData.days_of_week[0] });
        setToast({ message: 'Rule updated!', type: 'success' });
      } else {
        // Create a rule for each selected day
        for (const day of formData.days_of_week) {
          await api.post('/api/admin/availability', { ...formData, day_of_week: day });
        }
        setToast({ message: `${formData.days_of_week.length} rule(s) created!`, type: 'success' });
      }
      setShowModal(false); fetchData();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this availability rule?')) return;
    try { await api.delete(`/api/admin/availability/${id}`); setToast({ message: 'Rule deleted!', type: 'success' }); fetchData(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  const filteredRules = selectedStaff ? rules.filter(r => r.staff_id === parseInt(selectedStaff)) : rules;
  const groupedByStaff = filteredRules.reduce((acc, r) => { const key = r.staff_name || 'Unknown'; if (!acc[key]) acc[key] = []; acc[key].push(r); return acc; }, {});

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Scheduling</h1><p className="page-subtitle">Manage appointments and availability</p></div>
      <SchedulingTabs active="/admin/scheduling/availability" />
      <div className="section-card">
        <div className="section-header">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select className="form-input" value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} style={{ maxWidth: '200px' }}>
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add Rule</button>
        </div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : Object.keys(groupedByStaff).length > 0 ? (
          Object.entries(groupedByStaff).map(([staffName, staffRules]) => (
            <div key={staffName} style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>{staffName}</h3>
              <div className="availability-grid">
                {staffRules.sort((a, b) => a.day_of_week - b.day_of_week).map(r => (
                  <div key={r.id} className="availability-card">
                    <div className="availability-day">{DAYS_OF_WEEK[r.day_of_week]}</div>
                    <div className="availability-time">{formatTime12(r.start_time)} - {formatTime12(r.end_time)}</div>
                    {(r.effective_from || r.effective_until) && <div className="availability-dates">{r.effective_from && `From: ${r.effective_from.slice(0, 10)}`} {r.effective_until && `Until: ${r.effective_until.slice(0, 10)}`}</div>}
                    <div className="action-btns" style={{ marginTop: '8px' }}><button className="action-btn edit" onClick={() => openEditModal(r)}>Edit</button><button className="action-btn delete" onClick={() => handleDelete(r.id)}>Del</button></div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : <div className="empty-state"><div className="empty-state-icon">📅</div><h3>No availability rules</h3><p>Add weekly availability for staff members.</p></div>}
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">{editingRule ? 'Edit Availability' : 'Add Availability'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div>
            <div className="admin-form-grid">
              <div className="form-group full-width"><label className="form-label">Staff Member *</label><select className="form-input" value={formData.staff_id} onChange={e => setFormData({ ...formData, staff_id: e.target.value })} disabled={saving || editingRule}><option value="">Select...</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div className="form-group full-width">
                <label className="form-label">{editingRule ? 'Day of Week' : 'Days of Week *'}</label>
                {editingRule ? (
                  <select className="form-input" value={formData.days_of_week[0]} onChange={e => setFormData({ ...formData, days_of_week: [parseInt(e.target.value)] })} disabled={saving}>{DAYS_OF_WEEK.map((day, i) => <option key={i} value={i}>{day}</option>)}</select>
                ) : (
                  <div className="days-checkbox-group">
                    {DAYS_OF_WEEK.map((day, i) => (
                      <label key={i} className={`day-checkbox ${formData.days_of_week.includes(i) ? 'selected' : ''}`}>
                        <input type="checkbox" checked={formData.days_of_week.includes(i)} onChange={() => toggleDay(i)} disabled={saving} />
                        <span>{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group"><label className="form-label">Start Time *</label><input type="time" className="form-input" value={formData.start_time} onChange={e => setFormData({ ...formData, start_time: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">End Time *</label><input type="time" className="form-input" value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Effective From</label><input type="date" className="form-input" value={formData.effective_from} onChange={e => setFormData({ ...formData, effective_from: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">Effective Until</label><input type="date" className="form-input" value={formData.effective_until} onChange={e => setFormData({ ...formData, effective_until: e.target.value })} disabled={saving} /></div>
            </div>
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function AdminTimeBlocksPage() {
  const [blocks, setBlocks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [formData, setFormData] = useState({ staff_id: '', block_type: 'lunch', title: '', start_dt: '', end_dt: '', is_all_day: false });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('ghl');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loadingPipelines, setLoadingPipelines] = useState(false); const [resendingId, setResendingId] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');

  const fetchData = async () => {
    try {
      const [blocksData, staffData] = await Promise.all([api.get('/api/admin/time-blocks'), api.get('/api/admin/staff')]);
      setBlocks(blocksData);
      setStaff(staffData);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openEditModal = (b) => {
    setEditingBlock(b);
    setFormData({ staff_id: b.staff_id, block_type: b.block_type, title: b.title || '', start_dt: b.start_dt.slice(0, 16), end_dt: b.end_dt.slice(0, 16), is_all_day: b.is_all_day });
    setShowModal(true);
  };

  const openNewModal = () => {
    setEditingBlock(null);
    const now = new Date();
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    setFormData({ staff_id: selectedStaff || '', block_type: 'lunch', title: '', start_dt: `${tomorrow.toISOString().slice(0, 10)}T12:00`, end_dt: `${tomorrow.toISOString().slice(0, 10)}T13:00`, is_all_day: false });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.staff_id || !formData.block_type || !formData.start_dt || !formData.end_dt) {
      setToast({ message: 'Staff, type, and dates are required', type: 'error' }); return;
    }
    setSaving(true);
    try {
      if (editingBlock) { await api.put(`/api/admin/time-blocks/${editingBlock.id}`, formData); }
      else { await api.post('/api/admin/time-blocks', formData); }
      setToast({ message: editingBlock ? 'Block updated!' : 'Block created!', type: 'success' });
      setShowModal(false); fetchData();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time block?')) return;
    try { await api.delete(`/api/admin/time-blocks/${id}`); setToast({ message: 'Block deleted!', type: 'success' }); fetchData(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  const filteredBlocks = selectedStaff ? blocks.filter(b => b.staff_id === parseInt(selectedStaff)) : blocks;
  const formatDateTime = (dt) => new Date(dt).toLocaleString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const getBlockColor = (type) => { if (type === 'pto') return '#f44336'; if (type === 'lunch') return '#ff9800'; if (type === 'meeting') return '#2196f3'; return '#9e9e9e'; };

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Scheduling</h1><p className="page-subtitle">Manage appointments and availability</p></div>
      <SchedulingTabs active="/admin/scheduling/time-blocks" />
      <div className="section-card">
        <div className="section-header">
          <select className="form-input" value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} style={{ maxWidth: '200px' }}>
            <option value="">All Staff</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="btn btn-gold btn-sm" onClick={openNewModal}>+ Add Block</button>
        </div>
        {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : filteredBlocks.length > 0 ? (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead><tr><th>Type</th><th>Staff</th><th>Title</th><th>Start</th><th>End</th><th>Actions</th></tr></thead>
              <tbody>
                {filteredBlocks.map(b => (
                  <tr key={b.id}>
                    <td><span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: getBlockColor(b.block_type), color: 'white', fontSize: '12px', textTransform: 'uppercase' }}>{b.block_type}</span></td>
                    <td>{b.staff_name}</td>
                    <td>{b.title || '-'}</td>
                    <td>{formatDateTime(b.start_dt)}</td>
                    <td>{formatDateTime(b.end_dt)}</td>
                    <td><div className="action-btns"><button className="action-btn edit" onClick={() => openEditModal(b)}>Edit</button><button className="action-btn delete" onClick={() => handleDelete(b.id)}>Del</button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="empty-state"><div className="empty-state-icon">⏰</div><h3>No time blocks</h3><p>Add PTO, lunch breaks, or meetings.</p></div>}
      </div>
      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">{editingBlock ? 'Edit Time Block' : 'Add Time Block'}</h2><button className="modal-close" onClick={() => setShowModal(false)} disabled={saving}>×</button></div>
            <div className="admin-form-grid">
              <div className="form-group"><label className="form-label">Staff</label><select className="form-input" value={formData.staff_id} onChange={e => setFormData({ ...formData, staff_id: e.target.value })} disabled={saving}><option value="">All Staff (clinic-wide)</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Type *</label><select className="form-input" value={formData.block_type} onChange={e => setFormData({ ...formData, block_type: e.target.value })} disabled={saving}><option value="lunch">Lunch</option><option value="pto">PTO</option><option value="meeting">Meeting</option><option value="training">Training</option><option value="holiday">Holiday</option><option value="other">Other</option></select></div>
              <div className="form-group full-width"><label className="form-label">Title</label><input type="text" className="form-input" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} disabled={saving} placeholder="Optional description" /></div>
              <div className="form-group"><label className="form-label">Start *</label><input type="datetime-local" className="form-input" value={formData.start_dt} onChange={e => setFormData({ ...formData, start_dt: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="form-label">End *</label><input type="datetime-local" className="form-input" value={formData.end_dt} onChange={e => setFormData({ ...formData, end_dt: e.target.value })} disabled={saving} /></div>
              <div className="form-group"><label className="consent-label"><input type="checkbox" checked={formData.is_all_day} onChange={e => setFormData({ ...formData, is_all_day: e.target.checked })} disabled={saving} /><span>All day event</span></label></div>
            </div>
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

function PatientBookingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);

  useEffect(() => { api.get('/api/booking/services').then(setServices).catch(console.error).finally(() => setLoading(false)); }, []);

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedDate('');
    setSelectedSlot(null);
    try {
      const staffData = await api.get(`/api/booking/staff/${service.id}`);
      setStaff(staffData);
      setStep(2);
    } catch (e) { setToast({ message: 'Failed to load providers', type: 'error' }); }
  };

  const handleStaffSelect = (staffMember) => {
    setSelectedStaff(staffMember);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
    setStep(3);
  };

  const handleDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedSlot(null);
    if (!date) return;
    setSlotsLoading(true);
    try {
      const slotsData = await api.get(`/api/booking/slots?staff_id=${selectedStaff.id}&service_id=${selectedService.id}&date=${date}`);
      setSlots(slotsData);
    } catch (e) { setToast({ message: 'Failed to load available times', type: 'error' }); setSlots([]); }
    finally { setSlotsLoading(false); }
  };

  const handleBook = async () => {
    if (!selectedSlot) { setToast({ message: 'Please select a time slot', type: 'error' }); return; }
    setBooking(true);
    try {
      const bookStaffId = selectedStaff.id === 'any' ? selectedSlot.staff_id : selectedStaff.id;
      await api.post('/api/booking/book', { service_id: selectedService.id, staff_id: bookStaffId, start_dt: selectedSlot.start, notes, referrer_url: document.referrer || null });
      const params = new URLSearchParams({
        service: selectedService.name,
        provider: selectedStaff.id === 'any' ? (selectedSlot.staff_name || '') : (selectedStaff.name || ''),
        date: selectedDate,
        time: selectedSlot.display || ''
      });
      window.location.href = '/thank-you?' + params.toString();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setBooking(false); }
  };

  const formatDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Denver', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const getMinDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };
  const getMaxDate = () => { const d = new Date(); d.setDate(d.getDate() + (selectedService?.max_advance_days || 30)); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };

  if (loading) return <div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div>;

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Book Appointment</h1><p className="page-subtitle">Schedule your next visit</p></div>
      <div className="booking-steps">
        <div className={`booking-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}><span>1</span> Service</div>
        <div className={`booking-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}><span>2</span> Provider</div>
        <div className={`booking-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}><span>3</span> Time</div>
        <div className={`booking-step ${step >= 4 ? 'active' : ''}`}><span>4</span> Confirm</div>
      </div>

      {step === 1 && (
        <div className="section-card">
          <h2 className="section-title">Select a Service</h2>
          <div className="booking-services-grid">
            {services.map(s => (
              <div key={s.id} className="booking-service-card" onClick={() => handleServiceSelect(s)}>
                <div className="booking-service-color" style={{ backgroundColor: s.color || '#3788d8' }} />
                <div className="booking-service-info">
                  <h3>{s.name}</h3>
                  <p>{s.description}</p>
                  <div className="booking-service-meta"><span>{s.duration_minutes} min</span><span>${parseFloat(s.price || 0).toFixed(2)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="section-card">
          <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)} style={{ marginBottom: '16px' }}>← Back to Services</button>
          <h2 className="section-title">Select a Provider</h2>
          {staff.length > 0 ? (
            <div className="booking-staff-grid">
              <div className="booking-staff-card" onClick={() => handleStaffSelect({ id: 'any', name: 'Any Provider' })}>
                <div className="booking-staff-avatar" style={{ backgroundColor: '#10b981' }}>★</div>
                <div className="booking-staff-name">Any Provider</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Next available</div>
              </div>
              {staff.map(s => (
                <div key={s.id} className="booking-staff-card" onClick={() => handleStaffSelect(s)}>
                  <div className="booking-staff-avatar">{s.name.split(' ').map(n => n[0]).join('')}</div>
                  <div className="booking-staff-name">{s.name}</div>
                </div>
              ))}
            </div>
          ) : <div className="empty-state"><p>No providers available for this service.</p></div>}
        </div>
      )}

      {step === 3 && (
        <div className="section-card">
          <button className="btn btn-ghost btn-sm" onClick={() => setStep(2)} style={{ marginBottom: '16px' }}>← Back to Providers</button>
          <h2 className="section-title">Select Date & Time</h2>
          <div className="booking-datetime">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={selectedDate} onChange={handleDateChange} min={getMinDate()} max={getMaxDate()} style={{ maxWidth: '200px' }} />
            </div>
            {selectedDate && (
              <div className="booking-slots">
                <label className="form-label">Available Times</label>
                {slotsLoading ? <div className="loading-spinner"><div className="spinner"></div></div> : slots.length > 0 ? (
                  <div className="booking-slots-grid">
                    {slots.map((slot, i) => (
                      <button key={i} className={`booking-slot ${selectedSlot === slot ? 'selected' : ''}`} onClick={() => { setSelectedSlot(slot); setStep(4); }}>{slot.display}{selectedStaff?.id === 'any' && slot.staff_name && <span style={{ display: 'block', fontSize: '11px', opacity: 0.8 }}>with {slot.staff_name.split(' ')[0]}</span>}</button>
                    ))}
                  </div>
                ) : <p style={{ color: 'var(--text-muted)' }}>No available times on this date. Please try another day.</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="section-card">
          <button className="btn btn-ghost btn-sm" onClick={() => setStep(3)} style={{ marginBottom: '16px' }}>← Back to Time Selection</button>
          <h2 className="section-title">Confirm Your Appointment</h2>
          <div className="booking-summary">
            <div className="booking-summary-item"><span>Service:</span><strong>{selectedService?.name}</strong></div>
            <div className="booking-summary-item"><span>Duration:</span><strong>{selectedService?.duration_minutes} min</strong></div>
            <div className="booking-summary-item"><span>Provider:</span><strong>{selectedStaff?.id === 'any' ? (selectedSlot?.staff_name || 'Any Provider') : selectedStaff?.name}</strong></div>
            <div className="booking-summary-item"><span>Date:</span><strong>{formatDate(selectedDate)}</strong></div>
            <div className="booking-summary-item"><span>Time:</span><strong>{selectedSlot?.display}</strong></div>
            <div className="booking-summary-item"><span>Price:</span><strong>${parseFloat(selectedService?.price || 0).toFixed(2)}</strong></div>
          </div>
          <div className="form-group" style={{ marginTop: '20px' }}>
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any special requests or information for your provider..." />
          </div>
          <button className="btn btn-gold" onClick={handleBook} disabled={booking} style={{ marginTop: '20px', width: '100%' }}>{booking ? 'Booking...' : 'Confirm Booking'}</button>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}



function QuickBookPage() {
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [patients, setPatients] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/public/services').then(r => r.json()),
      fetch('/api/admin/users', { credentials: 'include' }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([servicesData, usersData]) => {
      setServices(servicesData);
      setPatients(Array.isArray(usersData) ? usersData.filter(u => u.role === 'patient') : []);
      setLoading(false);
    }).catch(console.error);
  }, []);

  const handlePatientSelect = (patientId, patient) => {
    setSelectedPatientId(patientId);
    if (patientId && patient) {
      setPatientName(patient.name || '');
      setPatientEmail(patient.email || '');
      setPatientPhone(patient.phone || '');
    }
  };

  useEffect(() => {
    if (selectedService) {
      fetch(`/api/public/staff/${selectedService}`).then(r => r.json()).then(setStaff).catch(console.error);
      setSelectedStaff('');
      setSlots([]);
      setSelectedSlot('');
    }
  }, [selectedService]);

  useEffect(() => {
    if (selectedStaff && selectedService && selectedDate) {
      setSlotsLoading(true);
      fetch(`/api/public/slots?staff_id=${selectedStaff}&service_id=${selectedService}&date=${selectedDate}`)
        .then(r => r.json())
        .then(data => { setSlots(data); setSelectedSlot(''); })
        .catch(console.error)
        .finally(() => setSlotsLoading(false));
    }
  }, [selectedStaff, selectedService, selectedDate]);

  const handleBook = async () => {
    if (!selectedService || !selectedStaff || !selectedSlot || !patientName) {
      setToast({ message: 'Please fill in service, provider, time, and patient name', type: 'error' });
      return;
    }
    setBooking(true);
    try {
      const slot = slots.find(s => s.start === selectedSlot);
      const res = await fetch('/api/public/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: parseInt(selectedService),
          staff_id: parseInt(selectedStaff),
          start_dt: selectedSlot,
          patient_name: patientName,
          patient_email: patientEmail || 'noemail@placeholder.com',
          patient_phone: patientPhone,
          notes,
          referrer_url: document.referrer || null,
          referral_code: getStoredReferralCode()
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Booking failed');
      setToast({ message: 'Appointment booked!', type: 'success' });
      // Reset form for next entry
      setSelectedPatientId('');
      setPatientName('');
      setPatientEmail('');
      setPatientPhone('');
      setNotes('');
      setSelectedSlot('');
      // Reload slots to reflect the new booking
      if (selectedStaff && selectedService && selectedDate) {
        const slotsRes = await fetch(`/api/public/slots?staff_id=${selectedStaff}&service_id=${selectedService}&date=${selectedDate}`);
        setSlots(await slotsRes.json());
      }
    } catch (error) { 
      setToast({ message: error.message, type: 'error' }); 
    } finally { 
      setBooking(false); 
    }
  };

  const getMinDate = () => new Date().toISOString().slice(0, 10);
  const getMaxDate = () => { const d = new Date(); d.setDate(d.getDate() + 60); return d.toISOString().slice(0, 10); };
  const selectedServiceObj = services.find(s => s.id === parseInt(selectedService));

  if (loading) return <div className="quick-book-page"><div className="quick-book-container"><div className="loading-spinner"><div className="spinner"></div></div></div></div>;

  return (
    <div className="quick-book-page">
      <div className="quick-book-container">
        <div className="quick-book-header">
          <h1>Quick Book</h1>
          <span>Staff Appointment Entry</span>
        </div>

        <div className="section-card">
          <div className="quick-book-grid">
            <div className="form-group">
              <label className="form-label">Service *</label>
              <select className="form-input" value={selectedService} onChange={e => setSelectedService(e.target.value)}>
                <option value="">Select service...</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Provider *</label>
              <select className="form-input" value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} disabled={!selectedService}>
                <option value="">Select provider...</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-input" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} min={getMinDate()} max={getMaxDate()} disabled={!selectedStaff} />
            </div>

            <div className="form-group">
              <label className="form-label">Time * {slotsLoading && <span style={{color: 'var(--text-muted)'}}>(loading...)</span>}</label>
              <select className="form-input" value={selectedSlot} onChange={e => setSelectedSlot(e.target.value)} disabled={!selectedDate || slotsLoading || slots.length === 0}>
                <option value="">{slots.length === 0 && selectedDate ? 'No availability' : 'Select time...'}</option>
                {slots.map(s => <option key={s.start} value={s.start}>{s.display}</option>)}
              </select>
            </div>
          </div>

          <hr style={{border: 'none', borderTop: '1px solid var(--border-color)', margin: '24px 0'}} />

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label className="form-label">Search Existing Patient (optional)</label>
            <SearchableSelect
              options={patients}
              value={selectedPatientId}
              onChange={handlePatientSelect}
              placeholder="Search by name or email, or enter new patient below..."
              displayKey="name"
              valueKey="id"
            />
          </div>

          <div className="quick-book-grid">
            <div className="form-group">
              <label className="form-label">Patient Name *</label>
              <input type="text" className="form-input" value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Full name" />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={patientEmail} onChange={e => setPatientEmail(e.target.value)} placeholder="email@example.com" />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input type="tel" className="form-input" value={patientPhone} onChange={e => setPatientPhone(e.target.value)} placeholder="(555) 555-5555" />
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <input type="text" className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
            </div>
          </div>

          <button 
            className="btn btn-gold" 
            onClick={handleBook} 
            disabled={booking || !selectedService || !selectedStaff || !selectedSlot || !patientName}
            style={{ marginTop: '24px', width: '100%', padding: '16px', fontSize: '16px' }}
          >
            {booking ? 'Booking...' : 'Book Appointment'}
          </button>
        </div>

        {selectedServiceObj && selectedSlot && (
          <div className="quick-book-summary">
            <strong>{selectedServiceObj.name}</strong> • {selectedServiceObj.duration_minutes} min • {staff.find(s => s.id === parseInt(selectedStaff))?.name} • {new Date(selectedSlot).toLocaleString('en-US', { timeZone: 'America/Denver', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        )}

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

function ThankYouPage() {
  const params = new URLSearchParams(window.location.search);
  const service = params.get('service');
  const provider = params.get('provider');
  const date = params.get('date');
  const time = params.get('time');
  const email = params.get('email');
  const isEmbed = params.get('embed') === 'true';

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Denver', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="public-booking-page" style={isEmbed ? { minHeight: 'auto', padding: '0' } : {}}>
      <div className="public-booking-container" style={isEmbed ? { paddingTop: '0' } : {}}>
        {!isEmbed && <div className="public-booking-header"><h1>Ascent Trim</h1><span>Appointment Confirmed</span></div>}
        <div className="section-card" style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>&#10003;</div>
          <h2 style={{ color: 'var(--accent-gold)', marginBottom: '20px' }}>You're All Set!</h2>
          <p style={{ marginBottom: '30px', color: 'var(--text-muted)' }}>
            Your appointment has been booked.{email ? ` A confirmation will be sent to ${email}.` : ''}
          </p>
          {(service || provider || date || time) && (
            <div className="booking-summary" style={{ textAlign: 'left', maxWidth: '400px', margin: '0 auto' }}>
              {service && <div className="booking-summary-item"><span>Service:</span><strong>{service}</strong></div>}
              {provider && <div className="booking-summary-item"><span>Provider:</span><strong>{provider}</strong></div>}
              {date && <div className="booking-summary-item"><span>Date:</span><strong>{formatDate(date)}</strong></div>}
              {time && <div className="booking-summary-item"><span>Time:</span><strong>{time}</strong></div>}
            </div>
          )}
          <div style={{ marginTop: '30px' }}>
            {!isEmbed && <Link to="/login" className="btn btn-gold">Sign In to Portal</Link>}
            {!isEmbed && <Link to="/book" className="btn btn-ghost" style={{ marginLeft: '10px' }}>Book Another</Link>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PublicBookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookedInfo, setBookedInfo] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { 
    fetch('/api/public/services').then(r => r.json()).then(setServices).catch(console.error).finally(() => setLoading(false)); 
  }, []);

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setSelectedStaff(null);
    setSelectedDate('');
    setSelectedSlot(null);
    try {
      const res = await fetch(`/api/public/staff/${service.id}`);
      const staffData = await res.json();
      setStaff(staffData);
      setStep(2);
    } catch (e) { setToast({ message: 'Failed to load providers', type: 'error' }); }
  };

  const handleStaffSelect = (staffMember) => {
    setSelectedStaff(staffMember);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
    setStep(3);
  };

  const handleDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedSlot(null);
    if (!date) return;
    setSlotsLoading(true);
    try {
      const res = await fetch(`/api/public/slots?staff_id=${selectedStaff.id}&service_id=${selectedService.id}&date=${date}`);
      const slotsData = await res.json();
      setSlots(slotsData);
    } catch (e) { setToast({ message: 'Failed to load available times', type: 'error' }); setSlots([]); }
    finally { setSlotsLoading(false); }
  };

  const handleBook = async () => {
    if (!patientName || !patientEmail) { setToast({ message: 'Please enter your name and email', type: 'error' }); return; }
    if (!selectedSlot) { setToast({ message: 'Please select a time slot', type: 'error' }); return; }
    setBooking(true);
    try {
      const res = await fetch('/api/public/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: selectedService.id,
          staff_id: selectedStaff.id === 'any' ? selectedSlot.staff_id : selectedStaff.id,
          start_dt: selectedSlot.start,
          patient_name: patientName,
          patient_email: patientEmail,
          patient_phone: patientPhone,
          notes,
          referrer_url: document.referrer || null,
          referral_code: getStoredReferralCode()
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Booking failed');
      // Redirect to thank-you page for conversion tracking (Google Ads, GTM)
      const params = new URLSearchParams({
        service: selectedService.name,
        provider: selectedStaff.id === 'any' ? (selectedSlot.staff_name || '') : (selectedStaff.name || ''),
        date: selectedDate,
        time: selectedSlot.display || '',
        email: patientEmail
      });
      window.location.href = '/thank-you?' + params.toString();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setBooking(false); }
  };

  const formatDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Denver', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const getMinDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };
  const getMaxDate = () => { const d = new Date(); d.setDate(d.getDate() + (selectedService?.max_advance_days || 30)); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };

  if (loading) return <div className="public-booking-page"><div className="public-booking-container"><div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div></div></div>;

  return (
    <div className="public-booking-page">
      <div className="public-booking-container">
        <div className="public-booking-header">
          <h1>Ascent Trim</h1>
          <span>Book Your Appointment</span>
          <div style={{ marginTop: '10px' }}><Link to="/login" style={{ color: 'var(--accent-gold)', fontSize: '14px' }}>Already a patient? Sign in</Link></div>
        </div>

        <div className="booking-steps">
          <div className={`booking-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}><span>1</span> Service</div>
          <div className={`booking-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}><span>2</span> Provider</div>
          <div className={`booking-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}><span>3</span> Time</div>
          <div className={`booking-step ${step >= 4 ? 'active' : ''}`}><span>4</span> Confirm</div>
        </div>

        {step === 1 && (
          <div className="section-card">
            <h2 className="section-title">Select a Service</h2>
            {services.length > 0 ? (
              <div className="booking-services-grid">
                {services.map(s => (
                  <div key={s.id} className="booking-service-card" onClick={() => handleServiceSelect(s)}>
                    <div className="booking-service-color" style={{ backgroundColor: s.color || '#3788d8' }} />
                    <div className="booking-service-info">
                      <h3>{s.name}</h3>
                      <p>{s.description}</p>
                      <div className="booking-service-meta"><span>{s.duration_minutes} min</span><span>${parseFloat(s.price || 0).toFixed(2)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="empty-state"><p>No services available for booking at this time.</p></div>}
          </div>
        )}

        {step === 2 && (
          <div className="section-card">
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)} style={{ marginBottom: '16px' }}>← Back to Services</button>
            <h2 className="section-title">Select a Provider</h2>
            {staff.length > 0 ? (
              <div className="booking-staff-grid">
                <div className="booking-staff-card" onClick={() => handleStaffSelect({ id: 'any', name: 'Any Provider' })}>
                  <div className="booking-staff-avatar" style={{ backgroundColor: '#10b981' }}>★</div>
                  <div className="booking-staff-name">Any Provider</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Next available</div>
                </div>
                {staff.map(s => (
                  <div key={s.id} className="booking-staff-card" onClick={() => handleStaffSelect(s)}>
                    <div className="booking-staff-avatar">{s.name.split(' ').map(n => n[0]).join('')}</div>
                    <div className="booking-staff-name">{s.name}</div>
                  </div>
                ))}
              </div>
            ) : <div className="empty-state"><p>No providers available for this service.</p></div>}
          </div>
        )}

        {step === 3 && (
          <div className="section-card">
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(2)} style={{ marginBottom: '16px' }}>← Back to Providers</button>
            <h2 className="section-title">Select Date & Time</h2>
            <div className="booking-datetime">
              <div className="form-group">
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={selectedDate} onChange={handleDateChange} min={getMinDate()} max={getMaxDate()} style={{ maxWidth: '200px' }} />
              </div>
              {selectedDate && (
                <div className="booking-slots">
                  <label className="form-label">Available Times</label>
                  {slotsLoading ? <div className="loading-spinner"><div className="spinner"></div></div> : slots.length > 0 ? (
                    <div className="booking-slots-grid">
                      {slots.map((slot, i) => (
                        <button key={i} className={`booking-slot ${selectedSlot === slot ? 'selected' : ''}`} onClick={() => { setSelectedSlot(slot); setStep(4); }}>{slot.display}</button>
                      ))}
                    </div>
                  ) : <p style={{ color: 'var(--text-muted)' }}>No available times on this date. Please try another day.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="section-card">
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(3)} style={{ marginBottom: '16px' }}>← Back to Time Selection</button>
            <h2 className="section-title">Your Information</h2>
            <div className="admin-form-grid">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input type="text" className="form-input" value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Your full name" required />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input type="email" className="form-input" value={patientEmail} onChange={e => setPatientEmail(e.target.value)} placeholder="your@email.com" required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={patientPhone} onChange={e => setPatientPhone(e.target.value)} placeholder="(555) 555-5555" />
              </div>
            </div>
            <h2 className="section-title" style={{ marginTop: '30px' }}>Appointment Summary</h2>
            <div className="booking-summary">
              <div className="booking-summary-item"><span>Service:</span><strong>{selectedService?.name}</strong></div>
              <div className="booking-summary-item"><span>Duration:</span><strong>{selectedService?.duration_minutes} min</strong></div>
              <div className="booking-summary-item"><span>Provider:</span><strong>{selectedStaff?.name}</strong></div>
              <div className="booking-summary-item"><span>Date:</span><strong>{formatDate(selectedDate)}</strong></div>
              <div className="booking-summary-item"><span>Time:</span><strong>{selectedSlot?.display}</strong></div>
              <div className="booking-summary-item"><span>Price:</span><strong>${parseFloat(selectedService?.price || 0).toFixed(2)}</strong></div>
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label className="form-label">Notes (optional)</label>
              <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any special requests or information for your provider..." />
            </div>
            <button className="btn btn-gold" onClick={handleBook} disabled={booking || !patientName || !patientEmail} style={{ marginTop: '20px', width: '100%' }}>{booking ? 'Booking...' : 'Confirm Booking'}</button>
          </div>
        )}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

// Dedicated landing page for Free In Clinic Assessment
// Multi-select options for the assessment screening step — kept in one place so
// the frontend UI, the backend tag builder, and future admin filters can all
// stay in sync. If you change these strings, update the ASSESSMENT_INTEREST_SLUGS
// map in server/index.js in the same commit.
const ASSESSMENT_INTEREST_OPTIONS = [
  'Medical Weight Loss',
  'Peptide Therapy',
  'Body Sculpting',
  'Red Light Therapy',
  'Pelvic Floor',
  'Coaching & Nutrition'
];
const ASSESSMENT_HOW_HEARD_OPTIONS = [
  'Google search',
  'Google Ads',
  'Facebook / Instagram',
  'Referred by friend or family',
  'Referred by another provider',
  'Drove by / saw signage',
  'Other'
];

function FreeAssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookedInfo, setBookedInfo] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  // NEW: screening + attribution state
  const [goal, setGoal] = useState('');
  const [interests, setInterests] = useState([]);
  const [smsConsent, setSmsConsent] = useState(false);
  const [howHeard, setHowHeard] = useState('');

  // Capture UTMs + gclid from the URL exactly once on mount. Kept in a ref
  // so re-renders don't lose them if the URL later changes (e.g. embed nav).
  const attributionRef = useRef(null);
  if (attributionRef.current === null) {
    const params = new URLSearchParams(window.location.search);
    attributionRef.current = {
      utm_source:   params.get('utm_source')   || null,
      utm_medium:   params.get('utm_medium')   || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content:  params.get('utm_content')  || null,
      gclid:        params.get('gclid')        || null
    };
  }

  const toggleInterest = (label) => {
    setInterests(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };

  // Embed mode detection
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';

  // Post height to parent window when in embed mode
  useEffect(() => {
    if (!isEmbed) return;
    const postHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'ascent-booking-height', height }, '*');
    };
    postHeight();
    const observer = new MutationObserver(postHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', postHeight);
    return () => { observer.disconnect(); window.removeEventListener('resize', postHeight); };
  }, [isEmbed, step, booked]);

  useEffect(() => {
    fetch('/api/public/services')
      .then(r => r.json())
      .then(services => {
        const assessment = services.find(s => s.name.toLowerCase().includes('free') && s.name.toLowerCase().includes('assessment'));
        if (assessment) {
          setService(assessment);
          return fetch(`/api/public/staff/${assessment.id}`);
        } else {
          setError('Service not available');
          return null;
        }
      })
      .then(r => r ? r.json() : [])
      .then(staffData => { if (staffData) setStaff(staffData); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Step 1 (screening + contact) → step 2 (provider). Minimal validation only —
  // deep validation happens on the final Book click.
  const handleContinueFromScreening = () => {
    if (!patientName.trim()) { setToast({ message: 'Please enter your full name', type: 'error' }); return; }
    if (!patientEmail.trim()) { setToast({ message: 'Please enter your email', type: 'error' }); return; }
    setStep(2);
  };

  const handleStaffSelect = (staffMember) => {
    setSelectedStaff(staffMember);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
    setStep(3);
  };

  const handleDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedSlot(null);
    if (!date || !service) return;
    setSlotsLoading(true);
    try {
      const staffId = selectedStaff.id === 'any' ? staff[0]?.id : selectedStaff.id;
      const res = await fetch(`/api/public/slots?staff_id=${selectedStaff.id === 'any' ? 'any' : selectedStaff.id}&service_id=${service.id}&date=${date}`);
      setSlots(await res.json());
    } catch (e) { setToast({ message: 'Failed to load times', type: 'error' }); setSlots([]); }
    finally { setSlotsLoading(false); }
  };

  const handleBook = async () => {
    if (!patientName || !patientEmail) { setToast({ message: 'Please enter your name and email', type: 'error' }); return; }
    if (!selectedSlot) { setToast({ message: 'Please select a time', type: 'error' }); return; }
    setBooking(true);
    try {
      const attribution = attributionRef.current || {};
      const res = await fetch('/api/public/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: service.id,
          staff_id: selectedStaff.id === 'any' ? selectedSlot.staff_id : selectedStaff.id,
          start_dt: selectedSlot.start,
          patient_name: patientName,
          patient_email: patientEmail,
          patient_phone: patientPhone,
          notes,
          referrer_url: document.referrer || null,
          screening: {
            goal: goal.trim() || null,
            interests,
            sms_consent: !!smsConsent,
            how_heard_about_us: howHeard || null
          },
          utm_source: attribution.utm_source,
          utm_medium: attribution.utm_medium,
          utm_campaign: attribution.utm_campaign,
          utm_content: attribution.utm_content,
          gclid: attribution.gclid,
          referral_code: getStoredReferralCode()
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Booking failed');
      // Redirect to thank-you page for conversion tracking
      const params = new URLSearchParams({
        service: service.name,
        provider: selectedStaff.id === 'any' ? (selectedSlot.staff_name || '') : (selectedStaff.name || ''),
        date: selectedDate,
        time: selectedSlot.display || '',
        email: patientEmail
      });
      if (isEmbed) {
        // In embed mode, tell parent to redirect (or navigate within iframe)
        window.parent.postMessage({ type: 'ascent-booking-redirect', url: '/thank-you?' + params.toString() }, '*');
      }
      window.location.href = '/thank-you?' + params.toString();
    } catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setBooking(false); }
  };

  const formatDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { timeZone: 'America/Denver', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const getMinDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };
  const getMaxDate = () => { const d = new Date(); d.setDate(d.getDate() + 60); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };

  if (loading) return <div className="public-booking-page"><div className="public-booking-container"><div className="loading-spinner" style={{ minHeight: '400px' }}><div className="spinner"></div></div></div></div>;

  if (error) return (
    <div className="public-booking-page" style={isEmbed ? { minHeight: 'auto', padding: '0' } : {}}>
      <div className="public-booking-container" style={isEmbed ? { paddingTop: '0' } : {}}>
        {!isEmbed && <div className="public-booking-header"><h1>Ascent Trim</h1><span>Schedule Assessment</span></div>}
        <div className="section-card" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: 'var(--text-muted)' }}>{error}</p>
          {!isEmbed && <Link to="/book" className="btn btn-gold" style={{ marginTop: '20px' }}>View All Services</Link>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="public-booking-page" style={isEmbed ? { minHeight: 'auto', padding: '0' } : {}}>
      <div className="public-booking-container" style={isEmbed ? { paddingTop: '0' } : {}}>
        {!isEmbed && (
          <div className="public-booking-header">
            <h1>Ascent Trim</h1>
            <span>Schedule In Clinic Assessment</span>
            <p style={{ marginTop: '10px', color: 'var(--text-muted)', fontSize: '14px', maxWidth: '400px', margin: '10px auto 0' }}>
              Start your wellness journey with a complimentary assessment. Meet with one of our experts to discuss your goals.
            </p>
          </div>
        )}

        <div className="booking-steps">
          <div className={`booking-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}><span>1</span> About you</div>
          <div className={`booking-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}><span>2</span> Provider</div>
          <div className={`booking-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}><span>3</span> Time</div>
          <div className={`booking-step ${step >= 4 ? 'active' : ''}`}><span>4</span> Confirm</div>
        </div>

        {step === 1 && (
          <div className="section-card">
            <h2 className="section-title">Tell us about yourself</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '20px', fontSize: '14px' }}>
              A few quick questions so we can prepare for your assessment.
            </p>
            <div className="admin-form-grid">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input type="text" className="form-input" value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="Your full name" required />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input type="email" className="form-input" value={patientEmail} onChange={e => setPatientEmail(e.target.value)} placeholder="your@email.com" required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={patientPhone} onChange={e => setPatientPhone(e.target.value)} placeholder="(555) 555-5555" />
              </div>
              <div className="form-group full-width">
                <label className="form-label">What's your goal?</label>
                <textarea className="form-input" value={goal} onChange={e => setGoal(e.target.value)} rows={3} placeholder="e.g. lose 25 lbs, sleep better, feel more energetic..." />
              </div>
              <div className="form-group full-width">
                <label className="form-label">What are you most interested in? (check all that apply)</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', marginTop: '6px' }}>
                  {ASSESSMENT_INTEREST_OPTIONS.map(label => (
                    <label key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', background: interests.includes(label) ? 'var(--gold-50, #fef3cd)' : 'transparent' }}>
                      <input type="checkbox" checked={interests.includes(label)} onChange={() => toggleInterest(label)} style={{ minWidth: '16px', minHeight: '16px' }} />
                      <span style={{ fontSize: '14px' }}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group full-width">
                <label className="form-label">Where did you hear about us?</label>
                <select className="form-input" value={howHeard} onChange={e => setHowHeard(e.target.value)}>
                  <option value="">Select an option…</option>
                  {ASSESSMENT_HOW_HEARD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div className="form-group full-width">
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)' }}>
                  <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} style={{ marginTop: '3px', minWidth: '16px', minHeight: '16px' }} />
                  <span>
                    I agree to receive appointment SMS reminders from Ascent Trim &amp; Wellness at the phone number above. Message and data rates may apply. Reply STOP to opt out.
                  </span>
                </label>
              </div>
            </div>
            <button className="btn btn-gold" onClick={handleContinueFromScreening} style={{ marginTop: '20px', width: '100%' }}>
              Continue to schedule →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="section-card">
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)} style={{ marginBottom: '16px' }}>← Back</button>
            <h2 className="section-title">Select a Provider</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>Choose who you'd like to meet with for your free assessment.</p>
            {staff.length > 0 ? (
              <div className="booking-staff-grid">
                <div className="booking-staff-card" onClick={() => handleStaffSelect({ id: 'any', name: 'Any Provider' })}>
                  <div className="booking-staff-avatar" style={{ backgroundColor: '#10b981' }}>★</div>
                  <div className="booking-staff-name">Any Provider</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Next available</div>
                </div>
                {staff.map(s => (
                  <div key={s.id} className="booking-staff-card" onClick={() => handleStaffSelect(s)}>
                    <div className="booking-staff-avatar">{s.name.split(' ').map(n => n[0]).join('')}</div>
                    <div className="booking-staff-name">{s.name}</div>
                  </div>
                ))}
              </div>
            ) : <div className="empty-state"><p>No providers available at this time.</p></div>}
          </div>
        )}

        {step === 3 && (
          <div className="section-card">
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(2)} style={{ marginBottom: '16px' }}>← Back</button>
            <h2 className="section-title">Select Date & Time</h2>
            <div className="booking-datetime">
              <div className="form-group">
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={selectedDate} onChange={handleDateChange} min={getMinDate()} max={getMaxDate()} style={{ maxWidth: '200px' }} />
              </div>
              {selectedDate && (
                <div className="booking-slots">
                  <label className="form-label">Available Times</label>
                  {slotsLoading ? <div className="loading-spinner"><div className="spinner"></div></div> : slots.length > 0 ? (
                    <div className="booking-slots-grid">
                      {slots.map((slot, i) => (
                        <button key={i} className={`booking-slot ${selectedSlot === slot ? 'selected' : ''}`} onClick={() => { setSelectedSlot(slot); setStep(4); }}>
                          {slot.display}
                          {selectedStaff?.id === 'any' && slot.staff_name && <span style={{ display: 'block', fontSize: '11px', opacity: 0.8 }}>with {slot.staff_name.split(' ')[0]}</span>}
                        </button>
                      ))}
                    </div>
                  ) : <p style={{ color: 'var(--text-muted)' }}>No available times on this date. Please try another day.</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="section-card">
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(3)} style={{ marginBottom: '16px' }}>← Back</button>
            <h2 className="section-title">Confirm your booking</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '13px' }}>
              Review below and confirm. We'll send you a text and email once it's booked.
            </p>
            <h2 className="section-title" style={{ marginTop: '10px' }}>Appointment Summary</h2>
            <div className="booking-summary">
              <div className="booking-summary-item"><span>Service:</span><strong>Free In Clinic Assessment</strong></div>
              <div className="booking-summary-item"><span>Duration:</span><strong>{service?.duration_minutes} min</strong></div>
              <div className="booking-summary-item"><span>Provider:</span><strong>{selectedStaff?.id === 'any' ? selectedSlot?.staff_name : selectedStaff?.name}</strong></div>
              <div className="booking-summary-item"><span>Date:</span><strong>{formatDate(selectedDate)}</strong></div>
              <div className="booking-summary-item"><span>Time:</span><strong>{selectedSlot?.display}</strong></div>
              <div className="booking-summary-item"><span>Price:</span><strong style={{ color: 'var(--success)' }}>FREE</strong></div>
            </div>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <label className="form-label">Notes (optional)</label>
              <textarea className="form-input" value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any questions or topics you'd like to discuss..." />
            </div>
            <button className="btn btn-gold" onClick={handleBook} disabled={booking || !patientName || !patientEmail} style={{ marginTop: '20px', width: '100%' }}>{booking ? 'Booking...' : 'Schedule My Free Assessment'}</button>
          </div>
        )}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

function PatientAppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null); const [resendingId, setResendingId] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleApt, setRescheduleApt] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAppointments = async () => { try { setAppointments(await api.get('/api/my-appointments')); } catch (e) { console.error(e); } finally { setLoading(false); } };
  useEffect(() => { fetchAppointments(); }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try { await api.put(`/api/my-appointments/${id}/cancel`); setToast({ message: 'Appointment cancelled', type: 'success' }); fetchAppointments(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); }
  };

  const openReschedule = (apt) => { setRescheduleApt(apt); setNewDate(''); setSlots([]); setSelectedSlot(null); setShowRescheduleModal(true); };

  const handleDateChange = async (e) => {
    const date = e.target.value;
    setNewDate(date);
    setSelectedSlot(null);
    if (!date || !rescheduleApt) return;
    setSlotsLoading(true);
    try { setSlots(await api.get(`/api/booking/slots?staff_id=${rescheduleApt.staff_id}&service_id=${rescheduleApt.service_id}&date=${date}`)); }
    catch (e) { setSlots([]); } finally { setSlotsLoading(false); }
  };

  const handleReschedule = async () => {
    if (!selectedSlot) { setToast({ message: 'Please select a new time', type: 'error' }); return; }
    setSaving(true);
    try { await api.put(`/api/my-appointments/${rescheduleApt.id}/reschedule`, { start_dt: selectedSlot.start }); setToast({ message: 'Appointment rescheduled!', type: 'success' }); setShowRescheduleModal(false); fetchAppointments(); }
    catch (error) { setToast({ message: error.message, type: 'error' }); } finally { setSaving(false); }
  };

  const formatDateTime = (dt) => new Date(dt).toLocaleString('en-US', { timeZone: 'America/Denver', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const getStatusColor = (status) => { if (status === 'scheduled') return 'var(--success)'; if (status === 'cancelled') return 'var(--danger)'; if (status === 'completed') return 'var(--text-muted)'; return 'var(--text-secondary)'; };
  const getMinDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA', { timeZone: 'America/Denver' }); };

  const upcoming = appointments.filter(a => new Date(a.start_dt) >= new Date() && a.status === 'scheduled');
  const past = appointments.filter(a => new Date(a.start_dt) < new Date() || a.status !== 'scheduled');

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">My Appointments</h1><p className="page-subtitle">View and manage your appointments</p></div>
        <Link to="/book-appointment" className="btn btn-gold">Book New</Link>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <>
          <div className="section-card" style={{ marginBottom: '24px' }}>
            <h2 className="section-title">Upcoming</h2>
            {upcoming.length > 0 ? (
              <div className="appointments-list">
                {upcoming.map(apt => (
                  <div key={apt.id} className="appointment-card">
                    <div className="appointment-info">
                      <h3>{apt.service_name || 'Appointment'}</h3>
                      <p><strong>{formatDateTime(apt.start_dt)}</strong></p>
                      <p>with {apt.staff_name}</p>
                      {apt.notes && <p className="appointment-notes">{apt.notes}</p>}
                    </div>
                    <div className="appointment-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => openReschedule(apt)}>Reschedule</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleCancel(apt.id)}>Cancel</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="empty-state"><p>No upcoming appointments. <Link to="/book-appointment">Book one now</Link></p></div>}
          </div>

          {past.length > 0 && (
            <div className="section-card">
              <h2 className="section-title">Past & Cancelled</h2>
              <div className="appointments-list">
                {past.map(apt => (
                  <div key={apt.id} className="appointment-card past">
                    <div className="appointment-info">
                      <h3>{apt.service_name || 'Appointment'}</h3>
                      <p>{formatDateTime(apt.start_dt)} with {apt.staff_name}</p>
                      <span className="appointment-status" style={{ color: getStatusColor(apt.status) }}>{apt.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {showRescheduleModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowRescheduleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Reschedule Appointment</h2><button className="modal-close" onClick={() => setShowRescheduleModal(false)} disabled={saving}>×</button></div>
            <div className="form-group">
              <label className="form-label">Select New Date</label>
              <input type="date" className="form-input" value={newDate} onChange={handleDateChange} min={getMinDate()} disabled={saving} />
            </div>
            {newDate && (
              <div className="form-group">
                <label className="form-label">Available Times</label>
                {slotsLoading ? <div className="loading-spinner"><div className="spinner"></div></div> : slots.length > 0 ? (
                  <div className="booking-slots-grid">{slots.map((slot, i) => (<button key={i} className={`booking-slot ${selectedSlot === slot ? 'selected' : ''}`} onClick={() => setSelectedSlot(slot)} disabled={saving}>{slot.display}</button>))}</div>
                ) : <p>No available times on this date.</p>}
              </div>
            )}
            <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setShowRescheduleModal(false)} disabled={saving}>Cancel</button><button className="btn btn-gold" onClick={handleReschedule} disabled={saving || !selectedSlot}>{saving ? 'Saving...' : 'Confirm'}</button></div>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ==================== END SCHEDULING SYSTEM COMPONENTS ====================


function ManageAppointmentPage() {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("view"); // view, cancel, reschedule
  const [cancelReason, setCancelReason] = useState("");
  const [toast, setToast] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    fetchAppointment();
  }, [token]);

  const fetchAppointment = async () => {
    try {
      const response = await fetch(`/api/appointment/manage/${token}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Appointment not found");
      }
      const data = await response.json();
      setAppointment(data);
      
      // Set default date for reschedule.
      // appointments.start_dt is naive-MT stored in a WITHOUT-TZ column; the API
      // serializes it with a Z suffix that misrepresents the value as UTC. To
      // read back the original wall-clock day we picked at booking, format
      // with timeZone: "UTC" so no offset shift is applied.
      const aptDate = new Date(data.start_dt);
      setSelectedDate(aptDate.toLocaleDateString("en-CA", { timeZone: "UTC" }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await fetch(`/api/appointment/manage/${token}/slots?date=${date}`);
      const slots = await response.json();
      setAvailableSlots(slots);
    } catch (err) {
      console.error("Failed to fetch slots:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (mode === "reschedule" && selectedDate) {
      fetchSlots(selectedDate);
    }
  }, [mode, selectedDate]);

  const handleCancel = async () => {
    try {
      const response = await fetch(`/api/appointment/manage/${token}/cancel`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: cancelReason })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }
      setToast({ message: "Appointment cancelled successfully", type: "success" });
      setAppointment({ ...appointment, status: "cancelled" });
      setMode("view");
    } catch (err) {
      setToast({ message: err.message, type: "error" });
    }
  };

  const handleReschedule = async () => {
    if (!selectedSlot) return;
    try {
      const response = await fetch(`/api/appointment/manage/${token}/reschedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_dt: selectedSlot.start })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }
      const result = await response.json();
      setToast({ message: "Appointment rescheduled successfully!", type: "success" });
      setAppointment({ ...appointment, start_dt: result.start_dt, end_dt: result.end_dt });
      setMode("view");
      setSelectedSlot(null);
    } catch (err) {
      setToast({ message: err.message, type: "error" });
    }
  };

  const formatDateTime = (dt) => {
    // appointments.start_dt is naive Mountain Time stored in a WITHOUT-TZ
    // column. node-postgres reads it as if it were UTC, JSON.stringify emits a
    // Z suffix, but the semantic content is still MT. Formatting with
    // timeZone: "UTC" displays the raw wall-clock value the patient booked;
    // using "America/Denver" here would double-convert and shift the display
    // 6-7 hours earlier (the bug that made an 11:00 AM appointment appear as
    // 5:00 AM on the manage-appointment page).
    const d = new Date(dt);
    return d.toLocaleDateString("en-US", { timeZone: "UTC", weekday: "long", month: "long", day: "numeric", year: "numeric" }) +
           " at " + d.toLocaleTimeString("en-US", { timeZone: "UTC", hour: "numeric", minute: "2-digit" });
  };

  // formatTime() was removed deliberately — do not bring it back for slots.
  //
  // It rendered with timeZone:"UTC", which is right for `appointment.start_dt`
  // (naive Mountain time wearing a misleading Z — that is what formatDateTime
  // above is for) and WRONG for slot times, which are genuine UTC instants.
  // Applying it to a slot displayed six hours ahead of the real time, and
  // patients booked what they were shown.
  //
  // Slot buttons render the server's `slot.display`, already formatted in
  // Mountain time by getAvailableSlots(). Let the server own the conversion.

  if (loading) {
    return (
      <div className="manage-appointment-page">
        <div className="manage-card loading">Loading appointment...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-appointment-page">
        <div className="manage-card error">
          <h2>Appointment Not Found</h2>
          <p>{error}</p>
          <p>This link may have expired or the appointment no longer exists.</p>
        </div>
      </div>
    );
  }

  const isPast = new Date(appointment.start_dt) < new Date();
  const isCancelled = appointment.status === "cancelled";

  return (
    <div className="manage-appointment-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="manage-card">
        <div className="manage-header">
          <img src="/logo.png" alt="Ascent Trim" className="manage-logo" />
          <h1>Manage Your Appointment</h1>
        </div>

        <div className="appointment-details">
          <div className={`status-badge ${appointment.status}`}>{appointment.status.toUpperCase()}</div>
          
          <div className="detail-row">
            <span className="label">Service:</span>
            <span className="value">{appointment.service_name}</span>
          </div>
          <div className="detail-row">
            <span className="label">Provider:</span>
            <span className="value">{appointment.staff_name}</span>
          </div>
          <div className="detail-row">
            <span className="label">Date & Time:</span>
            <span className="value">{formatDateTime(appointment.start_dt)}</span>
          </div>
          <div className="detail-row">
            <span className="label">Duration:</span>
            <span className="value">{appointment.duration_minutes} minutes</span>
          </div>
          {appointment.notes && (
            <div className="detail-row">
              <span className="label">Notes:</span>
              <span className="value">{appointment.notes}</span>
            </div>
          )}
        </div>

        {mode === "view" && !isCancelled && !isPast && (
          <div className="manage-actions">
            <button className="btn-reschedule" onClick={() => setMode("reschedule")}>
              Reschedule Appointment
            </button>
            <button className="btn-cancel" onClick={() => setMode("cancel")}>
              Cancel Appointment
            </button>
          </div>
        )}

        {mode === "cancel" && (
          <div className="cancel-form">
            <h3>Cancel Appointment</h3>
            <p>Are you sure you want to cancel this appointment?</p>
            <textarea
              placeholder="Reason for cancellation (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => setMode("view")}>Go Back</button>
              <button className="btn-danger" onClick={handleCancel}>Confirm Cancellation</button>
            </div>
          </div>
        )}

        {mode === "reschedule" && (
          <div className="reschedule-form">
            <h3>Reschedule Appointment</h3>
            <div className="date-picker">
              <label>Select a new date:</label>
              <input
                type="date"
                value={selectedDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => { setSelectedDate(e.target.value); setSelectedSlot(null); }}
              />
            </div>
            
            {loadingSlots && <p className="loading-slots">Loading available times...</p>}
            
            {!loadingSlots && availableSlots.length === 0 && selectedDate && (
              <p className="no-slots">No available times on this date. Please try another day.</p>
            )}
            
            {!loadingSlots && availableSlots.length > 0 && (
              <div className="time-slots">
                <label>Select a time:</label>
                <div className="slots-grid">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.start}
                      className={`slot-btn ${selectedSlot?.start === slot.start ? "selected" : ""}`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      {slot.display}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="form-actions">
              <button className="btn-secondary" onClick={() => { setMode("view"); setSelectedSlot(null); }}>Go Back</button>
              <button className="btn-primary" onClick={handleReschedule} disabled={!selectedSlot}>
                Confirm New Time
              </button>
            </div>
          </div>
        )}

        {(isCancelled || isPast) && (
          <div className="manage-info">
            {isCancelled && <p>This appointment has been cancelled.</p>}
            {isPast && !isCancelled && <p>This appointment has already occurred.</p>}
          </div>
        )}
      </div>

      <div className="manage-footer">
        <p>Questions? Contact us at <a href="mailto:info@ascenttrim.com">info@ascenttrim.com</a></p>
      </div>
    </div>
  );
}


function ForceChangePasswordScreen() {
  const { user, refreshUser, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (newPassword.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      await refreshUser();
    } catch (err) { setError(err.message || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: '420px' }}>
        <div className="login-logo"><h1>Ascent Trim</h1><span>Password Change Required</span></div>
        <p style={{ color: 'var(--text-muted)', marginBottom: '20px', fontSize: '14px', textAlign: 'center' }}>
          Welcome, {user?.name}! Please set a new password before continuing.
        </p>
        {error && <div className="error-message"><span>⚠️</span> {error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label className="form-label">Current Password</label><input type="password" className="form-input" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required disabled={saving} placeholder="Enter the password you were given" /></div>
          <div className="form-group"><label className="form-label">New Password</label><input type="password" className="form-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} required disabled={saving} placeholder="At least 6 characters" /></div>
          <div className="form-group"><label className="form-label">Confirm New Password</label><input type="password" className="form-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required disabled={saving} placeholder="Re-enter new password" /></div>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%', marginTop: '10px' }}>{saving ? 'Changing...' : 'Set New Password'}</button>
        </form>
        <button className="btn btn-ghost" onClick={logout} style={{ width: '100%', marginTop: '10px' }}>Sign Out</button>
      </div>
    </div>
  );
}

function QuillEditor({ initialValue, onChange, placeholder }) {
  const elRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (quillRef.current || !elRef.current) return;
    const quill = new Quill(elRef.current, {
      theme: 'snow',
      placeholder: placeholder || 'Write your post here...',
      modules: {
        toolbar: {
          container: [
            [{ header: [2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'link', 'image'],
            ['clean'],
          ],
          handlers: {
            image() {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = async () => {
                const file = input.files && input.files[0];
                if (!file) return;
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await fetch('/api/admin/uploads', { method: 'POST', credentials: 'include', body: fd });
                  if (!res.ok) throw new Error('upload failed');
                  const data = await res.json();
                  const range = quill.getSelection(true);
                  quill.insertEmbed(range.index, 'image', data.url, 'user');
                  quill.setSelection(range.index + 1);
                } catch (err) {
                  window.alert('Image upload failed. Please try again.');
                }
              };
              input.click();
            },
          },
        },
      },
    });
    if (initialValue) quill.clipboard.dangerouslyPasteHTML(initialValue);
    quill.on('text-change', () => {
      const html = quill.root.innerHTML;
      onChangeRef.current(html === '<p><br></p>' ? '' : html);
    });
    quillRef.current = quill;
  }, []);
  return <div className="blog-quill"><div ref={elRef} /></div>;
}

function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [pub, setPub] = useState({ state: 'idle', message: '', at: '' });
  const pubTimer = useRef(null);
  const checkPub = useCallback(() => {
    api.get('/api/admin/publish-status').then((s) => {
      setPub(s || { state: 'idle' });
      if (s && s.state === 'building') pubTimer.current = setTimeout(checkPub, 4000);
    }).catch(() => {});
  }, []);
  useEffect(() => { checkPub(); return () => clearTimeout(pubTimer.current); }, [checkPub]);
  const doPublish = async () => {
    setPub({ state: 'building', message: 'Starting...' });
    try { await api.post('/api/admin/publish', {}); setTimeout(checkPub, 2500); }
    catch (e) { setPub({ state: 'failed', message: e.message || 'Failed to start' }); }
  };
  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/blog-posts').then(setPosts).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const upd = (patch) => setEditing((e) => ({ ...e, ...patch }));
  const startNew = () => setEditing({ title: '', excerpt: '', body: '', cover_image_url: '', cover_image_alt: '', author: '', status: 'draft', sort_order: 0 });
  const save = async () => {
    if (!editing.title || !editing.title.trim()) { setToast({ type: 'error', msg: 'Please enter a title' }); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/api/admin/blog-posts/${editing.id}`, editing);
      else await api.post('/api/admin/blog-posts', editing);
      setToast({ type: 'success', msg: 'Post saved' });
      setEditing(null);
      load();
    } catch (err) { setToast({ type: 'error', msg: err.message || 'Save failed' }); }
    finally { setSaving(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/api/admin/blog-posts/${p.id}`); setToast({ type: 'success', msg: 'Post deleted' }); load(); }
    catch (err) { setToast({ type: 'error', msg: err.message || 'Delete failed' }); }
  };
  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/admin/uploads', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('upload failed');
      const data = await res.json();
      upd({ cover_image_url: data.url });
    } catch (err) { setToast({ type: 'error', msg: 'Cover image upload failed' }); }
  };
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  return (
    <div className="admin-page blog-admin">
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {!editing ? (
        <>
          <div className="blog-header">
            <div><h1>Blog</h1><p className="blog-sub">Write and publish posts for the website.</p></div>
            <div className="blog-publish">
              {pub.state === 'building' ? <span className="blog-pub-status building">Publishing... (~1-2 min)</span> : null}
              {pub.state === 'success' ? <span className="blog-pub-status ok">Website is up to date</span> : null}
              {pub.state === 'failed' ? <span className="blog-pub-status err">Publish failed - try again</span> : null}
              <button className="btn" onClick={doPublish} disabled={pub.state === 'building'} title="Rebuild the public website with the latest posts">
                {pub.state === 'building' ? 'Publishing...' : 'Publish to website'}
              </button>
              <button className="btn btn-primary" onClick={startNew}>+ New Post</button>
            </div>
          </div>
          <div className="section-card">
            {loading ? <p>Loading...</p> : posts.length === 0 ? (
              <div className="blog-empty">No posts yet. Click <strong>New Post</strong> to write your first one.</div>
            ) : (
              <table className="blog-table">
                <thead><tr><th>Title</th><th>Status</th><th>Published</th><th></th></tr></thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.id}>
                      <td className="blog-title-cell">
                        {p.cover_image_url ? <img src={p.cover_image_url} alt="" className="blog-thumb" /> : <div className="blog-thumb blog-thumb-empty" />}
                        <span>{p.title}</span>
                      </td>
                      <td><span className={`blog-badge ${p.status === 'published' ? 'is-live' : 'is-draft'}`}>{p.status}</span></td>
                      <td>{fmtDate(p.published_at)}</td>
                      <td className="blog-actions">
                        <button className="btn btn-sm" onClick={() => setEditing({ ...p })}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => remove(p)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="blog-header">
            <h1>{editing.id ? 'Edit Post' : 'New Post'}</h1>
            <div className="blog-header-actions">
              <button className="btn" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Post'}</button>
            </div>
          </div>
          <div className="section-card blog-form">
            <label className="blog-field"><span>Title</span>
              <input type="text" value={editing.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Post title" />
            </label>
            <label className="blog-field"><span>Short summary <em>(shown on the blog list)</em></span>
              <textarea rows={2} value={editing.excerpt} onChange={(e) => upd({ excerpt: e.target.value })} placeholder="One or two sentences describing the post..." />
            </label>
            <div className="blog-field">
              <span>Cover image</span>
              <div className="cover-upload">
                {editing.cover_image_url ? <img src={editing.cover_image_url} alt="cover" className="cover-preview" /> : <div className="cover-placeholder">No image yet</div>}
                <div className="cover-buttons">
                  <input id="blog-coverfile" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadCover(e.target.files[0])} />
                  <button type="button" className="btn btn-sm" onClick={() => document.getElementById('blog-coverfile').click()}>Upload image</button>
                  {editing.cover_image_url ? <button type="button" className="btn btn-sm" onClick={() => upd({ cover_image_url: '' })}>Remove</button> : null}
                </div>
              </div>
            </div>
            <div className="blog-field">
              <span>Body</span>
              <QuillEditor key={editing.id || 'new'} initialValue={editing.body} onChange={(html) => upd({ body: html })} />
            </div>
            <div className="blog-row">
              <label className="blog-field"><span>Author <em>(optional)</em></span>
                <input type="text" value={editing.author} onChange={(e) => upd({ author: e.target.value })} placeholder="e.g. The Ascent Trim Team" />
              </label>
              <label className="blog-field"><span>Status</span>
                <select value={editing.status} onChange={(e) => upd({ status: e.target.value })}>
                  <option value="draft">Draft (not visible on site)</option>
                  <option value="published">Published (live on site)</option>
                </select>
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PublishButton() {
  const [pub, setPub] = useState({ state: 'idle' });
  const timer = useRef(null);
  const check = useCallback(() => {
    api.get('/api/admin/publish-status').then((s) => {
      setPub(s || { state: 'idle' });
      if (s && s.state === 'building') timer.current = setTimeout(check, 4000);
    }).catch(() => {});
  }, []);
  useEffect(() => { check(); return () => clearTimeout(timer.current); }, [check]);
  const go = async () => {
    setPub({ state: 'building' });
    try { await api.post('/api/admin/publish', {}); setTimeout(check, 2500); }
    catch (e) { setPub({ state: 'failed' }); }
  };
  return (
    <div className="blog-publish">
      {pub.state === 'building' ? <span className="blog-pub-status building">Publishing... (~1-2 min)</span> : null}
      {pub.state === 'success' ? <span className="blog-pub-status ok">Website is up to date</span> : null}
      {pub.state === 'failed' ? <span className="blog-pub-status err">Publish failed - try again</span> : null}
      <button className="btn" onClick={go} disabled={pub.state === 'building'} title="Rebuild the public website with the latest content">
        {pub.state === 'building' ? 'Publishing...' : 'Publish to website'}
      </button>
    </div>
  );
}

function ListField({ items, onChange, placeholder, addLabel }) {
  return (
    <div className="rc-list">
      {items.map((it, i) => (
        <div className="rc-row" key={i}>
          <input value={it} placeholder={placeholder} onChange={(e) => onChange(items.map((x, k) => (k === i ? e.target.value : x)))} />
          <button type="button" className="rc-x" onClick={() => onChange(items.filter((_, k) => k !== i))} aria-label="Remove">&times;</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...items, ''])}>+ {addLabel}</button>
    </div>
  );
}

const RECIPE_CATEGORIES = ['Dinner', 'Appetizer', 'Dessert', 'Snack'];

function AdminRecipePage() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/admin/recipes').then(setRecipes).catch(() => setRecipes([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const upd = (patch) => setEditing((e) => ({ ...e, ...patch }));
  const startNew = () => setEditing({ title: '', category: 'Dinner', short_description: '', description: '', image_url: '', image_alt: '', serves: '', badges: [], ingredients: [''], directions: [''], nutritionRows: [{ label: '', value: '' }], status: 'published', sort_order: 0 });
  const startEdit = (r) => setEditing({ ...r, badges: r.badges || [], ingredients: r.ingredients || [], directions: r.directions || [], nutritionRows: Object.entries(r.nutrition || {}).map(([label, value]) => ({ label, value })) });
  const save = async () => {
    if (!editing.title || !editing.title.trim()) { setToast({ type: 'error', msg: 'Please enter a title' }); return; }
    setSaving(true);
    const payload = {
      ...editing,
      badges: (editing.badges || []).map((x) => x.trim()).filter(Boolean),
      ingredients: (editing.ingredients || []).map((x) => x.trim()).filter(Boolean),
      directions: (editing.directions || []).map((x) => x.trim()).filter(Boolean),
      nutrition: Object.fromEntries((editing.nutritionRows || []).filter((r) => r.label.trim()).map((r) => [r.label.trim(), r.value])),
    };
    delete payload.nutritionRows;
    try {
      if (editing.id) await api.put(`/api/admin/recipes/${editing.id}`, payload);
      else await api.post('/api/admin/recipes', payload);
      setToast({ type: 'success', msg: 'Recipe saved' });
      setEditing(null);
      load();
    } catch (err) { setToast({ type: 'error', msg: err.message || 'Save failed' }); }
    finally { setSaving(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/api/admin/recipes/${r.id}`); setToast({ type: 'success', msg: 'Recipe deleted' }); load(); }
    catch (err) { setToast({ type: 'error', msg: err.message || 'Delete failed' }); }
  };
  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch('/api/admin/uploads', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('upload failed');
      const data = await res.json();
      upd({ image_url: data.url });
    } catch (err) { setToast({ type: 'error', msg: 'Image upload failed' }); }
  };
  const nutRow = (i, patch) => upd({ nutritionRows: editing.nutritionRows.map((r, k) => (k === i ? { ...r, ...patch } : r)) });

  return (
    <div className="admin-page blog-admin">
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {!editing ? (
        <>
          <div className="blog-header">
            <div><h1>Recipes</h1><p className="blog-sub">Manage the recipes shown on the website.</p></div>
            <div className="blog-publish">
              <PublishButton />
              <button className="btn btn-primary" onClick={startNew}>+ New Recipe</button>
            </div>
          </div>
          <div className="section-card">
            {loading ? <p>Loading...</p> : recipes.length === 0 ? (
              <div className="blog-empty">No recipes yet. Click <strong>New Recipe</strong> to add one.</div>
            ) : (
              <table className="blog-table">
                <thead><tr><th>Recipe</th><th>Category</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {recipes.map((r) => (
                    <tr key={r.id}>
                      <td className="blog-title-cell">
                        {r.image_url ? <img src={r.image_url} alt="" className="blog-thumb" /> : <div className="blog-thumb blog-thumb-empty" />}
                        <span>{r.title}</span>
                      </td>
                      <td>{r.category}</td>
                      <td><span className={`blog-badge ${r.status === 'published' ? 'is-live' : 'is-draft'}`}>{r.status}</span></td>
                      <td className="blog-actions">
                        <button className="btn btn-sm" onClick={() => startEdit(r)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => remove(r)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="blog-header">
            <h1>{editing.id ? 'Edit Recipe' : 'New Recipe'}</h1>
            <div className="blog-header-actions">
              <button className="btn" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Recipe'}</button>
            </div>
          </div>
          <div className="section-card blog-form">
            <div className="blog-row">
              <label className="blog-field"><span>Title</span>
                <input type="text" value={editing.title} onChange={(e) => upd({ title: e.target.value })} placeholder="Recipe name" />
              </label>
              <label className="blog-field"><span>Category</span>
                <select value={editing.category} onChange={(e) => upd({ category: e.target.value })}>
                  {RECIPE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <label className="blog-field"><span>Short description <em>(shown on the recipe cards)</em></span>
              <textarea rows={2} value={editing.short_description} onChange={(e) => upd({ short_description: e.target.value })} placeholder="One sentence..." />
            </label>
            <label className="blog-field"><span>Full description <em>(shown on the recipe page)</em></span>
              <textarea rows={3} value={editing.description} onChange={(e) => upd({ description: e.target.value })} placeholder="A short paragraph..." />
            </label>
            <div className="blog-field">
              <span>Photo</span>
              <div className="cover-upload">
                {editing.image_url ? <img src={editing.image_url} alt="recipe" className="cover-preview" /> : <div className="cover-placeholder">No image yet</div>}
                <div className="cover-buttons">
                  <input id="rc-coverfile" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadCover(e.target.files[0])} />
                  <button type="button" className="btn btn-sm" onClick={() => document.getElementById('rc-coverfile').click()}>Upload photo</button>
                  {editing.image_url ? <button type="button" className="btn btn-sm" onClick={() => upd({ image_url: '' })}>Remove</button> : null}
                </div>
              </div>
            </div>
            <div className="blog-row">
              <label className="blog-field"><span>Photo alt text <em>(for accessibility)</em></span>
                <input type="text" value={editing.image_alt} onChange={(e) => upd({ image_alt: e.target.value })} placeholder="Describe the photo" />
              </label>
              <label className="blog-field"><span>Serves / best for <em>(optional)</em></span>
                <input type="text" value={editing.serves || ''} onChange={(e) => upd({ serves: e.target.value })} placeholder="e.g. Serves 4" />
              </label>
            </div>
            <div className="blog-field"><span>Badges <em>(e.g. High protein, Low carb)</em></span>
              <ListField items={editing.badges} onChange={(v) => upd({ badges: v })} placeholder="Badge" addLabel="Add badge" />
            </div>
            <div className="blog-field"><span>Ingredients</span>
              <ListField items={editing.ingredients} onChange={(v) => upd({ ingredients: v })} placeholder="1 cup..." addLabel="Add ingredient" />
            </div>
            <div className="blog-field"><span>Directions</span>
              <ListField items={editing.directions} onChange={(v) => upd({ directions: v })} placeholder="Step..." addLabel="Add step" />
            </div>
            <div className="blog-field"><span>Nutrition <em>(label and value, e.g. Protein / 20g)</em></span>
              <div className="rc-list">
                {editing.nutritionRows.map((r, i) => (
                  <div className="rc-row rc-nut" key={i}>
                    <input value={r.label} placeholder="Protein" onChange={(e) => nutRow(i, { label: e.target.value })} />
                    <input value={r.value} placeholder="20g" onChange={(e) => nutRow(i, { value: e.target.value })} />
                    <button type="button" className="rc-x" onClick={() => upd({ nutritionRows: editing.nutritionRows.filter((_, k) => k !== i) })} aria-label="Remove">&times;</button>
                  </div>
                ))}
                <button type="button" className="btn btn-sm" onClick={() => upd({ nutritionRows: [...editing.nutritionRows, { label: '', value: '' }] })}>+ Add nutrition fact</button>
              </div>
            </div>
            <label className="blog-field" style={{ maxWidth: '280px' }}><span>Status</span>
              <select value={editing.status} onChange={(e) => upd({ status: e.target.value })}>
                <option value="published">Published (live on site)</option>
                <option value="draft">Draft (not visible)</option>
              </select>
            </label>
          </div>
        </>
      )}
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false, section = null }) {
  const { user, loading, hasSection } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) return <div className="login-page"><div className="loading-spinner"><div className="spinner"></div></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  if (section && !hasSection(section)) return (
    <div className="main-layout">
      <Sidebar isOpen={false} onClose={() => {}} />
      <main className="main-content">
        <div className="section-card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ marginBottom: '10px' }}>Access Restricted</h2>
          <p style={{ color: 'var(--text-muted)' }}>You don't have permission to access this section. Contact your administrator to update your role.</p>
        </div>
      </main>
    </div>
  );
  return (
    <div className="main-layout">
      <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
      </button>
      {mobileMenuOpen && <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />}
      <Sidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <main className="main-content">{children}</main>
    </div>
  );
}

function getAdminHome(u) {
  if (!u || u.role !== 'admin') return '/dashboard';
  if (!u.allowedSections) return '/admin/users';
  const map = { patients: '/admin/users', scheduling: '/admin/calendar', commerce: '/admin/products', analytics: '/admin/metrics', patient_board: '/admin/patient-board', patient_sources: '/admin/patient-sources', reminders: '/admin/reminders', ghl: '/admin/campaigns', settings: '/admin/admins', audit_log: '/admin/audit-log', api_keys: '/admin/api-keys', guide: '/admin/guide' };
  for (const s of u.allowedSections) { if (map[s]) return map[s]; }
  return '/admin/guide';
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="login-page"><div className="loading-spinner"><div className="spinner"></div></div></div>;
  // Block all navigation if password change is required
  if (user && user.mustChangePassword) return <ForceChangePasswordScreen />;
  const adminHome = user ? getAdminHome(user) : '/login';
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === 'admin' ? adminHome : '/dashboard'} replace /> : <LoginPage />} />
      <Route path="/intake" element={user ? <Navigate to={user.role === 'admin' ? adminHome : '/dashboard'} replace /> : <IntakeFormPage />} />
      <Route path="/book" element={<PublicBookingPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/schedule-assessment" element={<FreeAssessmentPage />} />
      <Route path="/quick-book" element={<QuickBookPage />} />
      <Route path="/appointment/manage/:token" element={<ManageAppointmentPage />} />
      <Route path="/sign/:token" element={<PublicSigningPage />} />
      <Route path="/nutrition/:token" element={<PublicNutritionPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
      <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
      <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
      <Route path="/memberships" element={<ProtectedRoute><MembershipsPage /></ProtectedRoute>} />
      <Route path="/my-orders" element={<ProtectedRoute><MyOrdersPage /></ProtectedRoute>} />
      <Route path="/my-products" element={<ProtectedRoute><MyProductsPage /></ProtectedRoute>} />
      <Route path="/my-info" element={<ProtectedRoute><MyInfoPage /></ProtectedRoute>} />
      <Route path="/new-intake" element={<ProtectedRoute><PatientIntakeFormPage /></ProtectedRoute>} />
      <Route path="/my-intake-forms" element={<ProtectedRoute><MyIntakeFormsPage /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
      <Route path="/my-referrals" element={<ProtectedRoute><MyReferralsPage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/book-appointment" element={<ProtectedRoute><PatientBookingPage /></ProtectedRoute>} />
      <Route path="/my-appointments" element={<ProtectedRoute><PatientAppointmentsPage /></ProtectedRoute>} />
      <Route path="/admin/calendar" element={<ProtectedRoute adminOnly section="scheduling"><AdminCalendarPage /></ProtectedRoute>} />
      <Route path="/admin/needs-review" element={<ProtectedRoute adminOnly section="scheduling"><AdminNeedsReviewPage /></ProtectedRoute>} />
      <Route path="/admin/reminders" element={<ProtectedRoute adminOnly section="reminders"><AdminRemindersPage /></ProtectedRoute>} />
      <Route path="/admin/scheduling" element={<ProtectedRoute adminOnly section="scheduling"><AdminSchedulingServicesPage /></ProtectedRoute>} />
      <Route path="/admin/scheduling/rooms" element={<ProtectedRoute adminOnly section="scheduling"><AdminRoomsPage /></ProtectedRoute>} />
      <Route path="/admin/scheduling/availability" element={<ProtectedRoute adminOnly section="scheduling"><AdminAvailabilityPage /></ProtectedRoute>} />
      <Route path="/admin/scheduling/time-blocks" element={<ProtectedRoute adminOnly section="scheduling"><AdminTimeBlocksPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly section="patients"><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/intake-reviews" element={<ProtectedRoute adminOnly section="patients"><IntakeReviewsPage /></ProtectedRoute>} />
      <Route path="/admin/nutrition-reviews" element={<ProtectedRoute adminOnly section="patients"><AdminNutritionReviewsPage /></ProtectedRoute>} />
      <Route path="/admin/metrics" element={<ProtectedRoute adminOnly section="analytics"><AdminMetricsPage /></ProtectedRoute>} />
      <Route path="/admin/patient-sources" element={<ProtectedRoute adminOnly section="patient_sources"><AdminPatientSourcesPage /></ProtectedRoute>} />
      <Route path="/admin/patient-board" element={<ProtectedRoute adminOnly section="patient_board"><AdminPatientBoardPage /></ProtectedRoute>} />
      <Route path="/admin/campaigns" element={<ProtectedRoute adminOnly section="analytics"><AdminCampaignsPage /></ProtectedRoute>} />
      <Route path="/admin/coupons" element={<ProtectedRoute adminOnly section="commerce"><AdminCouponsPage /></ProtectedRoute>} />
      <Route path="/admin/doctor-queue" element={<ProtectedRoute adminOnly section="patients"><DoctorQueuePage /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute adminOnly section="commerce"><AdminOrdersPage /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute adminOnly section="commerce"><AdminProductsPage /></ProtectedRoute>} />
      <Route path="/admin/membership-plans" element={<ProtectedRoute adminOnly section="commerce"><AdminMembershipPlansPage /></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute adminOnly section="audit_log"><AdminAuditLogPage /></ProtectedRoute>} />
      <Route path="/admin/admins" element={<ProtectedRoute adminOnly section="settings"><AdminManagementPage /></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute adminOnly section="settings"><AdminRolesPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute adminOnly section="settings"><AdminSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/nutrition-roadmaps" element={<ProtectedRoute adminOnly section="patients"><AdminNutritionRoadmapsPage /></ProtectedRoute>} />
      <Route path="/admin/patient-reminder" element={<ProtectedRoute adminOnly section="patients"><AdminPatientReminderPage /></ProtectedRoute>} />
      <Route path="/admin/webhook-logs" element={<ProtectedRoute adminOnly section="settings"><AdminWebhookLogsPage /></ProtectedRoute>} />
      <Route path="/admin/api-keys" element={<ProtectedRoute adminOnly section="api_keys"><AdminApiKeysPage /></ProtectedRoute>} />
      <Route path="/admin/report-issue" element={<ProtectedRoute adminOnly section="settings"><AdminReportIssuePage /></ProtectedRoute>} />
      <Route path="/admin/guide" element={<ProtectedRoute adminOnly section="guide"><AdminGuidePage /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute adminOnly section="content"><AdminBlogPage /></ProtectedRoute>} />
      <Route path="/admin/recipes" element={<ProtectedRoute adminOnly section="content"><AdminRecipePage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to={user ? (user.role === 'admin' ? adminHome : '/dashboard') : '/login'} replace />} />
    </Routes>
  );
}

// Capture ?ref= as early as possible — before routing, before auth — so the code
// survives a redirect to login or a bounce through the booking flow.
captureReferralFromUrl();

// Patient-facing referral hub. Don's requirement: visibility for the referrer,
// not just the clinic. Reward totals arrive in Phase 3/4 — this ships the link
// and the referral list so patients can start sharing immediately.
function MyReferralsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/api/my/referral').then(setData).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, []);

  const copy = async () => {
    if (!data || !data.referral_link) return;
    try {
      await navigator.clipboard.writeText(data.referral_link);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard blocked — the link is visible and selectable anyway */ }
  };

  if (loading) return <div className="page"><div className="loading-spinner"><div className="spinner"></div></div></div>;
  if (err) return <div className="page"><div className="empty-state"><h3>Could not load your referrals</h3><p>{err}</p></div></div>;

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">Refer a Friend</h1></div>

      <div className="card" style={{ marginBottom: '16px' }}>
        <h3 style={{ marginTop: 0 }}>Your referral link</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: 0 }}>
          Share this link with friends and family. When they book through it, we'll know they came from you.
        </p>
        {/* The benefit belongs to the friend, not the person sharing. Say exactly
            that — anything hinting at a reward for referring would misrepresent
            the program. */}
        <div style={{ padding: '12px 14px', borderRadius: '8px', background: 'var(--bg-alt, #f5f5f5)', fontSize: '14px', marginTop: '4px' }}>
          <strong>What your friend gets:</strong> a free assessment with a body composition
          scan and time with one of our coaches — and if they decide to start with us,
          <strong> $25 off a first purchase of $250–$499, or $50 off $500 or more</strong>.
          One time, at their first visit.
        </div>
        {data.referral_link ? (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' }}>
              <code style={{ flex: '1 1 280px', padding: '10px 12px', background: 'var(--bg-alt, #f5f5f5)', borderRadius: '8px', wordBreak: 'break-all', fontSize: '13px' }}>
                {data.referral_link}
              </code>
              <button className="btn btn-primary" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
            </div>
            <div style={{ marginTop: '10px', fontSize: '14px' }}>
              Your code: <strong style={{ letterSpacing: '0.5px' }}>{data.referral_code}</strong>
            </div>
          </>
        ) : <p>Your referral link is being set up — please check back shortly.</p>}
      </div>

      <div className="stats-grid" style={{ marginBottom: '16px' }}>
        <div className="stat-card"><div className="stat-value">{data.total_referred}</div><div className="stat-label">People referred</div></div>
        <div className="stat-card"><div className="stat-value">{data.total_purchased}</div><div className="stat-label">Who became clients</div></div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Your referrals</h3>
        {data.referred.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px 12px' }}>
            <div className="empty-state-icon">🤝</div>
            <h3>No referrals yet</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Share your link above to get started.</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead><tr><th>Friend</th><th>Referred</th><th>Status</th></tr></thead>
            <tbody>
              {data.referred.map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.first_name}</strong></td>
                  <td style={{ fontSize: '13px' }}>{r.referred_at ? new Date(r.referred_at).toLocaleDateString('en-US', { timeZone: 'America/Denver', month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</td>
                  <td>
                    {r.has_purchased
                      ? <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#d1fae5', color: '#065f46' }}>Became a client</span>
                      : <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>Not yet purchased</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.referred_by && (
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '14px', marginBottom: 0 }}>
            You were referred by <strong>{data.referred_by}</strong>.
          </p>
        )}
      </div>
    </div>
  );
}

// Capture ?ref= as early as possible — before routing, before auth — so the code
// survives a redirect to login or a bounce through the booking flow.
captureReferralFromUrl();

function App() { return <BrowserRouter><AuthProvider><AppRoutes /></AuthProvider></BrowserRouter>; }
export default App;
